const { useState, useEffect, useMemo, useCallback, useRef } = React;

/* ============ Path-based router (History API) ============ */
const STATIC_ROUTES = ["contact","about","process","partners","careers","terms","privacy","cookies","book"];
const parsePath = () => {
  const p = window.location.pathname;
  const search = window.location.search;
  if (p === "/" || p === "") return { name: "home" };
  if (/^\/home-v2\/?$/.test(p)) {
    history.replaceState(null, "", `/${search}`);
    return { name: "home" };
  }
  const mp = p.match(/^\/product\/([\w-]+)/);
  if (mp) return { name: "product", id: mp[1] };
  const mc = p.match(/^\/category\/([\w-]+)/);
  if (mc) return { name: "category", id: mc[1] };
  if (/^\/custom\/?$/.test(p)) return { name: "custom" };
  if (/^\/white-label\/?$/.test(p)) return { name: "wlabel" };
  if (/^\/hubspot-for-saas\/?$/.test(p)) return { name: "saas" };
  if (/^\/academy-revenue-operations\/?$/.test(p)) return { name: "academy-revops" };
  if (/^\/hubspot-onboarding-packages\/?$/.test(p)) return { name: "onboarding-packages" };
  if (/^\/outbound-infrastructure\/?$/.test(p)) return { name: "outbound-infrastructure" };
  if (/^\/outbound-optimization\/?$/.test(p)) return { name: "outbound-optimization" };
  if (/^\/hubspot-admin\/?$/.test(p)) return { name: "admin" };
  if (/^\/plans\/?$/.test(p)) return { name: "plans" };
  if (/^\/hubspot-support-plans\/?$/.test(p)) return { name: "support" };
  if (/^\/hubspot-technical-developers\/?$/.test(p)) return { name: "techsupport" };
  if (/^\/hubspot-consultation\/?$/.test(p)) return { name: "consultation" };
  if (/^\/checkout\/thank-you\/?$/.test(p)) return { name: "thankyou" };
  if (/^\/checkout\/?$/.test(p)) return { name: "checkout" };
  if (/^\/contact-us\/?$/.test(p)) {
    history.replaceState(null, "", `/contact${search}`);
    return { name: "contact" };
  }
  /* SEO content routes — reached via /learn (footer) and direct links / search. */
  if (/^\/learn\/?$/.test(p)) return { name: "learn" };
  if (/^\/hubspot-solutions-partner\/?$/.test(p)) return { name: "pillar" };
  if (/^\/uk\/?$/.test(p)) return { name: "region", id: "uk" };
  if (/^\/us\/?$/.test(p)) return { name: "region", id: "us" };
  if (/^\/hubspot\/?$/.test(p)) return { name: "services" };
  const msvc = p.match(/^\/hubspot\/([\w-]+)/);
  if (msvc) return { name: "service", id: msvc[1] };
  const mi = p.match(/^\/industries\/([\w-]+)/);
  if (mi) return { name: "industry", id: mi[1] };
  const mm = p.match(/^\/migrate\/([\w-]+)/);
  if (mm) return { name: "migration", id: mm[1] };
  const mt = p.match(/^\/integrations\/([\w-]+)/);
  if (mt) return { name: "integration", id: mt[1] };
  const mg = p.match(/^\/glossary\/([\w-]+)/);
  if (mg) return { name: "glossary", id: mg[1] };
  if (/^\/case-studies\/?$/.test(p)) return { name: "case-studies" };
  const mcs = p.match(/^\/case-studies\/([\w-]+)/);
  if (mcs) return { name: "case-study", id: mcs[1] };
  /* Free interactive tools — /tools index + /tools/:slug per-tool pages. */
  if (/^\/tools\/?$/.test(p)) return { name: "tools" };
  const mtl = p.match(/^\/tools\/([\w-]+)/);
  if (mtl) return { name: "tool", id: mtl[1] };
  for (const r of STATIC_ROUTES) {
    if (new RegExp(`^/${r}/?$`).test(p)) return { name: r };
  }
  if (/^\/shop(\/|\?|$)/.test(p)) {
    const params = new URLSearchParams(search);
    let cat = params.get("cat") || "all";
    if (LEGACY_CATEGORY_REDIRECT[cat]) cat = LEGACY_CATEGORY_REDIRECT[cat];
    return {
      name: "shop",
      cat,
      sort: params.get("sort") || "featured",
      q: params.get("q") || "",
    };
  }
  return { name: "notfound", path: p };
};

/** Old category URLs → canonical slugs (Lifecycle / Campaigns / AI → HubSpot; Web → Development). */
const LEGACY_CATEGORY_REDIRECT = {
  lifecycle: "hubspot",
  campaigns: "hubspot",
  ai: "hubspot",
  web: "development",
};

const resolveCategoryPath = (path) => {
  const m = String(path).match(/^\/category\/([\w-]+)/);
  if (!m) return path;
  const nid = LEGACY_CATEGORY_REDIRECT[m[1]];
  return nid ? `/category/${nid}` : path;
};

let _setRoute = null;
const navigate = (path) => {
  const next = resolveCategoryPath(path);
  history.pushState(null, "", next);
  if (_setRoute) _setRoute(parsePath());
  window.scrollTo({ top: 0, behavior: "instant" });
};

const useRoute = () => {
  const [route, setRoute] = useState(() => {
    if (typeof window === "undefined") return { name: "home" };
    const p = window.location.pathname;
    const fixed = resolveCategoryPath(p);
    if (fixed !== p) history.replaceState(null, "", fixed);
    return parsePath();
  });
  useEffect(() => {
    _setRoute = setRoute;
    const p = window.location.pathname;
    const search = window.location.search || "";
    /* Normalize legacy /shop?cat= URLs in the address bar */
    if (p.startsWith("/shop")) {
      const params = new URLSearchParams(search);
      const raw = params.get("cat");
      if (raw && LEGACY_CATEGORY_REDIRECT[raw]) {
        params.set("cat", LEGACY_CATEGORY_REDIRECT[raw]);
        const qs = params.toString();
        history.replaceState(null, "", qs ? `/shop?${qs}` : "/shop");
        setRoute(parsePath());
      }
    }
    // Backward compat: redirect old #/ hash URLs to clean paths
    const hash = window.location.hash;
    if (hash && hash.startsWith("#/")) {
      history.replaceState(null, "", hash.slice(1));
      setRoute(parsePath());
    }
    const onPop = () => {
      const p = window.location.pathname;
      const fixed = resolveCategoryPath(p);
      if (fixed !== p) history.replaceState(null, "", fixed);
      setRoute(parsePath());
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("popstate", onPop);
    return () => { window.removeEventListener("popstate", onPop); _setRoute = null; };
  }, []);
  return route;
};
/* ---------- SEO: per-route document title, meta description, canonical, OG, JSON-LD ---------- */
const SITE_URL  = "https://vestalhub.com";
const SITE_NAME = "Vestal Hub";
const HOME_TITLE = "Vestal Hub — Revenue and pipeline systems built to scale";
const HOME_DESC  = "HubSpot done right. Outbound that books meetings. Whether you need one or both, we build systems that drive pipeline and revenue.";
const HOME_OG_IMAGE = `${SITE_URL}/vestal-og-v2.png`;

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};
const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};
const setJsonLd = (data) => {
  const el = document.getElementById("vh-route-jsonld");
  if (el) el.textContent = data ? JSON.stringify(data) : "{}";
};

/* hreflang region cluster. The homepage is x-default + global English; /us and /uk are
 * region-targeted equivalents. The cluster tags belong only on those three pages — every
 * other route is single-locale. On SPA nav we clear any inherited cluster tags and re-add
 * them only when landing on a cluster node, keeping SSR + client output consistent. */
const HREFLANG_CLUSTER = [
  { hreflang: "en",        href: `${SITE_URL}/` },
  { hreflang: "en-US",     href: `${SITE_URL}/us` },
  { hreflang: "en-GB",     href: `${SITE_URL}/uk` },
  { hreflang: "x-default", href: `${SITE_URL}/` },
];
const setHreflangCluster = (canonicalPath) => {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  if (canonicalPath === "/" || canonicalPath === "/us" || canonicalPath === "/uk") {
    for (const a of HREFLANG_CLUSTER) {
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", a.hreflang);
      el.setAttribute("href", a.href);
      document.head.appendChild(el);
    }
  }
};

const ROBOTS_INDEX   = "index, follow, max-image-preview:large, max-snippet:-1";
const ROBOTS_NOINDEX = "noindex, nofollow";

/* Shared JSON-LD builder for any content-page type (pillar, region, industry,
 * migration, integration, glossary, case study). Always emits an Article + a
 * BreadcrumbList; conditionally appends FAQPage when the entry has an faq
 * array. Returns a @graph with everything in one block. */
const articleAndFaqJsonld = (d, url) => {
  const graph = [
    {
      "@type": "Article",
      "headline": d.h1 || d.title,
      "description": d.desc,
      "url": url,
      "image": HOME_OG_IMAGE,
      "author":    { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL },
      "publisher": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL,
                     "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.webp` } },
      "inLanguage": "en",
      "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",  "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Learn", "item": `${SITE_URL}/learn` },
        { "@type": "ListItem", "position": 3, "name": d.h1 || d.title, "item": url },
      ],
    },
  ];
  if (Array.isArray(d.faq) && d.faq.length) {
    graph.push({
      "@type": "FAQPage",
      "mainEntity": d.faq.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
};

const useDocumentMeta = (route) => {
  useEffect(() => {
    let title = HOME_TITLE;
    let desc  = HOME_DESC;
    let path  = "/";
    let canonicalPath = "/";
    let image = HOME_OG_IMAGE;
    let jsonld = null;
    let noindex = false;

    if (route.name === "product") {
      const p = (window.PRODUCTS || []).find(x => x.id === route.id);
      if (p) {
        title = `${p.name} — ${SITE_NAME}`;
        desc  = (p.desc || HOME_DESC).slice(0, 220);
        path  = `/product/${p.id}`;
        canonicalPath = path;
        image = p.image
          ? (p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image.startsWith("/") ? "" : "/"}${p.image}`)
          : HOME_OG_IMAGE;
        const offer = {
          "@type": "Offer",
          "price": String(p.price || 0),
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "url": `${SITE_URL}${path}`,
          "seller": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL }
        };
        jsonld = {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "name": p.name,
              "description": p.desc || desc,
              "image": image,
              "sku": p.id,
              "category": p.category || "HubSpot service",
              "brand": { "@type": "Brand", "name": SITE_NAME },
              "offers": offer
            },
            {
              "@type": "Service",
              "name": p.name,
              "description": p.desc || desc,
              "image": image,
              "serviceType": p.category || "HubSpot service",
              "url": `${SITE_URL}${path}`,
              "provider": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL },
              "areaServed": ["US", "GB", "EU"],
              "offers": offer
            }
          ]
        };
      }
    } else if (route.name === "category") {
      const meta = (window.CATEGORY_META || {})[route.id];
      const label = meta?.name || route.id;
      title = `${label} services — ${SITE_NAME}`;
      desc  = meta?.sub || `Browse fixed-scope ${label.toLowerCase()} services. Priced like software, shipped by senior operators.`;
      path  = `/category/${route.id}`;
      canonicalPath = path;
    } else if (route.name === "shop") {
      title = route.q
        ? `Search "${route.q}" — ${SITE_NAME} shop`
        : `Browse all services — ${SITE_NAME} shop`;
      desc  = "25+ fixed-scope HubSpot, RevOps & AI plays. Pricing on every card. Add what you need to your cart and we email a SOW within one business day.";
      const qs = [];
      if (route.cat && route.cat !== "all") qs.push(`cat=${route.cat}`);
      if (route.q) qs.push(`q=${encodeURIComponent(route.q)}`);
      path  = `/shop${qs.length ? "?" + qs.join("&") : ""}`;
      // Canonical never includes filter params — every facet collapses to /shop.
      canonicalPath = "/shop";
    } else if (route.name === "custom") {
      title = `Custom work — hours packs & monthly retainers — ${SITE_NAME}`;
      desc  = "Senior HubSpot & RevOps operators on-tap. Hours packs (5/15/40h tiers) or monthly retainers. For work that doesn't fit a fixed SOW.";
      path  = "/custom";
      canonicalPath = path;
    } else if (route.name === "wlabel") {
      title = `White-Label HubSpot Development — Apps, Custom Code & CMS | ${SITE_NAME}`;
      desc  = "White-label technical HubSpot delivery for agencies and partners: HubSpot apps, custom code workflow actions, CMS themes, UI extensions, and integrations. 3 apps live on the HubSpot Marketplace, 1 CMS theme in production. We ship under your brand.";
      path  = "/white-label";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "name": "White-Label HubSpot Development",
            "serviceType": "White-label HubSpot technical delivery",
            "description": desc,
            "url": `${SITE_URL}${path}`,
            "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
            "areaServed": ["US", "GB", "EU"],
            "inLanguage": "en",
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "White-label HubSpot development", "item": `${SITE_URL}${path}` },
            ],
          },
        ],
      };
    } else if (route.name === "saas") {
      title = `HubSpot for SaaS & Tech Startups — Revenue, MRR & Pipeline | ${SITE_NAME}`;
      desc  = "HubSpot experts for SaaS and tech startups. 12+ years turning CRM, Stripe and product data into pipeline, MRR/ARR visibility and growth you can report on. Trusted by 100+ SaaS teams.";
      path  = "/hubspot-for-saas";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "name": "HubSpot for SaaS & Tech Startups",
            "serviceType": "HubSpot implementation & RevOps for SaaS",
            "description": desc,
            "url": `${SITE_URL}${path}`,
            "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
            "areaServed": ["US", "GB", "EU"],
            "audience": { "@type": "BusinessAudience", "name": "SaaS, fintech and tech startups" },
            "inLanguage": "en",
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "HubSpot for SaaS & tech startups", "item": `${SITE_URL}${path}` },
            ],
          },
        ],
      };
    } else if (route.name === "academy-revops") {
      title = `HubSpot for Professional Academies — Enquiry to Enrollment | ${SITE_NAME}`;
      desc  = "HubSpot for professional academies, certification providers and training companies. Clearer admissions, automatic LMS enrollment, learner engagement and reporting leadership can trust. Book a free consultation.";
      path  = "/academy-revenue-operations";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "name": "Academy Revenue Operations",
            "serviceType": "HubSpot revenue operations for professional academies",
            "description": desc,
            "url": `${SITE_URL}${path}`,
            "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
            "areaServed": ["US", "GB", "EU"],
            "audience": { "@type": "BusinessAudience", "name": "Professional academies, certification providers and training companies" },
            "inLanguage": "en",
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "HubSpot for professional academies", "item": `${SITE_URL}${path}` },
            ],
          },
        ],
      };
    } else if (route.name === "onboarding-packages") {
      title = `HubSpot Onboarding Packages — Proven Framework, Done For You | ${SITE_NAME}`;
      desc  = "Proven HubSpot onboarding framework used by 100+ companies. Done-for-you Sales, Marketing, Service and Operations Hub setup in 4–6 weeks — at HubSpot DIY prices or less. Book a free consultation.";
      path  = "/hubspot-onboarding-packages";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "name": "HubSpot Onboarding Packages",
            "serviceType": "Proven done-for-you HubSpot onboarding framework",
            "description": desc,
            "url": `${SITE_URL}${path}`,
            "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
            "areaServed": ["US", "GB", "EU"],
            "inLanguage": "en",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "HubSpot onboarding packages",
              "itemListElement": [
                { "@type": "Offer", "name": "Sales Hub Onboarding — Pro", "price": "1500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Sales Hub Onboarding — Enterprise", "price": "3000", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Marketing Hub Onboarding — Pro", "price": "3000", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Marketing Hub Onboarding — Enterprise", "price": "4500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Service Hub Onboarding — Pro", "price": "1500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Service Hub Onboarding — Enterprise", "price": "3000", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
                { "@type": "Offer", "name": "Operations Hub Onboarding — Pro", "price": "1500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              ],
            },
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "HubSpot onboarding packages", "item": `${SITE_URL}${path}` },
            ],
          },
        ],
      };
    } else if (route.name === "outbound-infrastructure") {
      title = `Outbound Infrastructure Setup | Instantly, Smartlead, Lemlist & HeyReach | ${SITE_NAME}`;
      desc  = "Build a production-ready cold outreach system with domains, mailboxes, deliverability, Instantly, Smartlead, Lemlist or HeyReach, HubSpot integration and campaign launch. Fixed-price implementation.";
      path  = "/outbound-infrastructure";
      canonicalPath = path;
      const obFaq = (window.OUTBOUND_FAQ || []);
      const obGraph = [
        {
          "@type": "Service",
          "name": "Outbound Infrastructure Setup",
          "serviceType": "Fixed-price cold outreach infrastructure implementation",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Outbound infrastructure setup packages",
            "itemListElement": [
              { "@type": "Offer", "name": "Infrastructure Launch", "price": "1490", "priceCurrency": "USD", "url": `${SITE_URL}${path}#setup-packages` },
              { "@type": "Offer", "name": "Outbound Launch", "price": "2990", "priceCurrency": "USD", "url": `${SITE_URL}${path}#setup-packages` },
              { "@type": "Offer", "name": "GTM Engine", "price": "4990", "priceCurrency": "USD", "url": `${SITE_URL}${path}#setup-packages` },
              { "@type": "Offer", "name": "LinkedIn + Email Multichannel Add-On", "price": "750", "priceCurrency": "USD", "url": `${SITE_URL}${path}#multichannel` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Outbound infrastructure", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (obFaq.length) {
        obGraph.push({
          "@type": "FAQPage",
          "mainEntity": obFaq.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": obGraph };
    } else if (route.name === "outbound-optimization") {
      title = `B2B Outbound Lead Generation & Appointment Setting | ${SITE_NAME}`;
      desc  = "Vestal Hub builds and manages B2B outbound campaigns end-to-end, from targeting and infrastructure to reply handling and qualified meeting booking.";
      path  = "/outbound-optimization";
      canonicalPath = path;
      const ooFaq = (window.OUTBOUND_OPTIMIZATION_FAQ || []);
      const ooGraph = [
        {
          "@type": "Service",
          "name": "Managed B2B Outbound Lead Generation",
          "serviceType": "B2B outbound lead generation and appointment setting",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Managed outbound engagement models",
            "itemListElement": [
              { "@type": "Offer", "name": "Fixed Monthly", "price": "3000", "priceCurrency": "USD", "url": `${SITE_URL}${path}#plans` },
              { "@type": "Offer", "name": "Pay Per Qualified Meeting — from $1,000/month + $300/meeting", "price": "1000", "priceCurrency": "USD", "url": `${SITE_URL}${path}#plans` },
              { "@type": "Offer", "name": "Revenue Partnership — base fee + % of generated revenue", "url": `${SITE_URL}${path}#plans` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Outbound infrastructure", "item": `${SITE_URL}/outbound-infrastructure` },
            { "@type": "ListItem", "position": 3, "name": "Managed B2B outbound", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (ooFaq.length) {
        ooGraph.push({
          "@type": "FAQPage",
          "mainEntity": ooFaq.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": ooGraph };
    } else if (route.name === "admin") {
      title = `HubSpot Admin & Support Plans — Done-For-You, Month-to-Month | ${SITE_NAME}`;
      desc  = "A dedicated HubSpot admin on tap. Month-to-month, done-for-you HubSpot support in 3 plans — Essentials (10h, $999), Growth (20h, $1,699) and Premium (40h, $2,999). No lock-in contracts. Fixes your setup and gets ROI in 30 days.";
      path  = "/hubspot-admin";
      canonicalPath = path;
      const adminFaq = (window.ADMIN_FAQ || []);
      const adminGraph = [
        {
          "@type": "Service",
          "name": "HubSpot Admin & Support",
          "serviceType": "Managed HubSpot administration & support",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "HubSpot support plans",
            "itemListElement": [
              { "@type": "Offer", "name": "Essentials (10 hours / month)", "price": "999", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Growth (20 hours / month)", "price": "1699", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Premium (40 hours / month)", "price": "2999", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "HubSpot admin & support", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (adminFaq.length) {
        adminGraph.push({
          "@type": "FAQPage",
          "mainEntity": adminFaq.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": adminGraph };
    } else if (route.name === "plans") {
      title = `HubSpot Onboarding, Support & Technical Projects — Plans & Pricing | ${SITE_NAME}`;
      desc  = "We make HubSpot easy for you. Three ways to work with a senior HubSpot expert: fixed-price onboarding, month-to-month ongoing support (from $1,699/mo), and fixed-quote technical projects. Every price published, no sales calls.";
      path  = "/plans";
      canonicalPath = path;
      const plansFaq = (window.PLANS_FAQ || []);
      const plansGraph = [
        {
          "@type": "Service",
          "name": "HubSpot Onboarding, Support & Technical Projects",
          "serviceType": "Done-for-you HubSpot onboarding, support and technical projects",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "HubSpot plans & pricing",
            "itemListElement": [
              { "@type": "Offer", "name": "Onboarding — Starter (one-time)", "price": "2500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Onboarding — Growth (one-time)", "price": "4200", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Onboarding — Full Platform (one-time)", "price": "5500", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Ongoing Support \u2014 Growth (20 hours / month)", "price": "1699", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Ongoing Support \u2014 Premium (40 hours / month)", "price": "2999", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Plans & pricing", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (plansFaq.length) {
        plansGraph.push({
          "@type": "FAQPage",
          "mainEntity": plansFaq.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": plansGraph };
    } else if (route.name === "support") {
      title = `HubSpot Support Plans — Month-to-Month, Done For You | ${SITE_NAME}`;
      desc  = "Ongoing HubSpot support from a senior expert. Month-to-month with no lock-in — Essentials (10h), Growth (20h), and Premium (40h). From $999/mo. Book a call to see if it fits.";
      path  = "/hubspot-support-plans";
      canonicalPath = path;
      const supportFaq = (window.PLANS_FAQ || []);
      const supportGraph = [
        {
          "@type": "Service",
          "name": "HubSpot Ongoing Support",
          "serviceType": "Done-for-you monthly HubSpot support",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "HubSpot support plans",
            "itemListElement": [
              { "@type": "Offer", "name": "Essentials (10 hours / month)", "price": "999", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Growth (20 hours / month)", "price": "1699", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Premium (40 hours / month)", "price": "2999", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "HubSpot support plans", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (supportFaq.length) {
        supportGraph.push({
          "@type": "FAQPage",
          "mainEntity": supportFaq.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": supportGraph };
    } else if (route.name === "techsupport") {
      title = `Technical & RevOps HubSpot Support Plans — Month-to-Month | ${SITE_NAME}`;
      desc  = "Ongoing technical HubSpot & RevOps support from a dedicated senior engineer. Custom code, API integrations, data engineering and complex RevOps — from $1,900/mo. Month-to-month, no lock-in. Book a technical scoping call.";
      path  = "/hubspot-technical-developers";
      canonicalPath = path;
      const techFaq = (window.TECH_SUPPORT_FAQ || []);
      const techGraph = [
        {
          "@type": "Service",
          "name": "HubSpot Technical & RevOps Support",
          "serviceType": "Done-for-you technical HubSpot & RevOps support",
          "description": desc,
          "url": `${SITE_URL}${path}`,
          "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "inLanguage": "en",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "HubSpot technical & RevOps support plans",
            "itemListElement": [
              { "@type": "Offer", "name": "Essential Care (monthly, up to 20h)", "price": "1900", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Growth Support (monthly, up to 40h)", "price": "3700", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
              { "@type": "Offer", "name": "Enterprise Plan (monthly, up to 60h)", "price": "4900", "priceCurrency": "USD", "url": `${SITE_URL}${path}` },
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Technical & RevOps HubSpot support", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (techFaq.length) {
        techGraph.push({
          "@type": "FAQPage",
          "mainEntity": techFaq.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": techGraph };
    } else if (route.name === "consultation") {
      title = `Free HubSpot Consultation with a Senior Expert — ${SITE_NAME}`;
      desc  = "30 minutes with a HubSpot Gold Solutions Partner. Bring your portal for a free, live audit and leave with a prioritized fix list and a fixed-price SOW if it fits. No discovery-call theatre.";
      path  = "/hubspot-consultation";
      canonicalPath = path;
      const consultFaq = (window.CONSULTATION_FAQ || []);
      const consultGraph = [
        {
          "@type": "WebPage",
          "name": "Free HubSpot consultation",
          "url": `${SITE_URL}${path}`,
          "description": desc,
          "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
        },
        {
          "@type": "Service",
          "name": "Free HubSpot Consultation",
          "serviceType": "HubSpot consultation",
          "description": "A 30-minute working session with a senior HubSpot expert. Free portal audit, prioritized fix list, and a fixed-price SOW if there's a fit.",
          "provider": { "@type": "Organization", "name": SITE_NAME, "url": SITE_URL },
          "areaServed": ["US", "GB", "EU"],
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": `${SITE_URL}${path}`,
          },
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Free HubSpot consultation", "item": `${SITE_URL}${path}` },
          ],
        },
      ];
      if (consultFaq.length) {
        consultGraph.push({
          "@type": "FAQPage",
          "mainEntity": consultFaq.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      jsonld = { "@context": "https://schema.org", "@graph": consultGraph };
    } else if (route.name === "checkout") {
      title = `Checkout — SOW within 1 business day — ${SITE_NAME}`;
      desc  = "Send your brief and receive a Statement of Work within one business day. No charge until signed. No discovery call required.";
      path  = "/checkout";
      canonicalPath = path;
      noindex = true;
    } else if (route.name === "thankyou") {
      title = `Brief received — ${SITE_NAME}`;
      desc  = "Your Statement of Work arrives within one business day.";
      path  = "/checkout/thank-you";
      canonicalPath = path;
      noindex = true;
    } else if (route.name === "contact") {
      title = `Contact — HubSpot help, or more pipeline? — ${SITE_NAME}`;
      desc  = "Same team either way. Choose HubSpot help or lead generation, send a few lines, we reply in one business day.";
      path  = "/contact";
      canonicalPath = path;
    } else if (route.name === "about") {
      title = `About — your dedicated HubSpot team — ${SITE_NAME}`;
      desc  = "HubSpot Gold Partner. We become your dedicated HubSpot team — one senior expert, month-to-month, no lock-in. Trusted by 100+ companies across SaaS, agencies, services, finance, and nonprofits.";
      path  = "/about";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "AboutPage",
            "name": `About ${SITE_NAME}`,
            "url": `${SITE_URL}${path}`,
            "description": desc,
            "mainEntity": { "@id": `${SITE_URL}/#organization` },
            "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
          },
          {
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`,
            "name": SITE_NAME,
            "url": SITE_URL,
            "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.webp` },
            "slogan": "We make HubSpot easy for you",
            "award": "HubSpot Gold Solutions Partner",
            "description": "HubSpot Gold Solutions Partner. A dedicated senior HubSpot expert on a flat monthly price — ongoing HubSpot and RevOps support, month-to-month with no lock-in, shipped by senior operators only.",
            "areaServed": ["US", "GB", "EU"],
            "knowsAbout": [
              "HubSpot", "HubSpot Onboarding", "HubSpot CRM Migration",
              "Salesforce to HubSpot Migration", "Pipedrive to HubSpot Migration",
              "Marketo to HubSpot Migration", "Revenue Operations", "RevOps",
              "Lead Scoring", "Marketing Attribution", "Lifecycle Stages",
              "HubSpot Workflows", "HubSpot Breeze AI", "Model Context Protocol",
              "HubSpot Stripe Integration", "HubSpot Salesforce Sync",
            ],
            "sameAs": [
              "https://www.linkedin.com/company/vestalhub/",
              "https://ecosystem.hubspot.com/marketplace/solutions/vestalhub",
            ],
            "contactPoint": [{
              "@type": "ContactPoint",
              "email": SOCIAL.email,
              "contactType": "customer service",
              "availableLanguage": ["en", "es"],
            }],
          },
        ],
      };
    } else if (route.name === "process") {
      title = `Process — how we ship — ${SITE_NAME}`;
      desc  = "The 4-stage delivery model behind every Vestal Hub engagement. Browse, brief, sign, ship. No surprises.";
      path  = "/process";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How Vestal Hub ships HubSpot work",
        "description": "From brief to live HubSpot delivery in 4 steps: pick or brief, SOW in 24 hours, sign and kick off in 7–14 days, weekly progress until done.",
        "url": `${SITE_URL}${path}`,
        "totalTime": "P14D",
        "step": [
          { "@type": "HowToStep", "position": 1, "name": "Pick services or send a brief",
            "text": "Browse the catalog and add fixed-scope services to your cart, or send a 3-line brief if your work doesn't fit a SKU. Bundles and add-ons stack at checkout. No sales call required.",
            "url": `${SITE_URL}${path}#step-1`,
          },
          { "@type": "HowToStep", "position": 2, "name": "SOW in 24 hours",
            "text": "For fixed-scope catalog services, you receive a signed-ready Statement of Work within 24 hours — exact deliverables, milestones, dates, and price. No surprises, no change orders. You don't pay until you sign.",
            "url": `${SITE_URL}${path}#step-2`,
          },
          { "@type": "HowToStep", "position": 3, "name": "Sign and kick off in 7–14 days",
            "text": "A senior operator (8+ years HubSpot / RevOps) is assigned within 24h of signing. We schedule a 30-minute kickoff to confirm goals, share access, and lock the timeline.",
            "url": `${SITE_URL}${path}#step-3`,
          },
          { "@type": "HowToStep", "position": 4, "name": "Weekly progress, fixed cadence",
            "text": "Every Monday: a written update on what shipped, what's next, what we need from you. Slack channel for the duration. No status meetings unless you want one.",
            "url": `${SITE_URL}${path}#step-4`,
          },
        ],
      };
    } else if (route.name === "partners") {
      title = `Partner program — ${SITE_NAME}`;
      desc  = "Refer or co-sell with Vestal Hub. For agencies, RevOps consultants, and HubSpot peers.";
      path  = "/partners";
      canonicalPath = path;
    } else if (route.name === "careers") {
      title = `Careers — senior HubSpot & RevOps operators — ${SITE_NAME}`;
      desc  = "We hire senior operators who've already shipped this work. No utilization targets, no juniors-shadowing-seniors model.";
      path  = "/careers";
      canonicalPath = path;
    } else if (route.name === "book") {
      title = `Book a call — HubSpot help, or more pipeline? — ${SITE_NAME}`;
      desc  = "Same team either way. Pick a 30-minute slot for HubSpot help or lead generation — no obligation.";
      path  = "/book";
      canonicalPath = path;
    } else if (route.name === "terms") {
      title = `Terms of service — ${SITE_NAME}`;
      desc  = "Vestal Hub terms of service.";
      path  = "/terms";
      canonicalPath = path;
    } else if (route.name === "privacy") {
      title = `Privacy policy — ${SITE_NAME}`;
      desc  = "How Vestal Hub handles your data.";
      path  = "/privacy";
      canonicalPath = path;
    } else if (route.name === "cookies") {
      title = `Cookies — ${SITE_NAME}`;
      desc  = "Vestal Hub cookies policy.";
      path  = "/cookies";
      canonicalPath = path;
    } else if (route.name === "learn") {
      const d = window.LEARN_HUB || {};
      title = d.title || `Learn — ${SITE_NAME}`;
      desc  = d.desc  || "Pillar guides, industry deep-dives, migration playbooks, integration guides, glossary and case studies for HubSpot teams.";
      path  = "/learn";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": d.h1 || "HubSpot & RevOps resources",
        "description": desc,
        "url": `${SITE_URL}${path}`,
      };
    } else if (route.name === "pillar") {
      const d = window.PILLAR || {};
      title = d.title || `HubSpot Solutions Partner — ${SITE_NAME}`;
      desc  = d.desc  || HOME_DESC;
      path  = "/hubspot-solutions-partner";
      canonicalPath = path;
      jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
    } else if (route.name === "region") {
      const d = (window.REGIONS || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/${d.id}`;
        canonicalPath = path;
        jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
      }
    } else if (route.name === "services") {
      const list = (window.SERVICES || []);
      title = "HubSpot Services — Implementation, RevOps, Integrations & AI | Vestal Hub";
      desc  = "Every HubSpot service we deliver as a Gold Solutions Partner — implementation, onboarding, RevOps, migrations, integrations, custom development and AI. Browse by what you need; talk to a senior operator.";
      path  = "/hubspot";
      canonicalPath = path;
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            "name": "HubSpot services",
            "url": `${SITE_URL}${path}`,
            "description": desc,
            "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "HubSpot services", "item": `${SITE_URL}${path}` },
            ],
          },
          {
            "@type": "ItemList",
            "itemListElement": list.map((s, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `${SITE_URL}/hubspot/${s.id}`,
              "name": s.name,
            })),
          },
        ],
      };
    } else if (route.name === "service") {
      const d = (window.SERVICES || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/hubspot/${d.id}`;
        canonicalPath = path;
        const graph = [
          {
            "@type": "Service",
            "name": d.name,
            "serviceType": d.serviceType || d.name,
            "description": d.desc,
            "url": `${SITE_URL}${path}`,
            "provider": { "@type": "Organization", "@id": `${SITE_URL}/#organization`, "name": SITE_NAME, "url": SITE_URL },
            "areaServed": ["US", "GB", "EU"],
            "inLanguage": "en",
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "HubSpot services", "item": `${SITE_URL}/hubspot` },
              { "@type": "ListItem", "position": 3, "name": d.name, "item": `${SITE_URL}${path}` },
            ],
          },
        ];
        if (Array.isArray(d.faq) && d.faq.length) {
          graph.push({
            "@type": "FAQPage",
            "mainEntity": d.faq.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          });
        }
        jsonld = { "@context": "https://schema.org", "@graph": graph };
      } else { noindex = true; }
    } else if (route.name === "industry") {
      const d = (window.INDUSTRIES || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/industries/${d.id}`;
        canonicalPath = path;
        jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
      } else { noindex = true; }
    } else if (route.name === "migration") {
      const d = (window.MIGRATIONS || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/migrate/${d.id}`;
        canonicalPath = path;
        jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
      } else { noindex = true; }
    } else if (route.name === "integration") {
      const d = (window.INTEGRATIONS || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/integrations/${d.id}`;
        canonicalPath = path;
        jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
      } else { noindex = true; }
    } else if (route.name === "glossary") {
      const d = (window.GLOSSARY || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/glossary/${d.id}`;
        canonicalPath = path;
        const base = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
        const graph = (base["@graph"] || []).concat([{
          "@type": "DefinedTerm",
          "name": d.term,
          "description": d.desc,
          "url": `${SITE_URL}${path}`,
          "inDefinedTermSet": { "@type": "DefinedTermSet", "name": `${SITE_NAME} HubSpot Glossary`, "url": `${SITE_URL}/learn` },
        }]);
        jsonld = { "@context": "https://schema.org", "@graph": graph };
      } else { noindex = true; }
    } else if (route.name === "case-studies") {
      title = `Case Studies — Results You Can Measure | ${SITE_NAME}`;
      desc  = "More meetings. More pipeline. More revenue. See the measurable business impact we’ve delivered for companies across SaaS, services, education, and more.";
      path  = "/case-studies";
      canonicalPath = path;
      const studies = (window.CASE_STUDIES || []).filter((x) => x && !x.hidden)
        .slice()
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            "name": "HubSpot case studies",
            "url": `${SITE_URL}${path}`,
            "description": desc,
            "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
              { "@type": "ListItem", "position": 2, "name": "Case studies", "item": `${SITE_URL}${path}` },
            ],
          },
          {
            "@type": "ItemList",
            "itemListElement": studies.map((s, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `${SITE_URL}/case-studies/${s.id}`,
              "name": s.company,
            })),
          },
        ],
      };
    } else if (route.name === "case-study") {
      const d = (window.CASE_STUDIES || []).find(r => r.id === route.id);
      if (d) {
        title = d.title; desc = d.desc;
        path  = `/case-studies/${d.id}`;
        canonicalPath = path;
        jsonld = articleAndFaqJsonld(d, `${SITE_URL}${path}`);
      } else { noindex = true; }
    } else if (route.name === "tools") {
      title = "Free HubSpot Tools — Calculators, Pickers, Generators | Vestal Hub";
      desc  = "Free interactive HubSpot tools by a HubSpot Gold Solutions Partner: tier picker, migration cost estimator, lead scoring template generator. Use immediately, no signup.";
      path  = "/tools";
      canonicalPath = path;
      const tools = window.TOOLS || [];
      jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            name: "Free HubSpot tools",
            url: `${SITE_URL}${path}`,
            description: desc,
            isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE_URL}/learn` },
              { "@type": "ListItem", position: 3, name: "Free tools", item: `${SITE_URL}${path}` },
            ],
          },
          {
            "@type": "ItemList",
            itemListElement: tools.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}/tools/${t.id}`,
              name: t.name,
            })),
          },
        ],
      };
    } else if (route.name === "tool") {
      const t = (window.TOOLS || []).find(r => r.id === route.id);
      if (t) {
        title = t.title; desc = t.desc;
        path  = `/tools/${t.id}`;
        canonicalPath = path;
        const graph = [
          {
            "@type": "WebApplication",
            name: t.h1 || t.name,
            description: t.desc,
            url: `${SITE_URL}${path}`,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Any",
            browserRequirements: "Requires JavaScript",
            isAccessibleForFree: true,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            inLanguage: "en",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE_URL}/learn` },
              { "@type": "ListItem", position: 3, name: "Free tools", item: `${SITE_URL}/tools` },
              { "@type": "ListItem", position: 4, name: t.name, item: `${SITE_URL}${path}` },
            ],
          },
        ];
        if (t.faq && t.faq.length) {
          graph.push({
            "@type": "FAQPage",
            mainEntity: t.faq.map(f => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          });
        }
        jsonld = { "@context": "https://schema.org", "@graph": graph };
      } else { noindex = true; }
    } else if (route.name === "notfound") {
      title = `Page not found — ${SITE_NAME}`;
      desc  = "The page you were looking for doesn't exist. Browse our HubSpot & RevOps services instead.";
      path  = "/404";
      canonicalPath = "/";
      noindex = true;
    }

    const fullUrl = `${SITE_URL}${path}`;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    document.title = title;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[name="robots"]',      "content", noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX);
    setLink("canonical", canonicalUrl);
    setHreflangCluster(canonicalPath);
    setMeta('meta[property="og:title"]',       "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]',         "content", canonicalUrl);
    setMeta('meta[property="og:image"]',       "content", image);
    setMeta('meta[name="twitter:title"]',      "content", title);
    setMeta('meta[name="twitter:description"]',"content", desc);
    setMeta('meta[name="twitter:image"]',      "content", image);
    setJsonLd(jsonld);

    // GA4 SPA pageview (auto pageview is disabled in index.html so we fire here).
    // PostHog auto-captures via capture_pageview: 'history_change' — no manual call needed.
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_title: title,
        page_location: fullUrl,
        page_path: path,
      });
    }
  }, [route.name, route.id, route.cat, route.q, route.sort, route.path]);
};

const goToProduct  = (id) => { navigate(`/product/${id}`); };
const goToCategory = (id) => { navigate(`/category/${id}`); };
// /shop 301-redirects to /hubspot at the edge (vercel.json); keep the SPA
// helper consistent so client-side navigation lands on the services index too.
const goToShop     = () => { navigate(`/hubspot`); };
const goToCustom   = () => { navigate(`/custom`); };
const goToWhiteLabel = () => { navigate(`/white-label`); };
const goToSaas = () => { navigate(`/hubspot-for-saas`); };
const goToAcademyRevops = () => { navigate(`/academy-revenue-operations`); };
const goToAdmin = () => { navigate(`/hubspot-admin`); };
const goToPlans = () => { navigate(`/plans`); };
const goToSupport = () => { navigate(`/hubspot-support-plans`); };
const goToOnboardingPackages = () => { navigate(`/hubspot-onboarding-packages`); };
const goToOutboundInfrastructure = () => { navigate(`/outbound-infrastructure`); };
const goToOutboundOptimization = () => { navigate(`/outbound-optimization`); };

const trackOutbound = (event, props = {}) => {
  try { window.gtag && window.gtag("event", event, props); } catch {}
  try { window.posthog && window.posthog.capture(event, props); } catch {}
};
const goToTechSupport = () => { navigate(`/hubspot-technical-developers`); };
const goToConsultation = () => { navigate(`/hubspot-consultation`); };
const goToContact  = () => { navigate(`/contact`); };
const goToCheckout = () => { navigate(`/checkout`); };
const goToThankYou = () => { navigate(`/checkout/thank-you`); };
const goToAbout    = () => { navigate(`/about`); };
const goToProcess  = () => { navigate(`/process`); };
const goToPartners = () => { navigate(`/partners`); };
const goToCareers  = () => { navigate(`/careers`); };
const goToTerms    = () => { navigate(`/terms`); };
const goToPrivacy  = () => { navigate(`/privacy`); };
const goToCookies  = () => { navigate(`/cookies`); };
const goHome       = () => { navigate("/"); };

/* Contact-form anti-spam. Keep heuristics in sync with api/_lib/spam.js.
 *
 * HubSpot Collected Forms (tracking script) serializes .contact-form on
 * submit-button click, independently of /api/contact. Real leads should
 * still be collected; spam clicks are stopped at window-capture so HubSpot
 * never sees them. The bot still gets a fake success via vh-spam-ok. */
const CONTACT_FORM_STARTED = Date.now();
const CONTACT_SPAM_PREFIX =
  /^(HubSpot |Free consultation|White-label inquiry|Service enquiry|Biggest HubSpot|Project details:|What they need:|\(no message\)|\(not specified\))/i;
const contactLooksGenerated = (s) => {
  const t = String(s || "").trim();
  if (t.length < 14) return false;
  if (/\s/.test(t)) return false;
  if (!/^[A-Za-z0-9]+$/.test(t)) return false;
  return /[a-z]/.test(t) && /[A-Z]/.test(t);
};
const contactSpamReason = (payload = {}) => {
  const name    = String(payload.name    || "").trim();
  const email   = String(payload.email   || "").trim();
  const company = String(payload.company || "").trim();
  const message = String(payload.message || "").trim();
  const website = String(payload.website || payload.company_url || "").trim();
  const started = Number(payload.t);
  if (website) return "honeypot";
  if (Number.isFinite(started) && started > 0) {
    const elapsed = Date.now() - started;
    if (elapsed >= 0 && elapsed < 1500) return "too-fast";
  }
  const [local = "", domain = ""] = email.toLowerCase().split("@");
  if ((domain === "gmail.com" || domain === "googlemail.com") &&
      (local.includes("..") || (local.match(/\./g) || []).length >= 3)) {
    return "dotted-gmail";
  }
  const body = message.split(/\n/).map((l) => l.trim()).filter(Boolean)
    .filter((l) => !CONTACT_SPAM_PREFIX.test(l)).join(" ");
  if ([name, company, body].filter(contactLooksGenerated).length >= 2) return "gibberish";
  return null;
};
const fieldsFromContactForm = (form) => {
  const email = form.querySelector('input[type="email"]')?.value || "";
  const texts = [...form.querySelectorAll('input[type="text"]')]
    .filter((el) => el.name !== "company_url")
    .map((el) => el.value.trim());
  return {
    name: texts[0] || "",
    email,
    company: texts[1] || "",
    message: form.querySelector("textarea")?.value || "",
    website: form.querySelector('input[name="company_url"]')?.value || "",
    t: CONTACT_FORM_STARTED,
  };
};
if (!window.__vhContactSpamGuard) {
  window.__vhContactSpamGuard = true;
  const isSubmitControl = (el, form) => {
    if (!el || !form.contains(el)) return false;
    if (el.tagName === "BUTTON") return !el.type || el.type.toLowerCase() === "submit";
    if (el.tagName === "INPUT") return (el.type || "").toLowerCase() === "submit";
    return false;
  };
  const blockSpamFromHubSpot = (e) => {
    let form = null;
    if (e.type === "submit") {
      form = e.target?.classList?.contains("contact-form") ? e.target : null;
    } else if (e.type === "click") {
      form = e.target?.closest?.("form.contact-form") || null;
      if (!form) return;
      if (!isSubmitControl(e.target.closest("button, input"), form)) return;
    }
    if (!form) return;
    if (!contactSpamReason(fieldsFromContactForm(form))) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (form.dataset.vhSpamOk) return;
    form.dataset.vhSpamOk = "1";
    form.dispatchEvent(new CustomEvent("vh-spam-ok"));
  };
  window.addEventListener("submit", blockSpamFromHubSpot, true);
  window.addEventListener("click", blockSpamFromHubSpot, true);
}
const HoneypotField = () => (
  <div className="hp-field" aria-hidden="true">
    <label>
      Company website
      <input type="text" name="company_url" tabIndex={-1} autoComplete="off" defaultValue=""/>
    </label>
  </div>
);
const GuardedForm = ({ className, onSubmit, children }) => {
  const ref = useRef(null);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  useEffect(() => {
    const form = ref.current;
    if (!form) return;
    const pass = () => onSubmitRef.current({
      preventDefault() {},
      target: form,
      currentTarget: form,
    });
    form.addEventListener("vh-spam-ok", pass);
    return () => form.removeEventListener("vh-spam-ok", pass);
  }, []);
  return <form ref={ref} className={className} onSubmit={onSubmit}>{children}</form>;
};
const postContact = async (fields, formEl) => {
  const website = (formEl && formEl.querySelector('input[name="company_url"]')?.value) || "";
  const payload = { ...fields, website, t: CONTACT_FORM_STARTED };
  if (contactSpamReason(payload)) return { ok: true, spam: true };
  const res = await fetch("/api/contact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const raw = await res.text();
  let json = {};
  try { json = raw ? JSON.parse(raw) : {}; } catch { /* non-JSON */ }
  if (!res.ok || !json.ok) {
    const detail = json.error || (raw && !json.error ? raw.slice(0, 140) : null);
    throw new Error(
      detail
        ? `${detail} (status ${res.status})`
        : `Submission failed (status ${res.status}). Please email hi@vestalhub.com directly.`
    );
  }
  return { ...json, spam: false };
};

/* SEO content surface — every link reachable through /learn (in the footer)
 * and direct deep links from organic search. Keep these out of the header
 * so transactional users (homepage, /shop, PDP) aren't distracted. */
const goToLearn        = () => { navigate(`/learn`); };
const goToPillar       = () => { navigate(`/hubspot-solutions-partner`); };
const goToRegion       = (id) => { navigate(`/${id}`); };
const goToServices     = () => { navigate(`/hubspot`); };
const goToService      = (id) => { navigate(`/hubspot/${id}`); };
const goToIndustry     = (id) => { navigate(`/industries/${id}`); };
const goToMigration    = (id) => { navigate(`/migrate/${id}`); };
const goToIntegration  = (id) => { navigate(`/integrations/${id}`); };
const goToGlossary     = (id) => { navigate(`/glossary/${id}`); };
const goToCaseStudies  = () => { navigate(`/case-studies`); };
const goToCaseStudy    = (id) => { navigate(`/case-studies/${id}`); };
const goToToolsIndex   = () => { navigate(`/tools`); };
const goToTool         = (id) => { navigate(`/tools/${id}`); };
const goToBook         = () => { navigate(`/book`); };

/* Vestal Hub social + brand constants */
const SOCIAL = {
  linkedin: "https://www.linkedin.com/company/vestalhub/",
  email:    "hi@vestalhub.com",
};

const Icon = ({ name, size = 18 }) => {
  const I = {
    search: <path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.3-4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    heart: <path d="M12 21s-7-4.5-9.5-9C1 8.5 3 5 6.5 5c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3C21 5 23 8.5 21.5 12 19 16.5 12 21 12 21z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    user: <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></g>,
    bag: <path d="M6 7h12l-1 13H7L6 7zm3 0a3 3 0 0 1 6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    check: <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    shield: <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    truck: <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"><rect x="2" y="7" width="12" height="10" rx="1"/><path d="M14 10h4l3 3v4h-7z"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></g>,
    refresh: <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    plus: <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>,
    arrow: <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    close: <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>,
    zap: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    star: <path d="M12 2l3 7 7 1-5 5 1 7-6-4-6 4 1-7-5-5 7-1 3-7z" fill="currentColor"/>,
    ship: <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"><path d="M3 20h18M5 20l-2-7h18l-2 7M12 3v10M8 7l4-4 4 4"/></g>,
    chat: <path d="M21 12c0 5-4 8-9 8-1.5 0-3-.3-4-1l-5 1 1-4c-1-1.5-1-3-1-4 0-5 4-8 9-8s9 3 9 8z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    rocket: <path d="M4 20l4-4M8 20l-4-4M15 5l4 4c0 0 0 5-5 9l-4 2-6-6 2-4c4-5 9-5 9-5zM13 11l-2 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    layers: <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    code: <path d="M8 6l-6 6 6 6M16 6l6 6-6 6M14 4l-4 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
    mail: <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 7l10 7 10-7"/></g>,
    chart: <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"><path d="M3 21h18M7 21V10M12 21V6M17 21v-8"/></g>,
    linkedin: <path d="M20.5 3h-17A.5.5 0 0 0 3 3.5v17a.5.5 0 0 0 .5.5h17a.5.5 0 0 0 .5-.5v-17a.5.5 0 0 0-.5-.5zM8.3 18.3H5.7V9.7h2.6v8.6zM7 8.6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11.3 9.7h-2.6v-4.2c0-1 0-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.3h-2.6V9.7h2.5v1.2c.4-.7 1.2-1.4 2.5-1.4 2.7 0 3.2 1.8 3.2 4.1v4.7z" fill="currentColor"/>,
    sparkle: <path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M19 5l-4 4M9 15l-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>,
    puzzle: <path d="M9 3a2 2 0 0 0-2 2v1H4a1 1 0 0 0-1 1v3h1a2 2 0 0 1 0 4H3v3a1 1 0 0 0 1 1h3v-1a2 2 0 0 1 4 0v1h3a1 1 0 0 0 1-1v-3h-1a2 2 0 0 1 0-4h1V7a1 1 0 0 0-1-1h-3V5a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24">{I[name]}</svg>;
};

const Stars = ({ r = 5 }) => (
  <div className="stars">{[1,2,3,4,5].map(i => <Icon key={i} name="star" size={12} />)}</div>
);

/* ============ Header ============ */
const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeAll = () => { setOpenMenu(null); setMobileOpen(false); };
  const go = (fn) => (e) => { e.preventDefault(); closeAll(); fn(); };

  const hubspotItems = [
    { label: "Onboarding packages", href: "/hubspot-onboarding-packages", onClick: go(goToOnboardingPackages) },
    { label: "HubSpot admin support", href: "/hubspot-admin", onClick: go(goToAdmin) },
    { label: "Technical RevOps", href: "/hubspot-technical-developers", onClick: go(goToTechSupport) },
  ];
  const outboundItems = [
    { label: "Cold email infrastructure", href: "/outbound-infrastructure", onClick: go(goToOutboundInfrastructure) },
    { label: "Get qualified meetings", href: "/outbound-optimization", onClick: go(goToOutboundOptimization) },
  ];

  // Hover-open is desktop-only. On the mobile menu (and on touch), a tap
  // synthesizes mouseenter then click, which would open then immediately close.
  const hoverOpensNav = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 781px)").matches;

  const Dropdown = ({ id, label, items }) => (
    <div
      className={`hdr-dd ${openMenu === id ? "open" : ""}`}
      onMouseEnter={() => { if (hoverOpensNav()) setOpenMenu(id); }}
      onMouseLeave={() => { if (hoverOpensNav()) setOpenMenu((m) => (m === id ? null : m)); }}
    >
      <button
        type="button"
        className="hdr-dd-trigger"
        aria-expanded={openMenu === id}
        aria-haspopup="true"
        onClick={() => setOpenMenu((m) => (m === id ? null : id))}
      >
        {label}
        <span className="hdr-dd-caret" aria-hidden="true"/>
      </button>
      <div className="hdr-dd-panel" role="menu">
        {items.map((it) => (
          <a key={it.href} href={it.href} role="menuitem" onClick={it.onClick}>{it.label}</a>
        ))}
      </div>
    </div>
  );

  return (
    <header className="hdr">
      <div className="wrap">
        <div className="hdr-inner">
          <a href="/" onClick={(e) => { e.preventDefault(); closeAll(); goHome(); }} className="logo" aria-label="Vestal Hub">
            <img src="Vestal-hub.png" alt="Vestal Hub" className="logo-img" width="183" height="38" decoding="async" />
          </a>

          <nav className={`hdr-menu ${mobileOpen ? "open" : ""}`} aria-label="Primary">
            <Dropdown id="hubspot" label="HubSpot" items={hubspotItems} />
            <Dropdown id="outbound" label="Lead generation" items={outboundItems} />
            <a href="/case-studies" className="hdr-link" onClick={go(goToCaseStudies)}>Case studies</a>
            <a href="/contact" className="hdr-link" onClick={go(goToContact)}>Contact us</a>
          </nav>

          <div className="hdr-actions">
            <a href="/book" className="btn btn-primary hdr-book" onClick={(e) => { e.preventDefault(); closeAll(); goToBook(); }}>Book a call</a>
            <button
              type="button"
              className={`hdr-burger ${mobileOpen ? "open" : ""}`}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span/><span/><span/>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

/* ============ Hero — simplified, centered ============ */
const Hero = () => (
  <section className="hero">
    <div className="wrap">
      <div className="hero-grid">
        <h1>We make HubSpot <span className="hl">easy for you.</span></h1>
        <p className="hero-lead">
          From quick fixes to complex implementations, we handle HubSpot so your team stays focused.
        </p>
        <div className="hero-ctas">
          <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>Book a call <Icon name="arrow" size={14}/></a>
          <a href="/shop" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToShop(); }}>Check our services</a>
        </div>
        <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
          <div className="hero-cred-cell">
            <img
              src="assets/trust/hubspot-gold-partner.png"
              alt="HubSpot Solutions Partner — Gold"
              className="hero-cred-badge"
              width="120" height="120" loading="lazy"
            />
            <span className="hero-cred-copy">
              <b>HubSpot</b>
              <span>Gold Solutions Partner</span>
            </span>
          </div>
          <span className="hero-cred-divider" aria-hidden="true"/>
          <div className="hero-cred-cell">
            <span className="hero-cred-avatars" aria-hidden="true">
              <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
              <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
              <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
              <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
              <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
            </span>
            <span className="hero-cred-copy">
              <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
              <span>Trusted by <b>100+ companies</b></span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============ Category Tiles ============ */
const Categories = () => {
  const cats = [
    { id: "hubspot",     n: "HubSpot",           i: "layers" },
    { id: "development", n: "Development",       i: "code" },
    { id: "addons",      n: "Add-ons",           i: "plus" },
    { id: "custom",      n: "Custom & Retainer", i: "refresh", href: "custom" },
  ];
  return (
    <section className="cat-section">
      <div className="wrap">
        <div className="section-head">
          <div>
            <h2>Shop <em>by category</em></h2>
            <div className="sub">{PRODUCTS.filter(p => !p.hidden).length} PROVEN PLAYS · BUILT FOR ANY INDUSTRY</div>
          </div>
          <a href="/shop" className="link" onClick={(e) => { e.preventDefault(); goToShop(); }}>View all <Icon name="arrow" size={12}/></a>
        </div>
        <div className="cat-tiles">
          {cats.map((c, i) => {
            const cnt = PRODUCTS.filter(p => p.cat === c.id && !p.hidden).length;
            return (
              <button key={c.id}
                      className="cat-tile cat-tile-link"
                      onClick={() => c.href === "custom" ? goToCustom() : goToCategory(c.id)}
                      aria-label={`Browse ${c.n} services`}>
              <div className="idx">[0{i+1}]</div>
              <div className="ic"><Icon name={c.i} size={20}/></div>
              <div>
                <div className="n">{c.n}</div>
                  <div className="c">{cnt} {cnt === 1 ? "SERVICE" : "SERVICES"}</div>
              </div>
                <span className="cat-tile-arrow"><Icon name="arrow" size={14}/></span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============ Custom engagement promo (June 2026) ============ */
const customPromoActive = () => {
  const promo = typeof CUSTOM_ENGAGEMENT_PROMO !== "undefined" ? CUSTOM_ENGAGEMENT_PROMO : null;
  if (!promo?.endsAt) return false;
  return Date.now() <= new Date(`${promo.endsAt}T23:59:59`).getTime();
};
const productHasCustomPromo = (p) => customPromoActive() && CUSTOM_ENGAGEMENT_PROMO?.productIds?.includes(p?.id);
const customPromoPrice = (price) => Math.round(price * (1 - (CUSTOM_ENGAGEMENT_PROMO?.discount || 0)));

const PromoPrice = ({ price, monthly, className = "" }) => {
  const unit = monthly ? "/mo" : "";
  const promo = customPromoPrice(price);
  return (
    <div className={`promo-price${className ? ` ${className}` : ""}`}>
      <s className="promo-price-was">${price.toLocaleString()}{unit}</s>
      <span className="promo-price-now">${promo.toLocaleString()}{unit}</span>
    </div>
  );
};

/* ============ Products ============ */
const Product = ({ p, onAdd, wish, onWish }) => {
  const open = (e) => {
    if (e.defaultPrevented) return;
    goToProduct(p.id);
  };
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <article className="product is-link" onClick={open} role="link" tabIndex={0}
             onKeyDown={(e) => { if (e.key === "Enter") open(e); }}>
        <div className="p-vis">
          {p.badge && (
            <div className="p-badges">
              <span className={`p-badge ${p.badge}`}>{p.badge === "new" ? "NEW" : "POPULAR"}</span>
            </div>
          )}
          {p.image ? (
            <img className="p-img" src={p.image} alt={p.name} loading="lazy"/>
          ) : (
          <div className="p-glyph">
            <span className="num">{p.num}</span>
            <span className="cat">{p.category}</span>
          </div>
          )}
        </div>
        <div className="p-body">
          <span className="p-cat">{p.category}</span>
          <h3 className="p-name">{p.name}</h3>
          <p className="p-desc">{p.desc}</p>
          <div className="p-meta">
            <span className="m"><Icon name="truck" size={12}/> {p.duration}</span>
            <span className="m"><Icon name="shield" size={12}/> SOW in 24h</span>
          </div>
          <div className="p-foot">
            <span className="p-learn">Learn more <Icon name="arrow" size={12}/></span>
          </div>
        </div>
    </article>
  );
};

/* Homepage Shop — featured HubSpot services + Custom Dashboard Build + Monthly Retainer (8 cards) */
const Shop = ({ onAdd }) => {
  const [wish, setWish] = useState({});
  const featured = useMemo(() => {
    const base = PRODUCTS.filter(p => p.cat === "hubspot" && p.featured && !p.hidden);
    const extraIds = ["a-dash", "x-retainer"];
    const extras = extraIds
      .map(id => PRODUCTS.find(p => p.id === id && !p.hidden))
      .filter(Boolean);
    const seen = new Set();
    return [...base, ...extras].filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, []);
  const toggleWish = (id) => setWish(w => ({...w, [id]: !w[id]}));
  return (
    <section id="shop" className="shop-section">
      <div className="wrap">
        <div className="section-head">
          <div>
            <h2>How can we <em>help you</em> today?</h2>
            <p className="shop-section-lead" style={{margin:"10px auto 0", maxWidth:"60ch", color:"var(--muted)", fontSize:"var(--fs-xl)", lineHeight:1.45}}>
              Choose between the following solutions or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} style={{color:"var(--brand)", textDecoration:"underline"}}>contact us</a> for a custom quote.
            </p>
            <div className="sub">HAND-PICKED · SENIOR-LED · FIXED SCOPE</div>
          </div>
        </div>
        <div className="products">
          {featured.map(p => <Product key={p.id} p={p} onAdd={onAdd} wish={wish[p.id]} onWish={toggleWish}/>)}
        </div>
        <div className="shop-cta-row">
          <p>Looking for a small fix, a block of senior hours, or a monthly retainer? We also ship CRM migrations, attribution dashboards, and full marketing-site refreshes. Browse the full catalog and filter by category.</p>
          <a href="/shop" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToShop(); }}>
            Browse all services <Icon name="arrow" size={12}/>
          </a>
        </div>
      </div>
    </section>
  );
};

/* Full catalog page with category filter + sort */
const ShopPage = ({ onAdd, initialCat = "all", initialSort = "featured", initialQ = "" }) => {
  const [active, setActive] = useState(initialCat);
  const [sort, setSort]     = useState(initialSort);
  const [q, setQ]           = useState(initialQ);
  const [wish, setWish]     = useState({});
  const toggleWish = (id) => setWish(w => ({...w, [id]: !w[id]}));

  useEffect(() => { setActive(initialCat); }, [initialCat]);
  useEffect(() => { setSort(initialSort); }, [initialSort]);
  useEffect(() => { setQ(initialQ); }, [initialQ]);

  const cats = useMemo(() => ([
    { id: "all",       name: "All",            count: PRODUCTS.filter(p => !p.hidden).length },
    ...["hubspot","development","addons","custom"].map(id => {
      const meta = CATEGORY_META[id];
      return { id, name: meta?.name || id, count: PRODUCTS.filter(p => p.cat === id && !p.hidden).length };
    }),
  ]), []);

  const list = useMemo(() => {
    let l = active === "all" ? PRODUCTS.filter(p => !p.hidden) : PRODUCTS.filter(p => p.cat === active && !p.hidden);
    const s = (q || "").trim().toLowerCase();
    if (s) {
      const tokens = s.split(/\s+/).filter(Boolean);
      l = l.filter(p => {
        const hay = `${p.name || ""} ${p.desc || ""} ${p.category || ""} ${p.cat || ""} ${Array.isArray(p.scope) ? p.scope.join(" ") : ""}`.toLowerCase();
        return tokens.every(t => hay.includes(t));
      });
    }
    if (sort === "price-asc")  l.sort((a,b) => a.price - b.price);
    if (sort === "price-desc") l.sort((a,b) => b.price - a.price);
    if (sort === "duration")   l.sort((a,b) => (parseInt(a.duration) || 999) - (parseInt(b.duration) || 999));
    if (sort === "featured")   l.sort((a,b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return l;
  }, [active, sort, q]);

  return (
    <>
      <section className="shop-page-hero">
        <div className="wrap">
          <nav className="cat-crumbs">
            <a href="#" onClick={(e)=>{e.preventDefault();goHome();}}>Home</a>
            <span className="sep">/</span>
            <span className="here">All services</span>
          </nav>
          <span className="shop-page-eye">FULL CATALOG · {PRODUCTS.filter(p => !p.hidden).length} PROVEN PLAYS</span>
          <h1>{q ? <>Search results for <em>"{q}"</em></> : "Every HubSpot service we ship."}</h1>
          <p className="shop-page-sub">
            {q
              ? <>Found <b>{list.length}</b> {list.length === 1 ? "service" : "services"} matching your search. Sharpen the filter, or <a href="/shop" onClick={(e) => { e.preventDefault(); setQ(""); window.history.replaceState(null, "", "/shop"); }}>clear and browse all {PRODUCTS.length} →</a></>
              : "Fixed scope. Senior-led. SOW in 24 hours, no sales calls. Filter by category or sort by what matters most to you."}
          </p>
        </div>
      </section>

      <section id="shop" className="shop-section shop-page-section">
        <div className="wrap">
        <div className="shop-bar">
            <div className="count">
              Showing <b>{list.length}</b> of <b>{PRODUCTS.filter(p => !p.hidden).length}</b> services
              {q && (
                <span className="shop-q-pill">
                  <Icon name="search" size={11}/> "{q}"
                  <button type="button" className="shop-q-clear" aria-label="Clear search" onClick={() => { setQ(""); window.history.replaceState(null, "", active === "all" ? "/shop" : `/shop?cat=${active}`); }}>
                    <Icon name="close" size={11}/>
                  </button>
                </span>
              )}
            </div>
          <div className="chips">
              {cats.map(c => (
              <button key={c.id} className={`chip ${active === c.id ? "on" : ""}`} onClick={() => setActive(c.id)}>
                {c.name}<span className="n">{c.count}</span>
              </button>
            ))}
          </div>
            <label className="sort-wrap">
              <span className="sort-lab">Sort</span>
              <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="featured">Featured</option>
                <option value="price-asc">Price · low → high</option>
                <option value="price-desc">Price · high → low</option>
                <option value="duration">Time to live</option>
              </select>
            </label>
        </div>

          {list.length === 0 ? (
            <div className="shop-empty">
              {q ? (
                <>
                  <p>No services match <b>"{q}"</b>{active !== "all" ? <> in <b>{cats.find(c => c.id === active)?.name}</b></> : null}.</p>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center"}}>
                    <button className="btn btn-secondary" onClick={() => { setQ(""); window.history.replaceState(null, "", active === "all" ? "/shop" : `/shop?cat=${active}`); }}>Clear search</button>
                    {active !== "all" && (
                      <button className="btn btn-ghost" onClick={() => { setActive("all"); }}>Search all categories</button>
                    )}
                    <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Talk about custom work <Icon name="arrow" size={12}/></a>
                  </div>
                </>
              ) : (
                <>
                  <p>No services in this category yet.</p>
                  <button className="btn btn-secondary" onClick={() => setActive("all")}>Show all services</button>
                </>
              )}
            </div>
          ) : (
        <div className="products">
          {list.map(p => <Product key={p.id} p={p} onAdd={onAdd} wish={wish[p.id]} onWish={toggleWish}/>)}
            </div>
          )}

          <div className="shop-page-foot">
            <div className="shop-page-foot-row">
              <div>
                <h3>Don't see exactly what you need?</h3>
                <p>Tell us what you're shipping and we'll scope it. Most custom SOWs go out within 48 hours.</p>
              </div>
              <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>
                Talk about custom work <Icon name="arrow" size={12}/>
              </a>
            </div>
        </div>
      </div>
    </section>
    </>
  );
};

/* ============ Product detail page ============ */
const tierPriceFor = (p, t) => (t.price != null ? t.price : Math.round(p.price * (t.mult ?? 1)));
const pickInitialTier = (tiers) => tiers.find(t => t.badge) || tiers[Math.min(1, tiers.length - 1)] || tiers[0];

const ProductPage = ({ id, onAdd }) => {
  const product = useMemo(() => PRODUCTS.find(p => p.id === id), [id]);
  const tiers   = useMemo(() => (product?.simple ? [] : (product?.tiers || [])), [product]);
  const simple  = !!product?.simple;
  const [tier, setTier]     = useState(() => (tiers.length ? pickInitialTier(tiers) : null));
  const [extras, setExtras] = useState({});
  const [qty, setQty]       = useState(1);
  const [tab, setTab]       = useState("overview");

  useEffect(() => {
    setTier(tiers.length ? pickInitialTier(tiers) : null);
    setExtras({});
    setQty(1);
  }, [id]);

  if (!product) {
    return (
      <section className="pdp pdp-empty">
        <div className="wrap">
          <h1>Service not found</h1>
          <p>That SKU isn't in the shop. <a href="/shop" onClick={(e)=>{e.preventDefault();goToShop();}}>Browse the catalog →</a></p>
        </div>
      </section>
    );
  }

  const p          = product;
  const activeTier = tier;
  const deliv      = p.pillars || DELIVERABLES[p.cat] || DELIVERABLES.hubspot;
  const productAddons = p.addonsOverride || ADDONS;
  const outcome    = (() => {
    const reviewId = OUTCOMES[p.id];
    const r = reviewId && REVIEWS.find(rev => rev.id === reviewId);
    if (!r) return null;
    return {
      metric:  r.metric || r.badge || "Verified client",
      quote:   (r.text || "").replace(/<\/?[a-z][^>]*>/gi, ""),
      who:     `${r.name} · ${r.role}, ${r.company}`,
      company: r.company,
    };
  })();
  const pairs      = PRODUCTS.filter(x => x.cat === p.cat && x.id !== p.id && !x.hidden).slice(0, 2);
  const isMonthly  = p.duration === "monthly";
  const priceUnit  = isMonthly ? "/mo" : "";
  const tierPrice  = simple || !tier ? p.price : tierPriceFor(p, tier);
  const extraTotal = Object.keys(extras).filter(k => extras[k]).reduce((s, k) => s + ((p.addonsOverride || ADDONS).find(a => a.id === k)?.price || 0), 0);
  const total      = (tierPrice + extraTotal) * qty;
  const cheapest   = useMemo(() => (tiers.length ? Math.min(...tiers.map(t => tierPriceFor(p, t))) : p.price), [p, tiers]);
  const showFromStrike = !simple && tier && tierPrice > cheapest;
  const hasPromo       = productHasCustomPromo(p);
  const promoPrice     = hasPromo ? customPromoPrice(tierPrice) : tierPrice;

  const tog = (k) => setExtras(e => ({...e, [k]: !e[k]}));

  const addCurrent = () => {
    const tierLabel = simple || !tier ? "" : ` · ${tier.name}`;
    const item = {
      ...p,
      _uid: `${p.id}-${(tier?.id || "base")}-${Date.now()}`,
      name: `${p.name}${tierLabel}`,
      price: tierPrice * qty,
      tier: tier?.id,
    };
    onAdd(item);
    Object.keys(extras).filter(k => extras[k]).forEach(k => {
      const a = ADDONS.find(x => x.id === k);
      if (!a) return;
      onAdd({
        id: `addon-${a.id}`,
        _uid: `${a.id}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        num: "+",
        name: `Add-on · ${a.name}`,
        category: "Add-on",
        duration: a.sub,
        price: a.price,
      });
    });
  };

  return (
    <section className="pdp">
      <div className="wrap">
        <nav className="pdp-crumbs">
          <a href="/shop" onClick={(e)=>{e.preventDefault();goToShop();}}>Shop</a>
          <span>/</span>
          <a href={`/category/${p.cat}`} onClick={(e)=>{e.preventDefault();goToCategory(p.cat);}}>{p.category}</a>
          <span>/</span>
          <b>{p.name}</b>
        </nav>

        <div className="pdp-grid">
          {/* ============ LEFT — gallery / proof ============ */}
          <div className="pdp-gallery">
            {/* Main visual */}
            <div className="pdp-hero-card">
              {p.badge && (
                <div className="p-badges pdp-hero-badges">
                  <span className={`p-badge ${p.badge}`}>{p.badge === "new" ? "NEW" : "POPULAR"}</span>
                </div>
              )}
              <div className="pdp-hero-meta">
                <span className="pdp-hero-num">{p.num}</span>
                <span className="pdp-hero-cat">{p.category}</span>
              </div>
              {p.image ? (
                <img className="pdp-hero-img" src={p.image} alt={p.name} loading="eager"/>
              ) : (
                <div className="pdp-hero-glyph">
                  <span className="num">{p.num}</span>
                  <span className="cat">{p.category}</span>
                </div>
              )}
              <div className="pdp-hero-foot">
                <span><Icon name="check" size={12}/> Senior-led</span>
                <span><Icon name="shield" size={12}/> Fixed price</span>
                <span><Icon name="zap" size={12}/> Live in {activeTier?.duration || p.duration}</span>
              </div>
            </div>

            {/* secondary image hidden for now
            {p.imageWide && (
              <div className="pdp-secondary-img">
                <img src={p.imageWide} alt={`${p.name} — detailed overview`} loading="lazy"/>
              </div>
            )}
            */}

            {/* What you get */}
            <div className="pdp-block">
              <div className="pdp-block-head">
                <span className="pdp-block-eye">CORE PILLARS</span>
                <h3>{p.pillars ? "What's included" : "What you get"}</h3>
              </div>
              <div className="pdp-deliv">
                {deliv.map(d => (
                  <div className="pdp-deliv-i" key={d.t}>
                    <div className="ic"><Icon name={d.i} size={18}/></div>
                    <div className="meta">
                      <div className="t">{d.t}</div>
                      <p>{d.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {p.sampleReport && (
                <a className="pdp-sample-report" href={p.sampleReport.href} target="_blank" rel="noopener noreferrer">
                  <Icon name="search" size={16}/>
                  <span>{p.sampleReport.label}</span>
                  <Icon name="arrow" size={16}/>
                </a>
              )}
            </div>

            {/* Detailed scope — per-service bullets, per-tier when available */}
            {(() => {
              const activeScope = activeTier?.scope || activeTier?.bullets || p.scope;
              const scopeTitle = p.scopeLabel
                ? (activeTier ? p.scopeLabel.replace(/Pro/, activeTier.name) : p.scopeLabel)
                : (activeTier ? `What's in the box · ${activeTier.name}` : "What's in the box");
              if (!activeScope?.length) return null;
              return (
                <div className="pdp-block">
                  <div className="pdp-block-head">
                    <span className="pdp-block-eye">DETAILED SCOPE</span>
                    <h3>{scopeTitle}</h3>
                  </div>
                  <ul className="pdp-scope">
                    {activeScope.map((s, i) => (
                      <li key={i}>
                        <span className="pdp-scope-ic" aria-hidden="true">
                          <Icon name="check" size={12}/>
                        </span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pdp-scope-foot">
                    {p.scopeNote || <>Quantities above are upper limits — final scope is confirmed in your SOW. Need something not listed? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Talk about custom scope →</a></>}
                  </div>
                  {activeTier?.excluded && (
                    <div className="pdp-tier-excluded">
                      <div className="pdp-tier-excluded-label">Does not include:</div>
                      <ul className="pdp-scope pdp-scope-excluded">
                        {activeTier.excluded.map((s, i) => (
                          <li key={i}>
                            <span className="pdp-scope-ic pdp-scope-ic-x" aria-hidden="true">✕</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Not included by default — tier-aware intro when available */}
            {(() => {
              const ni = activeTier?.notIncluded || p.notIncluded;
              if (!ni) return null;
              const introText = activeTier
                ? ni.intro.replace(/^This package/, `This ${activeTier.name} package`)
                : ni.intro;
              return (
                <div className="pdp-block">
                  <div className="pdp-block-head">
                    <span className="pdp-block-eye">EXCLUSIONS</span>
                    <h3>Not included by default</h3>
                  </div>
                  <p className="pdp-scope-foot" style={{marginBottom:12}}>{introText}</p>
                  <ul className="pdp-scope pdp-scope-excluded">
                    {ni.items.map((s, i) => (
                      <li key={i}>
                        <span className="pdp-scope-ic pdp-scope-ic-x" aria-hidden="true">✕</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Is this the right fit? — tier-aware override when present */}
            {(() => {
              const fit = activeTier?.bestFit || p.bestFit;
              if (!fit) return null;
              return (
                <div className="pdp-block">
                  <div className="pdp-block-head">
                    <span className="pdp-block-eye">FIT CHECK</span>
                    <h3>Is this the right fit?</h3>
                  </div>
                  <div className="pdp-fit-grid">
                    <div className="pdp-fit-col pdp-fit-good">
                      <div className="pdp-fit-label"><Icon name="check" size={14}/> Best fit</div>
                      <ul>
                        {fit.good.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                    <div className="pdp-fit-col pdp-fit-bad">
                      <div className="pdp-fit-label"><Icon name="close" size={14}/> Not the best fit</div>
                      <ul>
                        {fit.bad.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  </div>
                  <p className="pdp-fit-note">Not the right fit? <a href="/shop" onClick={(e) => { e.preventDefault(); goToShop(); }}>Browse all services</a> or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>request custom scope</a>.</p>
                </div>
              );
            })()}

            {/* Outcome card (lifestyle equivalent) */}
            {outcome && (
              <div className="pdp-outcome">
                <span className="pdp-outcome-eye">IN THEIR WORDS · {outcome.company.toUpperCase()}</span>
                <div className="pdp-outcome-row">
                  <div className="pdp-outcome-metric">{outcome.metric}</div>
                  <div className="pdp-outcome-body">
                    <p>"{outcome.quote}"</p>
                    <small>— {outcome.who}</small>
                  </div>
                </div>
              </div>
            )}

            {/* Service highlights */}
            <div className="pdp-highlights">
              <div className="pdp-block-head">
                <span className="pdp-block-eye">SERVICE SPECS</span>
                <h3>Highlights</h3>
              </div>
              {(() => {
                const sp = activeTier?.specs;
                if (sp) return (
                  <div className="pdp-hi-grid">
                    <div className="pdp-hi"><div className="ic"><Icon name="rocket" size={16}/></div><div><b>{sp.timeline}</b><span>Time to live</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="layers" size={16}/></div><div><b>{sp.deliverables}</b><span>Fixed scope</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="user" size={16}/></div><div><b>{sp.team}</b><span>Operator</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="chat" size={16}/></div><div><b>{sp.support}</b><span>During build</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="mail" size={16}/></div><div><b>SOW</b><span>{sp.sow}</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="refresh" size={16}/></div><div><b>{sp.updates}</b><span>Status</span></div></div>
                    {sp.extra && <div className="pdp-hi"><div className="ic"><Icon name="zap" size={16}/></div><div><b>{sp.extra}</b><span>Included</span></div></div>}
                  </div>
                );
                return (
                  <div className="pdp-hi-grid">
                    <div className="pdp-hi"><div className="ic"><Icon name="rocket" size={16}/></div><div><b>{p.duration}</b><span>Time to live</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="layers" size={16}/></div><div><b>{deliv.length} deliverables</b><span>Fixed scope</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="user" size={16}/></div><div><b>1 senior</b><span>Operator owner</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="chat" size={16}/></div><div><b>Slack</b><span>Async support</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="mail" size={16}/></div><div><b>SOW</b><span>in 24 hours</span></div></div>
                    <div className="pdp-hi"><div className="ic"><Icon name="refresh" size={16}/></div><div><b>Weekly</b><span>Progress updates</span></div></div>
                  </div>
                );
              })()}
            </div>

            {/* Tabs */}
            <div className="pdp-tabs">
              <div className="pdp-tabs-bar">
                {[["overview","Overview"],["process","Process"],["faq","FAQ"]].map(([k,l]) => (
                  <button key={k} className={`pdp-tab ${tab===k?"on":""}`} onClick={()=>setTab(k)}>{l}</button>
                ))}
              </div>
              <div className="pdp-tabs-body">
                {tab === "overview" && (
                  <div>
                    {activeTier && activeTier.desc && (
                      <p style={{borderLeft:"3px solid var(--brand)",paddingLeft:12,marginBottom:14}}>
                        <b>{activeTier.name} scope:</b> {activeTier.desc}
                      </p>
                    )}
                    {(() => {
                      const ov = activeTier?.overview || p.overview;
                      if (ov) return ov.split("\n\n").map((para, i) => <p key={i}>{para}</p>);
                      return (
                        <>
                          <p>{p.desc}</p>
                          {simple ? (
                            <p>You get a fixed-scope add-on, not a hand-rolled SOW. We start the moment the SOW is signed and hand off in <b>{activeTier?.duration || p.duration}</b> with QA, a Loom walkthrough, and a 7-day Slack window for questions.</p>
                          ) : isMonthly ? (
                            <p>You get a senior operator embedded with your team on the <b>{tier.name}</b> plan — month-to-month, cancel any time. Slack-first, weekly working session, and a sprint board you prioritize.</p>
                          ) : (
                            <p>You get a fixed-scope engagement, not a hand-rolled SOW. <b>{tier.name}</b> {tiers === TIERS ? "tier" : "package"} ships {tier.bullets[1]?.toLowerCase().replace(/^\+ /, "") || "the published scope"} on a {activeTier?.duration || p.duration} timeline. We've shipped this play to dozens of teams — same formula, calibrated to your stack.</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {tab === "process" && (
                  <ol className="pdp-steps">
                    {p.processSteps ? p.processSteps.map((s, i) => (
                      <li key={i}><b>Step {i+1} — {s.title}.</b> {s.body}</li>
                    )) : (
                      <>
                        <li><b>Day 0 — checkout.</b> Reserve a spot. Zero charge.</li>
                        <li><b>Day 1 — SOW.</b> A scoped Statement of Work in your inbox within 24 hours.</li>
                        <li><b>Day 7 — kickoff.</b> Senior operator assigned, Slack channel opened.</li>
                        <li><b>Week 2 → handoff.</b> Weekly progress, no surprises. Ship on the date we promised.</li>
                      </>
                    )}
                  </ol>
                )}
                {tab === "faq" && (
                  <div className="pdp-faq">
                    {(p.productFaq || FAQ.slice(0,3)).map((f,i) => (
                      <details key={i} open={i===0}>
                        <summary>{f.q}</summary>
                        <p>{f.a}</p>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ============ RIGHT — buy column ============ */}
          <aside className="pdp-side">
            <span className="pdp-eye">{p.category} · SKU {p.num}</span>
            <h1 className="pdp-title">{p.name}</h1>
            <p className="pdp-tagline">{p.tagline || p.desc}</p>
            {p.supporting && <p className="pdp-supporting">{p.supporting}</p>}

            {/* Tier list — names and bullets only, no prices */}
            {!simple && tiers.length > 0 && (
              <div className="pdp-tier">
                <div className="pdp-l">{isMonthly ? "Plan" : "Engagement tier"}: <b>{tier.name}</b> <span className="muted">— {tier.tag}</span></div>
                <div className="pdp-tier-row">
                  {tiers.map(t => (
                    <button key={t.id}
                            className={`pdp-tier-btn ${tier.id === t.id ? "on" : ""}`}
                            onClick={() => setTier(t)}>
                      {t.badge && <span className="pdp-tier-badge">★</span>}
                      <span className="t">{t.name}</span>
                    </button>
                  ))}
                </div>
                <ul className="pdp-tier-bullets">
                  {tier.bullets.map((b, i) => (
                    <li key={i}><Icon name="check" size={12}/> {b}</li>
                  ))}
                </ul>
                {tier.desc && <p className="pdp-tier-desc">{tier.desc}</p>}
              </div>
            )}

            {/* Book a call CTA */}
            <a href="/" onClick={(e) => { e.preventDefault(); goHome(); }} className="btn btn-primary pdp-book-cta">
              Book a free consultation <Icon name="arrow" size={14}/>
            </a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} className="pdp-payopts">Or send us a message →</a>

            {/* Status box */}
            <div className="pdp-pickup">
              <div className="pdp-pickup-ic"><Icon name="check" size={14}/></div>
              <div className="pdp-pickup-meta">
                <b>Senior operator assigned within 24 hours</b>
                <span>SOW signed within 48h · zero charge until you sign</span>
              </div>
            </div>

            <div className="pdp-share">
              <span>Share:</span>
              <button aria-label="Email"><Icon name="mail" size={14}/></button>
              <button aria-label="Twitter">𝕏</button>
              <button aria-label="LinkedIn">in</button>
              <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} className="pdp-help"><Icon name="chat" size={14}/> Need help?</a>
            </div>


            {/* Trust badges */}
            <div className="pdp-trust">
              <div className="i"><Icon name="check"  size={14}/> Fixed price · no surprises</div>
              <div className="i"><Icon name="shield" size={14}/> SOW within 24 hours, free</div>
              <div className="i"><Icon name="zap"    size={14}/> 8+ year senior operator</div>
              <div className="i"><Icon name="refresh"size={14}/> No payment until you sign the SOW</div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

/* ============ Category page ============ */
const CategoryPage = ({ id, onAdd }) => {
  const meta     = CATEGORY_META[id];
  const [sort, setSort]     = useState("featured");
  const [wish, setWish]     = useState({});
  const toggleWish = (pid) => setWish(w => ({...w, [pid]: !w[pid]}));

  if (!meta) {
    return (
      <section className="pdp pdp-empty">
        <div className="wrap">
          <h1>Category not found</h1>
          <p>That category doesn't exist. <a href="/shop" onClick={(e)=>{e.preventDefault();goToShop();}}>Browse all services →</a></p>
        </div>
      </section>
    );
  }

  const products = useMemo(() => {
    const list = PRODUCTS.filter(p => p.cat === id && !p.hidden);
    if (sort === "price-asc")  return [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") return [...list].sort((a, b) => b.price - a.price);
    if (sort === "fastest")    return [...list].sort((a, b) => parseInt(a.duration) - parseInt(b.duration));
    return [...list].sort((a, b) => (b.badge === "pop" ? 1 : 0) - (a.badge === "pop" ? 1 : 0));
  }, [id, sort]);

  const reviews   = REVIEWS.filter(r => r.slot !== "micro").slice(0, 2);
  const faqItems  = (meta.faqIds || []).map(i => FAQ[i]).filter(Boolean);
  const otherCats = Object.values(CATEGORY_META).filter(c => c.id !== id);

  return (
    <>
      {/* ===== Hero ===== */}
      <section className="cat-hero" style={{"--cat-color": meta.color}}>
        <div className="wrap">
          <nav className="pdp-crumbs cat-crumbs">
            <a href="/shop" onClick={(e)=>{e.preventDefault();goToShop();}}>Shop</a>
            <span>/</span>
            <b>{meta.name}</b>
          </nav>

          <div className="cat-hero-grid">
            <div className="cat-hero-main">
              <span className="cat-eye">{meta.eyebrow}</span>
              <h1 className="cat-h1">{meta.hero}</h1>
              <p className="cat-sub">{meta.sub}</p>
              <div className="cat-hero-meta">
                <span className="m"><Icon name="layers" size={13}/> <b>{products.length}</b> proven {products.length === 1 ? "play" : "plays"}</span>
                <span className="m"><Icon name="zap"    size={13}/> Live in 2&ndash;6 weeks</span>
                <span className="m"><Icon name="shield" size={13}/> Fixed price · senior-led</span>
                <span className="m"><Icon name="check"  size={13}/> SOW in 24h, free</span>
              </div>
            </div>
            <div className={`cat-hero-card${meta.image ? " cat-hero-card-img" : ""}`}>
              {meta.image ? (
                <img src={meta.image} alt={`${meta.name} services overview`} className="cat-hero-img" loading="eager" />
              ) : (
                <div className="cat-card-glyph"><Icon name={meta.icon} size={56}/></div>
              )}
              <div className="cat-card-foot">
                <span>CATEGORY</span>
                <b>{meta.name}</b>
              </div>
            </div>
          </div>

          <div className="cat-fit">
            <span className="cat-fit-eye">FIT</span>
            <p>{meta.fitFor}</p>
          </div>
        </div>
      </section>

      {/* ===== Benefits strip ===== */}
      <section className="cat-benefits">
        <div className="wrap">
          <div className="cat-ben-grid">
            {meta.benefits.map((b, i) => (
              <div className="cat-ben" key={b.t}>
                <div className="cat-ben-num">0{i+1}</div>
                <div className="cat-ben-ic"><Icon name={b.i} size={20}/></div>
                <h3>{b.t}</h3>
                <p>{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Product grid ===== */}
      <section className="cat-grid-section">
        <div className="wrap">
          <div className="cat-grid-bar">
            <div className="count">
              Showing <b>all {products.length}</b> {products.length === 1 ? "service" : "services"} in <b>{meta.name}</b>
            </div>
            <div className="cat-jumps">
              {Object.values(CATEGORY_META).map(c => (
                <button key={c.id}
                        className={`chip ${c.id === id ? "on" : ""}`}
                        onClick={() => goToCategory(c.id)}>
                  {c.name}
                </button>
              ))}
              <button className="chip" onClick={() => goToShop()}>
                All ({PRODUCTS.length})
              </button>
            </div>
            <select className="cat-sort" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
              <option value="featured">Sort: Featured</option>
              <option value="price-asc">Price · low → high</option>
              <option value="price-desc">Price · high → low</option>
              <option value="fastest">Fastest delivery</option>
            </select>
          </div>

          <div className="products cat-products">
            {products.map(p => (
              <Product key={p.id} p={p} onAdd={onAdd} wish={wish[p.id]} onWish={toggleWish}/>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Reviews mini ===== */}
      {reviews.length > 0 && (
        <section className="cat-reviews">
          <div className="wrap">
            <span className="cat-section-eye">SOCIAL PROOF · TEAMS WHO SHIPPED THIS</span>
            <h2>What changed after launch.</h2>
            <div className="cat-reviews-grid">
              {reviews.map(r => (
                <article className="cat-review" key={r.id}>
                  <Stars/>
                  <p dangerouslySetInnerHTML={{__html:`"${r.text}"`}}/>
                  <div className="cr-author">
                    <img src={r.avatar} alt={r.name} loading="lazy"/>
                    <div>
                      <b>{r.name}</b>
                      <span>{r.role} · <em>{r.company}</em></span>
                    </div>
                    {r.logo && <img className="cr-logo" src={r.logo} alt={r.company} loading="lazy"/>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FAQ specific ===== */}
      {faqItems.length > 0 && (
        <section className="cat-faq">
          <div className="wrap">
            <div className="cat-faq-grid">
              <div>
                <span className="cat-section-eye">COMMON QUESTIONS · {meta.name.toUpperCase()}</span>
                <h2>Before you check out.</h2>
                <p>Need something not in the shop? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Tell us what you're shipping</a> and we'll scope a custom SOW within 48 hours.</p>
              </div>
              <div className="cat-faq-list">
                {faqItems.map((f, i) => (
                  <details key={i} open={i === 0}>
                    <summary>
                      <span>{f.q}</span>
                      <span className="plus"><Icon name="plus" size={14}/></span>
                    </summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Cross-category ===== */}
      <section className="cat-related">
        <div className="wrap">
          <div className="cat-related-head">
            <h3>Browse other categories</h3>
            <a href="/shop" onClick={(e)=>{e.preventDefault();goToShop();}} className="link">All services <Icon name="arrow" size={12}/></a>
          </div>
          <div className="cat-related-grid">
            {otherCats.map(c => (
              <button key={c.id} className="cat-related-tile" onClick={() => goToCategory(c.id)}>
                <div className="ic"><Icon name={c.icon} size={20}/></div>
                <div>
                  <div className="n">{c.name}</div>
                  <div className="c">{PRODUCTS.filter(p => p.cat === c.id && !p.hidden).length} services</div>
                </div>
                <Icon name="arrow" size={14}/>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Reuse the existing CTA band */}
      <Cta />
    </>
  );
};

/* ============ Features strip ============ */
const Features = () => {
  const f = [
    {
      i: "user",
      n: "01 / PROVEN EXPERIENCE",
      t: "Trusted by",
      e: "100+ companies.",
      d: "We've shipped HubSpot systems across SaaS, agencies, services, e-commerce, finance, and nonprofits — so you're not paying for someone's first attempt.",
      k: "Proven across industries",
    },
    {
      i: "shield",
      n: "02 / FIXED SCOPE",
      t: "Buy the playbook,",
      e: "not the hours.",
      d: "Each service comes with clear deliverables, fixed pricing, and a defined scope — so you know what you're getting before work starts.",
      k: "No open-ended retainers",
    },
    {
      i: "zap",
      n: "03 / SENIOR OPERATORS",
      t: "Senior, all the",
      e: "way down.",
      d: "Your engagement is owned end-to-end by an experienced HubSpot and revenue-ops operator. No junior teams, no handoffs.",
      k: "Built by senior operators",
    },
  ];
  return (
    <section className="features">
      <div className="wrap">
        <div className="feat-grid">
          {f.map(x => (
            <div className="feat" key={x.n}>
              <div className="ic"><Icon name={x.i} size={26}/></div>
              <div className="n">{x.n}</div>
              <h3>{x.t}<br/><em>{x.e}</em></h3>
              <p>{x.d}</p>
              <div className="feat-divider" aria-hidden="true"/>
              <div className="feat-foot">
                <span className="feat-foot-ic" aria-hidden="true"><Icon name="check" size={12}/></span>
                <span>{x.k}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ How it works (buying steps) ============ */
const HowItWorks = () => {
  const pills = [
    "SOW in 24 hours",
    "Fixed price · no surprises",
    "Built by senior operators",
  ];
  const steps = [
    { i: "search", n: "01", t: "Browse services", d: "Pick a package or pre-built bundle based on the HubSpot work you need." },
    { i: "bag",    n: "02", t: "Build your cart", d: "Stack services, compare pricing, and apply bundle discounts automatically." },
    { i: "mail",   n: "03", t: "Approve the SOW", d: "We send a clear Statement of Work with deliverables, timeline, and fixed pricing within 24 hours." },
    { i: "rocket", n: "04", t: "Kickoff & ship",  d: "Once signed, your senior operator starts the work and sends weekly progress updates." },
  ];
  return (
    <section className="how-section" id="how">
      <div className="wrap">
        <div className="how-pills">
          {pills.map(p => (
            <span className="how-pill" key={p}>
              <Icon name="check" size={12}/>
              {p}
            </span>
          ))}
        </div>

        <div className="how-head">
          <h2>A <em>simple process</em> to get started</h2>
          <p>Choose a fixed-price HubSpot service, approve the SOW, and start with a senior operator. <span className="how-head-accent">No payment required</span> until the scope is signed.</p>
        </div>

        <div className="how-grid">
          {steps.map((s, idx) => (
            <div className="how-step" key={s.t}>
              <div className="hs-num">{s.n}</div>
              <div className="hs-ic"><Icon name={s.i} size={22}/></div>
              <h3>{s.t}</h3>
              <div className="hs-divider" aria-hidden="true"/>
              <p>{s.d}</p>
              {idx < steps.length - 1 && <div className="hs-arrow"><Icon name="arrow" size={14}/></div>}
            </div>
          ))}
        </div>

        <div className="how-paths">
          <div className="how-path how-path-shop">
            <div className="hp-ic"><Icon name="bag" size={22}/></div>
            <div className="hp-body">
              <h3>Buying a defined package?</h3>
              <p>Browse services, add them to cart, and receive your SOW in 24 hours.</p>
              <a href="/shop" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToShop(); }}>
                Browse services <Icon name="arrow" size={12}/>
              </a>
            </div>
          </div>
          <div className="how-paths-or" aria-hidden="true"><span>OR</span></div>
          <div className="how-path how-path-custom">
            <div className="hp-ic hp-ic-custom"><Icon name="puzzle" size={22}/></div>
            <div className="hp-body">
              <h3>Need a custom build?</h3>
              <p>Tell us what you're trying to ship. We'll scope a custom fixed-price engagement within 48 hours.</p>
              <a href="/contact" className="btn btn-custom" onClick={(e) => { e.preventDefault(); goToContact(); }}>
                Talk about custom work <Icon name="arrow" size={12}/>
              </a>
            </div>
          </div>
        </div>

        <p className="how-foot">
          <Icon name="shield" size={14}/>
          <span>No payment required until the scope is approved and the SOW is signed.</span>
        </p>
      </div>
    </section>
  );
};

/* ============ Bundles ============ */
const Bundles = ({ onAdd }) => {
  const fmt = (n) => `$${n.toLocaleString("en-US")}`;
  return (
  <section className="bundles-section" id="bundles">
    <div className="wrap">
      <div className="section-head">
        <div>
          <h2>Save <em>with a bundle</em></h2>
            <div className="sub">FOUR JOURNEYS · BUILT FROM REAL CATALOG SERVICES · 15–25% OFF</div>
        </div>
          <a href="/contact" className="link" onClick={(e) => { e.preventDefault(); goToContact(); }}>Need a custom mix? <Icon name="arrow" size={12}/></a>
      </div>
      <div className="bundles">
          {BUNDLES.map((b, i) => {
            const itemsTotal = b.items.reduce((s, it) => s + it.price, 0);
            const regular = b.regular || itemsTotal;
            const savings = Math.max(0, regular - b.price);
            const pct = regular > 0 ? Math.round((savings / regular) * 100) : 0;
            return (
              <div key={b.id} className={`bundle ${b.featured ? "featured" : ""}`}
                   style={{ "--bundle-color": b.color }}>
            {b.featured && <span className="b-tag"><span className="d"/> MOST POPULAR</span>}
                <div className="b-head">
                  <span className="b-glyph" aria-hidden="true">
                    <Icon name={b.icon || "prism"} size={18}/>
                  </span>
                  <div className="b-head-text">
                    <span className="b-eye">{b.eyebrow}</span>
              <h3 className="b-name">{b.name} <em>bundle</em></h3>
                  </div>
                </div>
              <p className="b-sub">{b.sub}</p>

                <div className="b-price-block">
                  <div className="b-price-row">
                    <span className="b-price">{fmt(b.price)}</span>
                    <span className="b-regular" aria-label={`Regularly ${fmt(regular)}`}>{fmt(regular)}</span>
            </div>
                  <div className="b-meta-row">
                    <span className="b-savings">Save {fmt(savings)} · {pct}% off</span>
                    <span className="b-fixed">Fixed · one-time</span>
            </div>
                </div>

                <div className="b-best">
                  <span className="b-best-l">Best for</span>
                  <span className="b-best-t">{b.bestFor}</span>
                </div>

            <ul className="b-list">
              {b.items.map((it, idx) => (
                    <li key={idx}>
                      <Icon name="check" size={14}/>
                      <span className="b-li-name">{it.name}</span>
                      <span className="b-li-price">{fmt(it.price)}</span>
                    </li>
                  ))}
                  {b.bonus && b.bonus.map((bn, idx) => (
                    <li key={`bn-${idx}`} className="b-li-bonus">
                      <Icon name="plus" size={14}/>
                      <span className="b-li-name">{bn}</span>
                      <span className="b-li-price">Included</span>
                    </li>
              ))}
            </ul>

            <button className={`btn ${b.featured ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => onAdd({
                    id:`bundle-${b.id}`, num:"B"+(i+1),
                    name:`${b.name} Bundle`, desc:b.sub,
                    price:b.price, duration:"bundle",
                    category:"Bundle", glyph:b.icon || "prism"
                  })}>
                  Add {b.name.toLowerCase()} bundle <Icon name="arrow" size={12}/>
            </button>
          </div>
            );
          })}
      </div>
    </div>
  </section>
);
};

/* ============ Reviews ============ */
const Reviews = ({ showDirectory }) => {
  const stacked = REVIEWS.filter(r => r.slot === "stack");
  const micro   = REVIEWS.filter(r => r.slot === "micro");
  return (
    <section className="reviews-section">
      <div className="wrap">
        <div className="reviews-head">
          <h2>Why teams <em>choose</em> Vestal Hub.</h2>
          <div className="rate">
            <Stars/>
            <b>5 / 5</b>
            <span className="rv-dot">·</span>
            <span>Trusted by 100+ companies</span>
          </div>
        </div>

        <div className="reviews-grid">
          {stacked.map((x, i) => (
            <article className="review review-small" key={x.id || i}>
              <div className="rv-head">
                <Stars/>
                <span className="rv-pill">{x.metric || x.badge}</span>
              </div>
              <p className="t" dangerouslySetInnerHTML={{__html:`"${x.text}"`}}/>
              <div className="w">
                <img className="av av-img" src={x.avatar} alt={x.name} loading="lazy"/>
                <div className="n">
                  <b>{x.name}</b>
                  <span>{x.role} · <em>{x.company}</em></span>
                </div>
                {x.logo && <img className="rv-co-logo" src={x.logo} alt={x.company} loading="lazy"/>}
              </div>
            </article>
          ))}
        </div>

        <div className="reviews-micro">
          {micro.map((x, i) => (
            <div className="rv-mini" key={x.id || i}>
              <Stars/>
              <p dangerouslySetInnerHTML={{__html:`"${x.text}"`}}/>
              <small><b>{x.name}</b>{x.role ? ` · ${x.role}` : ""} · {x.company}</small>
            </div>
          ))}
        </div>

        {showDirectory && (
          <p className="reviews-directory">
            Check more testimonials at the{" "}
            <a href="https://ecosystem.hubspot.com/marketplace/solutions/vestalhub" target="_blank" rel="noopener noreferrer">
              HubSpot Solutions Partner Directory
            </a>
          </p>
        )}
      </div>
    </section>
  );
};

/* ============ Logos ============ */
const LogoMarquee = ({ items, title, label, className = "" }) => (
  <section className={"logo-bar" + (className ? " " + className : "")} aria-label={label}>
    <div className="wrap">
      <div className="t">{title}</div>
    </div>
    <div className="logo-marquee">
      <div className="logo-marquee-track">
        {[0, 1].map(copy => (
          <div className="logo-marquee-set" key={copy} aria-hidden={copy === 1 ? "true" : undefined}>
            {items.map(l => {
              const className = "lg" + (l.tall ? " lg-tall" : "");
              const img = <img src={l.src} alt={copy === 0 ? l.name : ""} decoding="async"/>;
              if (l.href) {
                return (
                  <a
                    className={className}
                    key={`${copy}-${l.name}`}
                    href={l.href}
                    title={l.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={copy === 1 ? -1 : undefined}
                  >
                    {img}
                  </a>
                );
              }
              return (
                <div className={className} key={`${copy}-${l.name}`} title={l.name}>
                  {img}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Logos = () => (
  <LogoMarquee
    items={LOGOS}
    title="Driving revenue for high-tech companies"
    label="Clients"
  />
);

const StackLogos = () => (
  <LogoMarquee
    className="stack-bar"
    items={typeof STACK_LOGOS !== "undefined" ? STACK_LOGOS : (window.STACK_LOGOS || [])}
    title="Experts connecting and improving your stack"
    label="Tools we connect"
  />
);

/* ============ Stats — second social proof band ============ */
const Stats = () => {
  const stats = [
    { n: "100+",  l: "Companies served" },
    { n: "5/5", l: "Average rating" },
    { n: "$24M+", l: "Pipeline shipped for clients" },
    { n: "28d",   l: "Average time to live" },
    { n: "100%",  l: "Fixed-price engagements" },
  ];
  return (
    <section className="stats-section">
      <div className="wrap">
        <div className="stats-inner">
          <div className="stats-head">
            <h2>Operators ship <em>real numbers</em>.</h2>
            <p>Not vanity metrics. Pipeline, ARR, and shipped systems — measured in dollars and weeks, not slides.</p>
          </div>
          <div className="stats-grid">
            {stats.map(s => (
              <div className="stat" key={s.l}>
                <div className="n">{s.n}</div>
                <div className="l">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ FAQ ============ */
const Faq = () => (
  <section className="faq-section">
    <div className="wrap">
      <div className="faq-grid">
        <div>
          <h2>Common <em>questions</em></h2>
          <p>Shopping for services is different. Here's what most teams want to know <em>before they check out</em>. Need something not in the shop? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Tell us what you're shipping</a>.</p>
        </div>
        <div className="faq-list">
          {FAQ.map((f, i) => (
            <details className="faq-item" key={i} open={i === 0}>
              <summary>
                <span>{f.q}</span>
                <span className="plus"><Icon name="plus" size={14}/></span>
              </summary>
              <p className="ans">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ============ CTA ============ */
const Cta = () => (
  <section className="cta-band" id="cta">
    <div className="wrap">
      <div className="cta-inner">
        <h2>Ready to compound your <em>revenue?</em></h2>
        <p>Pick a service or send a custom brief. SOW in your inbox within 24 hours — <em>no sales calls, no decks.</em></p>
        <div className="btns">
          <a href="/shop" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToShop(); }}>Shop services <Icon name="arrow" size={14}/></a>
          <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} className="btn btn-ghost">Talk about custom work</a>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   HUBSPOT FOR SAAS / TECH STARTUPS — /hubspot-for-saas
   Homepage-style conversion LP for the SaaS/tech niche. Leads
   with results + 12+ years of niche expertise; reuses the shared
   Logos + Reviews bands so social proof stays in sync with data.
============================================================ */
// LinkedIn conversion ID for the "HubSpot for SaaS lead" conversion (separate
// from the /hubspot-consultation one). Fires on contact-form submit below.
const LINKEDIN_SAAS_CONVERSION_ID = 28448594;

const HubSpotForSaasPage = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* Inline HubSpot Meetings embed for the "Book a call" section. Mirrors
   * BookingPage — the loader script needs a sibling div with data-src. */
  const meetingsRef = useRef(null);
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => { if (meetingsRef.current) meetingsRef.current.innerHTML = ""; };
  }, []);

  /* Bottom-of-page contact form → /api/contact (same endpoint as ContactPage). */
  const [form, setForm] = useState({ name:"", email:"", company:"", msg:"" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const posted = await postContact({
          name: form.name,
          email: form.email,
          company: form.company,
          topic: "saas",
          message: `HubSpot for SaaS lead (/hubspot-for-saas)\n\n${form.msg.trim() || "(no message)"}`,
        }, e.target);
      if (!posted.spam) {
        try { window.gtag && window.gtag("event", "generate_lead", { value: 30, currency: "USD", method: "saas_contact_form" }); } catch {}
        try { window.posthog && window.posthog.capture("saas_lead_submitted", { company: form.company || null }); } catch {}
        try { if (window.lintrk && LINKEDIN_SAAS_CONVERSION_ID) window.lintrk("track", { conversion_id: LINKEDIN_SAAS_CONVERSION_ID }); } catch {}
      }
      setSent(true);
      setForm({ name:"", email:"", company:"", msg:"" });
    } catch (err) {
      const msg = err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.";
      console.error("[saas:submit] failed:", err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const results = [
    {
      i: "rocket",
      n: "01 / PIPELINE",
      t: "Pipeline that",
      e: "compounds.",
      d: "A clean lifecycle from signup and trial to paid and expansion, with scoring and routing so your reps spend time on the accounts most likely to convert and grow.",
      k: "Built for product-led & sales-led motions",
    },
    {
      i: "chart",
      n: "02 / REVENUE",
      t: "Revenue you",
      e: "can actually see.",
      d: "MRR, ARR, churn and expansion wired straight from Stripe and your product data into HubSpot dashboards — so you know what's growing and what's leaking, in real time.",
      k: "Board-ready reporting, no spreadsheets",
    },
    {
      i: "zap",
      n: "03 / SCALE",
      t: "A stack that",
      e: "scales with you.",
      d: "Integrations, automation and AI that hold up from seed to Series B and beyond — built by people who've done it dozens of times, not learned it on your portal.",
      k: "Seed → Series B and beyond",
    },
  ];

  const stats = [
    { n: "12+ yrs", l: "In HubSpot & RevOps" },
    { n: "100+",    l: "SaaS & tech teams served" },
    { n: "5 / 5",   l: "Average client rating" },
    { n: "$24M+",   l: "Pipeline shipped for clients" },
    { n: "Gold",    l: "HubSpot Solutions Partner" },
  ];

  const faq = [
    { q: "Do you only work with SaaS and tech companies?",
      a: "It's our specialty. We've spent 12+ years shipping HubSpot and revenue operations almost exclusively for SaaS, fintech and AI startups — so we already know your motion, your metrics (MRR, ARR, NRR, churn) and the integrations you'll need. You're not paying us to learn your world." },
    { q: "We just raised our seed round — is it too early for HubSpot?",
      a: "That's often the best time. Getting your CRM, lifecycle and reporting right before you scale headcount saves you an expensive rebuild later. We set you up with a foundation that grows with you instead of one you'll outgrow in six months." },
    { q: "Can you connect HubSpot to Stripe and our product data?",
      a: "Yes — this is core to what we do. We pipe subscription and product usage data into HubSpot so MRR, ARR, upgrades, downgrades and churn live right next to your CRM records, and your dashboards reflect what's actually happening in the business." },
    { q: "We already have HubSpot but it's a mess. Can you fix it?",
      a: "Almost always. We start by auditing what's there, keep what works, and rebuild what's holding you back — clean data model, lifecycle, automation and reporting your team will actually trust." },
    { q: "How do engagements work and what do they cost?",
      a: "Every engagement is owned end-to-end by a senior HubSpot expert and scoped to your goals. The easiest start is a free 30-minute audit — we'll show you the quickest wins for your portal and exactly what we'd do next, with no obligation." },
    { q: "Who actually does the work?",
      a: "Your engagement is led by a dedicated, HubSpot-certified senior expert \u2014 your main solution architect from day one. The same person every week, who knows your portal and your business inside out. You\u2019re also backed by an account manager who keeps delivery on track and holds us to the quality and timelines you deserve. No juniors, no handoffs." },
  ];

  return (
    <div className="saas-lp">
      {/* ===== Hero ===== */}
      <section className="hero">
        <div className="wrap">
          <div className="hero-grid">
            <span className="saas-eyebrow">HUBSPOT FOR SAAS &amp; TECH STARTUPS</span>
            <h1>Turn HubSpot into your <span className="hl">revenue engine.</span></h1>
            <p className="hero-lead">
              We're the HubSpot experts SaaS and tech startups trust to turn messy CRM and revenue data into pipeline, MRR visibility, and growth you can report on.
            </p>
            <div className="hero-ctas">
              <a href="#book-call" className="btn btn-primary" onClick={scrollTo("book-call")}>Book a call <Icon name="arrow" size={14}/></a>
              <a href="#contact" className="btn btn-ghost" onClick={scrollTo("contact")}>Send us a message</a>
            </div>
            <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
              <div className="hero-cred-cell">
                <img
                  src="assets/trust/hubspot-gold-partner.png"
                  alt="HubSpot Solutions Partner — Gold"
                  className="hero-cred-badge"
                  width="120" height="120" loading="lazy"
                />
                <span className="hero-cred-copy">
                  <b>HubSpot</b>
                  <span>Gold Solutions Partner</span>
                </span>
              </div>
              <span className="hero-cred-divider" aria-hidden="true"/>
              <div className="hero-cred-cell">
                <span className="hero-cred-avatars" aria-hidden="true">
                  <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                </span>
                <span className="hero-cred-copy">
                  <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                  <span>Trusted by <b>100+ SaaS &amp; tech teams</b></span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Logos (shared) ===== */}
      <Logos />

      {/* ===== Revenue insights showcase ===== */}
      <section className="saas-showcase">
        <div className="wrap">
          <div className="saas-showcase-grid">
            <div className="saas-showcase-copy">
              <span className="saas-eyebrow saas-eyebrow-dark">FROM RAW DATA TO REVENUE INSIGHT</span>
              <h2>Your revenue data, finally <em>telling you something.</em></h2>
              <p>
                We wire HubSpot to the tools that actually run your business — Stripe, your product, your billing — and turn the raw numbers into live dashboards your whole team trusts. The metrics that decide your next move, in one place.
              </p>
              <ul className="saas-showcase-bullets">
                <li><Icon name="check" size={16}/> MRR &amp; ARR at a glance, always current</li>
                <li><Icon name="check" size={16}/> New, upgrade, downgrade &amp; churn broken out month over month</li>
                <li><Icon name="check" size={16}/> Subscription &amp; product data flowing straight into the CRM</li>
                <li><Icon name="check" size={16}/> Board-ready reporting without the spreadsheet gymnastics</li>
              </ul>
              <a href="#book-call" className="btn btn-primary" onClick={scrollTo("book-call")}>
                Show me what's possible <Icon name="arrow" size={14}/>
              </a>
            </div>
            <div className="saas-showcase-media">
              <div className="saas-frame">
                <div className="saas-frame-bar" aria-hidden="true">
                  <span/><span/><span/>
                </div>
                <img
                  src="assets/HubSpot-Stripe-Dashboard.png"
                  alt="HubSpot dashboard showing Stripe subscription KPIs — MRR, ARR, MRR change by month, and cumulative new, upgrade, downgrade and churn revenue"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Results grid ===== */}
      <section className="features saas-results" id="results">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT CHANGES</span>
            <h2>What happens when SaaS teams run HubSpot <em>right.</em></h2>
            <p>Less time fighting the CRM, more time compounding revenue. Here's what our clients walk away with.</p>
          </div>
          <div className="feat-grid">
            {results.map(x => (
              <div className="feat" key={x.n}>
                <div className="ic"><Icon name={x.i} size={26}/></div>
                <div className="n">{x.n}</div>
                <h3>{x.t}<br/><em>{x.e}</em></h3>
                <p>{x.d}</p>
                <div className="feat-divider" aria-hidden="true"/>
                <div className="feat-foot">
                  <span className="feat-foot-ic" aria-hidden="true"><Icon name="check" size={12}/></span>
                  <span>{x.k}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Reviews (shared) ===== */}
      <Reviews />

      {/* ===== Expertise / stats band ===== */}
      <section className="stats-section">
        <div className="wrap">
          <div className="stats-inner">
            <div className="stats-head">
              <h2>Experts in this niche — for <em>12+ years.</em></h2>
              <p>We're not a generalist agency dabbling in tech. Shipping HubSpot and RevOps for SaaS, fintech and AI startups is the only thing we do — and we've done it for a decade-plus.</p>
            </div>
            <div className="stats-grid">
              {stats.map(s => (
                <div className="stat" key={s.l}>
                  <div className="n">{s.n}</div>
                  <div className="l">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Common <em>questions</em></h2>
              <p>Everything SaaS and tech founders ask before they bring us in. Still wondering something? <a href="#book-call" onClick={scrollTo("book-call")}>Book a quick call</a> and ask us live.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-band">
        <div className="wrap">
          <div className="cta-inner">
            <h2>Ready to make HubSpot your <em>growth engine?</em></h2>
            <p>Two ways to start: grab a 30-minute slot on a senior expert's calendar, or send us a note. <em>No obligation either way.</em></p>
            <div className="btns">
              <a href="#book-call" className="btn btn-primary" onClick={scrollTo("book-call")}>Book a call <Icon name="arrow" size={14}/></a>
              <a href="#contact" className="btn btn-ghost" onClick={scrollTo("contact")}>Send us a message</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Book a call ===== */}
      <section className="saas-book" id="book-call">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>Grab 30 minutes with a <em>senior HubSpot expert.</em></h2>
            <p>Pick a time that works for you. We'll review where your HubSpot and revenue data stand today and show you the quickest wins for your stack — no obligation.</p>
          </div>
          <div className="book-embed" ref={meetingsRef}/>
        </div>
      </section>

      {/* ===== Contact form (bottom) ===== */}
      <section className="saas-contact" id="contact">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">CONTACT US</span>
            <h2>Ready to get the most out of <em>HubSpot?</em></h2>
            <p>Send us a note and a senior HubSpot expert will get back to you within one business day.</p>
          </div>
          <div className="contact-form-card saas-contact-card">
            <GuardedForm onSubmit={submit} className="contact-form">
              <HoneypotField />
              <label>
                <span>Your name *</span>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Jane Doe"/>
              </label>
              <label>
                <span>Work email *</span>
                <input type="email" required value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="jane@acme.com"/>
              </label>
              <label>
                <span>Company</span>
                <input type="text" value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder="Acme Inc."/>
              </label>
              <label>
                <span>What's slowing HubSpot down? *</span>
                <textarea required rows={5} value={form.msg} onChange={e => setForm({...form, msg:e.target.value})} placeholder="What you're trying to fix: …&#10;Where you are today: …"></textarea>
              </label>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Sending…" : <>Send message <Icon name="arrow" size={14}/></>}
              </button>
              {sent && (
                <div className="contact-form-sent">
                  <b>Got it.</b> A senior HubSpot expert will reply within one business day at the email you provided.
                </div>
              )}
              {error && <div className="checkout-err">{error}</div>}
            </GuardedForm>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ===========================================================
   HUBSPOT ADMIN / SUPPORT LANDING PAGE — /hubspot-admin
   Month-to-month "done-for-you" HubSpot support LP. Three
   support plans, book-a-call CTA (no online payment). Reuses
   the shared .saas-lp shell + Logos + Reviews bands so social
   proof and styling stay in sync with the rest of the site.
=========================================================== */
// LinkedIn conversion ID for the "HubSpot support lead" conversion. Set to a
// real Campaign Manager conversion id to start firing; until then lintrk track
// is intentionally a no-op (guarded below).
const LINKEDIN_ADMIN_CONVERSION_ID = null;

const HubSpotAdminPage = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* Inline HubSpot Meetings embed for the "Book a call" section. Mirrors
   * BookingPage — the loader script needs a sibling div with data-src. */
  const meetingsRef = useRef(null);
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => { if (meetingsRef.current) meetingsRef.current.innerHTML = ""; };
  }, []);

  /* Lead capture → /api/contact (same endpoint as ContactPage). Two forms
   * share this helper: the hero qualifying form and the bottom contact form,
   * each with its own independent state so submitting one doesn't affect the
   * other. */
  const sendLead = async (fields, formEl) => {
    const posted = await postContact({
        name: fields.name,
        email: fields.email,
        company: fields.company,
        topic: "admin",
        message: `HubSpot support lead (/hubspot-admin)\n\n${(fields.msg || "").trim() || "(no message)"}`,
      }, formEl);
    if (posted.spam) return;
    try { window.gtag && window.gtag("event", "generate_lead", { value: 30, currency: "USD", method: "admin_contact_form" }); } catch {}
    try { window.posthog && window.posthog.capture("admin_lead_submitted", { company: fields.company || null }); } catch {}
    try { if (window.lintrk && LINKEDIN_ADMIN_CONVERSION_ID) window.lintrk("track", { conversion_id: LINKEDIN_ADMIN_CONVERSION_ID }); } catch {}
  };

  /* Hero qualifying form. */
  const [hForm, setHForm] = useState({ name:"", email:"", company:"", msg:"" });
  const [hSubmitting, setHSubmitting] = useState(false);
  const [hSent, setHSent] = useState(false);
  const [hError, setHError] = useState(null);
  const heroSubmit = async (e) => {
    e.preventDefault();
    setHError(null);
    setHSubmitting(true);
    try {
      await sendLead(hForm, e.target);
      setHSent(true);
      setHForm({ name:"", email:"", company:"", msg:"" });
    } catch (err) {
      console.error("[admin:heroSubmit] failed:", err);
      setHError(err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.");
    } finally {
      setHSubmitting(false);
    }
  };

  /* Bottom-of-page contact form. */
  const [form, setForm] = useState({ name:"", email:"", company:"", msg:"" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await sendLead(form, e.target);
      setSent(true);
      setForm({ name:"", email:"", company:"", msg:"" });
    } catch (err) {
      console.error("[admin:submit] failed:", err);
      setError(err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const problems = [
    { i: "zap",     n: "FOLLOW-UP",   t: "Leads slip through the cracks",
      d: "Deals go cold because no one remembers to follow up. We wire sequences, tasks and reminders so every qualified opportunity gets worked — automatically." },
    { i: "user",    n: "ADOPTION",    t: "A portal your team quietly abandoned",
      d: "If reps don't trust HubSpot, they live in spreadsheets. We rebuild the setup around how your team actually works, then train them until it sticks." },
    { i: "chart",   n: "REPORTING",   t: "Reports nobody believes",
      d: "Messy data makes every dashboard suspect. We audit, dedupe and standardize so the numbers your leadership looks at are finally worth acting on." },
    { i: "refresh", n: "SILOS",       t: "Tools that don't talk to each other",
      d: "Data trapped in five apps is data you can't use. We connect your stack to HubSpot so it becomes the single source of truth your whole team shares." },
    { i: "shield",  n: "BUSYWORK",    t: "Manual work eating the week",
      d: "Your team should be selling and serving, not copy-pasting between tabs. We automate the repetitive work and hand hours back to every person on the team." },
    { i: "rocket",  n: "OWNERSHIP",   t: "No one actually owns HubSpot",
      d: "\"Admin\" is a hat someone wears badly on top of their real job. We become the owner — a senior operator responsible for keeping HubSpot healthy and moving forward." },
  ];

  const included = [
    { i: "user",    t: "Your dedicated admin",
      items: ["A senior operator, not a project manager", "Learns your business, goals and motion", "Best-practice recommendations", "Standing calls + planning sessions", "Tailored team training"] },
    { i: "zap",     t: "Build & automation",
      items: ["Pipelines, routing & sequences", "Complex workflow automation", "Lifecycle stages & lead scoring", "Sales, marketing & service setup", "Custom reports & dashboards"] },
    { i: "chart",   t: "Data hygiene",
      items: ["Data audits & health checks", "Deduplication & merging", "Property cleanup & standards", "Ongoing hygiene, not one-offs", "Imports & migrations"] },
    { i: "refresh", t: "Integrations",
      items: ["Native & third-party connectors", "Stripe, Slack, Calendly, Segment", "Two-way CRM syncs", "Custom API & webhook work", "HubSpot as your source of truth"] },
    { i: "rocket",  t: "AI on HubSpot",
      items: ["Breeze agents & Copilot setup", "Lead enrichment & qualification", "Support & knowledge chatbots", "AI-assisted content", "Custom AI automations"] },
    { i: "code",    t: "Design & development",
      items: ["Emails & landing pages", "Website & blog templates", "Custom CMS modules", "Programmable automations", "Brand-matched, on-spec builds"] },
  ];

  const plans = [
    { id: "essentials", name: "Essentials", price: "$999", hours: "10 hours / month", tag: "On standby",
      desc: "A HubSpot expert on standby — perfect for low-maintenance portals, or trying us out.",
      bullets: ["10 senior hours every month", "Troubleshooting, quick questions and small fixes", "Your same senior expert — always on standby", "Perfect for low-maintenance portals", "Cancel or pause anytime, no lock-in"] },
    { id: "growth", name: "Growth", price: "$1,699", hours: "20 hours / month", tag: "Most popular", badge: true,
      desc: "Done-for-you HubSpot support every week — unlimited requests, quick fixes in 2 business days.",
      bullets: ["20 senior hours every month", "Unlimited requests — send everything; we keep the list", "Quick fixes shipped in 2 business days", "Larger builds get a dated plan before work starts", "Slack or Microsoft Teams channel with your expert", "Friday recap, in writing, of everything delivered", "Cancel or pause anytime, no lock-in"] },
    { id: "premium", name: "Premium", price: "$2,999", hours: "40 hours / month", tag: "Daily HubSpot teams",
      desc: "For teams that lean on HubSpot daily — faster response, priority channel, monthly strategy.",
      bullets: ["40 senior hours every month", "Everything in Growth, faster and closer", "Next-business-day quick fixes", "Priority Slack or Microsoft Teams channel", "Same-day response line when something's on fire", "Monthly strategy call — what's new in HubSpot worth your time", "Quarterly portal scorecard, in writing"] },
  ];

  const stats = [
    { n: "30 days", l: "To your first measurable win" },
    { n: "Gold",    l: "HubSpot Solutions Partner" },
    { n: "8+ yrs",  l: "Average operator experience" },
    { n: "Zero",    l: "Lock-in contracts, ever" },
    { n: "100+",    l: "Teams served" },
  ];

  const faq = [
    { q: "Is this a contract or can I cancel?",
      a: "It's month-to-month with no lock-in. You can scale your plan up or down, or cancel with 30 days' notice — no annual commitment and no cancellation penalty. We'd rather earn the next month than trap you in a contract." },
    { q: "What counts as a \"support hour\"?",
      a: "Anything that moves your HubSpot forward: building workflows and reports, cleaning data, fixing broken automations, standing up integrations, training your team, or planning what's next. Your dedicated operator tracks hours transparently and you always see where they went." },
    { q: "Who actually does the work?",
      a: "Your engagement is led by a dedicated, HubSpot-certified senior expert \u2014 your main solution architect from day one. The same person every week, who knows your portal and your business inside out. You\u2019re also backed by an account manager who keeps delivery on track and holds us to the quality and timelines you deserve. No juniors, no handoffs." },
    { q: "What if I don't use all my hours in a month?",
      a: "Plans are sized to a realistic monthly workload so you're not over-buying. Hours are scoped to the month rather than banked indefinitely, but if your needs are lumpy we'll right-size your plan or point you to a one-off engagement instead. We'll always tell you the cheapest way to get what you need." },
    { q: "We already have HubSpot but it's a mess. Can you fix it?",
      a: "Almost always. Most plans start by auditing what's there, keeping what works, and rebuilding what's holding you back — clean data, sane automation and reporting your team will actually trust — then we keep it healthy month after month." },
    { q: "How fast can we start seeing results?",
      a: "Your operator is assigned within a couple of business days and we aim for a visible win in the first 30 days — a fixed report, a working automation, a cleaner database. Bigger builds are planned across your first few sprints so momentum never stalls." },
  ];

  return (
    <div className="saas-lp">
      {/* ===== Hero (split: copy + qualifying form) ===== */}
      <section className="hero admin-hero">
        <div className="wrap">
          <div className="hero-grid admin-hero-grid">
            <div className="admin-hero-copy">
              <span className="saas-eyebrow">HUBSPOT SUPPORT · DONE FOR YOU</span>
              <h1>Your dedicated HubSpot <span className="hl">admin, on tap.</span></h1>
              <p className="hero-lead">
                Month-to-month, done-for-you HubSpot support that fixes your setup and gets you real ROI in 30 days. A senior operator owns your portal — no lock-in contracts, no bloated agency price tag.
              </p>
              <ul className="admin-hero-bullets">
                <li><Icon name="check" size={16}/> A senior HubSpot operator owns your portal — never an SDR</li>
                <li><Icon name="check" size={16}/> Automations, reporting, data hygiene &amp; integrations, done for you</li>
                <li><Icon name="check" size={16}/> Month-to-month · cancel anytime · first win in 30 days</li>
              </ul>
              <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
                <div className="hero-cred-cell">
                  <img
                    src="assets/trust/hubspot-gold-partner.png"
                    alt="HubSpot Solutions Partner — Gold"
                    className="hero-cred-badge"
                    width="120" height="120" loading="lazy"
                  />
                  <span className="hero-cred-copy">
                    <b>HubSpot</b>
                    <span>Gold Solutions Partner</span>
                  </span>
                </div>
                <span className="hero-cred-divider" aria-hidden="true"/>
                <div className="hero-cred-cell">
                  <span className="hero-cred-avatars" aria-hidden="true">
                    <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                  </span>
                  <span className="hero-cred-copy">
                    <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                    <span>Trusted by <b>100+ HubSpot teams</b></span>
                  </span>
                </div>
              </div>
            </div>

            <div className="lp-form-card admin-hero-form" id="hero-form">
              <span className="lp-form-eye">GET A FREE HUBSPOT AUDIT</span>
              <h2>What's slowing your HubSpot down?</h2>
              <p className="lp-form-lead">Tell us where you're stuck and a senior operator will reply within one business day — with the quickest wins for your portal and which plan (if any) fits. No obligation.</p>
              <GuardedForm onSubmit={heroSubmit} className="lp-form contact-form">
                <HoneypotField />
                <label>
                  <span>Your name *</span>
                  <input type="text" required value={hForm.name} onChange={e => setHForm({...hForm, name:e.target.value})} placeholder="Jane Doe"/>
                </label>
                <label>
                  <span>Work email *</span>
                  <input type="email" required value={hForm.email} onChange={e => setHForm({...hForm, email:e.target.value})} placeholder="jane@acme.com"/>
                </label>
                <label>
                  <span>Company</span>
                  <input type="text" value={hForm.company} onChange={e => setHForm({...hForm, company:e.target.value})} placeholder="Acme Inc."/>
                </label>
                <label>
                  <span>What's your biggest HubSpot headache? (optional)</span>
                  <textarea rows={3} value={hForm.msg} onChange={e => setHForm({...hForm, msg:e.target.value})} placeholder="e.g. broken workflows, messy data, reports nobody trusts, no one owns HubSpot…"></textarea>
                </label>
                <button className="btn btn-primary lp-form-submit" type="submit" disabled={hSubmitting}>
                  {hSubmitting ? "Sending…" : <>Get my free audit <Icon name="arrow" size={14}/></>}
                </button>
                <p className="lp-form-microcopy">No credit card. No obligation. Reply within 1 business day.</p>
                <p className="lp-form-note">
                  <Icon name="shield" size={12}/> Your details go straight to a senior operator — never an SDR. We don't share or sell them. <a href="/privacy" onClick={(e) => { e.preventDefault(); goToPrivacy(); }}>Privacy</a>
                </p>
                {hSent && (
                  <div className="lp-form-sent">
                    <b>Got it.</b> A senior HubSpot operator will reply within one business day. Prefer to talk now? <a href="#book-call" onClick={scrollTo("book-call")}>Grab a slot below.</a>
                  </div>
                )}
                {hError && <div className="checkout-err">{hError}</div>}
              </GuardedForm>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Logos (shared) ===== */}
      <StackLogos />

      {/* ===== Problems we fix ===== */}
      <section className="features" id="problems">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">THE HUBSPOT PROBLEMS WE FIX</span>
            <h2>You bought HubSpot. Now make it <em>earn its keep.</em></h2>
            <p>Most teams use a fraction of what they pay for. These are the six problems we see over and over — and fix for good.</p>
          </div>
          <div className="feat-grid">
            {problems.map((x, i) => (
              <div className="feat" key={x.t}>
                <div className="ic"><Icon name={x.i} size={26}/></div>
                <div className="n">{`0${i + 1} / ${x.n}`}</div>
                <h3>{x.t}</h3>
                <p>{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== What's included ===== */}
      <section className="incl-section">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT'S INCLUDED</span>
            <h2>A whole HubSpot team, for <em>less than one hire.</em></h2>
            <p>Every plan gives you a dedicated admin plus specialists on call — the full done-for-you stack, whenever the work needs it.</p>
          </div>
          <div className="incl-grid">
            {included.map((g) => (
              <div className="incl-card" key={g.t}>
                <div className="incl-ic"><Icon name={g.i} size={22}/></div>
                <h3>{g.t}</h3>
                <ul>
                  {g.items.map((it, i) => (
                    <li key={i}><Icon name="check" size={12}/> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Logos />

      {/* ===== Plans & pricing ===== */}
      <section className="plans-section" id="plans">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">PLANS &amp; PRICING</span>
            <h2>Pick a plan.<br />See results in <em>30 days.</em></h2>
            <p>Three levels of month-to-month HubSpot support. Not sure which fits? Book a free call and we'll recommend one — or tell you that you don't need us yet.</p>
          </div>
          <div className="plans-grid">
            {plans.map((pl) => (
              <div className={`plan-card ${pl.badge ? "on" : ""}`} key={pl.id}>
                {pl.badge && <span className="plan-badge">★ Most popular</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{pl.price}</b><small>/mo</small></div>
                {pl.hours && <div className="plan-hours">{pl.hours}</div>}
                <p className="plan-desc">{pl.desc}</p>
                <ul className="plan-bullets">
                  {pl.bullets.map((b, i) => (
                    <li key={i}><Icon name="check" size={13}/> {b}</li>
                  ))}
                </ul>
                <a href="#book-call"
                   className={`btn ${pl.badge ? "btn-primary" : "btn-ghost"}`}
                   onClick={scrollTo("book-call")}>
                  Book a free call <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>
          <p className="plans-currency">Prices are in USD</p>
          <p className="plans-foot">
            <Icon name="check" size={13}/> No lock-in contracts
            <span aria-hidden="true"> · </span>
            <Icon name="check" size={13}/> Month-to-month
            <span aria-hidden="true"> · </span>
            <Icon name="check" size={13}/> Cancel anytime
          </p>
          <p className="plans-note">
            Plans cover ongoing HubSpot support, configuration and standard build work. Complex custom-code projects — bespoke integrations, programmable automations, large data migrations — are scoped separately and may carry an additional cost. We'll always quote it up front before starting.
          </p>
        </div>
      </section>

      {/* ===== Reviews (shared) ===== */}
      <Reviews />

      {/* ===== Stats band ===== */}
      <section className="stats-section">
        <div className="wrap">
          <div className="stats-inner">
            <div className="stats-head">
              <h2>The safe way to <em>finally fix HubSpot.</em></h2>
              <p>No annual contract to sign, no six-week discovery before anything ships. A senior operator starts working your portal in days — and you can walk any month.</p>
            </div>
            <div className="stats-grid">
              {stats.map((s) => (
                <div className="stat" key={s.l}>
                  <div className="n">{s.n}</div>
                  <div className="l">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Common <em>questions</em></h2>
              <p>Everything teams ask before starting a support plan. Still wondering something? <a href="#book-call" onClick={scrollTo("book-call")}>Book a quick call</a> and ask us live.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-band">
        <div className="wrap">
          <div className="cta-inner">
            <h2>Frustrated with your <em>HubSpot setup?</em></h2>
            <p>Grab 30 minutes with a senior operator. We'll look at your portal, show you the quickest wins, and tell you honestly whether a plan makes sense. <em>No hard sell.</em></p>
            <div className="btns">
              <a href="#book-call" className="btn btn-primary" onClick={scrollTo("book-call")}>Book a free call <Icon name="arrow" size={14}/></a>
              <a href="#contact" className="btn btn-ghost" onClick={scrollTo("contact")}>Send us a message</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Book a call ===== */}
      <section className="saas-book" id="book-call">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>It's time we <em>talk.</em></h2>
            <p>Use the calendar to grab a free 30-minute consultation. We'll analyze your setup, show you what it's costing you, and present a plan — no obligation.</p>
          </div>
          <div className="book-embed" ref={meetingsRef}/>
        </div>
      </section>

      {/* ===== Contact form (bottom) ===== */}
      <section className="saas-contact" id="contact">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">CONTACT US</span>
            <h2>Prefer to <em>write first?</em></h2>
            <p>Send us a note and a senior HubSpot operator will get back to you within one business day.</p>
          </div>
          <div className="contact-form-card saas-contact-card">
            <GuardedForm onSubmit={submit} className="contact-form">
              <HoneypotField />
              <label>
                <span>Your name *</span>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Jane Doe"/>
              </label>
              <label>
                <span>Work email *</span>
                <input type="email" required value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="jane@acme.com"/>
              </label>
              <label>
                <span>Company</span>
                <input type="text" value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder="Acme Inc."/>
              </label>
              <label>
                <span>What's slowing HubSpot down? *</span>
                <textarea required rows={5} value={form.msg} onChange={e => setForm({...form, msg:e.target.value})} placeholder="What you're trying to fix: …&#10;Where you are today: …"></textarea>
              </label>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Sending…" : <>Send message <Icon name="arrow" size={14}/></>}
              </button>
              {sent && (
                <div className="contact-form-sent">
                  <b>Got it.</b> A senior HubSpot operator will reply within one business day at the email you provided.
                </div>
              )}
              {error && <div className="checkout-err">{error}</div>}
            </GuardedForm>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ===========================================================
   PLANS — "We make HubSpot easy for you" — /plans
   Master sales page presenting the three ways to work with us:
   fixed-price onboarding (Pro/Enterprise toggle), month-to-month
   ongoing support, and fixed-quote technical projects. Reuses the
   shared .saas-lp shell + Logos + Reviews bands + .plans-grid cards
   so styling and social proof stay in sync with the rest of the
   site. Adapts the standalone "vestal-landing" mockup structure to
   the live design system. */
const PlansPage = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* Onboarding price tier: Professional vs Enterprise portals (+~70%). */
  const [tier, setTier] = useState("pro");

  const tickets = [
    { when: "Request · Mon 09:12", status: "Shipped",   cls: "shipped",
      body: "\u201CLead routing is sending everything to Ana \u2014 can you fix it?\u201D", stamp: "\u2713 Tue 11:40" },
    { when: "Request · Tue 15:03", status: "Plan sent",  cls: "plan",
      body: "\u201CWe need a lead scoring model sales actually trusts.\u201D", stamp: "Live Jul 24" },
    { when: "Request · Wed 10:47", status: "Queued · #3", cls: "queue",
      body: "\u201CCEO wants a pipeline dashboard for the board meeting.\u201D", stamp: "Est. Jul 29" },
  ];

  const pains = [
    { n: "01", strong: "You're paying for the whole platform",  rest: "\u2014 and using maybe a third of it." },
    { n: "02", strong: "Something's always half-built.",         rest: "The build never quite gets finished." },
    { n: "03", strong: "HubSpot ships new features every week.", rest: "Nobody on your team has time to keep up." },
    { n: "04", strong: "The forecast doesn't match reality",     rest: "\u2014 so nobody trusts the dashboard." },
    { n: "05", strong: "Every agency wants six weeks of discovery", rest: "before they'll even give you a price." },
  ];

  const cred = [
    "HubSpot Gold Solutions Partner",
    "12+ years in the platform",
    "100+ companies · 50+ portals",
    "15+ certifications · Top Rated Plus",
  ];

  const doors = [
    { i: "refresh", kind: "Monthly · Cancel anytime", t: "Ongoing Support", to: "support",
      d: "\u201CWe're on HubSpot but there's always something broken, half-built, or unlearned.\u201D", price: "from $1,699/mo", go: "See plans" },
    { i: "rocket", kind: "One-time · Fixed price", t: "Onboarding", to: "onboarding",
      d: "\u201CWe just bought HubSpot and nobody has time to set it up right.\u201D", price: "from $2,500", go: "See packages" },
    { i: "code", kind: "Project · Fixed quote", t: "Technical Projects", to: "technical",
      d: "\u201CWe need something HubSpot can't do out of the box.\u201D", price: "SOW in 24h", go: "Describe it" },
  ];

  const onboarding = [
    { id: "starter", name: "Starter", tag: "One Hub, set up right.", pro: "$2,500", ent: "$4,250", live: "Live in 21 days",
      feats: ["Portal foundations: users, permissions, properties, clean data import",
              "One Hub built right \u2014 Sales, Marketing or Service",
              "3 core automations", "2 dashboards your team will actually open", "3 training calls, recorded"] },
    { id: "growth", name: "Growth", tag: "Two Hubs, properly connected.", pro: "$4,200", ent: "$7,150", live: "Live in 30 days", hot: true,
      feats: ["Everything in Starter, for both Hubs",
              "Lifecycle stages that make the marketing \u2192 sales handoff real",
              "Lead routing + starter scoring", "5 automations, 4 dashboards", "5 training calls, recorded"] },
    { id: "full", name: "Full Platform", tag: "Marketing + Sales + Service, one system.", pro: "$5,500", ent: "$9,350", live: "Live in 45 days",
      feats: ["Everything in Growth, across all three Hubs",
              "Full-funnel reporting: first touch \u2192 closed-won \u2192 ticket",
              "Attribution foundations", "8 automations, 6 dashboards", "7 training calls, recorded"] },
  ];

  const support = [
    { id: "support", name: "Growth", tag: "Done-for-you HubSpot support, every week.", price: "$1,699", live: "20 hours / month", hot: true,
      feats: ["20 senior hours every month",
              "Unlimited requests \u2014 send everything; we keep the list",
              "Larger builds get a dated plan before work starts",
              "Slack or Microsoft Teams channel with your expert",
              "Friday recap, in writing, of everything delivered",
              "Your senior expert, every week \u2014 the same person who knows your portal",
              "Cancel or pause anytime, no lock-in"] },
    { id: "priority", name: "Premium", tag: "For teams that lean on HubSpot daily.", price: "$2,999", live: "40 hours / month",
      feats: ["40 senior hours every month",
              "Everything in Growth, faster and closer",
              "Priority Slack or Microsoft Teams channel",
              "Same-day response line when something's on fire",
              "Monthly strategy call \u2014 what's new in HubSpot worth your time",
              "Quarterly portal scorecard, in writing"] },
  ];

  const rules = [
    { h: "What's a quick fix?", p: "Anything under a couple of hours: a property, a broken workflow step, a report tweak, a template change, a \u201Chow should we\u2026?\u201D question. Shipped in 2 business days (next day on Premium)." },
    { h: "What's a build?", p: "Anything bigger: a scoring model, a pipeline rebuild, a dashboard suite, a nurture program. You get a dated milestone plan within one business day \u2014 approved by you before work starts." },
    { h: "One priority moves at a time.", p: "We'll always tell you what's in motion and when yours starts. On Premium, your urgent items jump the line. You're never guessing." },
    { h: "What's not covered \u2014 proudly.", p: "Custom code, API integrations, migrations and website builds are fixed-quote projects with a SOW in 24 hours. Members get priority scheduling and member pricing." },
  ];

  const whynot = [
    { h: "\u2026hire an admin?", p: "$80\u2013120k a year, three months to ramp \u2014 and they still can't cover the code. We start Monday." },
    { h: "\u2026use a cheap task service?", p: "You get certified juniors working tickets. Certifications are courses; this is 12 years in the platform." },
    { h: "\u2026call a big agency?", p: "Six weeks of discovery before you see a quote. We'll have shipped by then \u2014 with the price agreed in writing first." },
  ];

  const counters = [
    { n: "12+",  l: "Years in HubSpot" },
    { n: "100+", l: "Companies served" },
    { n: "50+",  l: "Portals built" },
    { n: "Gold", l: "Solutions Partner" },
  ];

  const faq = (window.PLANS_FAQ && window.PLANS_FAQ.length) ? window.PLANS_FAQ : [
    { q: "Who actually does the work?", a: "Your engagement is led by a dedicated, HubSpot-certified senior expert \u2014 your main solution architect from day one. The same person every week, who knows your portal and your business inside out. You\u2019re also backed by an account manager who keeps delivery on track and holds us to the quality and timelines you deserve. No juniors, no handoffs." },
    { q: "What counts as a quick fix vs. a build?", a: "A quick fix is anything under a couple of hours: a property, a workflow step, a report tweak, a question. A build is bigger \u2014 a scoring model, a pipeline rebuild, a dashboard suite \u2014 and always gets a dated plan, approved by you, before work starts." },
    { q: "How fast will my request ship?", a: "Quick fixes: 2 business days on Growth, next business day on Premium. Builds: you get the dates up front. Either way, you always know when." },
    { q: "Is it really unlimited?", a: "Yes \u2014 send everything. One priority moves at a time, and you always see what's in motion and what's next. On Premium, urgent items jump the line." },
    { q: "What's not included in the plans?", a: "Custom code, API integrations, migrations and website builds. Those are fixed-quote projects \u2014 describe the problem and you'll have a fixed-price SOW within 24 hours. Plan members get priority scheduling and member pricing." },
    { q: "Do I have to get on calls?", a: "No. Everything works async and in writing. Calls exist if you want them \u2014 and Premium includes a monthly strategy call with your expert." },
    { q: "Contracts?", a: "None. Month-to-month, pause or cancel anytime, no lock-in and no cancellation penalty." },
  ];

  return (
    <div className="saas-lp plp">
      {/* ===== Hero (split: copy + ticket artifacts) ===== */}
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">DONE-FOR-YOU HUBSPOT</span>
              <h1>We make HubSpot <span className="hl">easy</span> for you.</h1>
              <p className="hero-lead">
                From quick fixes to complex implementations, we handle HubSpot so your team stays focused — a senior HubSpot expert with <b>12+ years</b> in the platform, doing the work for you.
              </p>
              <div className="plp-hero-ctas">
                <a href="#support" className="btn btn-primary" onClick={scrollTo("support")}>See plans &amp; prices <Icon name="arrow" size={14}/></a>
                <a href="/hubspot-consultation" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToConsultation(); }}>Get a free audit in 48h</a>
              </div>
              <p className="plp-hero-note">HubSpot Gold Solutions Partner</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Doors overview ===== */}
      <section className="plp-doors-section" id="doors">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">THREE WAYS IN</span>
            <h2>A solution for every kind of <em>HubSpot support you need.</em></h2>
          </div>
          <div className="plp-doors">
            {doors.map((d) => (
              <a className="plp-door" href={`#${d.to}`} key={d.t} onClick={scrollTo(d.to)}>
                <span className="plp-door-ic"><Icon name={d.i} size={22}/></span>
                <span className="plp-door-kind">{d.kind}</span>
                <h3>{d.t}</h3>
                <p>{d.d}</p>
                <span className="plp-door-price">{d.price}</span>
                <span className="plp-door-go">{d.go} <Icon name="arrow" size={13}/></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pain (dark band) ===== */}
      <section className="plp-band-dark plp-pain">
        <div className="wrap">
          <span className="saas-eyebrow">SOUND FAMILIAR?</span>
          <h2>We help you run HubSpot, so you can run your business.</h2>
          <div className="plp-pain-list">
            {pains.map((p) => (
              <div className="plp-pain-item" key={p.n}>
                <span className="plp-pain-num">{p.n}</span>
                <p><strong>{p.strong}</strong> <span className="rest">{p.rest}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Promise + expert card ===== */}
      <section className="plp-promise">
        <div className="wrap plp-promise-grid">
          <div className="plp-promise-copy">
            <span className="saas-eyebrow saas-eyebrow-dark">THE FIX</span>
            <h2>What if HubSpot just <em>got handled?</em></h2>
            <p>You ask. A senior HubSpot expert ships it. You get it in writing — the scope, the price, the date, the Friday recap. Your team keeps doing their actual jobs while someone with 12+ years in the platform keeps HubSpot working for you, not the other way around.</p>
            <p>That's the whole model: <strong>premium HubSpot support, done for you.</strong></p>
          </div>
          <aside className="plp-expert" aria-label="Credentials">
            <h3>Senior expertise, verifiable</h3>
            <p className="plp-expert-role">Who you're working with</p>
            <ul className="plp-cred">
              {cred.map((c) => (<li key={c}><Icon name="check" size={13}/> {c}</li>))}
            </ul>
          </aside>
        </div>
      </section>

      {/* ===== Logos (shared) ===== */}
      <Logos />

      {/* ===== Ongoing Support ===== */}
      <section className="plans-section plp-support" id="support">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">ONGOING SUPPORT</span>
            <h2>Your senior HubSpot expert. <em>On call, every week.</em></h2>
            <p>HubSpot changes weekly. Your business changes monthly. Somebody has to keep the two in sync — and it shouldn't be your sales lead at 9pm with a support article open. You ask; it ships; you get a written recap every Friday.</p>
          </div>
          <div className="plans-grid two">
            {support.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.hot && <span className="plan-badge">★ Most teams start here</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{pl.price}</b><small>/month</small></div>
                <div className="plan-hours">{pl.live}</div>
                <ul className="plan-bullets">
                  {pl.feats.map((b, i) => (<li key={i}><Icon name="check" size={13}/> {b}</li>))}
                </ul>
                <a href="/contact" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={(e) => { e.preventDefault(); goToContact(); }}>
                  Start {pl.name} <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>

          <div className="plp-rules">
            {rules.map((r) => (
              <div className="plp-rule" key={r.h}>
                <h4>{r.h}</h4>
                <p>{r.p}</p>
              </div>
            ))}
          </div>

          <div className="plp-recap" aria-label="Example Friday recap">
            <div className="plp-recap-head"><span>friday-recap.txt</span><span>Fri 18:02</span></div>
            <div className="plp-recap-body">
              <b>Shipped this week</b><br/>
              <span className="ok">✓</span> Lead routing fixed — round-robin by territory (Tue)<br/>
              <span className="ok">✓</span> "Deals stuck &gt;30 days" report added to sales dashboard (Wed)<br/>
              <span className="ok">✓</span> Duplicate contacts merged: 312 records cleaned (Thu)<br/>
              <b>In motion</b><br/>
              <span className="next">→</span> Lead scoring model — live Jul 24, on plan<br/>
              <b>Queued next</b><br/>
              <span>·</span> Board pipeline dashboard · Renewal reminder workflow
            </div>
          </div>
          <p className="plp-recap-cap">Every Friday, in writing. You always know what's shipping and when.</p>

          <div className="plp-tickets-block">
            <p className="plp-tickets-cap">A few real requests — and how they move.</p>
            <div className="plp-tickets" aria-label="Example support requests">
              {tickets.map((t, i) => (
                <div className="plp-ticket" key={i}>
                  <div className="plp-ticket-head">
                    <span>{t.when}</span>
                    <span className={`plp-status s-${t.cls}`}>{t.status}</span>
                  </div>
                  <div className="plp-ticket-body">{t.body}</div>
                  {t.stamp && <div className={`plp-stamp s-${t.cls}`}>{t.stamp}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Onboarding ===== */}
      <section className="plans-section plp-onb" id="onboarding">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">ONBOARDING</span>
            <h2>You bought HubSpot to grow — not to become a <em>HubSpot expert.</em></h2>
            <p>HubSpot charges ~$3,000 to coach you through setting it up yourself, homework included. For the same money, a senior expert just sets it up. You attend a few calls; we do the rest.</p>
          </div>
          <div className="plp-toggle" role="group" aria-label="Choose your HubSpot tier">
            <button type="button" aria-pressed={tier === "pro"} onClick={() => setTier("pro")}>Professional</button>
            <button type="button" aria-pressed={tier === "ent"} onClick={() => setTier("ent")}>Enterprise</button>
          </div>
          <div className="plans-grid">
            {onboarding.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.hot && <span className="plan-badge">★ Most popular</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{tier === "ent" ? pl.ent : pl.pro}</b><small>one-time</small></div>
                <div className="plan-hours">{pl.live}</div>
                <ul className="plan-bullets">
                  {pl.feats.map((b, i) => (<li key={i}><Icon name="check" size={13}/> {b}</li>))}
                </ul>
                <a href="/contact" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={(e) => { e.preventDefault(); goToContact(); }}>
                  Get started <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>
          <p className="plans-note">
            Full inclusion checklist published per package · Fixed price, in writing · A call only if you want one. Prices shown are for {tier === "ent" ? "Enterprise" : "Professional"} portals.
          </p>
        </div>
      </section>

      {/* ===== Technical projects (dark band) ===== */}
      <section className="plp-band-dark plp-tech" id="technical">
        <div className="wrap">
          <span className="saas-eyebrow">TECHNICAL PROJECTS</span>
          <h2>The work support plans won't touch.</h2>
          <div className="plp-tech-grid">
            <div>
              <p className="plp-tech-lead">Built directly by a senior expert with 12+ years in the platform — not handed to a task queue.</p>
              <ul className="plp-tech-list">
                <li><Icon name="arrow" size={15}/> Custom-coded workflows &amp; programmable automation</li>
                <li><Icon name="arrow" size={15}/> API integrations that pull your product data into the CRM — Stripe, Segment, PostHog, anything with an API</li>
                <li><Icon name="arrow" size={15}/> Migrations from Salesforce, Pipedrive or Marketo — without losing history</li>
              </ul>
            </div>
            <div className="plp-sow">
              <h3>Describe it. Get a price tomorrow.</h3>
              <p>Tell us the problem in a few sentences. Within 24 hours you'll have a fixed-price SOW — scope, timeline and cost, in writing. No discovery weeks, no meetings required.</p>
              <p className="plp-sow-line">Typical integrations $6k–15k · Migrations quoted custom · One expert, limited slots</p>
              <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Describe your project <Icon name="arrow" size={14}/></a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Why not… ===== */}
      <section className="plp-why-section">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">THE ALTERNATIVES</span>
            <h2>Why not just…</h2>
          </div>
          <div className="plp-why-grid">
            {whynot.map((w) => (
              <div className="plp-why" key={w.h}>
                <h4>{w.h}</h4>
                <p>{w.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Proof: counters + reviews ===== */}
      <section className="stats-section plp-proof">
        <div className="wrap">
          <div className="stats-inner">
            <div className="stats-head">
              <h2>Senior means <em>something here.</em></h2>
              <p>No juniors learning on your portal, no account-manager telephone. One senior expert, a track record you can verify.</p>
            </div>
            <div className="stats-grid plp-counters">
              {counters.map((c) => (
                <div className="stat" key={c.l}>
                  <div className="n">{c.n}</div>
                  <div className="l">{c.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Reviews />

      {/* ===== Teardown CTA ===== */}
      <section className="cta-band" id="teardown">
        <div className="wrap">
          <div className="cta-inner">
            <h2>Not sure what you need? <em>Let the portal decide.</em></h2>
            <p>Give us a look at your HubSpot and within 48 hours you'll have a free, written teardown: what's holding it back, what it's costing you, and exactly what fixing it costs. If it's healthy, we'll tell you that too.</p>
            <div className="btns">
              <a href="/hubspot-consultation" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToConsultation(); }}>Get the free teardown <Icon name="arrow" size={14}/></a>
              <a href="/contact" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send us a message</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Plain answers, <em>in writing.</em> Obviously.</h2>
              <p>Everything teams ask before working with us. Still wondering something? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send us a note</a> and we'll reply within one business day.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ============ HubSpot Support page (/hubspot-support-plans) ============
   A simpler, single-offer version of /plans: month-to-month ongoing
   support only. Book-a-call is the primary conversion (inline HubSpot
   Meetings embed). Anything else — onboarding, technical builds — is
   routed to /contact. Reuses the .saas-lp .plp design system + shared
   Logos / Reviews bands so it stays in sync with the rest of the site. */
/* ============ HOME — HubSpot + outbound service-chooser (`/`) */

const TYPICAL_AGENCY_ITEMS = [
  "Specializes in either CRM or outbound",
  "Requires a full engagement",
  "Cold email disconnected from CRM",
  "Poor sending infrastructure",
  "Stops at meetings booked",
  "Multiple vendors and handoffs",
];

const VESTAL_HUB_ITEMS = [
  "Deep expertise in HubSpot + outbound",
  "HubSpot, outbound, or both",
  "Outbound connected directly to HubSpot",
  "Deliverability-first infrastructure",
  "Pipeline and revenue visibility",
  "One team can own the entire system",
];

const ComparisonItem = ({ variant, title, index }) => (
  <li className={`hv2-cmp-item hv2-cmp-item-${variant}`} style={{ "--i": index }}>
    <span className="hv2-cmp-ico" aria-hidden="true">
      <Icon name={variant === "good" ? "check" : "close"} size={14}/>
    </span>
    <span className="hv2-cmp-item-copy">
      {variant === "good" ? <b>{title}</b> : title}
    </span>
  </li>
);

const RevenueEngineComparison = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setInView(true); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.16, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={`hv2-cmp${inView ? " is-in" : ""}`}
      aria-labelledby="hv2-cmp-title"
    >
      <div className="wrap">
        <div className="hv2-cmp-intro">
          <h2 id="hv2-cmp-title">Most agencies only <br className="hv2-br-desktop"/>solve half the problem.</h2>
          <p>Lead gen agencies focus on outreach. HubSpot agencies focus on the CRM. We are experts in both, and connect them when your business needs it.</p>
        </div>

        <div className="hv2-cmp-grid">
          <article className="hv2-cmp-card hv2-cmp-card-typical">
            <h3>Typical agency</h3>
            <ul className="hv2-cmp-list">
              {TYPICAL_AGENCY_ITEMS.map((title, i) => (
                <ComparisonItem key={title} variant="bad" title={title} index={i}/>
              ))}
            </ul>
          </article>

          <div className="hv2-cmp-vs" aria-hidden="true"><span>VS</span></div>

          <article className="hv2-cmp-card hv2-cmp-card-vestal">
            <h3>Vestal Hub</h3>
            <ul className="hv2-cmp-list">
              {VESTAL_HUB_ITEMS.map((title, i) => (
                <ComparisonItem key={title} variant="good" title={title} index={i}/>
              ))}
            </ul>
          </article>
        </div>

        <div className="hv2-cmp-foot">
          <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>
            Book a call <Icon name="arrow" size={14}/>
          </a>
        </div>
      </div>
    </section>
  );
};

const visibleCaseStudies = () =>
  (typeof CASE_STUDIES !== "undefined" ? CASE_STUDIES : (window.CASE_STUDIES || []))
    .filter((x) => x && !x.hidden);

const caseStudyLogo = (s) => {
  if (s.logo) return s.logo;
  if (s.quotes && s.quotes[0] && s.quotes[0].logo) return s.quotes[0].logo;
  const reviews = typeof REVIEWS !== "undefined" ? REVIEWS : (window.REVIEWS || []);
  const review = s.reviewId && reviews.find((r) => r.id === s.reviewId);
  if (review && review.logo) return review.logo;
  const logos = typeof LOGOS !== "undefined" ? LOGOS : (window.LOGOS || []);
  const match = logos.find((l) => l.name === s.company);
  return (match && match.src) || "";
};

const CaseStudyCard = ({ s, featured = false }) => {
  const logo = caseStudyLogo(s);
  return (
    <a
      href={`/case-studies/${s.id}`}
      className={"cs-card" + (featured ? " cs-card-featured" : "")}
      onClick={(e) => { e.preventDefault(); goToCaseStudy(s.id); }}
    >
      <div className="cs-card-main">
        <div className="cs-card-meta">
          {featured && <span className="cs-card-badge">Featured</span>}
          <span className="cs-card-kicker">
            {s.industry || "Case study"}{s.stage ? ` · ${s.stage}` : ""}
          </span>
        </div>
        <div className="cs-card-brand">
          {logo && (
            <img
              className={"cs-card-logo" + (s.logoTall ? " cs-card-logo-tall" : "")}
              src={logo}
              alt={s.logoMark ? "" : s.company}
              loading="lazy"
              decoding="async"
            />
          )}
          {(!logo || s.logoMark) && <div className="cs-card-company">{s.company}</div>}
        </div>
        <div className="cs-card-headline">{s.headline || s.h1}</div>
        {(s.cardSub || s.sub) && <p className="cs-card-sub">{s.cardSub || s.sub}</p>}
        <span className="cs-card-cta">Read the case study <Icon name="arrow" size={14}/></span>
      </div>
      {s.outcomes?.length > 0 && (
        <div className="cs-card-metrics" aria-label="Key outcomes">
          {s.outcomes.slice(0, 3).map((o) => (
            <span key={o.metric} className="cs-card-metric">
              <b>{o.metric}</b>
              {featured && <span>{o.label}</span>}
            </span>
          ))}
        </div>
      )}
    </a>
  );
};

const FeaturedCaseStudies = () => {
  const featured = visibleCaseStudies().filter((s) => s.featured);
  if (!featured.length) return null;
  return (
    <section className="hv2-cases" aria-labelledby="hv2-cases-title">
      <div className="wrap">
        <div className="hv2-cases-head">
          <span className="saas-eyebrow saas-eyebrow-dark">CASE STUDIES</span>
          <h2 id="hv2-cases-title">Results you can measure.</h2>
          <p>See how we’ve helped companies turn better HubSpot, outbound, and GTM systems into more meetings, pipeline, and revenue.</p>
        </div>
        {featured.map((s) => <CaseStudyCard key={s.id} s={s} featured />)}
        <a
          href="/case-studies"
          className="hv2-cases-more"
          onClick={(e) => { e.preventDefault(); goToCaseStudies(); }}
        >
          See all case studies <Icon name="arrow" size={14}/>
        </a>
      </div>
    </section>
  );
};

const HomeV2Page = () => {
  const scrollToServices = (e) => {
    e?.preventDefault();
    document.getElementById("hv2-services")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToEngine = (e) => {
    e?.preventDefault();
    document.getElementById("hv2-engine")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const hubspotOffers = [
    {
      name: "Onboarding packages",
      blurb: "Sales, Marketing, Service or Ops Hub — set up right from day one.",
      href: "/hubspot-onboarding-packages",
      go: goToOnboardingPackages,
    },
    {
      name: "HubSpot admin support",
      blurb: "Month-to-month senior admin — unlimited requests, Friday recap.",
      href: "/hubspot-admin",
      go: goToAdmin,
    },
    {
      name: "Technical RevOps",
      blurb: "Custom code, APIs, data engineering and complex RevOps builds.",
      href: "/hubspot-technical-developers",
      go: goToTechSupport,
    },
  ];

  const outboundOffers = [
    {
      name: "Infrastructure setup",
      blurb: "Domains, warm-up, sequences and HubSpot sync — you own the stack.",
      href: "/outbound-infrastructure",
      go: goToOutboundInfrastructure,
    },
    {
      name: "Managed outbound",
      blurb: "We run targeting, campaigns, replies and booking — qualified meetings on your calendar.",
      href: "/outbound-optimization",
      go: goToOutboundOptimization,
    },
  ];

  return (
    <div className="hv2">
      <section className="hero hv2-hero">
        <div className="wrap">
          <div className="hero-grid hv2-hero-grid">
            <h1 className="hv2-anim hv2-anim-1">Revenue and pipeline systems<br/><span className="hl">built to scale.</span></h1>
            <p className="hero-lead hv2-anim hv2-anim-3">
              HubSpot done right. Outbound that books meetings. Whether you need one or both, we build systems that drive pipeline and revenue.
            </p>
            <div className="hero-ctas hv2-hero-ctas hv2-anim hv2-anim-4">
              <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>
                Book a call <Icon name="arrow" size={14}/>
              </a>
              <a href="#hv2-services" className="btn btn-ghost" onClick={scrollToServices}>
                Explore services
              </a>
            </div>
            <div className="hero-cred hv2-anim hv2-anim-5" role="complementary" aria-label="Partner status and customer trust">
              <div className="hero-cred-cell">
                <img
                  src="assets/trust/hubspot-gold-partner.png"
                  alt="HubSpot Solutions Partner — Gold"
                  className="hero-cred-badge"
                  width="120" height="120" loading="lazy"
                />
                <span className="hero-cred-copy">
                  <b>HubSpot</b>
                  <span>Gold Solutions Partner</span>
                </span>
              </div>
              <span className="hero-cred-divider" aria-hidden="true"/>
              <div className="hero-cred-cell">
                <span className="hero-cred-avatars" aria-hidden="true">
                  <img src="assets/trust/avatar-copilotkit.webp" alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-mavenagi.webp" alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-catio.webp" alt="" width="32" height="32" loading="lazy"/>
                  <img src="assets/trust/avatar-kindsight.webp" alt="" width="32" height="32" loading="lazy"/>
                </span>
                <span className="hero-cred-copy">
                  <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                  <span>Trusted by <b>100+ companies</b></span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StackLogos />

      <section className="hv2-services" id="hv2-services">
        <div className="wrap">
          <div className="hv2-services-head">
            <h2>One partner<br/><em>Two areas of expertise.</em></h2>
            <p>Need help with HubSpot? We can fix, build, or manage it. Need more pipeline? We can build and run your outbound engine. Need both? We connect everything into one GTM system.</p>
          </div>

          <div className="hv2-practice">
            <div className="hv2-pillars">
              <article className="hv2-pillar hv2-pillar-ob">
                <div className="hv2-pillar-head">
                  <h3>Outbound</h3>
                  <p className="hv2-pillar-lead">Build a predictable pipeline without destroying your domain reputation.</p>
                  <p>From infrastructure and deliverability to targeting, enrichment, campaigns and managed outbound.</p>
                </div>
                <ul className="hv2-offer-list">
                  {outboundOffers.map((o) => (
                    <li key={o.href}>
                      <a href={o.href} className="hv2-offer" onClick={(e) => { e.preventDefault(); o.go(); }}>
                        <span className="hv2-offer-copy">
                          <b>{o.name}</b>
                          <span>{o.blurb}</span>
                        </span>
                        <span className="hv2-offer-meta">
                          <span className="hv2-offer-price">more info</span>
                          <Icon name="arrow" size={14}/>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="hv2-pillar hv2-pillar-hs">
                <div className="hv2-pillar-head">
                  <h3>HubSpot</h3>
                  <p className="hv2-pillar-lead">Make HubSpot work the way your business actually sells.</p>
                  <p>From onboarding and CRM architecture to automation, integrations, RevOps, reporting and ongoing administration.</p>
                </div>
                <ul className="hv2-offer-list">
                  {hubspotOffers.map((o) => (
                    <li key={o.href}>
                      <a href={o.href} className="hv2-offer" onClick={(e) => { e.preventDefault(); o.go(); }}>
                        <span className="hv2-offer-copy">
                          <b>{o.name}</b>
                          <span>{o.blurb}</span>
                        </span>
                        <span className="hv2-offer-meta">
                          <span className="hv2-offer-price">more info</span>
                          <Icon name="arrow" size={14}/>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            <div className="hv2-join-rail">
              <span className="hv2-join-stems" aria-hidden="true"><span/><span/></span>
              <a href="#hv2-engine" className="hv2-join" onClick={scrollToEngine}>
                <span className="hv2-join-q">Need both?</span>
                <span className="hv2-join-cta">Build a Connected GTM System <Icon name="arrow" size={14}/></span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="hv2-engine" id="hv2-engine" aria-label="HubSpot and outbound working together">
        <div className="wrap">
          <div className="hv2-engine-head">
            <span className="saas-eyebrow saas-eyebrow-dark">ONE CONNECTED GROWTH ENGINE</span>
            <h2>Outbound to get meetings<br/>HubSpot to close deals</h2>
            <p>We wire both sides so leads land in HubSpot clean, get worked, and show up in reports your team trusts.</p>
          </div>
          <div className="hv2-engine-stage">
            <figure className="hv2-engine-panel">
              <img src="assets/homepage/outbound-engine.png" alt="Outbound engine: targeting, personalization, sending infrastructure, replies, and qualified meetings booked" width="1024" height="682" loading="lazy"/>
            </figure>
            <div className="hv2-engine-flow" aria-hidden="true">
              <span className="hv2-engine-arrow hv2-engine-arrow-fwd">→</span>
              <span className="hv2-engine-arrow hv2-engine-arrow-back">←</span>
            </div>
            <figure className="hv2-engine-panel">
              <img src="assets/homepage/hubspot-engine.png" alt="HubSpot engine: lead scoring, pipeline management, reporting, and closed deals" width="1024" height="682" loading="lazy"/>
            </figure>
          </div>
        </div>
      </section>

      <RevenueEngineComparison />

      <Logos />

      <FeaturedCaseStudies />

      <section className="hv2-growth" aria-labelledby="hv2-growth-title">
        <div className="wrap">
          <h2 id="hv2-growth-title">Let’s build what drives your next stage of growth.</h2>
          <p>HubSpot, outbound, or both — we build and implement the systems your team needs to generate pipeline, convert opportunities, and scale.</p>
          <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>
            Talk to a GTM Expert <Icon name="arrow" size={14}/>
          </a>
        </div>
      </section>

      <OutboundFeaturedTestimonial reviews={(typeof OUTBOUND_INFRASTRUCTURE !== "undefined" ? OUTBOUND_INFRASTRUCTURE : window.OUTBOUND_INFRASTRUCTURE).featuredReviews}/>

      <Reviews showDirectory />

      <section className="hv2-cta">
        <div className="wrap">
          <h2>Not sure where to start?</h2>
          <p>Thirty minutes with a senior expert. We’ll tell you the right engagement — or that you don’t need us yet.</p>
          <div className="hv2-cta-actions">
            <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>
              Book a call <Icon name="arrow" size={14}/>
            </a>
            <a href="/contact" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToContact(); }}>
              Contact us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const HubSpotSupportPage = () => {
  const meetingsRef = useRef(null);

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = scrollToId("book");

  /* Mount the HubSpot Meetings embed once on render. Mirrors ConsultationPage —
   * the loader script needs a sibling div with data-src to find. */
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  const support = [
    { id: "essentials", name: "Essentials", tag: "A HubSpot expert on standby.", price: "$999", hours: "10 hours / month",
      feats: ["10 senior hours every month",
              "Troubleshooting, quick questions and small fixes",
              "Your same senior expert \u2014 always on standby",
              "Perfect for low-maintenance portals \u2014 or trying us out",
              "Cancel or pause anytime, no lock-in"] },
    { id: "support", name: "Growth", tag: "Done-for-you HubSpot support, every week.", price: "$1,699", hours: "20 hours / month", hot: true,
      feats: ["20 senior hours every month",
              "Unlimited requests \u2014 send everything; we keep the list",
              "Quick fixes shipped in 2 business days",
              "Larger builds get a dated plan before work starts",
              "Slack or Microsoft Teams channel with your expert",
              "Friday recap, in writing, of everything delivered",
              "Your senior expert, every week \u2014 the same person who knows your portal",
              "Cancel or pause anytime, no lock-in"] },
    { id: "priority", name: "Premium", tag: "For teams that lean on HubSpot daily.", price: "$2,999", hours: "40 hours / month",
      feats: ["40 senior hours every month",
              "Everything in Growth, faster and closer",
              "Next-business-day quick fixes",
              "Priority Slack or Microsoft Teams channel",
              "Same-day response line when something's on fire",
              "Monthly strategy call \u2014 what's new in HubSpot worth your time",
              "Quarterly portal scorecard, in writing"] },
  ];

  const rules = [
    { h: "What's a quick fix?", p: "Anything under a couple of hours: a property, a broken workflow step, a report tweak, a template change, a \u201Chow should we\u2026?\u201D question. Shipped in 2 business days (next day on Premium)." },
    { h: "What's a build?", p: "Anything bigger: a scoring model, a pipeline rebuild, a dashboard suite, a nurture program. You get a dated milestone plan within one business day \u2014 approved by you before work starts." },
    { h: "One priority moves at a time.", p: "We'll always tell you what's in motion and when yours starts. On Premium, your urgent items jump the line. You're never guessing." },
    { h: "Cancel or pause anytime.", p: "Month-to-month, no lock-in and no cancellation penalty. Pause when things are quiet, come back when they're not." },
  ];

  const helps = [
    { i: "user", t: "CRM & data hygiene",
      pts: [<><b>Properties, pipelines, permissions</b> and lifecycle stages set up right</>,
            <><b>Deduping and clean imports</b> — data you can finally trust</>,
            <><b>Contact, company and deal records</b> standardized</>] },
    { i: "zap", t: "Sales Hub & pipeline",
      pts: [<><b>Lead routing, deal stages and rotation</b> that reflect how you actually sell</>,
            <><b>Sequences, snippets, quotes</b> and meeting links</>,
            <><b>Forecasting and pipeline reports</b> leadership believes</>] },
    { i: "ship", t: "Marketing Hub & campaigns",
      pts: [<><b>Emails, forms, landing pages and CTAs</b> that convert</>,
            <><b>Nurtures and lifecycle campaigns</b> wired end-to-end</>,
            <><b>Segmentation, lists</b> and subscription management</>] },
    { i: "shield", t: "Service Hub & support",
      pts: [<><b>Ticket pipelines, SLAs and routing</b></>,
            <><b>Knowledge base, help desk and live chat</b> / chatbots</>,
            <><b>Feedback surveys and CSAT</b> reporting</>] },
    { i: "refresh", t: "Automation & workflows",
      pts: [<>Build, fix and untangle <b>workflows</b> that quietly broke</>,
            <><b>Lead scoring models</b> sales actually trusts</>,
            <><b>Internal notifications, handoffs</b> and data-sync automations</>] },
    { i: "chart", t: "Reporting & dashboards",
      pts: [<><b>Custom reports and dashboards</b> your CEO actually opens</>,
            <><b>Attribution and funnel reporting</b>: first touch → closed-won</>,
            <><b>Weekly, monthly and board-ready</b> views</>] },
  ];

  const baseFaq = (window.PLANS_FAQ && window.PLANS_FAQ.length) ? window.PLANS_FAQ : [
    { q: "Who actually does the work?", a: "Your engagement is led by a dedicated, HubSpot-certified senior expert \u2014 your main solution architect from day one. The same person every week, who knows your portal and your business inside out. You\u2019re also backed by an account manager who keeps delivery on track and holds us to the quality and timelines you deserve. No juniors, no handoffs." },
    { q: "How fast will my request ship?", a: "Quick fixes: 2 business days on Growth, next business day on Premium. Builds: you get the dates up front. Either way, you always know when." },
    { q: "Is it really unlimited?", a: "Yes \u2014 send everything. One priority moves at a time, and you always see what's in motion and what's next." },
    { q: "Contracts?", a: "None. Month-to-month, pause or cancel anytime, no lock-in and no cancellation penalty." },
  ];
  // The "how it works" rules that used to sit under the pricing now live in the
  // main FAQ at the bottom of the page.
  const faq = [...baseFaq, ...rules.map((r) => ({ q: r.h, a: r.p }))];

  return (
    <div className="saas-lp plp">
      {/* ===== Hero ===== */}
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">ONGOING HUBSPOT SUPPORT</span>
              <h1>We Handle HubSpot<br/><span className="hl">So Your Team Can Grow</span></h1>
              <p className="hero-lead">
                From <b>CRM administration and automations to integrations, reporting, AI, and custom development</b>, we become your dedicated HubSpot team, so your team can focus on what they do best.
              </p>
              <div className="plp-hero-ctas">
                <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Get a free consultation <Icon name="arrow" size={14}/></a>
                <a href="#support" className="btn btn-ghost" onClick={scrollToId("support")}>See the plans</a>
              </div>
              <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
                <div className="hero-cred-cell">
                  <img
                    src="assets/trust/hubspot-gold-partner.png"
                    alt="HubSpot Solutions Partner — Gold"
                    className="hero-cred-badge"
                    width="120" height="120" loading="lazy"
                  />
                  <span className="hero-cred-copy">
                    <b>HubSpot</b>
                    <span>Gold Solutions Partner</span>
                  </span>
                </div>
                <span className="hero-cred-divider" aria-hidden="true"/>
                <div className="hero-cred-cell">
                  <span className="hero-cred-avatars" aria-hidden="true">
                    <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                  </span>
                  <span className="hero-cred-copy">
                    <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                    <span>Trusted by <b>100+ companies</b></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Logos (shared) ===== */}
      <Logos />

      {/* ===== What we help with ===== */}
      <section className="incl-section">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT WE HANDLE</span>
            <h2>Everything HubSpot, <em>handled for you.</em></h2>
            <p>One senior expert covers the whole platform — whatever comes up that week. If it lives in HubSpot, it's on the list.</p>
          </div>
          <div className="incl-grid">
            {helps.map((c) => (
              <div className="incl-card" key={c.t}>
                <span className="incl-ic"><Icon name={c.i} size={20}/></span>
                <h3>{c.t}</h3>
                <ul>
                  {c.pts.map((p, i) => (<li key={i}><Icon name="check" size={14}/> <span>{p}</span></li>))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Support plans ===== */}
      <section className="plans-section plp-support" id="support">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">ONGOING SUPPORT</span>
            <h2>One expert. <em>Every week.</em> A flat monthly price.</h2>
            <p>HubSpot changes weekly. Your business changes monthly. Somebody has to keep the two in sync — and it shouldn't be your sales lead at 9pm with a support article open. You ask; it ships; you get a written recap every Friday.</p>
          </div>
          <div className="plans-grid">
            {support.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.hot && <span className="plan-badge">★ Most teams start here</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{pl.price}</b><small>/month</small></div>
                {pl.hours && <div className="plan-hours">{pl.hours}</div>}
                <ul className="plan-bullets">
                  {pl.feats.map((b, i) => (<li key={i}><Icon name="check" size={13}/> {b}</li>))}
                </ul>
                <a href="#book" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={scrollToBook}>
                  Book a call <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>
          <p className="plans-currency">Prices are in USD</p>

          <p className="plans-more">
            Need heavier technical or RevOps work — custom code, integrations, complex builds?{" "}
            <a href="/hubspot-technical-developers" onClick={(e) => { e.preventDefault(); goToTechSupport(); }}>See our technical &amp; RevOps support plans</a>, or{" "}
            <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>contact us</a> for a tailored plan.
          </p>
        </div>
      </section>

      {/* ===== Need something else? -> contact ===== */}
      <section className="plp-band-dark plp-else" id="other">
        <div className="wrap">
          <div className="plp-else-head">
            <span className="saas-eyebrow">NEED SOMETHING ELSE?</span>
            <h2>Onboarding or a one-off technical build?</h2>
            <p>Support plans cover the ongoing work. For a fresh HubSpot setup or a fixed-scope project, tell us what you need and we'll come back with a fixed price — no discovery weeks, no meetings.</p>
          </div>

          <div className="plp-else-grid">
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="rocket" size={20}/></span>
              <h3>New onboarding</h3>
              <p>One Hub or the full platform, set up right from day one.</p>
            </div>
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="code" size={20}/></span>
              <h3>Custom dev &amp; integrations</h3>
              <p>Custom-coded workflows, API integrations and product-data sync.</p>
            </div>
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="refresh" size={20}/></span>
              <h3>Migrations</h3>
              <p>From Salesforce, Pipedrive, Marketo or any other CRM — without losing history.</p>
            </div>
          </div>

          <div className="plp-else-cta">
            <div className="plp-else-cta-copy">
              <b>Tell us what you need.</b>
              <span>Send a few sentences and you'll get a fixed-price scope back. Plan members get priority scheduling and member pricing.</span>
            </div>
            <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact us <Icon name="arrow" size={14}/></a>
          </div>
        </div>
      </section>

      {/* ===== Reviews (shared) ===== */}
      <Reviews />

      {/* ===== Book a call (primary CTA) ===== */}
      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>Grab 30 minutes with <em>a senior expert.</em></h2>
            <p>Pick a live slot below. We'll look at your portal together, answer your questions, and figure out whether a support plan is the right fit — no obligation.</p>
          </div>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Plain answers, <em>in writing.</em> Obviously.</h2>
              <p>Everything teams ask before working with us. Still wondering something? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send us a note</a> and we'll reply within one business day.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ============ Academy Revenue Operations — /academy-revenue-operations ============
   Homepage hero shell (.saas-lp .plp) + academy-specific modules below:
   tool logos → pain/gain split → journey outcomes → packages (no prices)
   → guarantee → client Logos → Reviews → Book a call → FAQ. */
const ACADEMY_TOOL_LOGOS = [
  { name: "HubSpot",   src: "assets/tools/hubspot.svg" },
  { name: "TalentLMS", src: "assets/tools/talentlms.svg" },
  { name: "Moodle",    src: "assets/tools/moodle.svg" },
  { name: "Stripe",    src: "assets/tools/stripe.svg" },
  { name: "Zapier",    src: "assets/tools/zapier.svg" },
  { name: "Credly",    src: "assets/tools/credly.svg" },
  { name: "Kajabi",    src: "assets/tools/kajabi.svg?v=4" },
  { name: "Shopify",   src: "assets/tools/shopify.svg" },
  { name: "Webflow",   src: "assets/tools/webflow.svg" },
  { name: "WordPress", src: "assets/tools/wordpress.svg" },
  { name: "Mailchimp", src: "assets/tools/mailchimp.svg" },
];

const AcademyRevenueOperationsPage = () => {
  const meetingsRef = useRef(null);

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = scrollToId("book");

  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  const pains = [
    "CRM, LMS, payments and ads each hold a different version of the truth",
    "Admissions chase enquiries by hand — response times slip when volume spikes",
    "Buyers pay, then wait for someone to create an LMS account",
    "Lead-to-enrollment reporting is a monthly spreadsheet exercise",
    "Inactive learners go quiet; completers are rarely invited to the next program",
    "B2B training opportunities live in inboxes, not a pipeline",
  ];

  const gains = [
    "One HubSpot-centred journey from first enquiry to course access",
    "Immediate follow-up, nurture and recovery for every admissions lead",
    "Automatic enrollment when Stripe (or your gateway) confirms payment",
    "Dashboards leadership trusts — by program, channel and cohort",
    "Onboarding, activation, upsell and credential workflows that run without chasing",
    "Clear B2C admissions and B2B corporate-training pipelines",
  ];

  const journey = [
    { n: "01 / Admissions", t: "A clearer path from enquiry to enrollment",
      d: "See every prospect in one pipeline. Know who needs a call, who is waiting on payment, and where admissions time is actually converting — for open programs and employer deals alike." },
    { n: "02 / Enrollment", t: "Payment that unlocks learning automatically",
      d: "When a student or company pays, HubSpot, your gateway and LMS stay in sync. Account creation, course access and confirmation emails happen without a manual handoff." },
    { n: "03 / Engagement", t: "Communication learners actually receive",
      d: "Program-specific onboarding, inactivity recovery and completion nudges keep students moving — so your team spends less time chasing and more time teaching." },
    { n: "04 / Growth", t: "Retention, credentials and the next sale",
      d: "Issue credentials when eligibility is met, surface renewals and upsells, and show marketing which channels fill seats — not just which ones generate clicks." },
  ];

  const packages = [
    { id: "foundation", name: "Foundation", tag: "For academies ready to stop stitching tools by hand.",
      live: "Live in ~30 days",
      feats: [
        "HubSpot audit, cleanup and academy lifecycle stages",
        "One B2C admissions pipeline with clear exit criteria",
        "Lead capture, ownership and same-day response workflows",
        "Payment + LMS auto-enrollment for one primary catalog",
        "Basic learner onboarding and an enrollment dashboard",
        "Team training, documentation and 30 days post-launch",
      ] },
    { id: "engine", name: "Revenue Engine", tag: "The connected system most growing academies need.",
      live: "Live in 45–60 days", hot: true,
      feats: [
        "Everything in Foundation",
        "B2C enrollment plus B2B / corporate-training pipelines",
        "Lead scoring, nurture and abandoned-enrollment recovery",
        "Onboarding, inactivity recovery and next-course upsells",
        "Advanced LMS + payment integration and Credly credentials",
        "CAC, conversion and program-attribution dashboards",
        "Two training sessions and 45 days post-launch support",
      ] },
    { id: "os", name: "Revenue OS", tag: "For multi-brand, multi-location education groups.",
      live: "Live in 8–12 weeks",
      feats: [
        "Everything in Revenue Engine",
        "Multi-brand or multi-location HubSpot architecture",
        "Advanced data model, routing and team governance",
        "Custom API and historical migration allowance",
        "Enterprise seats, cohorts and renewal workflows",
        "Executive dashboards and change-management plan",
        "Admin training and 60 days post-launch support",
      ] },
  ];

  const faq = [
    { q: "Who is this for?",
      a: "Professional academies, certification providers, language schools, workforce-training companies and corporate learning businesses — not traditional K–12 or university CRM projects. If you sell courses or employer training with real lead volume and an LMS that does not talk to your commercial tools, this is built for you." },
    { q: "How is this different from a generic HubSpot onboarding?",
      a: "Generic onboarding configures HubSpot. Academy Revenue Operations connects the whole commercial journey: admissions, payments, LMS enrollment, learner lifecycle, credentials and reporting. The LMS stays where learning happens — HubSpot becomes the system that sells, enrolls and grows the academy." },
    { q: "Which tools do you connect?",
      a: "HubSpot as the revenue hub, plus stacks academies already run — TalentLMS, Moodle or Kajabi; Stripe (and similar gateways); Credly; Shopify or Webflow/WordPress checkout pages; Zapier or Make when a native link is not enough; Mailchimp migrations into HubSpot where needed. We confirm your exact stack on the call." },
    { q: "How long until it is live?",
      a: "Foundation is scoped around 30 days. Revenue Engine — what most growing academies choose — is 45–60 days. Revenue OS for multi-team or multi-location groups runs 8–12 weeks. Timelines assume timely access, approvals and one accountable client owner." },
    { q: "What if something is not working at launch?",
      a: "If agreed capture, assignment, enrollment and reporting are not correct for at least 95% of valid in-scope cases at launch, implementation continues at no extra fee until they are — provided we have the access and decisions we need." },
    { q: "How do we start — and what does it cost?",
      a: "Book a free consultation. We will map your enquiry-to-enrollment journey, recommend Foundation, Revenue Engine or Revenue OS, and walk through scope and investment. Prices are not published here — every academy stack is scoped on a call." },
  ];

  return (
    <div className="saas-lp plp">
      {/* ===== Hero — same module as homepage ===== */}
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">HUBSPOT FOR PROFESSIONAL ACADEMIES</span>
              <h1>We Connect the Journey<br/><span className="hl">From Enquiry to Enrollment</span></h1>
              <p className="hero-lead">
                Professional academies choose us when they want <b>clearer admissions, automatic LMS enrollment, stronger learner engagement, and reporting leadership can trust</b> — with HubSpot joining marketing, sales, payments and your learning stack.
              </p>
              <div className="plp-hero-ctas">
                <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Get a free consultation <Icon name="arrow" size={14}/></a>
                <a href="#how" className="btn btn-ghost" onClick={scrollToId("how")}>See how it works</a>
              </div>
              <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
                <div className="hero-cred-cell">
                  <img
                    src="assets/trust/hubspot-gold-partner.png"
                    alt="HubSpot Solutions Partner — Gold"
                    className="hero-cred-badge"
                    width="120" height="120" loading="lazy"
                  />
                  <span className="hero-cred-copy">
                    <b>HubSpot</b>
                    <span>Gold Solutions Partner</span>
                  </span>
                </div>
                <span className="hero-cred-divider" aria-hidden="true"/>
                <div className="hero-cred-cell">
                  <span className="hero-cred-avatars" aria-hidden="true">
                    <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                  </span>
                  <span className="hero-cred-copy">
                    <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                    <span>Trusted by <b>100+ companies</b></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Tool logos ===== */}
      <section className="aro-tools" aria-label="Systems we connect">
        <div className="wrap">
          <div className="t">Built for the tools professional academies already use</div>
          <div className="aro-tools-row">
            {ACADEMY_TOOL_LOGOS.map((l) => (
              <div className="lg" key={l.name} title={l.name}>
                <img src={l.src} alt={l.name} loading="lazy" width="120" height="28"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pain / gain split ===== */}
      <section className="aro-split" id="how">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHY ACADEMIES STRUGGLE</span>
            <h2>Disconnected systems hold back <em>admissions, enrollment and growth.</em></h2>
            <p>Education teams face the same pattern HubSpot partners see everywhere in the sector: manual processes, limited visibility, and tools that never quite join up — only here the stakes are seats filled, learners activated and B2B contracts won.</p>
          </div>
          <div className="aro-split-grid">
            <div className="aro-split-col pain">
              <h3>Where revenue leaks today</h3>
              <p className="lead">If this looks familiar, you do not need another LMS feature — you need a commercial operating system around the one you have.</p>
              <ul>
                {pains.map((t) => (
                  <li key={t}><Icon name="close" size={16}/> <span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="aro-split-col">
              <h3>What changes with Academy RevOps</h3>
              <p className="lead">In 45–60 days for most academies, enquiry → payment → LMS access → retention becomes one owned, measurable journey.</p>
              <ul>
                {gains.map((t) => (
                  <li key={t}><Icon name="check" size={16}/> <span>{t}</span></li>
                ))}
              </ul>
              <div style={{ marginTop: 24 }}>
                <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Get a free consultation <Icon name="arrow" size={14}/></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Journey outcomes ===== */}
      <section className="aro-journey" id="outcomes">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT YOUR TEAMS GET</span>
            <h2>Clarity for admissions. Automation for enrollment. <em>Confidence for leadership.</em></h2>
            <p>We configure HubSpot around how professional education actually sells — open programs, cohorts, certifications and employer training — then wire the LMS and payments so the journey keeps moving.</p>
          </div>
          <div className="aro-journey-grid">
            {journey.map((c) => (
              <div className="aro-journey-card" key={c.n}>
                <span className="n">{c.n}</span>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Packages (no prices) ===== */}
      <section className="plans-section plp-support" id="packages">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">HOW WE HELP</span>
            <h2>Three fixed-scope setups. <em>One exploratory call to choose.</em></h2>
            <p>Picked by operational complexity — programs, lead sources, B2B motion and locations — not headcount alone. Investment is scoped on the call; every package CTA is the same: talk to a senior expert.</p>
          </div>
          <div className="plans-grid">
            {packages.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.hot && <span className="plan-badge">★ Most academies start here</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{pl.live}</b></div>
                <div className="plan-hours">Fixed scope · Book a call for investment</div>
                <ul className="plan-bullets">
                  {pl.feats.map((b, i) => (<li key={i}><Icon name="check" size={13}/> {b}</li>))}
                </ul>
                <a href="#book" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={scrollToBook}>
                  Book a call <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Guarantee ===== */}
      <section className="plp-band-dark plp-else" id="guarantee">
        <div className="wrap">
          <div className="plp-else-head">
            <span className="saas-eyebrow">BUILT TO BE OWNED</span>
            <h2>A system that works — or we keep fixing it.</h2>
            <p>If agreed capture, assignment, enrollment and reporting are not right for at least 95% of valid in-scope cases at launch, implementation continues at no extra fee until they are.</p>
          </div>
          <div className="plp-else-grid">
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="ship" size={20}/></span>
              <h3>Admissions &amp; partnerships</h3>
              <p>HubSpot lifecycle, B2C and B2B pipelines, routing and sales sequences your team can run.</p>
            </div>
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="zap" size={20}/></span>
              <h3>Payments &amp; LMS</h3>
              <p>Stripe into TalentLMS, Moodle or Kajabi — auto-enroll on purchase, alerts when something fails.</p>
            </div>
            <div className="plp-else-tile">
              <span className="plp-else-ic"><Icon name="shield" size={20}/></span>
              <h3>Credentials &amp; support</h3>
              <p>Credly eligibility where included, ticket routing and dashboards that replace the monthly export.</p>
            </div>
          </div>
          <div className="plp-else-cta">
            <div className="plp-else-cta-copy">
              <b>Ready to see if it fits your academy?</b>
              <span>Thirty minutes to map enquiry-to-enrollment leakage and recommend the right setup — no obligation.</span>
            </div>
            <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Book a call <Icon name="arrow" size={14}/></a>
          </div>
        </div>
      </section>

      <Logos />
      <Reviews />

      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>Grab 30 minutes with <em>a senior expert.</em></h2>
            <p>Pick a live slot. We will look at your admissions journey, LMS and payment stack, and whether Foundation, Revenue Engine or Revenue OS is the right fit — no obligation.</p>
          </div>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>

      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Plain answers for <em>academy teams.</em></h2>
              <p>Still wondering something? <a href="#book" onClick={scrollToBook}>Book a call</a> or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>send us a note</a>.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ============ HubSpot Onboarding Packages — /hubspot-onboarding-packages ============
   Dedicated onboarding LP (inspired by the category of partner onboarding pages):
   credentials → why DFY → process → us-vs-DIY inclusions → per-Hub price tables
   (HubSpot DIY vs us + savings) → advanced work → Logos/Reviews → book → FAQ.
   Pricing/scope match the standard partner onboarding offer; voice is ours. */
const OnboardingPackagesPage = () => {
  const meetingsRef = useRef(null);
  const [hubTab, setHubTab] = useState("sales");

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = scrollToId("book");

  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  const creds = [
    { t: "Proven onboarding framework", d: "The same playbook we’ve refined across 100+ portals — discovery, mapping, weekly build, QA, handoff. Repeatable, not reinvented every time." },
    { t: "Gold Solutions Partner", d: "HubSpot-certified delivery. You’re working with a partner who lives in the platform — not a marketplace ticket queue." },
    { t: "12+ years, senior-owned", d: "One senior operator owns your engagement end-to-end. No juniors learning on your portal." },
  ];

  const why = [
    { i: "layers", t: "1-to-1 strategy", d: "Meet weekly with your dedicated HubSpot expert. We map a plan of attack so you’re live in 4–6 weeks — using a framework that’s already been proven." },
    { i: "zap", t: "Done for you", d: "Your expert is backed by senior operators. We handle the technical setup — pipelines, properties, automations, templates, reporting — so you stay focused on the business." },
    { i: "user", t: "HubSpot specialists", d: "HubSpot is what we do. The framework covers Sales, Marketing, Service and Operations — calibrated to your motion, not a generic Academy checklist." },
  ];

  const steps = [
    { n: "01", t: "Discovery call", d: "Within about a week you’re on a call with your expert. We learn your use case and what the framework needs to cover for your team." },
    { n: "02", t: "Mapping call", d: "We map your sales, marketing and/or service processes so the build matches how you actually work — not HubSpot’s defaults." },
    { n: "03", t: "Weekly meetings", d: "Meet weekly to keep momentum, answer questions, and review what’s shipped. You always know where the framework stands." },
    { n: "04", t: "Technical setup", d: "Between meetings we configure HubSpot behind the scenes — users, CRM, automation, templates, reporting — against the package scope." },
    { n: "05", t: "Portal review & handoff", d: "A focused review call to walk through everything, train your team, and hand over a portal you can run." },
    { n: "06", t: "Ongoing support", d: "Goal is self-sufficiency in 4–6 weeks. If you want HubSpot kept handled after that, month-to-month support is available." },
  ];

  const hubs = [
    {
      id: "sales", name: "Sales Hub",
      diyNote: "HubSpot’s DIY Sales Hub Pro onboarding is typically $1,500 — coaching and homework. We do the technical setup for the same price (or less on Enterprise).",
      us: [
        "Discovery call + sales-process mapping call",
        "Weekly meeting with your dedicated HubSpot expert",
        "Written onboarding plan + 30-minute portal review / handoff",
        "Users & permissions setup",
        "Account settings, tracking codes, filter internal IPs",
        "Branding: logo, fonts, colors, email footers & signatures",
        "GDPR compliance settings + DKIM / SPF",
        "Migrate existing deal pipelines & stages",
        "Import contacts, companies, deals and products",
        "Build 1 sales pipeline from your process + deal-creation workflow",
        "Contact Us form connected to the pipeline (if applicable)",
        "5 snippets + 2 sequences (max 5 emails each)",
        "Import opt-out lists; sync ownership contact ↔ company",
        "Standard sales reports & dashboards",
        "Meetings tool via Gmail or Outlook + Insights enrichment",
        "Gmail / Outlook integration guidance",
      ],
      enterprise: [
        "2 custom sales reports",
        "3 sales email templates",
        "5 sequences (max 5 emails each)",
        "1 sales playbook",
        "Map custom objects to HubSpot objects",
        "Migrate parent / child relationships",
      ],
      diy: [
        "Limited discovery call",
        "Meeting every 2 weeks (guidance, not build)",
        "Account settings & branding walkthrough",
        "Limited pipeline / import coaching",
        "Essential sales reports & dashboards",
        "You still do most of the configuration yourself",
      ],
      rows: [
        { tier: "Pro", hs: "$1,500", us: "$1,500", save: "—" },
        { tier: "Enterprise", hs: "$3,500", us: "$3,000", save: "$500", hot: true },
      ],
    },
    {
      id: "marketing", name: "Marketing Hub",
      diyNote: "HubSpot’s DIY Marketing Hub Pro onboarding is typically $3,000. Same price with us — and we build the assets, not just coach you through them.",
      us: [
        "Discovery call + lead / marketing / campaign mapping call",
        "Weekly meeting with your dedicated HubSpot expert",
        "Written onboarding plan + 30-minute portal review / handoff",
        "Users & permissions setup",
        "Account settings, tracking codes, filter internal IPs",
        "Branding: logo, fonts, colors, email footers & signatures",
        "GDPR compliance settings + DKIM / SPF",
        "Landing page, subdomain & blog settings",
        "Import contacts / companies + opt-out lists",
        "Segmentation lists + buyer-persona property setup",
        "1 landing page template (brand-matched; no custom modules)",
        "Migrate or create 1 landing page from templates",
        "3 drag-and-drop email templates",
        "Blog listing + blog post template + 1 pillar page template",
        "2 workflows (max 5 emails/actions each)",
        "Contact Us form with workflow",
        "3 CTAs + essential marketing reports & dashboards",
        "Social media setup; Gmail / Outlook native integration help",
      ],
      enterprise: [
        "2 custom marketing reports",
        "Lead scoring implementation",
        "Map up to 3 custom objects",
        "1 additional email template + 1 additional LP template",
        "2 additional workflows (max 5 emails each)",
        "Up to 5 domains included (then $100/domain)",
        "Up to 10 blog posts included (then $20/post)",
        "Additional business units: $1,000 each",
      ],
      diy: [
        "Limited discovery call",
        "Meeting every 2 weeks (guidance, not build)",
        "Limited account / branding / GDPR walkthrough",
        "Landing page, subdomain & blog settings guidance",
        "Contact import coaching",
        "You build most emails, LPs and workflows yourself",
      ],
      rows: [
        { tier: "Pro", hs: "$3,000", us: "$3,000", save: "—" },
        { tier: "Enterprise", hs: "$6,000", us: "$4,500", save: "$1,500", hot: true },
      ],
    },
    {
      id: "service", name: "Service Hub",
      diyNote: "HubSpot’s DIY Service Hub Pro onboarding is typically $1,500. Same price — we configure ticketing, inbox, SLAs and reporting for you.",
      us: [
        "Discovery call + support-process mapping call",
        "Weekly meeting with your dedicated HubSpot expert",
        "Written onboarding plan + 30-minute portal review / handoff",
        "Users & permissions setup",
        "Account settings, tracking codes, filter internal IPs",
        "Branding: logo, fonts, colors, email footers & signatures",
        "GDPR compliance settings",
        "Import contacts / companies + opt-out lists",
        "Team inbox for service requests",
        "Ticket routing & notifications",
        "Migrate existing tickets (CSV) + service stages",
        "5 snippets for service agents",
        "Gmail / Outlook native integration help",
        "1 feedback survey",
        "Essential service reports & dashboards",
        "SLA agreements",
      ],
      enterprise: [
        "Customer portal setup",
        "Knowledge base settings & subdomain",
        "System pages setup",
        "Migrate 10 KB articles ($20/article after that)",
        "3 custom service workflows",
        "2 custom service reports",
        "5 email templates",
        "1 chatbot",
        "2 playbooks",
      ],
      diy: [
        "Limited discovery call",
        "Meeting every 2 weeks (guidance, not build)",
        "GDPR settings walkthrough",
        "Contact import coaching",
        "Ticket migration / stage tips",
        "Essential service reports — you finish the rest",
      ],
      rows: [
        { tier: "Pro", hs: "$1,500", us: "$1,500", save: "—" },
        { tier: "Enterprise", hs: "$3,500", us: "$3,000", save: "$500", hot: true },
      ],
    },
    {
      id: "operations", name: "Operations Hub",
      diyNote: "HubSpot doesn’t sell a packaged DIY Operations Hub onboarding at a published fee. We set Ops Hub Pro up for $1,500; Enterprise is scoped on a call.",
      us: [
        "Discovery call + data / ops process mapping",
        "Weekly meeting with your dedicated HubSpot expert",
        "Written onboarding plan + portal review / handoff",
        "Users & permissions for ops / RevOps roles",
        "Data quality foundations: duplicate management approach",
        "Property hygiene and calculated properties (within plan)",
        "Programmable automation / custom coded actions (as licensed)",
        "Data sync setup for included connectors (as licensed)",
        "Essential operations reports & dashboards",
        "Handoff doc so your team can maintain the setup",
      ],
      enterprise: [
        "Expanded sync / data-quality architecture",
        "Additional custom coded workflow actions",
        "Multi-team ops governance & reporting",
        "Scoped to your portal on the consultation call",
      ],
      diy: [
        "No published HubSpot DIY Ops onboarding package",
        "Self-serve docs and Academy courses only",
        "You configure data quality and sync yourself",
      ],
      rows: [
        { tier: "Pro", hs: "—", us: "$1,500", save: "—" },
        { tier: "Enterprise", hs: "—", us: "Custom", save: "—", hot: true },
      ],
    },
  ];

  const activeHub = hubs.find((h) => h.id === hubTab) || hubs[0];

  const advanced = [
    { i: "refresh", t: "Complex migration", d: "Migrate large, complex datasets from third-party systems (like Salesforce) into HubSpot.", price: "From $3,000", href: "/hubspot/data-migration" },
    { i: "code", t: "Custom API integration", d: "Build 1- and 2-way integrations with HubSpot using the HubSpot API.", price: "Scoped on a call", href: "/hubspot/integrations" },
    { i: "layers", t: "HubSpot CMS migration", d: "Migrate an existing website onto HubSpot CMS.", price: "From $3,000", href: "/hubspot/website-development" },
    { i: "user", t: "Customized training", d: "Two 1-hour training sessions with a strategist — ideal for larger teams after onboarding.", price: "$500", href: "/hubspot/hubspot-training" },
  ];

  const faq = [
    { q: "What do you mean by a proven framework?",
      a: "We don’t invent a new approach for every portal. Discovery → mapping → weekly build → QA → handoff is the same playbook we’ve run across 100+ companies. Your process and HubSpot tier customize the framework — the structure that gets you live in 4–6 weeks stays consistent." },
    { q: "How is this different from HubSpot’s own onboarding?",
      a: "HubSpot’s DIY onboarding coaches your team through homework. Ours applies a proven done-for-you framework: a senior expert configures the portal to your process, meets you weekly, and hands it over in 4–6 weeks — usually at the same published DIY fee, or less on Enterprise." },
    { q: "Do HubSpot subscription fees count toward this?",
      a: "No. Package prices cover our implementation work only. HubSpot licenses are billed by HubSpot. As a Solutions Partner we can help you pick the right plan and negotiate where it makes sense." },
    { q: "Is data migration included?",
      a: "Standard contact, company, deal, product and ticket imports listed in each Hub package are included. Large / complex migrations (e.g. Salesforce) start at $3,000 as an advanced service." },
    { q: "Who actually does the work?",
      a: "A HubSpot-certified senior expert owns your engagement end-to-end — discovery, mapping, weekly meetings, build and handoff. You’re also backed by an account manager who keeps delivery on track. No juniors, no handoffs mid-project." },
    { q: "When do we pay?",
      a: "Book a consultation, we confirm scope, then you get a short SOW. You don’t pay until the package and price are clear in writing." },
    { q: "What if we need more than one Hub?",
      a: "We onboard each Hub as its own package (Sales, Marketing, Service, Operations). On the call we’ll recommend sequence and whether to run them in parallel." },
    { q: "Can we keep you after handoff?",
      a: "Yes. Month-to-month ongoing support starts at $999/mo (10 hours) if you want HubSpot handled after go-live — cancel anytime." },
  ];

  return (
    <div className="saas-lp plp hop">
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">PROVEN HUBSPOT ONBOARDING FRAMEWORK</span>
              <h1>Proven frameworks used by <span className="hl">100+ companies</span> to set up HubSpot.</h1>
              <p className="hero-lead">
                Stay focused on scaling your business while a senior HubSpot expert customizes Sales, Marketing, Service or Operations Hub to your needs — <b>done for you in 4–6 weeks, at HubSpot DIY prices (or less)</b>.
              </p>
              <div className="plp-hero-ctas">
                <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Book a free consultation <Icon name="arrow" size={14}/></a>
                <a href="#pricing" className="btn btn-ghost" onClick={scrollToId("pricing")}>See packages &amp; prices</a>
              </div>
              <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
                <div className="hero-cred-cell">
                  <img src="assets/trust/hubspot-gold-partner.png" alt="HubSpot Solutions Partner — Gold" className="hero-cred-badge" width="120" height="120" loading="lazy" />
                  <span className="hero-cred-copy"><b>HubSpot</b><span>Gold Solutions Partner</span></span>
                </div>
                <span className="hero-cred-divider" aria-hidden="true"/>
                <div className="hero-cred-cell">
                  <span className="hero-cred-avatars" aria-hidden="true">
                    <img src="assets/trust/avatar-copilotkit.webp" alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-mavenagi.webp" alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-catio.webp" alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-kindsight.webp" alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                  </span>
                  <span className="hero-cred-copy">
                    <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                    <span>Trusted by <b>100+ companies</b></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="plp-band-dark hop-creds">
        <div className="wrap">
          <span className="saas-eyebrow">THE FRAMEWORK</span>
          <h2>A proven playbook — not a DIY checklist.</h2>
          <div className="hop-cred-grid">
            {creds.map((c) => (
              <div className="hop-cred-card" key={c.t}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="incl-section" id="why">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">DONE-FOR-YOU SETUP</span>
            <h2>You bought HubSpot to grow — <em>not to become a HubSpot expert.</em></h2>
            <p>We’ve run this framework across 100+ companies. HubSpot’s DIY option coaches you through homework; for the same price — or less on Enterprise — we apply the playbook and build the portal for you.</p>
          </div>
          <div className="incl-grid">
            {why.map((c) => (
              <div className="incl-card" key={c.t}>
                <span className="incl-ic"><Icon name={c.i} size={20}/></span>
                <h3>{c.t}</h3>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "var(--fs-base)", lineHeight: 1.55 }}>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aro-journey" id="process">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">THE SETUP PROCESS</span>
            <h2>The framework that gets you live in <em>4–6 weeks.</em></h2>
            <p>Same six steps every engagement — proven across industries — then customized to your process, HubSpot tier and team.</p>
          </div>
          <div className="hop-process">
            {steps.map((s) => (
              <div className="hop-step" key={s.n}>
                <span className="n">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="plans-section" id="included">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT’S INCLUDED</span>
            <h2>The framework’s technical setup — <em>done for you.</em></h2>
            <p>Pick a Hub. Same proven package scope every time — customized to your process. Compare it with HubSpot’s limited DIY coaching; Enterprise adds the extras below.</p>
          </div>
          <div className="hop-tabs" role="tablist" aria-label="HubSpot Hub">
            {hubs.map((h) => (
              <button key={h.id} type="button" role="tab" aria-selected={hubTab === h.id} className={hubTab === h.id ? "on" : ""} onClick={() => setHubTab(h.id)}>
                {h.name}
              </button>
            ))}
          </div>
          <p className="hop-diy-note">{activeHub.diyNote}</p>
          <div className="hop-compare">
            <div className="hop-compare-col us">
              <div className="hop-compare-head">
                <span className="tag">Vestal Hub</span>
                <h3>Done for you</h3>
              </div>
              <ul>
                {activeHub.us.map((item) => (
                  <li key={item}><Icon name="check" size={14}/> <span>{item}</span></li>
                ))}
              </ul>
              {activeHub.enterprise?.length > 0 && (
                <div className="hop-ent-extras">
                  <h4>Enterprise adds</h4>
                  <ul>
                    {activeHub.enterprise.map((item) => (
                      <li key={item}><Icon name="check" size={14}/> <span>{item}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="hop-compare-col diy">
              <div className="hop-compare-head">
                <span className="tag muted">HubSpot DIY onboarding</span>
                <h3>Limited support</h3>
              </div>
              <ul>
                {activeHub.diy.map((item) => (
                  <li key={item}><Icon name="close" size={14}/> <span>{item}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <p className="hop-value-note">30+ additional hours of support and technical setup versus DIY coaching — typically $3,000+ of extra build value in the package.</p>
        </div>
      </section>

      <section className="plans-section hop-pricing" id="pricing">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">HUBSPOT SETUP PRICING</span>
            <h2>Same price as HubSpot DIY — <em>or less.</em> We do the work.</h2>
            <p>HubSpot’s do-it-yourself onboarding coaches your team through homework. For the same fee (or less on Enterprise), we complete the technical setup.</p>
          </div>

          <div className="hop-price-grid" aria-label="HubSpot onboarding prices by Hub">
            {hubs.map((h) => (
              <article className={`hop-price-table ${hubTab === h.id ? "on" : ""}`} key={h.id}>
                <header>
                  <h3>{h.name}</h3>
                  <span>Savings vs HubSpot DIY</span>
                </header>
                <div className="hop-price-cols" aria-hidden="true">
                  <span>Tier</span>
                  <span>HubSpot DIY</span>
                  <span>Vestal Hub</span>
                  <span>You save</span>
                </div>
                {h.rows.map((r) => (
                  <div className={`hop-price-row ${r.hot ? "hot" : ""}`} key={r.tier}>
                    <span className="tier">{r.tier}</span>
                    <span className={r.hs !== "—" ? "hs" : "na"}>{r.hs}</span>
                    <span className="us">{r.us}</span>
                    <span className={`save ${r.save === "—" ? "na" : ""}`}>{r.save}</span>
                  </div>
                ))}
                <a href="#book" className="btn btn-ghost" onClick={scrollToBook}>Book a consultation <Icon name="arrow" size={13}/></a>
              </article>
            ))}
          </div>
          <p className="hop-diy-note" style={{ marginTop: 28 }}>Book a consultation to confirm HubSpot tier, Hub mix, and exact package pricing for your portal.</p>
        </div>
      </section>

      <section className="plp-band-dark plp-else" id="advanced">
        <div className="wrap">
          <div className="plp-else-head">
            <span className="saas-eyebrow">BEYOND ONBOARDING</span>
            <h2>Migrations, integrations and deeper work.</h2>
            <p>Need more than a clean Hub launch? Same senior operators — fixed-quote projects with a SOW in 24 hours.</p>
          </div>
          <div className="plp-else-grid">
            {advanced.map((a) => (
              <a className="plp-else-tile" href={a.href} key={a.t} onClick={(e) => { e.preventDefault(); navigate(a.href); }} style={{ textDecoration: "none" }}>
                <span className="plp-else-ic"><Icon name={a.i} size={20}/></span>
                <h3>{a.t}</h3>
                <p>{a.d}</p>
                <span style={{ color: "var(--brand-ink)", fontWeight: 700, fontSize: "var(--fs-sm)", marginTop: 6 }}>{a.price}</span>
              </a>
            ))}
          </div>
          <div className="plp-else-cta">
            <div className="plp-else-cta-copy">
              <b>Not sure which package fits?</b>
              <span>Thirty minutes with a senior expert — we’ll map your HubSpot plan to the right onboarding path. No obligation.</span>
            </div>
            <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Book a call <Icon name="arrow" size={14}/></a>
          </div>
        </div>
      </section>

      <Logos />
      <StackLogos />
      <Reviews />

      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>Claim your free consultation.</h2>
            <p>Pick a live slot. We’ll confirm HubSpot tier, which Hub(s) to start with, and send a short SOW if it’s a fit — no discovery gauntlet.</p>
          </div>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>

      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Questions teams ask <em>before kickoff.</em></h2>
              <p>Still wondering something? <a href="#book" onClick={scrollToBook}>Book a call</a> or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>send us a note</a>.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* Shared featured testimonials (Kenneth Hamner / Heidi Spitz) — homepage + outbound LPs. */
const OutboundFeaturedTestimonial = ({ review, reviews }) => {
  const items = (reviews && reviews.length) ? reviews : (review ? [review] : []);
  if (!items.length) return null;
  return (
    <section className="incl-section obi-featured" id="testimonial">
      <div className="wrap">
        <div className="saas-head">
          <span className="saas-eyebrow saas-eyebrow-dark">{items.length > 1 ? "FEATURED TESTIMONIALS" : "FEATURED TESTIMONIAL"}</span>
          <h2>What people say after working with us</h2>
        </div>
        <div className={`obi-featured-grid${items.length > 1 ? " is-multi" : ""}`}>
          {items.map((r) => (
            <article className="obi-featured-card" key={r.id || r.name}>
              <div className="obi-featured-meta">
                <span className="obi-featured-stars" aria-label={`${r.rating || "5/5"} stars`}>
                  <Stars/>
                  <b>{r.rating || "5/5"}</b>
                </span>
                {r.date && <span className="obi-featured-date">{r.date}</span>}
              </div>
              {r.headline && <h3 className="obi-featured-headline">{r.headline}</h3>}
              <p className="obi-featured-quote" dangerouslySetInnerHTML={{ __html: `"${r.text}"` }}/>
              <div className="cr-author">
                <img src={r.avatar} alt={r.name} loading="lazy" width="48" height="48"/>
                <div>
                  <b>{r.name}</b>
                  <span className="obi-featured-byline">
                    {r.role} · <em>{r.company}</em>
                    {r.chip && <span className="obi-featured-chip">{r.chip}</span>}
                  </span>
                </div>
                {r.logo && <img className="cr-logo" src={r.logo} alt={r.company} loading="lazy"/>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ Outbound Infrastructure — /outbound-infrastructure ============
   Setup-only LP (monthly optimization reserved for future /outbound-optimization).
   Content: window.OUTBOUND_INFRASTRUCTURE. Same .saas-lp .plp / .hop system. */
const OutboundInfrastructurePage = () => {
  const meetingsRef = useRef(null);
  const data = window.OUTBOUND_INFRASTRUCTURE || {};
  const setupPlans = data.setupPlans || [];
  const addOns = data.addOns || [];
  const comparison = data.comparison || [];
  const platforms = data.platforms || [];
  const benefits = data.benefits || [];
  const steps = data.steps || [];
  const hubspotBullets = data.hubspotBullets || [];
  const ownership = data.ownership || [];
  const faq = data.faq || window.OUTBOUND_FAQ || [];
  const featuredReviews = data.featuredReviews || (data.featuredReview ? [data.featuredReview] : []);
  const partners = data.partners || [];
  const multiAddon = addOns.find((a) => a.id === "linkedin-multichannel");
  const planById = (id) => setupPlans.find((p) => p.id === id);

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = (e) => {
    e?.preventDefault();
    trackOutbound("outbound_final_cta", { location: "book_scroll" });
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onHeroPackages = (e) => {
    trackOutbound("outbound_hero_cta", { cta: "view_setup_packages" });
    scrollToId("setup-packages")(e);
  };
  const onHeroBook = (e) => {
    trackOutbound("outbound_hero_cta", { cta: "book_consultation" });
    scrollToBook(e);
  };
  const onSetupCta = (plan) => (e) => {
    e.preventDefault();
    trackOutbound("outbound_setup_pricing_cta", { package: plan.id });
    if (plan.event) trackOutbound(plan.event);
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onMultichannelCta = (e) => {
    e.preventDefault();
    trackOutbound("outbound_multichannel_addon_cta");
    trackOutbound("outbound_addon_linkedin");
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onOngoingTeaser = (e) => {
    e.preventDefault();
    trackOutbound("outbound_ongoing_teaser_cta");
    goToOutboundOptimization();
  };

  const formatCell = (v) => {
    let content;
    if (v === true) content = <Icon name="check" size={16}/>;
    else if (v === false) content = <span className="obi-na">—</span>;
    else content = v;
    return <span className="obi-cell">{content}</span>;
  };

  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  return (
    <div className="saas-lp plp hop obi">
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">OUTBOUND INFRASTRUCTURE</span>
              <h1>Build Your Outbound Engine.<br/><span className="hl">Own It From Day One.</span></h1>
              <p className="hero-lead">
                We build the domains, mailboxes, deliverability, outreach platform, CRM connections and first campaigns your team needs to run outbound — then hand the entire system over to you.
              </p>
              <div className="plp-hero-ctas">
                <a href="#setup-packages" className="btn btn-primary" onClick={onHeroPackages}>View Setup Packages <Icon name="arrow" size={14}/></a>
                <a href="#book" className="btn btn-ghost" onClick={onHeroBook}>Book a Free Consultation</a>
              </div>
              {partners.length > 0 && (
                <div className="obi-partners" role="list" aria-label="Platform partners">
                  {partners.map((p) => {
                    const cls = `obi-partner${p.official ? " is-official" : ""}${p.square ? " is-square" : ""}`;
                    const inner = p.official ? (
                      <img
                        src={p.src}
                        alt={p.alt}
                        className="obi-partner-official"
                        width={p.square ? 80 : 240}
                        height={p.square ? 80 : 64}
                      />
                    ) : (
                      <>
                        <img src={p.src} alt="" className="obi-partner-mark" width="32" height="32" />
                        <span className="obi-partner-rule" aria-hidden="true"/>
                        <span className="obi-partner-copy">
                          <b>{p.name}</b>
                          <small>{p.role}</small>
                        </span>
                      </>
                    );
                    return p.href ? (
                      <a key={p.name} className={cls} href={p.href} target="_blank" rel="noopener noreferrer" role="listitem">
                        {inner}
                      </a>
                    ) : (
                      <div key={p.name} className={cls} role="listitem">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="obi-stack-line">Fixed-price implementation · Full ownership · Instantly, Smartlead, Lemlist or HeyReach</p>
            </div>
          </div>
        </div>
      </section>

      <StackLogos />

      <section className="plp-band-dark hop-creds" id="differentiation">
        <div className="wrap">
          <span className="saas-eyebrow">WHY THIS IS DIFFERENT</span>
          <h2>Stop Renting Your Outbound From an Agency</h2>
          <p className="obi-dark-lead">Most outbound agencies run everything inside systems they control.</p>
          <p className="obi-dark-lead"><b>We do the opposite.</b> VestalHub builds the outbound infrastructure inside your own accounts, launches it with you and hands over a documented system your team can operate.</p>
          <div className="hop-cred-grid obi-benefit-grid">
            {benefits.map((c) => (
              <div className="hop-cred-card" key={c.t}>
                <span className="obi-ic"><Icon name={c.i} size={18}/></span>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aro-journey" id="how-it-works">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">HOW IT WORKS</span>
            <h2>From Zero to <em>Production-Ready Outbound</em></h2>
          </div>
          <div className="hop-process obi-process">
            {steps.map((s) => (
              <div className="hop-step" key={s.n}>
                <span className="n">{s.n}</span>
                <h3>{s.t}</h3>
                <ul>
                  {s.items.map((item) => (
                    <li key={item}><Icon name="check" size={13}/> <span>{item}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OutboundFeaturedTestimonial reviews={featuredReviews}/>

      <section className="plans-section hop-pricing" id="setup-packages">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">FIXED-PRICE IMPLEMENTATION</span>
            <h2>Choose Your <em>Outbound Setup</em></h2>
            <p>No long-term contract required. We build the system, launch it and hand it over.</p>
          </div>
          <div className="plans-grid">
            {setupPlans.map((pl) => {
              const base = pl.plusOf ? planById(pl.plusOf) : null;
              const addon = pl.addonId ? addOns.find((a) => a.id === pl.addonId) : null;
              return (
                <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                  {pl.badge && <span className="plan-badge">{pl.badge}</span>}
                  <div className="plan-head">
                    <span className="plan-name">{pl.name}</span>
                    <span className="plan-tag">{pl.tag}</span>
                  </div>
                  <div className="plan-price"><b>${pl.price.toLocaleString()}</b><small>{pl.suffix}</small></div>
                  <p className="plan-desc">{pl.positioning}</p>
                  <div className="plan-hours">{pl.timeline}</div>
                  {pl.sendDaily && (
                    <div className="obi-volume">
                      <b>~{pl.sendDaily} emails/day</b>
                      <span>~{pl.sendMonthly}/month · {pl.mailboxes} mailboxes × 15–30 emails/day</span>
                    </div>
                  )}
                  <ul className="plan-bullets">
                    {base && <li className="obi-plus"><Icon name="check" size={13}/> Everything in {base.name}</li>}
                    {(pl.features || []).map((b) => (<li key={b}><Icon name="check" size={13}/> {b}</li>))}
                  </ul>
                  {addon && (
                    <div className="obi-inline-addon">
                      <div className="obi-inline-addon-head">
                        <span>Optional Add-On</span>
                        <b>+${addon.oneTimePrice.toLocaleString()} one-time</b>
                      </div>
                      <strong>{addon.name}</strong>
                      <p className="obi-addon-note" style={{ marginTop: 6 }}>See details below — available with this package.</p>
                    </div>
                  )}
                  <a href="#book" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={onSetupCta(pl)}>
                    {pl.cta} <Icon name="arrow" size={14}/>
                  </a>
                </div>
              );
            })}
          </div>
          <div className="obi-disclaimer">
            <p>Software subscriptions, domains, mailbox licenses and third-party data costs are billed separately and paid directly by the client.</p>
            <p>Send estimates assume 5 mailboxes per domain and 15–30 emails per mailbox per day after warmup (~20 sending days/month). Actual volume ramps during warmup and depends on deliverability.</p>
            <p>Final mailbox and domain volume may vary based on sending strategy.</p>
          </div>
        </div>
      </section>

      <section className="plans-section" id="comparison">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">PACKAGE COMPARISON</span>
            <h2>What’s included in each <em>setup package.</em></h2>
          </div>
          <div className="obi-table-wrap">
            <table className="obi-table">
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">Infrastructure Launch</th>
                  <th scope="col" className="hot">Outbound Launch <span>MOST POPULAR</span></th>
                  <th scope="col">GTM Engine</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature}>
                    <th scope="row">{row.feature}</th>
                    <td>{formatCell(row.infrastructure)}</td>
                    <td className="hot">{formatCell(row.launch)}</td>
                    <td>{formatCell(row.gtm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="incl-section" id="multichannel">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">OPTIONAL ADD-ON</span>
            <h2>Add LinkedIn to Your <em>Outbound</em></h2>
          </div>
          {multiAddon && (
            <article className="obi-addon-card obi-addon-card-sm">
              <div className="obi-addon-main">
                <div className="obi-addon-icons" aria-hidden="true">
                  <span><Icon name="mail" size={18}/></span>
                  <span className="obi-addon-arrow">→</span>
                  <span><Icon name="linkedin" size={18}/></span>
                </div>
                <div className="obi-addon-copy">
                  <div className="obi-addon-title-row">
                    <h3>{multiAddon.name}</h3>
                    <span className="obi-pill">Included in GTM Engine</span>
                  </div>
                  <div className="obi-addon-price"><b>+${multiAddon.oneTimePrice.toLocaleString()}</b><small>one-time</small></div>
                  <p>{multiAddon.bestFor}</p>
                  <ul>
                    {multiAddon.features.map((f) => (
                      <li key={f}><Icon name="check" size={13}/> {f}</li>
                    ))}
                  </ul>
                  <p className="obi-addon-note">{multiAddon.note}</p>
                </div>
              </div>
              <a href="#book" className="btn btn-primary" onClick={onMultichannelCta}>Add Multichannel Outreach <Icon name="arrow" size={14}/></a>
            </article>
          )}
        </div>
      </section>

      <section className="plans-section" id="stack">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">PLATFORM CHOICE</span>
            <h2>Choose the Right <em>Outbound Platform</em></h2>
          </div>
          <div className="incl-grid obi-platform-grid">
            {platforms.map((p) => (
              <div className="incl-card" key={p.id}>
                <h3>{p.name}</h3>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "var(--fs-base)", lineHeight: 1.55 }}><b>Best for:</b> {p.bestFor}</p>
              </div>
            ))}
          </div>
          <p className="plans-note">We recommend the stack based on your sales motion instead of forcing every client into the same platform.</p>
        </div>
      </section>

      <section className="plp-band-dark plp-else" id="hubspot">
        <div className="wrap">
          <div className="plp-else-head">
            <span className="saas-eyebrow">HUBSPOT CONNECTION</span>
            <h2>Outbound Shouldn’t Live Outside Your CRM</h2>
            <p>Outbound tools are good at sending. HubSpot should remain the source of truth for your sales process.</p>
            <p>Depending on the package, we connect your outbound stack back into HubSpot so replies and opportunities reach the people who need them.</p>
          </div>
          <ul className="obi-hub-list">
            {hubspotBullets.map((b) => (
              <li key={b}><Icon name="check" size={14}/> {b}</li>
            ))}
          </ul>
          <div className="plp-else-cta">
            <div className="plp-else-cta-copy">
              <b>Need HubSpot + outbound wired correctly?</b>
              <span>We’ll map sync capabilities to your HubSpot tier and chosen outreach platform.</span>
            </div>
            <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Talk to a HubSpot + Outbound Expert <Icon name="arrow" size={14}/></a>
          </div>
        </div>
      </section>

      <section className="incl-section" id="ownership">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">FULL OWNERSHIP</span>
            <h2>Your Engine. Your Accounts. <em>Your Data.</em></h2>
            <p>We don't build your outbound system inside agency-owned accounts. Everything is configured under your ownership.</p>
          </div>
          <ul className="obi-own-list">
            {ownership.map((item) => (
              <li key={item}><Icon name="check" size={15}/> {item}</li>
            ))}
          </ul>
          <p className="plans-note">Run it internally, hire your own SDRs or keep VestalHub involved after launch.</p>
        </div>
      </section>

      <section className="plans-section" id="after-launch">
        <div className="wrap">
          <div className="obi-teaser">
            <div>
              <span className="saas-eyebrow saas-eyebrow-dark">AFTER LAUNCH</span>
              <h2>Want Us to Run Outbound and Book Meetings?</h2>
              <p>Every setup is designed to work without Vestal Hub after handover. If you’d rather have us operate the engine — targeting, campaigns, replies, qualification and meeting booking — we offer managed outbound with fixed, per-meeting or revenue-share pricing.</p>
            </div>
            <a href="/outbound-optimization" className="btn btn-ghost" onClick={onOngoingTeaser}>View Managed Outbound <Icon name="arrow" size={14}/></a>
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Outbound setup <em>questions.</em></h2>
              <p>Still wondering something? <a href="#book" onClick={scrollToBook}>Book a call</a> or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>send us a note</a>.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">GET STARTED</span>
            <h2>Build an Outbound Engine Your Team <em>Actually Owns</em></h2>
            <p>Get your domains, mailboxes, outreach platform, HubSpot integration and campaigns built properly — without committing to a traditional lead-generation agency.</p>
          </div>
          <div className="plp-hero-ctas" style={{ justifyContent: "center", marginBottom: 28 }}>
            <a href="#book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); trackOutbound("outbound_final_cta", { cta: "book_consultation" }); }}>Book a Free Consultation <Icon name="arrow" size={14}/></a>
            <a href="#setup-packages" className="btn btn-ghost" onClick={(e) => { trackOutbound("outbound_final_cta", { cta: "view_setup_packages" }); scrollToId("setup-packages")(e); }}>View Setup Packages</a>
          </div>
          <p className="plans-note" style={{ marginBottom: 28 }}>Fixed-price implementation · Full ownership · Optional support after launch</p>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>
    </div>
  );
};

/* ============ Outbound Optimization — /outbound-optimization ============
   Managed B2B outbound lead generation / appointment setting.
   URL kept; content is a fully managed meeting-booking service, not
   post-launch maintenance. Data: window.OUTBOUND_OPTIMIZATION. */
const OutboundOptimizationPage = () => {
  const meetingsRef = useRef(null);
  const data = window.OUTBOUND_OPTIMIZATION || {};
  const plans = data.plans || window.OUTBOUND_MONTHLY_PLANS || [];
  const faq = data.faq || window.OUTBOUND_OPTIMIZATION_FAQ || [];
  const featuredReviews = data.featuredReviews || (data.featuredReview ? [data.featuredReview] : []);
  const steps = data.steps || [];
  const capabilities = data.capabilities || [];
  const fitGood = data.fitGood || [];
  const fitBad = data.fitBad || [];
  const onboarding = data.onboarding || [];
  const why = data.why || [];
  const related = data.related || [];

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = (e) => {
    e?.preventDefault();
    trackOutbound("outbound_opt_final_cta", { location: "book_scroll" });
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const onPlanCta = (plan) => (e) => {
    e.preventDefault();
    trackOutbound("outbound_monthly_pricing_cta", { package: plan.id });
    if (plan.event) trackOutbound(plan.event);
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const relatedGo = (href) => (e) => {
    e.preventDefault();
    trackOutbound("outbound_opt_related", { href });
    if (href === "/hubspot-admin") return goToAdmin();
    if (href === "/hubspot-onboarding-packages") return goToOnboardingPackages();
    if (href === "/outbound-infrastructure") return goToOutboundInfrastructure();
    if (href === "/hubspot-technical-developers") return goToTechSupport();
  };

  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  return (
    <div className="saas-lp plp hop obi">
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">MANAGED B2B OUTBOUND</span>
              <h1>We Book Qualified B2B Meetings <span className="hl">for Your Sales Team</span></h1>
              <p className="hero-lead">
                We build and manage your outbound engine end-to-end — targeting, prospect data, email infrastructure, campaigns, reply handling, qualification and meeting booking.
              </p>
              <div className="plp-hero-ctas">
                <a href="#book" className="btn btn-primary" onClick={(e) => { trackOutbound("outbound_opt_hero_cta", { cta: "book" }); scrollToBook(e); }}>Book a Strategy Call <Icon name="arrow" size={14}/></a>
                <a href="#how-it-works" className="btn btn-ghost" onClick={(e) => { trackOutbound("outbound_opt_hero_cta", { cta: "how" }); scrollToId("how-it-works")(e); }}>See How It Works</a>
              </div>
              <p className="obi-stack-line">No need to hire an SDR team or manage outbound tools yourself.</p>
            </div>
          </div>
        </div>
      </section>

      <StackLogos />

      <section className="aro-journey" id="how-it-works">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">HOW IT WORKS</span>
            <h2>From ICP to <em>Qualified Meeting</em></h2>
          </div>
          <div className="hop-process obi-process-5">
            {steps.map((s) => (
              <div className="hop-step" key={s.n}>
                <span className="n">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OutboundFeaturedTestimonial reviews={featuredReviews}/>

      <section className="incl-section" id="what-we-manage">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT WE MANAGE</span>
            <h2>One Partner for the Entire <em>Outbound Engine</em></h2>
            <p>ICP and targeting, lead sourcing, domains and mailboxes, deliverability, cold email, optimization, reply handling, SDR follow-up, qualification, meeting booking, CRM integration and reporting.</p>
          </div>
          <div className="obi-cap-grid">
            {capabilities.map((c) => (
              <article className="hop-step" key={c.t}>
                <h3>{c.t}</h3>
                <ul>
                  {c.items.map((item) => (
                    <li key={item}><Icon name="check" size={13}/> <span>{item}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Logos />

      <section className="plans-section hop-pricing" id="plans">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WAYS TO WORK TOGETHER</span>
            <h2>Flexible Pricing Models That Aligns With <em>Your Goals</em></h2>
            <p>Different companies prefer different levels of predictability and performance alignment. We offer three ways to structure the engagement — not a menu of “pick whatever you want,” but flexible models depending on how you prefer to share risk and upside.</p>
          </div>
          <div className="plans-grid">
            {plans.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.badge && <span className="plan-badge">{pl.badge}</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                </div>
                {pl.priceDisplay ? (
                  <div className="plan-price"><b className="plan-price-text">{pl.priceDisplay}</b></div>
                ) : (
                  <div className="plan-price">
                    {pl.pricePrefix && <small>{pl.pricePrefix}</small>}
                    <b>${pl.price.toLocaleString()}</b>
                    <small>{pl.suffix}</small>
                  </div>
                )}
                {pl.extra && <p className="plan-hours">{pl.extra}</p>}
                {pl.note && <p className="plan-hours">{pl.note}</p>}
                <p className="plan-desc">{pl.description}</p>
                <ul className="plan-bullets">
                  {(pl.features || []).map((b) => (
                    <li key={b}><Icon name="check" size={13}/> {b}</li>
                  ))}
                </ul>
                <a href="#book" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={onPlanCta(pl)}>
                  {pl.cta} <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>
          <p className="plans-note">One-time launch fee may apply depending on the scope of the outbound infrastructure required.</p>
        </div>
      </section>

      <section className="plans-section" id="fit">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHO THIS IS FOR</span>
            <h2>Best Fit for <em>Proven B2B Offers</em></h2>
            <p>This works best when the offer, ICP and sales process already exist. Outbound amplifies what is working — it does not replace product-market fit.</p>
          </div>
          <div className="pdp-fit-grid">
            <div className="pdp-fit-col pdp-fit-good">
              <div className="pdp-fit-label">Great Fit</div>
              <ul>
                {fitGood.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="pdp-fit-col pdp-fit-bad">
              <div className="pdp-fit-label">Probably Not Yet</div>
              <ul>
                {fitBad.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="aro-journey" id="onboarding">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">CLIENT EXPERIENCE</span>
            <h2>We Handle <em>the Complexity</em> and deliver results</h2>
            <p>No need to create or manage multiple outbound software accounts yourself.</p>
          </div>
          <div className="hop-process obi-process-5">
            {onboarding.map((s) => (
              <div className="hop-step" key={s.n}>
                <span className="n">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="plp-band-dark hop-creds" id="why">
        <div className="wrap">
          <span className="saas-eyebrow">WHY VESTAL HUB</span>
          <h2>Outbound Built by RevOps Experts</h2>
          <p className="obi-dark-lead">Most lead-generation agencies only focus on sending volume.</p>
          <p className="obi-dark-lead">Vestal Hub combines outbound, deliverability, CRM, automation, RevOps, attribution and sales process. We don’t just send emails. We build the technical and operational system required to turn outbound into a measurable pipeline channel.</p>
          <div className="hop-cred-grid obi-benefit-grid">
            {why.map((c) => (
              <div className="hop-cred-card" key={c.t}>
                <span className="obi-ic"><Icon name={c.i} size={18}/></span>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Managed outbound <em>questions.</em></h2>
              <p>Still wondering something? <a href="#book" onClick={scrollToBook}>Book a strategy call</a> or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>send us a note</a>.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="logo-bar obi-cross">
          <div className="wrap">
            <p>Also available from Vestal Hub</p>
            <div className="obi-cross-links">
              {related.map((r) => (
                <a key={r.href} href={r.href} onClick={relatedGo(r.href)}>{r.label}</a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">GET STARTED</span>
            <h2>Want More Qualified <em>Sales Conversations?</em></h2>
            <p>Tell us who you want to reach and how you prefer to structure the engagement. We’ll recommend the outbound model that makes the most sense for your business.</p>
          </div>
          <div className="plp-hero-ctas" style={{ justifyContent: "center", marginBottom: 28 }}>
            <a href="#book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); trackOutbound("outbound_opt_final_cta", { cta: "book" }); }}>Book a Strategy Call <Icon name="arrow" size={14}/></a>
            <a href="/contact" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); trackOutbound("outbound_opt_final_cta", { cta: "contact" }); goToContact(); }}>Talk to Vestal Hub</a>
          </div>
          <p className="plans-note" style={{ marginBottom: 28 }}>Fixed monthly · Pay per qualified meeting · Revenue-share partnership</p>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>
    </div>
  );
};

/* ============ Technical & RevOps Support page (/hubspot-technical-developers) ============
   The heavier-duty sibling of the homepage support plans. Same .saas-lp .plp
   design system and shared Logos / Reviews / Meetings bands, but positioned
   for technical + RevOps work (custom code, API integrations, complex RevOps
   implementations) with the higher hours-based pricing from the previous
   grow.vestalhub.com/hubspot-support page. "N tasks at a time" limits are
   intentionally dropped in favour of monthly senior-engineering hours, and
   each plan is enriched with the ongoing-support niceties from the homepage
   pricing table (same expert weekly, written recaps, response SLAs, no
   lock-in). */
const HubSpotTechnicalSupportPage = () => {
  const meetingsRef = useRef(null);

  const scrollToId = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToBook = scrollToId("book");

  /* Mount the HubSpot Meetings embed once on render (mirrors HubSpotSupportPage). */
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  /* Pricing carried over from the previous grow.vestalhub.com/hubspot-support
   * page. The "N tasks at a time" cap is removed per the new model; instead we
   * lead with monthly senior-engineering hours and borrow the best bits of the
   * homepage support table (same expert weekly, written recaps, response SLAs,
   * pause/cancel anytime). */
  const plans = [
    { id: "essential", name: "Essential Care", tag: "Senior RevOps help, on tap.", price: "$1,900", live: "Up to 20 hours a month",
      feats: ["Up to 20 hours of senior HubSpot & RevOps work each month",
              "1 strategy / working session a month",
              "Access to a private client portal to track every request",
              "Email + portal support",
              "Onboarding & portal audit call to kick things off",
              "Your same senior expert every month \u2014 the person who knows your portal",
              "Cancel or pause anytime, no lock-in"] },
    { id: "growth", name: "Growth Support", tag: "Your embedded RevOps team.", price: "$3,700", live: "Up to 40 hours a month", hot: true,
      feats: ["Up to 40 hours of senior HubSpot & RevOps work each month",
              "2 strategy / working sessions a month",
              "Access to a private client portal",
              "Email + portal + Slack / Microsoft Teams support",
              "Onboarding & portal audit call",
              "Quick fixes shipped in 2 business days",
              "Written recap, every Friday, of everything delivered",
              "Your senior expert, every week \u2014 the same person start to finish",
              "Cancel or pause anytime, no lock-in"] },
    { id: "enterprise", name: "Enterprise Plan", tag: "Full technical HubSpot management.", price: "$4,900", live: "Up to 60 hours a month",
      feats: ["Up to 60 hours of senior HubSpot & RevOps work each month",
              "4 strategy / working sessions a month",
              "Access to a private client portal",
              "Priority email + portal + Slack / Microsoft Teams support",
              "Onboarding & portal audit call",
              "Custom-coded workflow actions & serverless functions",
              "Custom API integrations & product-data sync",
              "Complex RevOps implementations \u2014 scoring, attribution, forecasting",
              "Next-business-day quick fixes",
              "Monthly strategy call + quarterly portal scorecard, in writing",
              "Cancel or pause anytime, no lock-in"] },
  ];

  const rules = [
    { h: "What's a quick fix?", p: "Anything under a couple of hours: a property, a broken workflow step, a report tweak, a stuck sync, a \u201Chow should we\u2026?\u201D question. Shipped in 2 business days (next day on Enterprise)." },
    { h: "What's a technical build?", p: "Anything bigger: a custom-coded workflow action, a serverless function, an API integration, a scoring model or a pipeline rebuild. You get a dated milestone plan within one business day \u2014 approved by you before work starts." },
    { h: "How your hours work.", p: "Each plan is sized to a monthly block of senior-engineering hours. We track them transparently and always tell you what's in motion and what's next. Need a heavier month? Upgrade anytime; unused hours don't roll over." },
    { h: "Cancel or pause anytime.", p: "Month-to-month, no lock-in and no cancellation penalty. Pause on a 31-day cycle and bank the remaining days for later." },
  ];

  const helps = [
    { i: "code", t: "Custom code & workflow actions",
      pts: ["Custom-coded workflow actions and programmable automation",
            "Serverless functions and scheduled jobs on HubSpot",
            "UI extensions and custom cards for your team's workflows"] },
    { i: "zap", t: "API integrations & data sync",
      pts: ["Two-way syncs with Salesforce, Stripe, your data warehouse and more",
            "Product-usage and billing data piped into HubSpot",
            "Custom webhooks, middleware and error-handled pipelines"] },
    { i: "chart", t: "RevOps architecture",
      pts: ["Lead scoring models sales actually trusts",
            "Lifecycle stages, routing and forecasting that match your motion",
            "Multi-touch attribution: first touch \u2192 closed-won"] },
    { i: "refresh", t: "Data engineering & migrations",
      pts: ["Clean migrations from Salesforce, Pipedrive or Marketo \u2014 history intact",
            "De-duplication, backfills and property re-architecture",
            "Datasets, calculated properties and reporting-ready models"] },
    { i: "shield", t: "Troubleshooting & optimization",
      pts: ["Diagnose and fix automations that quietly broke",
            "Untangle tech debt from rushed implementations",
            "Ongoing optimization as HubSpot and your stack evolve"] },
    { i: "layers", t: "HubSpot CMS web development",
      pts: ["Marketing sites, themes and templates on HubSpot CMS",
            "Custom modules, landing pages and blog templates",
            "Website migrations onto HubSpot CMS"] },
  ];

  const baseFaq = (window.TECH_SUPPORT_FAQ && window.TECH_SUPPORT_FAQ.length) ? window.TECH_SUPPORT_FAQ : [
    { q: "How fast do you turn around requests?", a: "Most requests are done in two business days or less \u2014 sometimes same day. Larger technical builds get a dated milestone plan within one business day, approved by you before work starts." },
    { q: "How many requests can I have in flight?", a: "Send as many as you like \u2014 we keep the backlog and work it down against your plan's monthly hours. One priority moves at a time and you always see what's next." },
    { q: "How does the pause feature work?", a: "Billing runs on a 31-day cycle. If you pause, the remaining days are saved for later. Month-to-month, no lock-in, cancel anytime." },
    { q: "Who actually does the work?", a: "A dedicated, HubSpot-certified senior engineer \u2014 the same person every week who knows your portal and your stack. No juniors, no handoffs." },
  ];
  // The "how it works" rules that used to sit under the pricing now live in the
  // main FAQ at the bottom of the page.
  const faq = [...baseFaq, ...rules.map((r) => ({ q: r.h, a: r.p }))];

  return (
    <div className="saas-lp plp">
      {/* ===== Hero ===== */}
      <section className="hero plp-hero" id="top">
        <div className="wrap">
          <div className="hero-grid plp-hero-grid">
            <div className="plp-hero-copy">
              <span className="saas-eyebrow">TECHNICAL &amp; REVOPS HUBSPOT SUPPORT</span>
              <h1>Ongoing HubSpot RevOps Support<br/><span className="hl">That Keeps You Growing</span></h1>
              <p className="hero-lead">
                For teams that have outgrown basic support. Custom code, API integrations, data engineering and complex RevOps work — a dedicated senior engineer on your portal every week, for a fixed monthly fee.
              </p>
              <div className="plp-hero-ctas">
                <a href="#book" className="btn btn-primary" onClick={scrollToBook}>Book a technical scoping call <Icon name="arrow" size={14}/></a>
                <a href="#support" className="btn btn-ghost" onClick={scrollToId("support")}>See pricing</a>
              </div>
              <div className="hero-cred" role="complementary" aria-label="Partner status and customer trust">
                <div className="hero-cred-cell">
                  <img
                    src="assets/trust/hubspot-gold-partner.png"
                    alt="HubSpot Solutions Partner — Gold"
                    className="hero-cred-badge"
                    width="120" height="120" loading="lazy"
                  />
                  <span className="hero-cred-copy">
                    <b>HubSpot</b>
                    <span>Gold Solutions Partner</span>
                  </span>
                </div>
                <span className="hero-cred-divider" aria-hidden="true"/>
                <div className="hero-cred-cell">
                  <span className="hero-cred-avatars" aria-hidden="true">
                    <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-catio.webp"       alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-kindsight.webp"   alt="" width="32" height="32" loading="lazy"/>
                    <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="32" height="32" loading="lazy"/>
                  </span>
                  <span className="hero-cred-copy">
                    <span className="hero-cred-stars" aria-label="5 out of 5 stars">★★★★★</span>
                    <span>Trusted by <b>100+ companies</b></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Logos (shared) ===== */}
      <StackLogos />

      {/* ===== What we help with ===== */}
      <section className="incl-section">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">WHAT WE HANDLE</span>
            <h2>The technical HubSpot work, <em>handled for you.</em></h2>
            <p>Beyond admin and quick fixes: the custom code, integrations, data engineering and RevOps architecture most teams can't staff for full-time — from one senior engineer who knows your portal.</p>
          </div>
          <div className="incl-grid">
            {helps.map((c) => (
              <div className="incl-card" key={c.t}>
                <span className="incl-ic"><Icon name={c.i} size={20}/></span>
                <h3>{c.t}</h3>
                <ul>
                  {c.pts.map((p, i) => (<li key={i}><Icon name="check" size={14}/> {p}</li>))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Logos />

      {/* ===== Support plans ===== */}
      <section className="plans-section plp-support" id="support">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">TECHNICAL SUPPORT PLANS</span>
            <h2>Senior engineering hours. <em>Every month.</em> A flat price.</h2>
            <p>Pick the monthly block of senior HubSpot & RevOps engineering that fits your roadmap. Every plan can be paused at any time — no lock-in, no change-order theatre.</p>
          </div>
          <div className="plans-grid">
            {plans.map((pl) => (
              <div className={`plan-card ${pl.hot ? "on" : ""}`} key={pl.id}>
                {pl.hot && <span className="plan-badge">★ Most popular</span>}
                <div className="plan-head">
                  <span className="plan-name">{pl.name}</span>
                  <span className="plan-tag">{pl.tag}</span>
                </div>
                <div className="plan-price"><b>{pl.price}</b><small>/month</small></div>
                <div className="plan-hours">{pl.live}</div>
                <ul className="plan-bullets">
                  {pl.feats.map((b, i) => (<li key={i}><Icon name="check" size={13}/> {b}</li>))}
                </ul>
                <a href="#book" className={`btn ${pl.hot ? "btn-primary" : "btn-ghost"}`} onClick={scrollToBook}>
                  Start now <Icon name="arrow" size={14}/>
                </a>
              </div>
            ))}
          </div>

          <p className="plans-more">
            Need lighter, admin-only support?{" "}
            <a href="/hubspot-support-plans" onClick={(e) => { e.preventDefault(); goToSupport(); }}>See our standard HubSpot support plans</a> from $999/mo.
          </p>
        </div>
      </section>

      {/* ===== Need something else? -> contact ===== */}
      <section className="plp-band-dark plp-tech" id="other">
        <div className="wrap">
          <span className="saas-eyebrow">NEED A ONE-OFF INSTEAD?</span>
          <h2>Fresh onboarding or a fixed-scope technical project?</h2>
          <div className="plp-tech-grid">
            <div>
              <p className="plp-tech-lead">Support plans cover the ongoing engineering. For a brand-new setup or a single scoped build, tell us what you need and we'll come back with a fixed price.</p>
              <ul className="plp-tech-list">
                <li><Icon name="arrow" size={15}/> New HubSpot onboarding — one Hub or the full platform, set up right</li>
                <li><Icon name="arrow" size={15}/> Custom-coded workflows, API integrations &amp; product-data sync</li>
                <li><Icon name="arrow" size={15}/> Migrations from Salesforce, Pipedrive or Marketo — without losing history</li>
              </ul>
            </div>
            <div className="plp-sow">
              <h3>Tell us what you need.</h3>
              <p>Send a few sentences and you'll get a fixed-price scope back — no discovery weeks, no meetings required. Plan members get priority scheduling and member pricing.</p>
              <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact us <Icon name="arrow" size={14}/></a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Reviews (shared) ===== */}
      <Reviews />

      {/* ===== Book a call (primary CTA) ===== */}
      <section className="plans-section plp-book-section" id="book">
        <div className="wrap">
          <div className="saas-head">
            <span className="saas-eyebrow saas-eyebrow-dark">BOOK A CALL</span>
            <h2>Schedule a <em>technical scoping call.</em></h2>
            <p>Pick a live slot below. We'll look at your portal and stack together, talk through your architecture and growth goals, and figure out which plan fits — no obligation.</p>
          </div>
          <div className="plp-book-embed" ref={meetingsRef}/>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section">
        <div className="wrap">
          <div className="faq-grid">
            <div>
              <h2>Plain answers, <em>in writing.</em> Obviously.</h2>
              <p>Everything teams ask before working with us. Still wondering something? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send us a note</a> and we'll reply within one business day.</p>
            </div>
            <div className="faq-list">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p className="ans">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ============ Custom Work page ============ */
const CustomPage = ({ onAdd }) => {
  const hours    = useMemo(() => PRODUCTS.find(p => p.id === "x-hours"),    []);
  const retainer = useMemo(() => PRODUCTS.find(p => p.id === "x-retainer"), []);
  const promoOn  = customPromoActive();
  const fmtPrice = (n) => n >= 1000 ? `$${(n/1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n}`;
  const fmtPromoTier = (n, monthly) => (
    promoOn
      ? <><s className="cwp-tier-was">{fmtPrice(n)}</s> <b>{fmtPrice(customPromoPrice(n))}</b>{monthly ? <small>/mo</small> : null}</>
      : <>{fmtPrice(n)}{monthly ? <small>/mo</small> : null}</>
  );

  const useCases = [
    { i: "code",    n: "Custom integrations",        d: "API work between HubSpot and your data warehouse, billing system, product analytics, or a tool that doesn't have a native connector." },
    { i: "chart",   n: "Bespoke reporting",          d: "Custom dashboards, BI exports, blended attribution models, and the report your CFO has been asking for since Q2." },
    { i: "refresh", n: "Multi-portal migrations",    d: "Merging two HubSpot portals after an acquisition, splitting one portal into two BUs, or migrating from another CRM with custom logic." },
    { i: "rocket",  n: "Launch crunch coverage",     d: "An extra senior pair of hands for 2–6 weeks around a launch, fundraise, or board meeting — without hiring full-time." },
    { i: "user",    n: "RevOps as a service",        d: "Fractional ops leadership: weekly priorities, sprint planning, hiring help, vendor evaluation — embedded with your team." },
    { i: "shield",  n: "Audits + remediation",       d: "Security review, GDPR / SOC 2 prep on the HubSpot side, deliverability rehab, or unwinding a previous agency mess." },
  ];

  const steps = [
    { n: "01", t: "Tell us what you're shipping",
      d: "Send a short brief — what you need, when, and any constraints. No sales call required, but we're happy to hop on one." },
    { n: "02", t: "SOW or proposal in 48h",
      d: "If it fits an Hours Package or Retainer, we send a 1-page SOW. If it's a true custom build, you get a fixed-price proposal with milestones." },
    { n: "03", t: "Senior on day one",
      d: "Same operator owns the engagement end-to-end — no juniors, no account managers, no handoffs. Slack channel up within 24h of signing." },
    { n: "04", t: "Weekly cadence + burndown",
      d: "Async-first via Slack, plus one live working session per week. You get a written burndown report so you always know where hours stand." },
  ];

  const customFaq = [
    { q: "When should I buy hours vs sign a retainer?",
      a: "Buy an Hours Package when you have a project with a clear end — a launch, a migration, a quarter of optimization. You pay once, burn the hours within 90 days, no monthly commitment. Sign a Retainer when you want an operator to own the long tail of HubSpot / RevOps work week after week — standing session, dedicated Slack, sprint planning. The trade is rate vs flexibility: Hours run $135–$175/hr, Retainer drops to $110–$140/hr in exchange for the ongoing commitment." },
    ...(promoOn ? [{ q: "What's the June promo?",
      a: "Through June 30: 50% off any Custom Hours block (one-time purchase) and 50% off your first 3 months on a Monthly Retainer — then list price from month 4. Promo pricing is confirmed on your SOW when you kick off." }] : []),
    { q: "What's actually a 'custom' engagement vs a service in the catalog?",
      a: "If 80% of what you need is already in the catalog, buy that and add an Hours block on top. If your scope is genuinely bespoke — a custom integration, a multi-system migration, fractional RevOps leadership — it's custom work, and we'll write a fixed-price proposal." },
    { q: "Can I roll over unused hours?",
      a: "Hours Packages are valid for 90 days. You can roll over up to 25% of unused hours into a follow-on block. Retainer hours don't roll over month to month — they're sized to your monthly burn rate so you're not over-buying." },
    { q: "Do you sign NDAs and DPAs?",
      a: "Yes. Standard mutual NDA and DPA templates are ready to sign. SOC 2 questionnaires welcomed — we run our practice on the same controls we help our clients implement." },
    { q: "What if my scope changes mid-engagement?",
      a: "We re-baseline the SOW or burndown together. No surprise change orders. If a project balloons past the original block, we'll tell you in week two — not week eight." },
    { q: "Can you work with our existing agency or in-house team?",
      a: "All the time. We're happy to be the senior pair of hands inside your team or a specialist your agency loops in for HubSpot / RevOps work specifically." },
  ];

  const renderModel = (p, eyebrow) => (
    <div className="cwp-model">
      <span className="cwp-model-eye">{eyebrow}</span>
      <div className="cwp-model-head">
        <div>
          <h3>{p.name}</h3>
          <p>{p.desc}</p>
        </div>
        <div className="cwp-model-from">
          <span className="l">{promoOn ? "From · June promo" : "From"}</span>
          <span className="p">
            {fmtPromoTier(Math.min(...p.tiers.map(t => t.price)), p.duration === "monthly")}
          </span>
        </div>
      </div>
      <div className="cwp-tiers">
        {p.tiers.map(t => (
          <button key={t.id}
                  className={`cwp-tier ${t.badge ? "on" : ""}`}
                  onClick={() => goToProduct(p.id)}>
            {t.badge && <span className="cwp-tier-badge">★ Popular</span>}
            <div className="cwp-tier-head">
              <span className="cwp-tier-n">{t.name}</span>
              <span className="cwp-tier-tag">{t.tag}</span>
            </div>
            <div className="cwp-tier-p">
              {fmtPromoTier(t.price, p.duration === "monthly")}
            </div>
            <ul>
              {t.bullets.slice(0, 3).map((b, i) => (
                <li key={i}><Icon name="check" size={11}/> {b}</li>
              ))}
              {t.bullets.length > 3 && <li className="more">+ {t.bullets.length - 3} more</li>}
            </ul>
          </button>
        ))}
      </div>
      <div className="cwp-model-foot">
        <button className="btn btn-primary" onClick={() => goToProduct(p.id)}>
          {p.duration === "monthly" ? "View retainer plans" : "View hours packages"} <Icon name="arrow" size={14}/>
        </button>
        <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} className="btn btn-ghost">Contact us about scope</a>
      </div>
    </div>
  );

  return (
    <>
      {/* ===== Hero ===== */}
      <section className="cat-hero cwp-hero" style={{"--cat-color": "#7C5CFC"}}>
        <div className="wrap">
          <nav className="pdp-crumbs cat-crumbs">
            <a href="/shop" onClick={(e) => { e.preventDefault(); goToShop(); }}>Shop</a>
            <span>/</span>
            <b>Custom work</b>
          </nav>

          <div className="cat-hero-grid">
            <div className="cat-hero-main">
              <span className="cat-eye">CUSTOM WORK · WHEN THE CATALOG ISN'T ENOUGH</span>
              <h1 className="cat-h1">When you need a senior on tap, <em>not a SKU.</em></h1>
              <p className="cat-sub">
                Two-thirds of our work fits a fixed service in the shop. For the other third — custom integrations, multi-portal migrations, fractional RevOps leadership, launch crunch coverage — pick the engagement model that matches your scope, and start with a senior operator on day one.
              </p>
              {promoOn && (
                <div className="cwp-promo-banner">
                  <span className="cwp-promo-tag">{CUSTOM_ENGAGEMENT_PROMO.label}</span>
                  <div className="cwp-promo-copy">
                    <b>{Math.round(CUSTOM_ENGAGEMENT_PROMO.discount * 100)}% off</b> Hours Packages &amp; your first {CUSTOM_ENGAGEMENT_PROMO.retainerMonths} months on Retainers
                    <span className="muted"> · through June 30</span>
                  </div>
                </div>
              )}
              <div className="cat-hero-meta">
                <span className="m"><Icon name="zap"    size={13}/> Kickoff in <b>5 days</b></span>
                <span className="m"><Icon name="user"   size={13}/> One senior operator, end-to-end</span>
                <span className="m"><Icon name="shield" size={13}/> Fixed price · no surprise change orders</span>
                <span className="m"><Icon name="check"  size={13}/> Cancel any time · no annual lock-in</span>
              </div>
              <div className="hero-ctas" style={{marginTop:18}}>
                <a href="#cwp-models" className="btn btn-primary"
                   onClick={(e) => { e.preventDefault(); document.getElementById("cwp-models")?.scrollIntoView({behavior:"smooth"}); }}>
                  See engagement models <Icon name="arrow" size={14}/>
                </a>
                <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} className="btn btn-ghost">Send us a brief</a>
              </div>
            </div>
            <div className="cat-hero-card cat-hero-card-img">
              <img src="assets/categories/cat-custom.png" alt="Custom work — pre-paid hours packages and monthly retainers with an embedded senior operator" className="cat-hero-img" loading="eager" />
              <div className="cat-card-foot">
                <span>ENGAGEMENT</span>
                <b>Custom &amp; Retainer</b>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Engagement models ===== */}
      <section className="cwp-models" id="cwp-models">
        <div className="wrap">
          <div className="section-head">
            <div>
              <h2>Two ways to <em>engage.</em></h2>
              <div className="sub">PICK THE ONE THAT MATCHES YOUR SCOPE</div>
            </div>
          </div>
          <div className="cwp-models-grid">
            {hours    && renderModel(hours,    "01 · ONE-OFF SCOPE")}
            {retainer && renderModel(retainer, "02 · ONGOING SUPPORT")}
          </div>
          <div className="cwp-compare">
            <div className="cwp-compare-row cwp-compare-h">
              <div></div>
              <div>Hours Package</div>
              <div>Monthly Retainer</div>
            </div>
            <div className="cwp-compare-row">
              <div>Best for</div>
              <div>A defined project with a clear end date</div>
              <div>Ongoing ops you want a senior to own week after week</div>
            </div>
            <div className="cwp-compare-row">
              <div>Commitment</div>
              <div>One-time payment · no monthly bill</div>
              <div>Month-to-month · cancel with 30 days' notice</div>
            </div>
            <div className="cwp-compare-row">
              <div>Cadence</div>
              <div>Async + working sessions you schedule when you need them</div>
              <div>Standing weekly session + dedicated Slack from day one</div>
            </div>
            <div className="cwp-compare-row">
              <div>Planning</div>
              <div>SOW + burndown against your block</div>
              <div>Sprint board · prioritized with you every 2 weeks</div>
            </div>
            <div className="cwp-compare-row">
              <div>Hours expiry</div>
              <div>90-day window · roll over up to 25% unused</div>
              <div>Sized to your monthly burn · no rollover</div>
            </div>
            <div className="cwp-compare-row">
              <div>Hourly rate</div>
              <div><b>$135–$175 / hr</b> (depending on block size)</div>
              <div><b>$110–$140 / hr</b> (lower in exchange for commitment)</div>
            </div>
            {promoOn && (
              <div className="cwp-compare-row cwp-compare-promo">
                <div>June promo</div>
                <div><b>50% off</b> any hours block through June 30</div>
                <div><b>50% off</b> first 3 months · then list price</div>
              </div>
            )}
            <div className="cwp-compare-row">
              <div>Starts at</div>
              <div>
                {promoOn
                  ? <><s>$1,750</s> <b>$875</b> · 10h block</>
                  : <>$1,750 · 10h block</>}
              </div>
              <div>
                {promoOn
                  ? <><s>$1,400</s> <b>$700</b> / month · Lite</>
                  : <>$1,400 / month · Lite</>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Use cases ===== */}
      <section className="cwp-uses">
        <div className="wrap">
          <div className="section-head">
            <div>
              <h2>What teams <em>use it for.</em></h2>
              <div className="sub">REAL ENGAGEMENTS · LAST 12 MONTHS</div>
            </div>
          </div>
          <div className="cwp-uses-grid">
            {useCases.map((u, i) => (
              <div className="cwp-use" key={u.n}>
                <div className="cwp-use-num">0{i+1}</div>
                <div className="cwp-use-ic"><Icon name={u.i} size={20}/></div>
                <h3>{u.n}</h3>
                <p>{u.d}</p>
              </div>
            ))}
          </div>
          <p className="cwp-uses-foot">
            Don't see your scope? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send us a 3-line brief</a> and we'll tell you within 24h whether it fits an Hours Package, a Retainer, or a fully custom proposal.
          </p>
        </div>
      </section>

      {/* ===== Process ===== */}
      <section className="cwp-process">
        <div className="wrap">
          <div className="section-head">
            <div>
              <h2>How <em>custom engagements</em> work.</h2>
              <div className="sub">FOUR STEPS · NO SALES THEATRE</div>
            </div>
          </div>
          <div className="cwp-steps">
            {steps.map((s) => (
              <div className="cwp-step" key={s.n}>
                <div className="cwp-step-n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Reviews ===== */}
      <section className="cat-reviews">
        <div className="wrap">
          <span className="cat-section-eye">FROM TEAMS WHO WORK WITH US BEYOND THE CATALOG</span>
          <h2>What an embedded senior changes.</h2>
          <div className="cat-reviews-grid">
            <article className="cat-review">
              <Stars/>
              <p>"Working with Vestal Hub has been game changing. Their <b>speed to value is incredible</b>. The engagement model — Slack-first plus a weekly working session — supports both ad-hoc tactical changes and larger project implementations."</p>
              <div className="cr-author">
                <img src="https://grow.vestalhub.com/hubfs/mavenagi_testimonial.webp" alt="Erin Parker" loading="lazy"/>
                <div>
                  <b>Erin Parker</b>
                  <span>Head of Revenue Operations · <em>Maven AGI</em></span>
                </div>
                <img className="cr-logo" src="https://grow.vestalhub.com/hubfs/mavenagi.webp" alt="Maven AGI" loading="lazy"/>
              </div>
            </article>
            <article className="cat-review">
              <Stars/>
              <p>"We bought a 25-hour block to get past a launch crunch. <b>Used it in 3 weeks, no drama.</b> Then rolled the rest into a Lite retainer so we always have a senior on hand for the long tail of HubSpot work."</p>
              <div className="cr-author">
                <img src="https://grow.vestalhub.com/hubfs/copilotkit_testimonial.webp" alt="Travis Murdock" loading="lazy"/>
                <div>
                  <b>Travis Murdock</b>
                  <span>Head of GTM · <em>CopilotKit</em></span>
                </div>
                <img className="cr-logo" src="https://grow.vestalhub.com/hubfs/copilotkit.svg" alt="CopilotKit" loading="lazy"/>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="cat-faq">
        <div className="wrap">
          <div className="cat-faq-grid">
            <div>
              <span className="cat-section-eye">COMMON QUESTIONS · CUSTOM WORK</span>
              <h2>Before you brief us.</h2>
              <p>Most teams have the same five questions. If yours isn't here, <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>contact us</a> — we reply within one business day.</p>
            </div>
            <div className="cat-faq-list">
              {customFaq.map((f, i) => (
                <details key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA band ===== */}
      <section className="cta-band" id="cta">
        <div className="wrap">
          <div className="cta-inner">
            <h2>Tell us what you're <em>shipping.</em></h2>
            <p>Three lines is enough: what you need, when you need it, any constraints. We'll reply within one business day with the right engagement model and a fixed price — no discovery call required.</p>
            <div className="btns">
              <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}
                 className="btn btn-primary">Send us a brief <Icon name="arrow" size={14}/></a>
              <a href="/shop" className="btn btn-ghost"
                 onClick={(e) => { e.preventDefault(); goToShop(); }}>Browse fixed-scope services</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============================================================
   STATIC PAGES — Contact, About, Process, Partners, Careers,
   Terms, Privacy, Cookies. All share the .page-* shell.
============================================================ */

const CONTACT_TOPIC_OPTIONS = [
  { value: "hubspot", label: "HubSpot help" },
  { value: "leadgen", label: "Lead generation" },
];

const formatContactTopics = (topics) =>
  CONTACT_TOPIC_OPTIONS.filter((o) => topics.includes(o.value)).map((o) => o.label).join(", ") || "general";

const readContactTopics = () => {
  try {
    const v = new URLSearchParams(window.location.search).get("lane");
    return v === "hubspot" || v === "leadgen" ? [v] : [];
  } catch {
    return [];
  }
};

const PageHero = ({ eyebrow, title, lead, accent = "var(--brand)" }) => (
  <section className="page-hero" style={{ "--page-accent": accent }}>
    <div className="wrap">
      <span className="page-eye">{eyebrow}</span>
      <h1>{title}</h1>
      {lead && <p className="page-lead">{lead}</p>}
    </div>
  </section>
);

/* ============ Booking page (HubSpot meetings embed) ============ */
const BookingPage = () => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/vestalhub-call?embed=true");
    containerRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    containerRef.current.appendChild(script);
    return () => {
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, []);
  return (
    <>
      <PageHero
        eyebrow="BOOK A CALL · 30 MINUTES"
        title={<>HubSpot help, <em>or more pipeline?</em></>}
        lead="Same team either way. Pick a live slot — HubSpot, lead generation, or both. No obligation."
        accent="#3B69FF"
      />
      <section className="page-body">
        <div className="wrap book-layout">
          <div className="book-calendar">
            <span className="page-section-eye">PICK A TIME</span>
            <h2>Grab 30 minutes.</h2>
            <p className="book-calendar-lead">We'll review what you need and recommend the next step. Same people who reply to briefs.</p>
            <div className="book-embed" ref={containerRef}/>
            <p className="book-calendar-note">
              Prefer to write it out? <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send a brief</a> — we reply in one business day.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============================================================
   CONSULTATION LANDING PAGE — /hubspot-consultation
   Stripped-chrome conversion LP. Skips global Header + Footer.
   Hero with qualifying form → /api/contact → smooth-scrolls
   to the inline HubSpot Meetings embed for the actual booking.
============================================================ */
// LinkedIn conversion ID for the "consultation lead" conversion, created in
// Campaign Manager (Account assets → Conversion tracking). Until it's set, the
// lintrk track call below is a no-op. Insight Tag partner ID lives in index.html.
const LINKEDIN_CONVERSION_ID = 28448322;

const ConsultationPage = () => {
  const [form, setForm] = useState({ name:"", email:"", company:"", blocker:"" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const meetingsRef = useRef(null);

  /* Mount the HubSpot Meetings embed once on render. Mirrors BookingPage —
   * the loader script needs a sibling div with data-src to find. */
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => {
      if (meetingsRef.current) meetingsRef.current.innerHTML = "";
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const message = [
        "Free consultation request from /hubspot-consultation",
        "",
        `Biggest HubSpot blocker right now:`,
        form.blocker.trim() || "(not specified)",
      ].join("\n");
      const posted = await postContact({
          name: form.name,
          email: form.email,
          company: form.company,
          topic: "consultation",
          message,
        }, e.target);
      if (!posted.spam) {
        try { window.gtag && window.gtag("event", "generate_lead", { value: 30, currency: "USD", method: "consultation_form" }); } catch {}
        try { window.posthog && window.posthog.capture("consultation_lead_submitted", { company: form.company || null }); } catch {}
        try { if (window.lintrk && LINKEDIN_CONVERSION_ID) window.lintrk("track", { conversion_id: LINKEDIN_CONVERSION_ID }); } catch {}
      }
      setSent(true);
      // Scroll to the calendar embed so submitters land directly on the booking surface.
      setTimeout(() => {
        document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (err) {
      const msg = err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.";
      console.error("[consultation:submit] failed:", err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* Pull two flagship testimonials from REVIEWS so the LP stays in sync
   * with whatever editorial copy lives in src/data.jsx. Fall back to the
   * first two stack reviews if Maven AGI / CopilotKit aren't present. */
  const allReviews = (typeof REVIEWS !== "undefined" ? REVIEWS : (window.REVIEWS || []));
  const pickReview = (id, fallbackIdx) => allReviews.find(r => r.id === id) || allReviews.filter(r => r.slot === "stack")[fallbackIdx];
  const r1 = pickReview("mavenagi", 0);
  const r2 = pickReview("copilotkit", 1);
  const logos = (typeof LOGOS !== "undefined" ? LOGOS : (window.LOGOS || []));
  const faq = (window.CONSULTATION_FAQ || []);

  const outcomes = [
    { i: "search", t: "Free portal audit",
      d: "We open your HubSpot together and run a free, live audit. A senior expert points out what's broken, what's wasted, and what's quietly costing pipeline — live, on your data." },
    { i: "check",  t: "Prioritized fix list",
      d: "You leave with a written list of the top 3–5 things to fix this week — ordered by impact, not by what we want to sell you." },
    { i: "ship",   t: "Fixed-price SOW (if it fits)",
      d: "If anything you need maps to a catalog SKU or custom scope, you get a signed-ready Statement of Work within 1 business day. Zero obligation." },
  ];

  return (
    <div className="lp lp-consultation">
      {/* ===== Minimal top bar (no nav, no search, no cart) ===== */}
      <div className="lp-topbar">
        <div className="wrap">
          <div className="lp-topbar-row">
            <a href="/" className="lp-topbar-logo" onClick={(e) => { e.preventDefault(); goHome(); }} aria-label="Vestal Hub home">
              <img src="Vestal-hub.png" alt="Vestal Hub" width="158" height="33" decoding="async"/>
            </a>
              <a href="#consultation-form" className="btn btn-primary lp-topbar-cta"
               onClick={(e) => { e.preventDefault(); document.getElementById("consultation-form")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
              Book my consultation <Icon name="arrow" size={14}/>
            </a>
          </div>
          <div className="lp-topbar-trust">
            <span><Icon name="shield" size={13}/> HubSpot Gold Solutions Partner</span>
            <span className="lp-topbar-dot" aria-hidden="true">·</span>
            <span><Icon name="star" size={12}/> 5 / 5 average rating</span>
            <span className="lp-topbar-dot" aria-hidden="true">·</span>
            <span><Icon name="check" size={13}/> Trusted by 100+ companies</span>
          </div>
        </div>
      </div>

      {/* ===== Hero (split: copy + qualifying form) ===== */}
      <section className="lp-hero">
        <div className="wrap">
          <div className="lp-hero-grid">
            <div className="lp-hero-copy">
              <span className="lp-eyebrow">FREE · 30 MINUTES · NO OBLIGATION</span>
              <h1>
                Struggling to navigate HubSpot? <em>We make it easy for you.</em>
              </h1>
              <p className="lp-hero-lead">
                Book a free, 30-minute review with an experienced HubSpot expert. We'll audit your portal live and pinpoint the quick wins and long-term solutions that keep your team focused — from quick fixes to complex implementations, with no obligation or unnecessary steps.
              </p>
              <ul className="lp-hero-bullets">
                <li><Icon name="check" size={16}/> Quick wins and long-term solutions, mapped live on your portal</li>
                <li><Icon name="check" size={16}/> An experienced HubSpot expert (8+ years) — never an SDR</li>
                <li><Icon name="check" size={16}/> Walk away with fixes you can ship this week — free.</li>
              </ul>
              <div className="lp-hero-proof">
                <div className="lp-hero-proof-avatars" aria-hidden="true">
                  <img src="assets/trust/avatar-copilotkit.webp"  alt="" width="36" height="36" loading="lazy"/>
                  <img src="assets/trust/avatar-mavenagi.webp"    alt="" width="36" height="36" loading="lazy"/>
                  <img src="assets/trust/avatar-catio.webp"       alt="" width="36" height="36" loading="lazy"/>
                  <img src="assets/trust/avatar-kindsight.webp"   alt="" width="36" height="36" loading="lazy"/>
                  <img src="assets/trust/avatar-talentcaddy.jpeg" alt="" width="36" height="36" loading="lazy"/>
                </div>
                <div className="lp-hero-proof-copy">
                  <span className="lp-hero-proof-stars" aria-label="5 out of 5 stars">★★★★★</span>
                  <span>Booked by ops leaders at <b>100+ SaaS, fintech, and services</b> teams</span>
                </div>
              </div>
            </div>

            <div className="lp-form-card" id="consultation-form">
              <span className="lp-form-eye">BOOK YOUR FREE HUBSPOT AUDIT</span>
              <h2>What's holding your HubSpot back?</h2>
              <p className="lp-form-lead">Just your name and email — then pick a live 30-minute slot on a senior expert's calendar. Prefer to write it out? We'll reply within one business day.</p>
              <GuardedForm onSubmit={submit} className="lp-form contact-form">
                <HoneypotField />
                <label>
                  <span>Your name *</span>
                  <input type="text" required value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Jane Doe"/>
                </label>
                <label>
                  <span>Work email *</span>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="jane@acme.com"/>
                </label>
                <label>
                  <span>Company</span>
                  <input type="text" value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder="Acme Inc."/>
                </label>
                <label>
                  <span>What's your biggest HubSpot headache? (optional)</span>
                  <textarea rows={3} value={form.blocker} onChange={e => setForm({...form, blocker:e.target.value})} placeholder="e.g. our lead routing is broken, dashboards lie, marketing-sales handoff is a mess…"></textarea>
                </label>
                <button className="btn btn-primary lp-form-submit" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : <>Book my free audit <Icon name="arrow" size={14}/></>}
                </button>
                <p className="lp-form-microcopy">No credit card. No obligation. ~30 minutes.</p>
                <p className="lp-form-note">
                  <Icon name="shield" size={12}/> Your details go straight to a senior expert — never an SDR. We don't share or sell them. <a href="/privacy" onClick={(e) => { e.preventDefault(); goToPrivacy(); }}>Privacy</a>
                </p>
                {sent && (
                  <div className="lp-form-sent">
                    <b>Got it.</b> A senior expert will follow up within 1 business day. Pick a live slot below to lock in faster.
                  </div>
                )}
                {error && <div className="checkout-err">{error}</div>}
              </GuardedForm>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Trust strip: logos ===== */}
      <section className="lp-logos">
        <div className="wrap">
          <div className="lp-logos-t">Driving revenue for high-tech companies</div>
          <div className="lp-logos-row">
            {logos.map(l => (
              <div className="lp-logo" key={l.name} title={l.name}>
                <img src={l.src} alt={l.name} loading="lazy"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Outcomes: what you'll get on the call ===== */}
      <section className="lp-outcomes">
        <div className="wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow lp-eyebrow-dark">WHAT YOU'LL LEAVE WITH</span>
            <h2>Three things, in <em>thirty minutes.</em></h2>
            <p>No discovery deck. No "let me put together a proposal and circle back." A live working session with a senior expert, then concrete output the same day.</p>
          </div>
          <div className="lp-outcomes-grid">
            {outcomes.map((o, i) => (
              <div className="lp-outcome" key={o.t}>
                <div className="lp-outcome-num">0{i+1}</div>
                <div className="lp-outcome-ic"><Icon name={o.i} size={22}/></div>
                <h3>{o.t}</h3>
                <p>{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Credibility: who you're talking to ===== */}
      <section className="lp-credibility">
        <div className="wrap">
          <div className="lp-credibility-grid">
            <div className="lp-credibility-copy">
              <span className="lp-eyebrow">WHO SHOWS UP</span>
              <h2>A senior expert. <em>Not an SDR.</em></h2>
              <p>Every Vestal Hub consultation is owned end-to-end by someone with 8+ years shipping HubSpot and revenue operations — the same person who'd run your engagement if we ended up working together. No sales reps qualifying you, no calendar-tag teams, no "let me get the right person on the line."</p>
              <div className="lp-credibility-stats">
                <div><b>8+ yrs</b><span>average HubSpot tenure</span></div>
                <div><b>100+</b><span>companies onboarded</span></div>
                <div><b>Gold</b><span>HubSpot Solutions Partner tier</span></div>
                <div><b>5 / 5</b><span>average client rating</span></div>
              </div>
            </div>
            <div className="lp-credibility-badge">
              <img
                src="assets/trust/hubspot-gold-partner.png"
                alt="HubSpot Solutions Partner — Gold"
                width="180" height="180" loading="lazy"
              />
              <div>
                <b>HubSpot Solutions Partner</b>
                <span>Gold tier · EU + Americas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section className="lp-reviews">
        <div className="wrap">
          <div className="lp-section-head lp-reviews-head">
            <span className="lp-eyebrow lp-eyebrow-dark">WHAT THEY SAY AFTER WORKING WITH US</span>
            <h2>The same experts who'd <em>own your work.</em></h2>
          </div>
          <div className="lp-reviews-grid">
            {[r1, r2].filter(Boolean).map((x) => (
              <article className="lp-review" key={x.id}>
                <div className="lp-review-stars" aria-label="5 out of 5 stars">★★★★★</div>
                <p dangerouslySetInnerHTML={{__html:`"${x.text}"`}}/>
                <div className="lp-review-author">
                  <img src={x.avatar} alt={x.name} loading="lazy"/>
                  <div>
                    <b>{x.name}</b>
                    <span>{x.role} · <em>{x.company}</em></span>
                  </div>
                  {x.logo && <img className="lp-review-logo" src={x.logo} alt={x.company} loading="lazy"/>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Embedded HubSpot Meetings calendar ===== */}
      <section className="lp-book" id="book">
        <div className="wrap">
          <div className="lp-section-head lp-section-head-center">
            <span className="lp-eyebrow">PICK A LIVE SLOT</span>
            <h2>Or grab the <em>next open time</em> below.</h2>
            <p>The form above gets you a reply within 1 business day. The calendar below locks in a live 30-minute slot directly on a senior expert's calendar — usually same week.</p>
          </div>
          <div className="lp-book-embed" ref={meetingsRef}/>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="lp-faq">
        <div className="wrap">
          <div className="lp-faq-grid">
            <div>
              <span className="lp-eyebrow lp-eyebrow-dark">BEFORE YOU BOOK</span>
              <h2>The questions everyone asks.</h2>
              <p>If yours isn't here, just include it in the "biggest blocker" field — we'll cover it on the call.</p>
            </div>
            <div className="lp-faq-list">
              {faq.map((f, i) => (
                <details key={i} open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <span className="plus"><Icon name="plus" size={14}/></span>
                  </summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Final CTA band ===== */}
      <section className="lp-final-cta">
        <div className="wrap">
          <div className="lp-final-inner">
            <h2>Still thinking about it?</h2>
            <p>Most teams that book leave the call with one or two free fixes they can ship by Friday. That's the worst case.</p>
            <a href="#consultation-form" className="btn btn-primary"
               onClick={(e) => { e.preventDefault(); document.getElementById("consultation-form")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
              Book my free consultation <Icon name="arrow" size={14}/>
            </a>
          </div>
        </div>
      </section>

      {/* ===== Minimal footer (no full sitemap) ===== */}
      <footer className="lp-footer">
        <div className="wrap">
          <div className="lp-footer-row">
            <div className="lp-footer-brand">
              <span>© 2026 Vestal Hub</span>
              <span aria-hidden="true">·</span>
              <span>HubSpot Gold Solutions Partner</span>
            </div>
            <div className="lp-footer-links">
              <a href={`mailto:${SOCIAL.email}`}>{SOCIAL.email}</a>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <a href="/terms"   onClick={(e) => { e.preventDefault(); goToTerms(); }}>Terms</a>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); goToPrivacy(); }}>Privacy</a>
              <a href="/cookies" onClick={(e) => { e.preventDefault(); goToCookies(); }}>Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ContactTopicSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const selected = CONTACT_TOPIC_OPTIONS.filter((o) => value.includes(o.value));
  const summary = selected.length ? selected.map((o) => o.label).join(", ") : "Select one or both";
  const toggle = (next) => {
    onChange(value.includes(next) ? value.filter((v) => v !== next) : [...value, next]);
  };
  return (
    <div className={`contact-ms${open ? " is-open" : ""}`} ref={ref}>
      <button
        type="button"
        className="contact-ms-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected.length ? "" : "is-ph"}>{summary}</span>
      </button>
      {open && (
        <div className="contact-ms-panel" role="listbox" aria-multiselectable="true">
          {CONTACT_TOPIC_OPTIONS.map((o) => (
            <label key={o.value} className={value.includes(o.value) ? "is-on" : ""}>
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactPage = () => {
  const [form, setForm] = useState({ name:"", email:"", company:"", topics: readContactTopics(), msg:"" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await postContact({
          name: form.name,
          email: form.email,
          company: form.company,
          topic: formatContactTopics(form.topics),
          message: form.msg,
        }, e.target);
      setSent(true);
      setForm({ name:"", email:"", company:"", topics: form.topics, msg:"" });
    } catch (err) {
      // Network failures (offline, DNS, CORS) come through here with a generic message;
      // surface that to the user instead of swallowing it.
      const msg = err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.";
      console.error("[contact:submit] failed:", err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      <PageHero
        eyebrow="CONTACT · ONE BUSINESS DAY RESPONSE"
        title={<>HubSpot help, <em>or more pipeline?</em></>}
        lead="Same team either way. Choose the work, send a few lines, we reply in one business day."
        accent="#3B69FF"
      />
      <section className="page-body">
        <div className="wrap contact-grid">
          <div className="contact-form-card">
            <span className="page-section-eye">SEND A BRIEF</span>
            <h2>A few lines is enough.</h2>
            <p className="contact-form-lead">What you need, any timing, any constraints. We reply in one business day.</p>
            <GuardedForm onSubmit={submit} className="contact-form">
              <HoneypotField />
              <label>
                <span>Your name *</span>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Jane Doe"/>
              </label>
              <label>
                <span>Work email *</span>
                <input type="email" required value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="jane@acme.com"/>
              </label>
              <label>
                <span>Company</span>
                <input type="text" value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder="Acme Inc."/>
              </label>
              <div className="contact-form-field">
                <span>Select which service you are interested</span>
                <ContactTopicSelect
                  value={form.topics}
                  onChange={(topics) => setForm({ ...form, topics })}
                />
              </div>
              <label>
                <span>Message *</span>
                <textarea required rows={6} value={form.msg} onChange={e => setForm({...form, msg:e.target.value})} placeholder="What you need: …&#10;When: …&#10;Constraints: …"></textarea>
              </label>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Sending…" : <>Send message <Icon name="arrow" size={14}/></>}
              </button>
              {sent && (
                <div className="contact-form-sent">
                  <b>Got it.</b> A senior operator will reply within one business day at the email you provided.
                </div>
              )}
              {error && <div className="checkout-err">{error}</div>}
            </GuardedForm>
          </div>

          <aside className="contact-side">
            <div className="contact-card">
              <span className="page-section-eye">EMAIL US DIRECTLY</span>
              <a className="contact-email" href={`mailto:${SOCIAL.email}`}>{SOCIAL.email}</a>
              <p>One inbox, same team — HubSpot or lead generation. We reply within one business day, weekends and holidays excluded.</p>
            </div>
            <div className="contact-card">
              <span className="page-section-eye">CONNECT</span>
              <ul className="contact-list">
                <li>
                  <Icon name="linkedin" size={18}/>
                  <div>
                    <b>LinkedIn</b>
                    <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer">linkedin.com/company/vestalhub</a>
                  </div>
                </li>
                <li>
                  <Icon name="shield" size={18}/>
                  <div>
                    <b>HubSpot Solutions Partner</b>
                    <span>Gold tier · Trusted by 100+ companies across SaaS, agencies, services, finance, and nonprofits.</span>
                  </div>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

/* ============ About ============ */
const AboutPage = () => {
  const principles = [
    { i:"shield",  t:"Senior, all the way down",
      d:"Every account is owned end-to-end by an expert with 8+ years in HubSpot and revenue ops. No juniors learning on your portal, ever." },
    { i:"user",    t:"The same expert, every week",
      d:"You get one dedicated, HubSpot-certified senior expert who learns your portal and your business inside out. No rotating account managers, no handoffs, no re-explaining." },
    { i:"rocket",  t:"You ask, it ships",
      d:"Quick fixes land in a couple of business days. Bigger builds get a dated plan before work starts. One priority moves at a time — you always know what's next." },
    { i:"chart",   t:"Everything in writing",
      d:"A recap of everything delivered lands in writing every Friday, and the work ties back to pipeline, ARR, or hours saved. If we can't measure it, we don't ship it." },
  ];
  return (
    <>
      <PageHero
        eyebrow="ABOUT · YOUR DEDICATED HUBSPOT TEAM"
        title={<>We handle HubSpot, <em>so your team can grow.</em></>}
        lead={<>Vestal Hub is a <a href="https://ecosystem.hubspot.com/marketplace/solutions/vestalhub" target="_blank" rel="noopener noreferrer" style={{textDecoration:"underline"}}>HubSpot Solutions Partner (Gold tier)</a>. We've helped 100+ companies across SaaS, agencies, services, finance, real estate, e-commerce, and nonprofits get more out of HubSpot. We become your dedicated HubSpot team — one senior expert, on a flat monthly price — so your team can focus on what they do best.</>}
        accent="#3B69FF"
      />
      <section className="page-body">
        <div className="wrap">
          <div className="about-stats">
            <div className="about-stat"><b>100+</b><span>Companies served</span></div>
            <div className="about-stat"><b>5 / 5</b><span>Average client rating</span></div>
          </div>

          <div className="page-section">
            <span className="page-section-eye">HOW WE GOT HERE</span>
            <h2>Why a <em>dedicated expert</em> beats the alternatives.</h2>
            <p className="page-prose">
              Most teams have three bad options for HubSpot. Hire a full-time admin — expensive, hard to find, and often underused. Hand it to a big agency — juniors, churn, and a status meeting for every small change. Or do it yourself — and watch your sales and marketing leads lose evenings to support articles. None of them give you senior help that actually sticks.
            </p>
            <p className="page-prose">
              So we built the thing we wished existed: <b>a dedicated senior HubSpot expert on a flat monthly price</b>. The same person every week, who knows your portal inside out. You send requests; they ship; you get a written recap every Friday. Month-to-month, no lock-in — pause when things are quiet, come back when they're not.
            </p>
            <p className="page-prose">
              Starting fresh, or need a one-off technical build? New onboarding, a migration, or a custom integration gets scoped as a fixed-price project with the same senior operators — <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>just tell us what you need</a>.
            </p>
          </div>

          <div className="page-section">
            <span className="page-section-eye">WHAT WE BELIEVE</span>
            <h2>Four principles, applied to every engagement.</h2>
            <div className="about-principles">
              {principles.map((p, i) => (
                <article key={i} className="about-principle">
                  <span className="about-principle-ic"><Icon name={p.i} size={20}/></span>
                  <h3>{p.t}</h3>
                  <p>{p.d}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="page-section page-cta-block">
            <h2>See if we're a <em>fit.</em></h2>
            <p>Grab 30 minutes with a senior expert. We'll look at your portal together — no obligation.</p>
            <div className="page-cta-row">
              <a href="/book" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToBook(); }}>Book a call <Icon name="arrow" size={14}/></a>
              <a href="/hubspot-support-plans" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToSupport(); }}>See support plans</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============ Process ============ */
const ProcessPage = () => {
  const steps = [
    { n:"01", t:"Pick services or send a brief",
      d:"Browse the catalog and add fixed-scope SKUs to your cart, or email us a 3-line brief if your work doesn't fit. Bundles and add-ons stack at checkout. Zero pressure to talk to sales." },
    { n:"02", t:"SOW in 24 hours",
      d:"For fixed-scope services from the catalog, you receive a signed-ready Statement of Work with exact deliverables, milestones, dates, and price within 24 hours. No surprises, no change orders. You don't pay until you sign — checkout is really a reservation.",
      note:"Custom briefs may take longer. If your work doesn't fit a published SKU, the SOW can take more than 24 hours depending on complexity, and may require an additional discovery call to scope it properly." },
    { n:"03", t:"Sign and kick off in 7–14 days",
      d:"A senior operator (8+ years HubSpot / RevOps) is assigned within 24h of signing. We schedule a 30-minute kickoff to confirm goals, share access, and lock the timeline." },
    { n:"04", t:"Weekly progress, fixed cadence",
      d:"Every Monday: a written update on what shipped, what's next, what we need from you. Slack channel for the duration. No status meetings unless you want one." },
    { n:"05", t:"QA + handoff",
      d:"Every workflow QA'd before handoff, every dashboard wired to live data, training delivered live and on Loom. You leave the engagement with documentation your team can actually use." },
    { n:"06", t:"30-day post-launch window",
      d:"After handoff, we stay on Slack for a 30-day window for tweaks, edge cases, and questions. No retainer required." },
  ];
  return (
    <>
      <PageHero
        eyebrow="PROCESS · HOW BUYING ACTUALLY WORKS"
        title={<>Six steps from <em>brief to live.</em></>}
        lead="Same process for every engagement — whether you're buying a $700 add-on or a $25k Scale bundle. Predictable in, predictable out."
        accent="#7C5CFC"
      />
      <section className="page-body">
        <div className="wrap">
          <div className="process-steps">
            {steps.map((s) => (
              <article key={s.n} className="process-step">
                <span className="process-step-num">{s.n}</span>
                <div className="process-step-body">
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                  {s.note && (
                    <p className="process-step-note">
                      <Icon name="shield" size={12}/> {s.note}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="page-section page-cta-block">
            <h2>Ready to brief us? <em>Three lines is enough.</em></h2>
            <p>What you need, when you need it, any constraints.</p>
            <div className="page-cta-row">
              <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send a brief <Icon name="arrow" size={14}/></a>
              <a href="/shop" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToShop(); }}>Browse fixed-scope services</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============ Partner program ============ */
const PartnersPage = () => {
  const tracks = [
    { t:"Referral partner",
      who:"Agencies, fractional CMOs, RevOps consultants who don't deliver HubSpot work themselves but talk to teams who need it.",
      payout:"10% of first-year revenue",
      ship:[
        "Email us a warm intro · we close, deliver, and pay quarterly",
        "No co-selling required · we run our own sales cycle",
        "Quarterly partner newsletter with new SKUs and case studies",
      ],
    },
    { t:"Tech / app partner",
      who:"Software companies in the HubSpot ecosystem (data, enrichment, attribution, sales tools) who want a delivery arm for joint customers.",
      payout:"Co-marketing · joint case studies",
      ship:[
        "Implementation playbooks for your app on HubSpot",
        "Co-authored case studies · joint webinars",
        "Reciprocal listing on partner pages",
      ],
    },
  ];
  return (
    <>
      <PageHero
        eyebrow="PARTNER PROGRAM · 2 TRACKS · ZERO MINIMUMS"
        title={<>Partner with senior <em>HubSpot operators.</em></>}
        lead="Two ways to work with us as a peer. Refer warm intros or co-build with us as a tech partner. No minimums, no exclusivity, fixed payouts."
        accent="#0E8A4F"
      />
      <section className="page-body">
        <div className="wrap">
          <div className="partner-tracks">
            {tracks.map((t, i) => (
              <article key={i} className="partner-track">
                <span className="partner-track-num">{`0${i+1}`}</span>
                <h3>{t.t}</h3>
                <div className="partner-payout">
                  <span className="partner-payout-l">Payout</span>
                  <span className="partner-payout-t">{t.payout}</span>
                </div>
                <p className="partner-track-who"><b>Who it's for:</b> {t.who}</p>
                <ul>
                  {t.ship.map((s, j) => (
                    <li key={j}><Icon name="check" size={14}/> {s}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="page-section page-cta-block">
            <h2>Sound interesting? <em>Let's talk.</em></h2>
            <p>One business day reply. We'll send a one-page partner agreement after a quick alignment call.</p>
            <div className="page-cta-row">
              <a href={`mailto:${SOCIAL.email}?subject=Partner%20program&body=Track%20I'm%20interested%20in%3A%20%0AWhat%20I%20do%3A%20%0AHow%20I'd%20like%20to%20work%20together%3A%20%0A`} className="btn btn-primary">
                Email partnerships <Icon name="arrow" size={14}/>
              </a>
              <a href="/contact" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Send a brief</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============ Careers ============ */
const CareersPage = () => {
  const roles = [
    { t:"Senior HubSpot Operator", loc:"Remote · Americas", type:"Full-time",
      d:"Own end-to-end delivery on 4–6 fixed-scope engagements per quarter. Sales, marketing, or service hub focus. 8+ years in HubSpot and revenue operations." },
    { t:"Lifecycle & Lead Scoring Lead", loc:"Remote · Americas", type:"Full-time",
      d:"Build lifecycle stages, scoring models, and attribution dashboards across the catalog. Strong product analytics + HubSpot reporting depth required." },
    { t:"HubSpot CMS Developer", loc:"Remote · global", type:"Full-time / contract",
      d:"Ship landing-page systems and marketing-site refreshes on HubSpot CMS. HubL, modular sections, Core Web Vitals. Bonus for design-system experience." },
    { t:"RevOps Apprentice", loc:"Remote · Americas", type:"Full-time · paid",
      d:"6-month structured apprenticeship pairing with senior operators on real client work. For ops-curious folks transitioning from sales, marketing, or CS." },
  ];
  const benefits = [
    "Async-first, deep-work culture · no daily standups",
    "Senior comp, profit share, and a real cohort to learn from",
    "Health, dental, vision (US) · stipend equivalent globally",
    "$2,000 / yr learning budget · annual team offsite",
    "4-day workweeks every other Friday",
    "No timesheets, no utilization targets · we measure shipped outcomes",
  ];
  return (
    <>
      <PageHero
        eyebrow="CAREERS · SENIOR OPERATORS ONLY"
        title={<>Join a team that ships <em>real outcomes.</em></>}
        lead="We hire senior operators who've already shipped this work somewhere else. No juniors, no rotating account managers, no utilization targets. Get paid to do your best work for clients who can tell the difference."
        accent="#FF7A29"
      />
      <section className="page-body">
        <div className="wrap">
          <div className="page-section">
            <span className="page-section-eye">OPEN ROLES</span>
            <h2>Currently <em>hiring.</em></h2>
            <div className="careers-roles">
              {roles.map((r, i) => (
                <article key={i} className="careers-role">
                  <div className="careers-role-head">
                    <h3>{r.t}</h3>
                    <span className="careers-role-meta">{r.loc} · {r.type}</span>
                  </div>
                  <p>{r.d}</p>
                  <a href={`mailto:${SOCIAL.email}?subject=Application%3A%20${encodeURIComponent(r.t)}&body=Hi%20Vestal%20Hub%2C%0A%0AI'd%20like%20to%20apply%20for%20${encodeURIComponent(r.t)}.%0A%0AAttached%3A%20resume%20%2F%20LinkedIn%0AThree%20projects%20I'm%20proud%20of%3A%20%0A`} className="careers-role-cta">
                    Apply by email <Icon name="arrow" size={12}/>
                  </a>
                </article>
              ))}
            </div>
          </div>

          <div className="page-section">
            <span className="page-section-eye">HOW WE WORK</span>
            <h2>What you get for <em>showing up.</em></h2>
            <ul className="careers-benefits">
              {benefits.map((b, i) => (
                <li key={i}><Icon name="check" size={14}/> {b}</li>
              ))}
            </ul>
          </div>

          <div className="page-section page-cta-block">
            <h2>Don't see your role? <em>We're still listening.</em></h2>
            <p>If you've shipped HubSpot or RevOps work for the last decade and want to do it without timesheets, send a note.</p>
            <div className="page-cta-row">
              <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>
                Contact us <Icon name="arrow" size={14}/>
              </a>
              <a href="/about" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToAbout(); }}>About us</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* ============ White-label technical HubSpot services ============
 *
 * Footer-only landing page. Sells our technical delivery (apps, custom code
 * workflow actions, CMS, UI extensions, integrations) to other HubSpot
 * partners and agencies who want to ship under their own brand. Not linked
 * in the header — discovery is via the footer + direct links / search.
 */
const WLABEL_BUILDS = [
  { i: "puzzle", t: "HubSpot apps", d: "Public & private apps — OAuth, webhooks, CRM cards, timeline events, and marketplace-ready packaging." },
  { i: "code",   t: "Custom code workflow actions", d: "Serverless JavaScript & Python actions inside HubSpot workflows for logic the native steps can't handle." },
  { i: "layers", t: "CMS themes & modules", d: "Production-grade HubSpot CMS themes, custom modules, and HubL templates built for Core Web Vitals." },
  { i: "search", t: "UI extensions & CRM cards", d: "React UI extensions that surface your data and actions right inside the record sidebar." },
  { i: "ship",   t: "Integrations & data sync", d: "Two-way syncs with Stripe, Salesforce, Slack, data warehouses, and any REST or GraphQL API." },
  { i: "rocket", t: "Serverless & automation", d: "Backend functions, scheduled jobs, and event pipelines that extend HubSpot well beyond the UI." },
];

const WLABEL_PROOF = [
  { t: "AdminCRM",      meta: "HubSpot Marketplace app", d: "Admin tooling for HubSpot teams — built, listed, and maintained as a public app.", url: "https://www.admincrm.io/" },
  { t: "CRM Email Sync", meta: "HubSpot Marketplace app", d: "Reliable two-way email synchronization shipped as a public HubSpot app.", url: "https://www.crmemailsync.net/" },
  { t: "ShipQuote",     meta: "HubSpot Marketplace app", d: "Shipping & quoting workflows packaged as a production HubSpot app.", url: "https://app.shipquote.io/" },
  { t: "Scale",         meta: "HubSpot CMS theme", d: "A full HubSpot CMS theme — design system, modules, and templates.", url: "https://scale.vestalhub.com/" },
];

const WhiteLabelPage = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", msg: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const meetingsRef = useRef(null);

  /* Mount the HubSpot Meetings embed once. Mirrors BookingPage — the loader
   * script needs a sibling div with data-src to find. */
  useEffect(() => {
    if (!meetingsRef.current) return;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    meetingsRef.current.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    meetingsRef.current.appendChild(script);
    return () => { if (meetingsRef.current) meetingsRef.current.innerHTML = ""; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const message = [
        "White-label inquiry from /white-label",
        "",
        "Project details:",
        form.msg.trim() || "(not specified)",
      ].join("\n");
      const posted = await postContact({
          name: form.name,
          email: form.email,
          company: form.company,
          topic: "partner",
          message,
        }, e.target);
      if (!posted.spam) {
        try { window.gtag && window.gtag("event", "generate_lead", { method: "white_label_form" }); } catch {}
        try { window.posthog && window.posthog.capture("white_label_lead_submitted", { company: form.company || null }); } catch {}
      }
      setSent(true);
      setForm({ name: "", email: "", company: "", msg: "" });
    } catch (err) {
      const msg = err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.";
      console.error("[white-label:submit] failed:", err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <>
    <PageHero
      eyebrow="WHITE-LABEL · TECHNICAL HUBSPOT DELIVERY"
      title={<>Your white-label <em>HubSpot dev team.</em></>}
      lead="We build the hard technical work — HubSpot apps, custom code workflow actions, CMS, and integrations — under your brand. You keep the client relationship; we ship behind the scenes for elite HubSpot partners."
      accent="#3B69FF"
    />
    <section className="page-body">
      <div className="wrap">
        <div className="page-section">
          <span className="page-section-eye">WHAT WE BUILD</span>
          <h2>Technical HubSpot work, <em>shipped under your name.</em></h2>
          <div className="partner-tracks">
            {WLABEL_BUILDS.map((b, i) => (
              <article key={b.t} className="partner-track">
                <span className="partner-track-num">{`0${i + 1}`}</span>
                <h3><Icon name={b.i} size={20}/> {b.t}</h3>
                <p className="partner-track-who">{b.d}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="page-section">
          <span className="page-section-eye">PROOF WE SHIP</span>
          <h2>3 apps on the marketplace. <em>1 CMS theme. All live.</em></h2>
          <p style={{ color: "var(--muted)", margin: "0 0 22px", fontSize: "var(--fs-lg)", maxWidth: "640px" }}>
            We don't just consult — we publish and maintain real HubSpot software. Three apps are live on the HubSpot Marketplace and one CMS theme is in production today.
          </p>
          <div className="wl-proof">
            {WLABEL_PROOF.map((p) => (
              <article key={p.t} className="wl-proof-card">
                <span className="wl-proof-meta">{p.meta}</span>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="careers-role-cta wl-proof-cta">
                  View it live <Icon name="arrow" size={12}/>
                </a>
              </article>
            ))}
          </div>
        </div>

        <div className="page-section">
          <span className="page-section-eye">WHY PARTNERS PICK US</span>
          <h2>Built to make <em>you</em> look good.</h2>
          <ul className="careers-benefits">
            <li><Icon name="check" size={14}/> We've delivered behind elite HubSpot Solutions & App Partners</li>
            <li><Icon name="check" size={14}/> Senior engineers only — no juniors learning on your client's portal</li>
            <li><Icon name="check" size={14}/> Fully white-label: your brand on every deliverable and touchpoint</li>
            <li><Icon name="check" size={14}/> Fixed-scope SOWs priced like software — no surprise change orders</li>
            <li><Icon name="check" size={14}/> Marketplace-ready: we know app review, OAuth scopes, and packaging</li>
            <li><Icon name="check" size={14}/> One operator, one channel — direct line, no account-manager telephone</li>
          </ul>
        </div>

        <div className="page-section" id="wl-contact">
          <span className="page-section-eye">START A PROJECT</span>
          <h2>Have a HubSpot build to hand off? <em>Tell us about it.</em></h2>
          <p style={{ color: "var(--muted)", margin: "0 0 22px", fontSize: "var(--fs-lg)", maxWidth: "640px" }}>
            Send a brief and get a fixed-price SOW within one business day. Everything ships under your brand.
          </p>
          <div className="contact-form-card">
            <GuardedForm onSubmit={submit} className="contact-form">
              <HoneypotField />
              <label>
                <span>Your name *</span>
                <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe"/>
              </label>
              <label>
                <span>Work email *</span>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@agency.com"/>
              </label>
              <label>
                <span>Agency / company</span>
                <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Partners"/>
              </label>
              <label>
                <span>What do you need built? *</span>
                <textarea required rows={6} value={form.msg} onChange={e => setForm({ ...form, msg: e.target.value })} placeholder="What you need: HubSpot app / custom code action / CMS / integration…&#10;Timeline: …&#10;Constraints: …"></textarea>
              </label>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Sending…" : <>Send brief <Icon name="arrow" size={14}/></>}
              </button>
              <p className="contact-form-lead" style={{ margin: "4px 0 0", fontSize: "var(--fs-sm)" }}>
                <Icon name="shield" size={12}/> Your details go straight to a senior engineer. We never share or sell them.
              </p>
              {sent && (
                <div className="contact-form-sent">
                  <b>Got it.</b> A senior engineer will reply within one business day. Prefer to talk live? Grab a slot below.
                </div>
              )}
              {error && <div className="checkout-err">{error}</div>}
            </GuardedForm>
          </div>
        </div>

        <div className="page-section" id="wl-book">
          <span className="page-section-eye">OR BOOK A CALL</span>
          <h2>Walk us through it <em>live.</em></h2>
          <p style={{ color: "var(--muted)", margin: "0 0 22px", fontSize: "var(--fs-lg)", maxWidth: "640px" }}>
            30 minutes with a senior engineer to scope the build. No SDRs, no sales pitch — pick a time that works.
          </p>
          <div className="book-embed" ref={meetingsRef}/>
        </div>
      </div>
    </section>
  </>
  );
};

/* ============ Legal: Terms / Privacy / Cookies ============ */
const LegalPage = ({ kind }) => {
  const today = new Date();
  const updated = today.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const docs = {
    terms: {
      eyebrow: "LEGAL · TERMS OF SERVICE",
      title: <>Terms of <em>service.</em></>,
      lead: "These terms govern any engagement you purchase from Vestal Hub LLC, a Florida company based in Miami. By signing a Statement of Work or paying an invoice, you agree to them.",
      sections: [
        { h: "1. About these terms", body: [
          "Vestal Hub LLC is a Florida limited liability company based in Miami, Florida (\"Vestal Hub\", \"we\", \"us\"). These terms govern any engagement, subscription, or purchase you make from us via this website, a Statement of Work (\"SOW\"), or an invoice.",
          "By signing an SOW, paying an invoice, or accepting a service we deliver, you (\"Client\", \"you\") agree to these terms. If your organization has a master services agreement we've countersigned, that agreement controls; otherwise, these terms apply.",
        ]},
        { h: "2. Engagements & scope changes", body: [
          "Every Vestal Hub engagement is governed by a written SOW. The SOW defines deliverables, timeline, price, acceptance criteria, and any client responsibilities. The SOW supersedes any conflicting language in marketing materials, email threads, or this catalog.",
          "Fixed-scope SKUs ship the deliverables published on their product page, subject to the upper limits stated there. Work outside the SOW (new requirements, expanded limits, additional surface area) is added via a written change order — a short addendum signed by both parties — or a new SOW. We don't accept uncontrolled change orders mid-engagement.",
          "Where a deliverable depends on third-party tools (HubSpot plans, integrations, ad platforms, LLM providers), the SOW assumes you maintain active subscriptions to those tools for the duration of the engagement.",
        ]},
        { h: "3. HubSpot support plans (ongoing)", body: [
          "Our ongoing HubSpot support plans (Essentials, Growth, and Premium) are month-to-month subscriptions billed monthly in advance. Each plan includes a stated maximum number of senior HubSpot consultant hours per calendar month, as described on the plan's page at the time you subscribe.",
          "Our commitment under a support plan is to make a senior HubSpot consultant available to you for up to that maximum number of hours each calendar month. We are not obligated to use, consume, or deliver the full hour allocation — if the requests you submit in a given month require fewer hours than the plan maximum, we deliver what you've requested and our commitment for that month is fully met.",
          "Unused hours do not roll over. Any hours not used within a calendar month expire at the end of that month and are not refundable, transferable, or convertible to account credit, a discount, or a future month. Each month starts fresh at the plan's stated maximum.",
          "Where a plan is described as offering \"unlimited requests\", that means you may submit any number of requests; we work through them one priority at a time, within the plan's monthly hour maximum. Requests that would exceed the monthly maximum are scheduled into a following month or, for larger builds, scoped separately as a fixed-price project under Section 2.",
          "Support plans are month-to-month with no minimum term. You may pause or cancel at any time under Section 10; the then-current month is billed in full and is not prorated or refunded.",
        ]},
        { h: "4. Payment", body: [
          "Payment is due on signature of the SOW unless otherwise stated, in USD. We accept ACH, wire transfer, and major credit cards via Stripe. Engagements over $10,000 may be split 50/50 between kickoff and acceptance.",
          "Ongoing support plans are billed monthly in advance and are month-to-month with no minimum term — see Section 3. Hours packs are billed in full at purchase and valid for 90 days from the purchase date. Monthly retainers are billed on the 1st of each calendar month, prorated for the first month, and require 30 days' written notice to cancel — see Section 10.",
          "Invoices not paid within 30 days of issue accrue interest at 1.5% per month, or the maximum rate permitted by Florida law, whichever is lower. We may pause work on past-due engagements after 15 days' written notice.",
        ]},
        { h: "5. Client responsibilities", body: [
          "Your timely responses keep our timelines honest. We expect: HubSpot Super Admin access (or a scoped equivalent) within 3 business days of SOW signature; decisions on key trade-offs within 3 business days of being asked; content, brand assets, and approvals as the SOW requires.",
          "If we're blocked on a client decision, access, or asset for more than 10 business days, the timeline pauses without affecting price. Engagements paused longer than 30 days may be re-scoped at our discretion to reflect any material changes since signature.",
          "You are responsible for the accuracy of data you provide and for any third-party tool fees (HubSpot, Zapier, OpenAI, ad platforms, etc.) used during the engagement.",
        ]},
        { h: "6. Acceptance", body: [
          "Each deliverable has a 5-business-day acceptance window from delivery. If you don't request changes within the window, the deliverable is deemed accepted. If you do, we'll address them and re-deliver within 5 business days. Repeated cycles of substantively new requirements are treated as a scope change under Section 2.",
          "Post-launch support windows (typically 30 days, where included) cover bug fixes, configuration tweaks, and minor tuning — not new scope.",
        ]},
        { h: "7. Intellectual property", body: [
          "On full payment of an SOW, you own all custom deliverables created specifically for you under that SOW: HubSpot configurations, custom code, copy, design files, and documentation. We assign to you all rights necessary to use, modify, and operate those deliverables.",
          "Vestal Hub retains ownership of our pre-existing IP — frameworks, templates, internal tools, generic code patterns, and methodologies developed independent of your engagement. We grant you a perpetual, royalty-free, worldwide license to use those elements as incorporated into your deliverables.",
        ]},
        { h: "8. Confidentiality", body: [
          "We treat all client data, configurations, business information, and access credentials as confidential. We sign mutual NDAs on request at no cost. Confidential information is used only to perform the engagement and protected for 3 years after engagement end (perpetually for trade secrets).",
          "We may reference engagements in case studies, on this website, and in proposals only with your prior written consent. You may withdraw that consent at any time, and we'll remove the reference within 30 days.",
        ]},
        { h: "9. Warranty & liability", body: [
          "Services are provided \"as-is\" with the warranty that they will perform substantially as described in the SOW. We disclaim all other warranties, express or implied, including merchantability and fitness for a particular purpose.",
          "Our total aggregate liability for any engagement is capped at the fees you paid for that engagement in the 12 months preceding the claim. Neither party is liable for indirect, incidental, consequential, special, or revenue-related damages — even if foreseeable. These limits do not apply to fraud, willful misconduct, or indemnification obligations.",
        ]},
        { h: "10. Cancellation & termination", body: [
          "You may cancel an engagement at any time by sending written notice to " + SOCIAL.email + ". As a general rule, fees for work already performed are non-refundable. What you owe at cancellation depends on the engagement type:",
          "Fixed-scope SKUs paid up-front: Fees are non-refundable. On cancellation, we stop further work immediately and hand off every completed deliverable plus any works-in-progress (drafts, partial configurations, written documentation) in their current state. You don't owe anything beyond the SOW price already paid.",
          "Fixed-scope SKUs with split payments (e.g. 50/50): Amounts already invoiced are non-refundable. You also owe the proportion of the total SOW fee corresponding to work delivered or in active production at the cancellation date, less amounts already paid. Unbilled future milestones beyond that point are not owed.",
          "Hours packs: Non-refundable. Unused hours may be transferred to another SOW signed within 90 days of the original purchase date.",
          "Support plans (ongoing): Month-to-month with no minimum term — you may pause or cancel at any time, effective at the end of the current billing month. The then-current month is billed in full and is not prorated or refunded, and unused hours for that month do not roll over. There is no cancellation penalty.",
          "Monthly retainers: You owe the current month's fee. Cancellation takes effect at the end of the calendar month following 30 days' written notice. Hours unused in the current month do not roll over after termination, and prepaid months are not refunded.",
          "We don't charge cancellation penalties beyond the rules above. Either party may terminate immediately for the other's uncured material breach (after 15 days' written notice to cure), insolvency, or violation of confidentiality.",
        ]},
        { h: "11. Non-solicitation", body: [
          "During the engagement and for 12 months after it ends, neither party will directly solicit for employment the other's employees or contractors who worked on the engagement, without the other party's prior written consent. General job postings and unsolicited applications to them are not considered solicitation.",
        ]},
        { h: "12. Independent contractor", body: [
          "Vestal Hub is an independent contractor. Nothing in these terms or any SOW creates an employment, partnership, joint venture, or agency relationship between the parties. Neither party may bind the other.",
        ]},
        { h: "13. Force majeure", body: [
          "Neither party is liable for delays or failures caused by events outside its reasonable control, including natural disasters, pandemics, government action, internet or hosting-provider outages, and acts of third parties. The affected party will notify the other promptly and resume performance as soon as practicable.",
        ]},
        { h: "14. Governing law & disputes", body: [
          "These terms are governed by the laws of the State of Florida, USA, without regard to conflict-of-laws principles. The parties first agree to attempt good-faith resolution of any dispute by direct discussion for 30 days.",
          "If that fails, disputes will be resolved by binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules, seated in Miami-Dade County, Florida. Either party may seek injunctive or equitable relief in a court of competent jurisdiction in Miami-Dade County, Florida.",
        ]},
        { h: "15. Miscellaneous", body: [
          "Notices: Written notices may be sent by email to " + SOCIAL.email + " (for Vestal Hub) and to the email address on the SOW (for Client). Notice is effective the next business day.",
          "Entire agreement: An executed SOW plus these terms is the entire agreement between the parties on its subject matter, superseding prior discussions. Amendments require a signed writing from both parties.",
          "Severability & waiver: If any provision is held unenforceable, the remainder stay in effect. Waivers must be in writing and apply only to the specific instance.",
          "Assignment: Neither party may assign these terms without the other's written consent, except to a successor in a merger, acquisition, or sale of substantially all assets.",
        ]},
      ],
    },
    privacy: {
      eyebrow: "LEGAL · PRIVACY POLICY",
      title: <>Privacy <em>policy.</em></>,
      lead: "We collect the minimum we need to deliver engagements, reply to inquiries, and operate this website. We don't sell your data.",
      sections: [
        { h: "What we collect", body: [
          "From this website: pages visited, basic device/browser info, and form submissions (name, email, company, message). We use first-party analytics with IP truncation by default.",
          "From engagements and support plans: the access credentials, account configurations, and data you grant us in HubSpot or related tools, only to the extent needed to deliver your support plan or SOW.",
          "From your inbox: email correspondence with us, retained per our records-retention schedule.",
        ]},
        { h: "What we use it for", body: [
          "Replying to your inquiries (one business day SLA).",
          "Delivering the support plan or engagement you signed up for.",
          "Sending operational updates (status emails, Friday recaps, invoices, SOW change notices).",
          "Improving the website and our services.",
        ]},
        { h: "What we don't do", body: [
          "We don't sell or rent your personal information to third parties.",
          "We don't run ad-tech retargeting on this site.",
          "We don't enrich your contact data with third-party brokers.",
        ]},
        { h: "Sub-processors", body: [
          "We use HubSpot (CRM and forms), Google Workspace (email, docs), Stripe (payment processing), Vercel (website hosting), Google Analytics 4 (aggregate traffic), and PostHog (product analytics, session replay, error tracking — IPs are anonymized). The full list is available on request.",
        ]},
        { h: "Your rights", body: [
          "You may request access to, correction of, or deletion of your personal information at any time by emailing " + SOCIAL.email + ". We respond within 30 days.",
          "If you're in the EU, UK, California, or any jurisdiction with similar rights, those rights apply to you regardless of your residency.",
        ]},
        { h: "Retention", body: [
          "We retain client engagement data for 24 months after your engagement or support plan ends, then delete or anonymize it unless legal obligations require longer retention. Inquiry emails are retained for 12 months.",
        ]},
        { h: "Contact", body: [
          "Questions? Email " + SOCIAL.email + " with the subject line \"Privacy\". We'll route it to the right person.",
        ]},
      ],
    },
    cookies: {
      eyebrow: "LEGAL · COOKIES NOTICE",
      title: <>Cookies <em>notice.</em></>,
      lead: "We use a small number of first-party cookies to keep this website working and to understand how it's used in aggregate, plus the LinkedIn Insight Tag to measure our LinkedIn ads.",
      sections: [
        { h: "Strictly necessary", body: [
          "Used to keep this website functional. Examples: storing your cart contents between page loads, remembering you've dismissed a banner, securing form submissions. These can't be disabled without breaking the site.",
        ]},
        { h: "Analytics", body: [
          "We use Google Analytics 4 and PostHog to understand which services and pages teams find useful, and to fix bugs faster via session replay. Both run with IPs anonymized; we don't track you across the rest of the web. You can opt out by sending Do Not Track in your browser — we honor it.",
        ]},
        { h: "Advertising", body: [
          "We run the LinkedIn Insight Tag so we can measure conversions from, and retarget visitors of, our LinkedIn ad campaigns. It sets LinkedIn cookies and reports aggregate conversion and audience data back to LinkedIn. You can manage these via LinkedIn's ad settings, your browser, or by sending Do Not Track — we honor it.",
        ]},
        { h: "What we don't use", body: [
          "We don't use Facebook Pixel, Google Ads remarketing, or any third-party advertising trackers beyond the LinkedIn Insight Tag described above.",
        ]},
        { h: "Managing cookies", body: [
          "You can clear or block cookies from your browser settings at any time. Doing so may break parts of this site (notably the cart). On a fresh visit, blocked cookies will be re-attempted on each page load.",
        ]},
        { h: "Updates", body: [
          "We update this notice when we add or remove tools. The \"Last updated\" date below always reflects the current version. Material changes are summarized at the top of the notice for 30 days.",
        ]},
      ],
    },
  };

  const doc = docs[kind];
  return (
    <>
      <PageHero eyebrow={doc.eyebrow} title={doc.title} lead={doc.lead} accent="#3B69FF" />
      <section className="page-body">
        <div className="wrap legal-grid">
          <aside className="legal-toc">
            <span className="page-section-eye">ON THIS PAGE</span>
            <ul>
              {doc.sections.map((s, i) => (
                <li key={i}><a href={`#sec-${i}`}>{s.h}</a></li>
              ))}
            </ul>
            <div className="legal-meta">
              <span>Last updated</span>
              <b>{updated}</b>
            </div>
            <div className="legal-meta">
              <span>Questions?</span>
              <a href={`mailto:${SOCIAL.email}?subject=Legal%20question`}>{SOCIAL.email}</a>
            </div>
          </aside>
          <article className="legal-body">
            {doc.sections.map((s, i) => (
              <section key={i} id={`sec-${i}`} className="legal-section">
                <h2>{s.h}</h2>
                {s.body.map((p, j) => <p key={j}>{p}</p>)}
              </section>
            ))}
            <p className="legal-foot">
              By using this website or signing a Statement of Work with Vestal Hub, you acknowledge you've read and accepted this notice.
              For anything else, email <a href={`mailto:${SOCIAL.email}`}>{SOCIAL.email}</a>.
            </p>
          </article>
        </div>
      </section>
    </>
  );
};

/* ============ Footer ============ */
/* ===================================================================
 * SEO content pages — pillar, region, industry, migration, integration,
 * glossary, case study. All reached via the /learn directory linked from
 * the footer; never linked from header/homepage so transactional surface
 * stays distraction-free.
 *
 * Strategy: one shared <ContentPage> renderer drives every content type.
 * Per-type wrappers just resolve the data object and pass a `kind` for
 * the right breadcrumb + related-section labels.
 * =================================================================== */

const PRODUCT_INDEX = (() => {
  const out = {};
  (window.PRODUCTS || []).forEach(p => { out[p.id] = p; });
  return out;
})();
const findById = (collection, id) => (window[collection] || []).find(x => x && x.id === id);

const ContentBreadcrumb = ({ items }) => (
  <nav aria-label="Breadcrumb" className="vh-crumbs" style={{
    fontSize: "13px", color: "var(--muted)", marginBottom: 18, display: "flex", flexWrap: "wrap", gap: 6,
  }}>
    {items.map((it, i) => (
      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {i > 0 && <span aria-hidden="true" style={{ opacity: 0.6 }}>›</span>}
        {it.href && i < items.length - 1 ? (
          <a href={it.href}
             onClick={(e) => { if (it.onClick) { e.preventDefault(); it.onClick(); } }}
             style={{ color: "var(--ink-2)", borderBottom: "1px solid transparent" }}
             onMouseOver={(e) => { e.currentTarget.style.color = "var(--brand-ink)"; }}
             onMouseOut={(e)  => { e.currentTarget.style.color = "var(--ink-2)"; }}
          >{it.label}</a>
        ) : (
          <span style={{ color: "var(--ink)" }}>{it.label}</span>
        )}
      </span>
    ))}
  </nav>
);

const ContentHero = ({ eyebrow, h1, sub, intro }) => (
  <header style={{ paddingTop: 8, paddingBottom: 36, borderBottom: "1px solid var(--line)", marginBottom: 36 }}>
    {eyebrow && (
      <span style={{
        display: "inline-block",
        padding: "5px 11px",
        background: "var(--brand-soft)",
        color: "var(--brand-ink)",
        borderRadius: "var(--r-pill)",
        fontSize: "var(--fs-sm)",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        marginBottom: 16,
      }}>{eyebrow}</span>
    )}
    <h1 style={{
      fontFamily: "var(--f-display)",
      fontSize: "clamp(34px, 5vw, 52px)",
      fontWeight: 800,
      letterSpacing: "-0.025em",
      lineHeight: 1.1,
      margin: "0 0 18px",
      color: "var(--ink)",
      maxWidth: 880,
    }}>{h1}</h1>
    {sub && (
      <p style={{
        fontSize: "clamp(17px, 1.5vw, 20px)",
        lineHeight: 1.55,
        color: "var(--ink-2)",
        maxWidth: 820,
        margin: "0 0 18px",
      }}>{sub}</p>
    )}
    {intro && (
      <p style={{
        fontSize: "var(--fs-xl)",
        lineHeight: 1.7,
        color: "var(--muted)",
        maxWidth: 820,
        margin: 0,
      }}>{intro}</p>
    )}
  </header>
);

const contentCtaGo = (href, onClick) => {
  if (onClick) { onClick(); return; }
  if (href === "/contact") goToContact();
  else if (href === "/book") goToBook();
  else if (href === "/case-studies") goToCaseStudies();
  else if (href) navigate(href);
};

const ContentCtaInline = ({
  tone = "primary",
  headline = "Want this for your company?",
  sub,
  primaryLabel = "Contact us",
  primaryHref = "/contact",
  onPrimary,
  secondaryLabel = "Book a 30-min consult",
  secondaryHref = "/book",
  onSecondary,
  showSecondary = true,
}) => {
  const isPrimary = tone === "primary";
  const stacked = !!sub;
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      flexDirection: stacked ? "column" : "row",
      gap: stacked ? 18 : 12,
      padding: isPrimary ? (stacked ? "32px 32px" : "26px 28px") : 0,
      borderRadius: isPrimary ? "var(--r-xl)" : 0,
      background: isPrimary ? "linear-gradient(135deg, rgba(59,105,255,0.13) 0%, rgba(255,122,41,0.10) 100%)" : "transparent",
      border: isPrimary ? "1px solid var(--line)" : "none",
      alignItems: stacked ? "flex-start" : "center",
      justifyContent: isPrimary && !stacked ? "space-between" : "flex-start",
      margin: isPrimary ? "32px 0" : "16px 0",
    }}>
      {isPrimary && (
        <div style={{ minWidth: 240, flex: stacked ? "none" : "1 1 320px", maxWidth: stacked ? 720 : undefined }}>
          <div style={{ fontFamily: "var(--f-display)", fontSize: stacked ? "clamp(22px, 2.4vw, 30px)" : 22, fontWeight: 700, lineHeight: 1.25, color: "var(--ink)" }}>
            {headline}
          </div>
          {sub && (
            <p style={{ margin: "10px 0 0", fontSize: "var(--fs-xl)", lineHeight: 1.6, color: "var(--ink-2)" }}>
              {sub}
            </p>
          )}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <button className="btn btn-primary" onClick={() => contentCtaGo(primaryHref, onPrimary)}>
          {primaryLabel} <Icon name="arrow" size={14}/>
        </button>
        {showSecondary && (
          <button className="btn btn-ghost" onClick={() => contentCtaGo(secondaryHref, onSecondary)}>
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const contentParas = (body) => {
  if (body == null || body === "") return [];
  return Array.isArray(body) ? body.filter((p) => p != null && p !== "") : [body];
};

const ContentParas = ({ body, className }) => {
  const paras = contentParas(body);
  if (!paras.length) return null;
  return paras.map((p, i) => (
    typeof p === "string" ? (
      <p key={i} className={className} style={{
        fontSize: "var(--fs-xl)",
        lineHeight: 1.7,
        color: "var(--ink-2)",
        margin: i === 0 ? 0 : "14px 0 0",
      }}>{p}</p>
    ) : (
      <div key={i} style={{ fontSize: "var(--fs-xl)", lineHeight: 1.7, color: "var(--ink-2)", margin: i === 0 ? 0 : "14px 0 0" }}>{p}</div>
    )
  ));
};

const ContentQuote = ({ q, featured = false }) => {
  if (!q || !q.text) return null;
  return (
    <blockquote className={featured ? "cs-quote cs-quote-feature" : "cs-quote"}>
      {q.headline && <p className="cs-quote-headline">{q.headline}</p>}
      <p
        className="cs-quote-text"
        dangerouslySetInnerHTML={{ __html: `"${q.text}"` }}
      />
      <footer className="cs-quote-foot">
        {q.avatar && (
          <img src={q.avatar} alt="" width="48" height="48" loading="lazy" className="cs-quote-avatar"/>
        )}
        <div className="cs-quote-by">
          <div className="cs-quote-name">{q.name || q.by}</div>
          {(q.role || q.company) && (
            <div className="cs-quote-meta">{[q.role, q.company].filter(Boolean).join(" · ")}</div>
          )}
        </div>
        {q.logo && (
          <img src={q.logo} alt="" loading="lazy" className="cs-quote-logo"/>
        )}
      </footer>
    </blockquote>
  );
};

const ContentFlow = ({ steps }) => {
  if (!steps || !steps.length) return null;
  return (
    <div className="cs-flow" aria-label={steps.join(" to ")}>
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          {i > 0 && <span className="cs-flow-arrow" aria-hidden="true">→</span>}
          <span className="cs-flow-step">{step}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

const ContentDiagram = ({ d }) => {
  if (!d) return null;
  const groups = d.groups && d.groups.length
    ? d.groups
    : (d.crms && d.crms.length ? [{ label: "Client CRMs", items: d.crms }] : []);
  return (
    <div
      className="cs-diagram"
      role="img"
      aria-label="SSR buyer intent and leads flow into HubSpot, which connects bidirectionally to client CRMs and marketing automation tools and surfaces pipeline and revenue."
    >
      <div className="cs-diagram-node">
        <strong>{d.top.title}</strong>
        {d.top.sub && <span>{d.top.sub}</span>}
      </div>
      <div className="cs-diagram-arrow" aria-hidden="true">↓</div>
      <div className="cs-diagram-node cs-diagram-hub">
        <strong>{d.hub.title}</strong>
        {d.hub.sub && <span>{d.hub.sub}</span>}
      </div>
      <div className="cs-diagram-arrow" aria-hidden="true">↕</div>
      <div className="cs-diagram-crms">
        {groups.map((g) => (
          <div key={g.label} className="cs-diagram-group">
            <b>{g.label}</b>
            <div className="cs-diagram-pills">
              {(g.items || []).map((c) => <span key={`${g.label}-${c}`} className="cs-diagram-pill">{c}</span>)}
            </div>
          </div>
        ))}
      </div>
      <div className="cs-diagram-arrow" aria-hidden="true">↓</div>
      <div className="cs-diagram-node cs-diagram-out">
        <strong>{d.out.title}</strong>
      </div>
    </div>
  );
};

const ContentResultCards = ({ cards }) => {
  if (!cards || !cards.length) return null;
  return (
    <div className="cs-result-grid">
      {cards.map((c) => (
        <article key={c.t} className="cs-result-card">
          <h3>{c.t}</h3>
          <p>{c.d}</p>
        </article>
      ))}
    </div>
  );
};

const ContentPills = ({ pills }) => {
  if (!pills || !pills.length) return null;
  return (
    <div className="cs-pills">
      {pills.map((p) => <span key={p} className="cs-pill">{p}</span>)}
    </div>
  );
};

const ContentSection = ({ h, eyebrow, body, highlight, children }) => {
  const rich = !!(eyebrow || highlight || children);
  return (
    <section style={{ marginBottom: rich ? 48 : 28 }}>
      {eyebrow && <span className="cs-sec-eye">{eyebrow}</span>}
      {h && (
        <h2 style={{
          fontFamily: "var(--f-display)",
          fontSize: "clamp(22px, 2.2vw, 28px)",
          fontWeight: 700,
          letterSpacing: "-0.015em",
          margin: eyebrow ? "0 0 12px" : "0 0 12px",
          color: "var(--ink)",
        }}>{h}</h2>
      )}
      <ContentParas body={body}/>
      {children}
      {highlight && <p className="cs-highlight">{highlight}</p>}
    </section>
  );
};

const ContentFigure = ({ img, fallbackAlt, onExpand, desktopShrink }) => {
  const expandable = !!(img.lightbox && onExpand);
  return (
    <figure className={desktopShrink ? "cs-figure-desk-sm" : undefined} style={{ margin: "18px auto 0" }}>
      {expandable ? (
        <button
          type="button"
          onClick={() => onExpand(img.src)}
          title="Click to expand"
          style={{
            display: "block",
            padding: 0,
            border: "1px solid var(--line)",
            borderRadius: "var(--r-l)",
            overflow: "hidden",
            cursor: "zoom-in",
            background: "var(--bg-2)",
            width: "100%",
            position: "relative",
          }}
        >
          <img
            src={img.src}
            alt={img.alt || fallbackAlt || ""}
            loading="lazy"
            style={{ display: "block", width: "100%", height: "auto" }}
          />
          <span style={{
            position: "absolute",
            right: 10,
            bottom: 10,
            background: "rgba(15,18,30,0.78)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
            padding: "3px 8px",
            borderRadius: "var(--r-pill)",
          }}>⤢ Expand</span>
        </button>
      ) : (
        <img
          src={img.src}
          alt={img.alt || fallbackAlt || ""}
          loading="lazy"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            borderRadius: "var(--r-l)",
            border: "1px solid var(--line)",
          }}
        />
      )}
      {img.caption && (
        <figcaption style={{
          marginTop: 10,
          fontSize: "var(--fs-sm)",
          color: "var(--muted)",
          lineHeight: 1.5,
          textAlign: "center",
        }}>{img.caption}</figcaption>
      )}
    </figure>
  );
};

const ContentFaq = ({ items }) => {
  const [open, setOpen] = useState(0);
  if (!items || !items.length) return null;
  return (
    <section style={{ marginTop: 48, marginBottom: 48 }}>
      <h2 style={{
        fontFamily: "var(--f-display)",
        fontSize: "clamp(24px, 2.4vw, 30px)",
        fontWeight: 700,
        margin: "0 0 22px",
        color: "var(--ink)",
      }}>Frequently asked questions</h2>
      <div>
        {items.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} style={{ borderTop: "1px solid var(--line)", padding: "18px 0" }}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "var(--f)",
                  fontSize: "var(--fs-2xl)",
                  fontWeight: 600,
                  color: "var(--ink)",
                  lineHeight: 1.4,
                }}
              >
                <span>{f.q}</span>
                <span aria-hidden="true" style={{
                  fontSize: 22, lineHeight: 1, marginTop: 2, color: "var(--brand-ink)",
                  transition: "transform .25s var(--ease)",
                  transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                }}>+</span>
              </button>
              {isOpen && (
                <p style={{
                  marginTop: 12, marginBottom: 0,
                  fontSize: "var(--fs-xl)", lineHeight: 1.65,
                  color: "var(--ink-2)", maxWidth: 820,
                }}>{f.a}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ContentRelated = ({ groups }) => {
  if (!groups || !groups.length) return null;
  return (
    <section style={{ marginTop: 48, marginBottom: 36, padding: "32px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <h2 style={{
        fontFamily: "var(--f-display)",
        fontSize: "clamp(22px, 2.2vw, 26px)",
        fontWeight: 700,
        margin: "0 0 18px",
        color: "var(--ink)",
      }}>Keep exploring</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
        {groups.map((g, i) => g.links.length === 0 ? null : (
          <div key={i}>
            <div style={{
              fontSize: "var(--fs-sm)",
              fontWeight: 700,
              color: "var(--brand-ink)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}>{g.label}</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {g.links.map((l, j) => (
                <li key={j} style={{ marginBottom: 6 }}>
                  <a href={l.href}
                     onClick={(e) => { if (l.onClick) { e.preventDefault(); l.onClick(); } }}
                     style={{
                       color: "var(--ink-2)",
                       fontSize: "var(--fs-lg)",
                       lineHeight: 1.5,
                       borderBottom: "1px solid transparent",
                     }}
                     onMouseOver={(e) => { e.currentTarget.style.color = "var(--brand-ink)"; }}
                     onMouseOut={(e)  => { e.currentTarget.style.color = "var(--ink-2)"; }}
                  >{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

const ContentRelatedProducts = () => null;

/* Resolve related ids to {label, href, onClick} pairs for ContentRelated.
 * Defensive: skip any entry marked `hidden:true` in src/data.jsx so a stale
 * relatedX array can never surface a service or page that's been pulled. */
const isVisible = (x) => x && !x.hidden;
const buildRelated = (data) => {
  const groups = [];
  if (data.relatedServices && data.relatedServices.length) {
    groups.push({
      label: "HubSpot services",
      links: data.relatedServices.map(id => {
        const x = (window.SERVICES || []).find(s => s.id === id);
        return x ? { label: x.name, href: `/hubspot/${id}`, onClick: () => goToService(id) } : null;
      }).filter(Boolean),
    });
  }
  if (data.relatedIndustries && data.relatedIndustries.length) {
    groups.push({
      label: "Related industries",
      links: data.relatedIndustries.map(id => {
        const x = findById("INDUSTRIES", id);
        return isVisible(x) ? { label: `HubSpot for ${x.name}`, href: `/industries/${id}`, onClick: () => goToIndustry(id) } : null;
      }).filter(Boolean),
    });
  }
  if (data.related && data.related.length) {
    /* sibling industries / migrations on industry/migration pages — relabel */
    groups.push({
      label: "More like this",
      links: data.related.map(id => {
        let x = findById("INDUSTRIES", id) || findById("MIGRATIONS", id);
        if (!isVisible(x)) return null;
        if (x.from)  return { label: `${x.from} → HubSpot`, href: `/migrate/${id}`,  onClick: () => goToMigration(id) };
        return { label: `HubSpot for ${x.name}`, href: `/industries/${id}`, onClick: () => goToIndustry(id) };
      }).filter(Boolean),
    });
  }
  if (data.relatedMigrations && data.relatedMigrations.length) {
    groups.push({
      label: "Migrate from",
      links: data.relatedMigrations.map(id => {
        const x = findById("MIGRATIONS", id);
        return isVisible(x) ? { label: `${x.from} → HubSpot`, href: `/migrate/${id}`, onClick: () => goToMigration(id) } : null;
      }).filter(Boolean),
    });
  }
  if (data.relatedIntegrations && data.relatedIntegrations.length) {
    groups.push({
      label: "Integrations",
      links: data.relatedIntegrations.map(id => {
        const x = findById("INTEGRATIONS", id);
        return isVisible(x) ? { label: x.name, href: `/integrations/${id}`, onClick: () => goToIntegration(id) } : null;
      }).filter(Boolean),
    });
  }
  if (data.relatedGlossary && data.relatedGlossary.length) {
    groups.push({
      label: "Read more",
      links: data.relatedGlossary.map(id => {
        const x = findById("GLOSSARY", id);
        return isVisible(x) ? { label: x.term, href: `/glossary/${id}`, onClick: () => goToGlossary(id) } : null;
      }).filter(Boolean),
    });
  }
  if (data.relatedCases && data.relatedCases.length) {
    groups.push({
      label: "Case studies",
      links: data.relatedCases.map(id => {
        const x = findById("CASE_STUDIES", id);
        return isVisible(x) ? { label: `${x.company} — ${x.industry}`, href: `/case-studies/${id}`, onClick: () => goToCaseStudy(id) } : null;
      }).filter(Boolean),
    });
  }
  // Drop empty groups so the section header doesn't render with zero links.
  return groups.filter(g => g.links && g.links.length);
};

const ContentPage = ({ data, kind, breadcrumbExtras }) => {
  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  React.useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxSrc(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);
  if (!data) return <NotFoundPage path={window.location.pathname} />;
  const crumbs = [
    { label: "Home", href: "/", onClick: goHome },
    { label: "Learn", href: "/learn", onClick: goToLearn },
    ...(breadcrumbExtras || []),
    { label: data.h1 || data.name || data.term || data.title },
  ];
  const related = buildRelated(data);
  const cta = (
    <ContentCtaInline
      tone="primary"
      headline={kind === "case-study"
        ? "Want this result for your company?"
        : "Want this for your company?"}
    />
  );
  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1080 }}>
      <ContentBreadcrumb items={crumbs}/>
      <ContentHero
        eyebrow={kind === "case-study" && data.featured ? "Featured case study" : data.eyebrow}
        h1={data.h1}
        sub={data.sub}
        intro={data.intro}
      />
      {data.topImage && (
        <figure style={{ margin: "0 auto 36px", maxWidth: data.topImage.maxWidth || 560 }}>
          {data.topImage.srcMobile ? (
            <picture>
              <source media="(max-width: 640px)" srcSet={data.topImage.srcMobile}/>
              <img
                src={data.topImage.src}
                alt={data.topImage.alt || data.h1 || data.company || ""}
                loading="lazy"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  borderRadius: "var(--r-l)",
                  border: "1px solid var(--line)",
                }}
              />
            </picture>
          ) : (
            <img
              src={data.topImage.src}
              alt={data.topImage.alt || data.h1 || data.company || ""}
              loading="lazy"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                borderRadius: "var(--r-l)",
                border: "1px solid var(--line)",
              }}
            />
          )}
          {data.topImage.caption && (
            <figcaption style={{ marginTop: 10, fontSize: "var(--fs-sm)", color: "var(--muted)", lineHeight: 1.5, textAlign: "center" }}>
              {data.topImage.caption}
            </figcaption>
          )}
        </figure>
      )}
      {kind === "case-study" && data.outcomes && data.outcomes.length && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 36 }}>
          {data.outcomes.map((o, i) => (
            <div key={i} style={{ padding: 22, background: "var(--bg-2)", border: "1px solid var(--line)", borderRadius: "var(--r-l)", textAlign: "center" }}>
              <div style={{ fontFamily: "var(--f-display)", fontSize: o.metric.length > 8 ? 20 : 28, fontWeight: 800, color: "var(--brand-ink)", marginBottom: 4, whiteSpace: "nowrap" }}>{o.metric}</div>
              <div style={{ fontSize: "var(--fs-base)", color: "var(--muted)", lineHeight: 1.45 }}>{o.label}</div>
            </div>
          ))}
        </div>
      )}
      {kind === "case-study" && (data.quotes || data.quote) && (
        <div className={"cs-quotes" + ((data.quotes && data.quotes.length > 1) ? " cs-quotes-grid" : "")}>
          {(data.quotes || [{ text: data.quote, by: data.quoteBy, name: data.quoteName, role: data.quoteRole, company: data.quoteCompany, avatar: data.quoteAvatar, logo: data.quoteLogo, headline: data.quoteHeadline }]).map((q, i) => (
            <ContentQuote key={i} q={q} />
          ))}
        </div>
      )}
      {data.sections && data.sections.map((s, i) => {
        if (s.quote && !s.h) {
          return <ContentQuote key={i} q={s.quote} featured />;
        }
        const inlineImages = s.images || (s.image ? [s.image] : []);
        return (
          <ContentSection key={i} h={s.h} eyebrow={s.eyebrow} body={s.body} highlight={s.highlight}>
            {s.flow && <ContentFlow steps={s.flow} />}
            {s.afterFlow && <div className="cs-after-flow"><ContentParas body={s.afterFlow} /></div>}
            {s.diagram && <ContentDiagram d={s.diagram} />}
            {s.cards && <ContentResultCards cards={s.cards} />}
            {s.pills && <ContentPills pills={s.pills} />}
            {inlineImages.map((img, j) => (
              <ContentFigure
                key={img.src || j}
                img={img}
                fallbackAlt={data.h1}
                onExpand={setLightboxSrc}
                desktopShrink={!!data.narrowFigures}
              />
            ))}
          </ContentSection>
        );
      })}
      {data.images && data.images.some(img => !img.lightbox) && data.images.filter(img => !img.lightbox).map((img, i) => (
        <figure key={i} style={{ margin: "0 0 36px" }}>
          <img
            src={img.src}
            alt={img.alt || data.h1 || ""}
            loading="lazy"
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              borderRadius: "var(--r-l)",
              border: "1px solid var(--line)",
            }}
          />
          {img.caption && (
            <figcaption style={{
              marginTop: 10,
              fontSize: "var(--fs-sm)",
              color: "var(--muted)",
              lineHeight: 1.5,
            }}>{img.caption}</figcaption>
          )}
        </figure>
      ))}
      {data.images && data.images.some(img => img.lightbox) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start", margin: "0 0 36px" }}>
          {data.images.filter(img => img.lightbox).map((img, i) => (
            <figure key={i} style={{ margin: 0, width: img.thumbWidth || 150 }}>
              <button
                type="button"
                onClick={() => setLightboxSrc(img.src)}
                title="Click to expand"
                style={{
                  display: "block",
                  padding: 0,
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-m)",
                  overflow: "hidden",
                  cursor: "zoom-in",
                  background: "var(--bg-2)",
                  width: "100%",
                  position: "relative",
                }}
              >
                <img
                  src={img.src}
                  alt={img.alt || data.h1 || ""}
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    height: img.thumbHeight || 190,
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                />
                <span style={{
                  position: "absolute",
                  right: 6,
                  bottom: 6,
                  background: "rgba(15,18,30,0.78)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 7px",
                  borderRadius: "var(--r-pill)",
                }}>⤢ Expand</span>
              </button>
              {img.caption && (
                <figcaption style={{ marginTop: 10, fontSize: "var(--fs-sm)", color: "var(--muted)", lineHeight: 1.5 }}>
                  {img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
      {data.cta ? <ContentCtaInline tone="primary" {...data.cta} /> : cta}
      <ContentRelatedProducts ids={data.relatedProducts}/>
      <ContentFaq items={data.faq}/>
      <ContentRelated groups={related}/>
      {!data.cta && cta}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15,18,30,0.82)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 16px",
            overflowY: "auto",
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", maxWidth: 1200, width: "100%" }}>
            <button
              type="button"
              onClick={() => setLightboxSrc(null)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "none",
                background: "rgba(255,255,255,0.92)",
                color: "var(--ink)",
                fontSize: 18,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              }}
            >✕</button>
            <img
              src={lightboxSrc}
              alt=""
              style={{ display: "block", width: "100%", height: "auto", borderRadius: "var(--r-l)" }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

const PillarPage  = () => <ContentPage data={window.PILLAR}  kind="pillar"
                                       breadcrumbExtras={[{ label: "Pillar guide" }]}/>;

const RegionPage = ({ id }) => {
  const d = (window.REGIONS || []).find(r => r.id === id);
  return <ContentPage data={d} kind="region"
                      breadcrumbExtras={[{ label: d?.name || id }]}/>;
};

const IndustryPage = ({ id }) => {
  const d = (window.INDUSTRIES || []).find(r => r.id === id);
  return <ContentPage data={d} kind="industry"
                      breadcrumbExtras={[{ label: "Industries", href: "/learn", onClick: goToLearn }, { label: d?.name || id }]}/>;
};

const MigrationPage = ({ id }) => {
  const d = (window.MIGRATIONS || []).find(r => r.id === id);
  return <ContentPage data={d} kind="migration"
                      breadcrumbExtras={[{ label: "Migrations", href: "/learn", onClick: goToLearn }, { label: d?.name || id }]}/>;
};

const IntegrationPage = ({ id }) => {
  const d = (window.INTEGRATIONS || []).find(r => r.id === id);
  return <ContentPage data={d} kind="integration"
                      breadcrumbExtras={[{ label: "Integrations", href: "/learn", onClick: goToLearn }, { label: d?.name || id }]}/>;
};

const GlossaryPage = ({ id }) => {
  const d = (window.GLOSSARY || []).find(r => r.id === id);
  return <ContentPage data={d} kind="glossary"
                      breadcrumbExtras={[{ label: "Glossary", href: "/learn", onClick: goToLearn }, { label: d?.term || id }]}/>;
};

const CaseStudyPage = ({ id }) => {
  const d = (window.CASE_STUDIES || []).find(r => r.id === id);
  return <ContentPage data={d} kind="case-study"
                      breadcrumbExtras={[{ label: "Case studies", href: "/learn", onClick: goToLearn }, { label: d?.company || id }]}/>;
};

/* ===================================================================
 * SERVICE GROUPS — ordering for the /hubspot index page. Any service id not
 * listed here is swept into a trailing "More services" group so nothing is
 * ever dropped when a new service is added. Mirrored in api/page.js
 * (SERVICE_GROUPS) for SSR.
 * =================================================================== */
const SERVICE_GROUPS = [
  { title: "HubSpot setup & onboarding", blurb: "Stand up or fix any Hub — configured around how your team actually sells, markets and supports.",
    ids: ["hubspot-implementation", "hubspot-onboarding", "sales-hub-setup", "marketing-hub-setup", "service-hub-setup", "content-hub-setup", "operations-hub-setup", "enterprise-implementation", "hire-hubspot-consultant", "hubspot-optimization", "portal-audit-rescue", "portal-merge", "users-teams-permissions", "sandbox-setup", "gdpr-consent-setup", "multi-currency-setup"] },
  { title: "RevOps & data", blurb: "The systems that make HubSpot trustworthy — scoring, lifecycle, pipelines, reporting, automation and clean data.",
    ids: ["revops-consulting", "hubspot-audit", "lead-scoring", "lifecycle-stages", "sales-pipeline-setup", "deal-stages-forecasting", "marketing-attribution", "custom-reporting-dashboards", "revenue-reporting", "workflow-automation", "lead-routing", "data-migration", "data-cleanup", "data-model-design", "custom-objects-setup", "sales-enablement", "email-marketing", "customer-success-setup"] },
  { title: "Sales & CRM configuration", blurb: "The selling motion inside HubSpot — sequences, playbooks, quotes, calling and everything reps touch daily.",
    ids: ["sequences-setup", "playbooks-setup", "quotes-cpq-setup", "product-library-setup", "commerce-hub-payments", "meeting-scheduler-setup", "calling-setup", "renewals-pipeline"] },
  { title: "Marketing & campaigns", blurb: "Campaigns that ship and report cleanly — from ABM and ads to email, personalization and the blog.",
    ids: ["abm-setup", "campaigns-setup", "email-template-design", "newsletter-setup", "forms-lead-capture", "email-deliverability", "smart-content-personalization", "ads-integration", "webinar-events-integration", "social-media-tools", "seo-tools-setup", "blog-migration", "wordpress-to-hubspot-cms"] },
  { title: "Service & customer success", blurb: "Support that scales — ticketing, SLAs, self-service and the feedback loop that keeps customers around.",
    ids: ["help-desk-ticketing", "sla-setup", "live-chat-setup", "knowledge-base-setup", "customer-portal-setup", "nps-surveys-setup"] },
  { title: "Integrations & development", blurb: "Connect HubSpot to your stack and build what configuration alone can't — integrations, CMS, modules and apps.",
    ids: ["integrations", "salesforce-integration", "stripe-integration", "slack-integration", "api-integration", "data-sync", "custom-development", "website-development", "landing-pages", "custom-modules", "theme-development", "app-development"] },
  { title: "AI on HubSpot", blurb: "Put HubSpot's AI and your own agents to work on clean data, with the right guardrails.",
    ids: ["breeze-ai", "ai-agent", "ai-chatbot"] },
  { title: "Managed services & support", blurb: "Ongoing senior HubSpot ownership — without the cost or wait of a full-time hire.",
    ids: ["managed-services", "fractional-admin", "ongoing-support", "hubspot-training"] },
];

const ServicesIndexPage = () => {
  const all = window.SERVICES || [];
  const byId = (id) => all.find(s => s.id === id);
  const claimed = new Set();
  const groups = SERVICE_GROUPS.map(g => {
    const items = g.ids.map(byId).filter(Boolean);
    items.forEach(s => claimed.add(s.id));
    return { ...g, items };
  }).filter(g => g.items.length);
  const leftover = all.filter(s => !claimed.has(s.id));
  if (leftover.length) groups.push({ title: "More services", blurb: "", items: leftover });

  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1080 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "HubSpot services" },
      ]}/>
      <ContentHero
        eyebrow="HUBSPOT SERVICES"
        h1="Everything we do on HubSpot"
        sub="A HubSpot Gold Solutions Partner that can implement, migrate, integrate, build and run HubSpot end to end."
        intro="Whatever you're trying to ship on HubSpot, there's a senior operator here who's done it before. Browse by what you need — setup and onboarding, RevOps and data, integrations and development, AI, or ongoing managed support — then tell us where you are and we'll map the path. No discovery theatre, no juniors learning on your portal."
      />

      {groups.map((g, gi) => (
        <section key={gi} style={{ marginBottom: 44 }}>
          <h2 style={{ fontFamily: "var(--f-display)", fontSize: "clamp(22px, 2.2vw, 28px)", fontWeight: 700, letterSpacing: "-0.015em", margin: "0 0 6px", color: "var(--ink)" }}>{g.title}</h2>
          {g.blurb && <p style={{ fontSize: "var(--fs-lg)", color: "var(--muted)", margin: "0 0 18px", maxWidth: 760, lineHeight: 1.55 }}>{g.blurb}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
            {g.items.map(s => (
              <a key={s.id} href={`/hubspot/${s.id}`}
                 onClick={(e) => { e.preventDefault(); goToService(s.id); }}
                 style={{ display: "flex", flexDirection: "column", padding: "18px 20px", background: "var(--bg-2)", border: "1px solid var(--line)", borderRadius: "var(--r-l)", transition: "border-color .2s var(--ease), transform .2s var(--ease)" }}
                 onMouseOver={(e) => { e.currentTarget.style.borderColor = "var(--brand-ink)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                 onMouseOut={(e) => { e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ fontFamily: "var(--f-display)", fontSize: "var(--fs-2xl)", fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>{s.name}</div>
                <div style={{ fontSize: "var(--fs-base)", color: "var(--muted)", lineHeight: 1.55, flex: 1 }}>{s.sub}</div>
                <span style={{ color: "var(--brand-ink)", fontSize: "var(--fs-base)", fontWeight: 600, marginTop: 12 }}>Learn more →</span>
              </a>
            ))}
          </div>
        </section>
      ))}

      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between",
        padding: "26px 28px", borderRadius: "var(--r-xl)", margin: "8px 0 0",
        background: "linear-gradient(135deg, rgba(59,105,255,0.13) 0%, rgba(255,122,41,0.10) 100%)",
        border: "1px solid var(--line)",
      }}>
        <div style={{ minWidth: 240, flex: "1 1 320px", fontFamily: "var(--f-display)", fontSize: 22, fontWeight: 700, lineHeight: 1.25, color: "var(--ink)" }}>
          Not sure which one you need? Tell us the problem.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="btn btn-primary" onClick={goToContact}>Contact us <Icon name="arrow" size={14}/></button>
          <button className="btn btn-ghost" onClick={goToBook}>Book a call</button>
        </div>
      </div>
    </section>
  );
};

/* ===================================================================
 * SERVICE LANDING PAGE — /hubspot/<slug>
 * Conversion-focused discovery page for "a HubSpot partner who can do X".
 * NO prices. Inline lead form (→ /api/contact, topic=service:<id>) plus the
 * same HubSpot Meetings calendar used elsewhere as the "book a call" CTA.
 * =================================================================== */
const ServiceLeadForm = ({ service, onSent }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const message = [
        `Service enquiry from /hubspot/${service.id} (${service.name})`,
        "",
        "What they need:",
        form.message.trim() || "(not specified)",
      ].join("\n");
      const posted = await postContact({
          name: form.name,
          email: form.email,
          company: form.company,
          topic: `service:${service.id}`,
          message,
        }, e.target);
      if (!posted.spam) {
        try { window.gtag && window.gtag("event", "generate_lead", { currency: "USD", method: "service_form", value: 0, service: service.id }); } catch {}
        try { window.posthog && window.posthog.capture("service_lead_submitted", { service: service.id, company: form.company || null }); } catch {}
        if (onSent) onSent();
      }
      setSent(true);
    } catch (err) {
      console.error("[service:submit] failed:", err);
      setError(err?.message || "Network error. Please email hi@vestalhub.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-form-card" id="service-form">
      <span className="lp-form-eye">TELL US WHAT YOU NEED</span>
      <h2 style={{ margin: "6px 0 8px" }}>Talk to a senior HubSpot operator.</h2>
      <p className="lp-form-lead">No sales pitch, no obligation. Describe your situation and a senior operator replies within one business day — or grab a live slot below.</p>
      <GuardedForm onSubmit={submit} className="lp-form contact-form">
        <HoneypotField />
        <label>
          <span>Your name *</span>
          <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe"/>
        </label>
        <label>
          <span>Work email *</span>
          <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@acme.com"/>
        </label>
        <label>
          <span>Company</span>
          <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Inc."/>
        </label>
        <label>
          <span>What do you need help with?</span>
          <textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={`e.g. ${service.formPlaceholder || "where you are today and what you're trying to achieve"}`}></textarea>
        </label>
        <button className="btn btn-primary lp-form-submit" type="submit" disabled={submitting}>
          {submitting ? "Sending…" : <>Contact us <Icon name="arrow" size={14}/></>}
        </button>
        <p className="lp-form-note">
          <Icon name="shield" size={12}/> Your details go straight to a senior operator. We never share or sell them. <a href="/privacy" onClick={(e) => { e.preventDefault(); goToPrivacy(); }}>Privacy</a>.
        </p>
        {sent && (
          <div className="lp-form-sent">
            <b>Got it.</b> A senior operator will reply within 1 business day. Prefer to talk now? Pick a live slot below.
          </div>
        )}
        {error && <div className="checkout-err">{error}</div>}
      </GuardedForm>
    </div>
  );
};

const ServicePage = ({ id }) => {
  const data = (window.SERVICES || []).find(s => s.id === id);
  const meetingsRef = useRef(null);

  useEffect(() => {
    if (!meetingsRef.current) return;
    const host = meetingsRef.current;
    const div = document.createElement("div");
    div.className = "meetings-iframe-container";
    div.setAttribute("data-src", "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call?embed=true");
    host.appendChild(div);
    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.async = true;
    host.appendChild(script);
    return () => { host.innerHTML = ""; };
  }, [id]);

  if (!data) return <NotFoundPage path={window.location.pathname}/>;

  const scrollToCalendar = () => {
    setTimeout(() => {
      document.getElementById("service-book")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const related = buildRelated(data);

  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1080 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "HubSpot services", href: "/hubspot", onClick: goToServices },
        { label: data.name },
      ]}/>

      {/* Hero: copy + inline lead form */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)", gap: 40, alignItems: "start", margin: "8px 0 24px" }} className="service-hero-grid">
        <div>
          {data.eyebrow && (
            <div style={{ fontSize: "var(--fs-sm)", fontWeight: 700, color: "var(--brand-ink)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{data.eyebrow}</div>
          )}
          <h1 style={{ fontFamily: "var(--f-display)", fontSize: "clamp(30px, 4vw, 44px)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1, margin: "0 0 16px", color: "var(--ink)" }}>{data.h1}</h1>
          <p style={{ fontSize: "clamp(17px, 1.6vw, 20px)", lineHeight: 1.55, color: "var(--ink-2)", margin: "0 0 18px" }}>{data.sub}</p>
          <p style={{ fontSize: "var(--fs-xl)", lineHeight: 1.7, color: "var(--ink-2)", margin: "0 0 20px" }}>{data.intro}</p>
          <ul style={{ listStyle: "none", margin: "0 0 22px", padding: 0, display: "grid", gap: 10 }}>
            {["HubSpot Gold Solutions Partner — senior operators only", "Done-for-you, fixed scope, no juniors learning on your portal", "Honest advice: we'll tell you if you don't need it"].map((t, i) => (
              <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: "var(--fs-lg)", color: "var(--ink-2)", lineHeight: 1.5 }}>
                <span style={{ color: "var(--brand-ink)", marginTop: 2, flexShrink: 0 }}><Icon name="check" size={16}/></span>{t}
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button className="btn btn-primary" onClick={() => document.getElementById("service-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Contact us <Icon name="arrow" size={14}/></button>
            <button className="btn btn-ghost" onClick={scrollToCalendar}>Book a call</button>
          </div>
        </div>
        <ServiceLeadForm service={data} onSent={scrollToCalendar}/>
      </div>

      {/* What we do */}
      {data.sections && data.sections.map((s, i) => (
        <ContentSection key={i} h={s.h} body={s.body}/>
      ))}

      {/* Mid-page conversion nudge */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between",
        padding: "26px 28px", borderRadius: "var(--r-xl)", margin: "32px 0",
        background: "linear-gradient(135deg, rgba(59,105,255,0.13) 0%, rgba(255,122,41,0.10) 100%)",
        border: "1px solid var(--line)",
      }}>
        <div style={{ minWidth: 240, flex: "1 1 320px", fontFamily: "var(--f-display)", fontSize: 22, fontWeight: 700, lineHeight: 1.25, color: "var(--ink)" }}>
          Need a HubSpot partner for this? Let's talk.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="btn btn-primary" onClick={() => document.getElementById("service-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Contact us <Icon name="arrow" size={14}/></button>
          <button className="btn btn-ghost" onClick={scrollToCalendar}>Book a call</button>
        </div>
      </div>

      <ContentFaq items={data.faq}/>
      <ContentRelated groups={related}/>

      {/* Book-a-call calendar */}
      <section id="service-book" style={{ marginTop: 40, paddingTop: 36, borderTop: "1px solid var(--line)" }}>
        <h2 style={{ fontFamily: "var(--f-display)", fontSize: "clamp(24px, 2.4vw, 30px)", fontWeight: 700, margin: "0 0 8px", color: "var(--ink)" }}>Book a call</h2>
        <p style={{ fontSize: "var(--fs-xl)", color: "var(--muted)", margin: "0 0 22px", maxWidth: 720, lineHeight: 1.6 }}>
          Grab a live 30-minute slot directly on a senior operator's calendar — usually same week. Or use the form above and we'll reply within one business day.
        </p>
        <div ref={meetingsRef}/>
      </section>
    </section>
  );
};

/* ===================================================================
 * /learn — the directory page. Linked only from the footer.
 * Lists every content page grouped by type with descriptions.
 * =================================================================== */

const LearnSection = ({ title, blurb, items, urlFn, labelFn, descFn, onNav }) => {
  // Drop the entire section when there's nothing to list — avoids orphan
  // headers when every item in a category is flagged hidden.
  if (!items || !items.length) return null;
  return (
  <section style={{ marginBottom: 56 }}>
    <h2 style={{
      fontFamily: "var(--f-display)",
      fontSize: "clamp(24px, 2.4vw, 30px)",
      fontWeight: 700,
      margin: "0 0 6px",
      color: "var(--ink)",
    }}>{title}</h2>
    {blurb && (
      <p style={{ fontSize: "var(--fs-xl)", color: "var(--muted)", lineHeight: 1.6, margin: "0 0 22px", maxWidth: 720 }}>{blurb}</p>
    )}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
      {items.map(it => (
        <a key={it.id}
           href={urlFn(it)}
           onClick={(e) => { e.preventDefault(); onNav(it); }}
           style={{
             display: "flex", flexDirection: "column",
             padding: "22px 24px",
             background: "var(--bg-2)",
             border: "1px solid var(--line)",
             borderRadius: "var(--r-l)",
             transition: "border-color .2s var(--ease), transform .2s var(--ease)",
           }}
           onMouseOver={(e) => { e.currentTarget.style.borderColor = "var(--brand-ink)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
           onMouseOut={(e)  => { e.currentTarget.style.borderColor = "var(--line)";        e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <div style={{ fontFamily: "var(--f-display)", fontSize: "var(--fs-2xl)", fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>
            {labelFn(it)}
          </div>
          <div style={{ fontSize: "var(--fs-base)", color: "var(--muted)", lineHeight: 1.55 }}>
            {descFn(it)}
          </div>
        </a>
      ))}
    </div>
  </section>
  );
};

const LearnHub = () => {
  const d = window.LEARN_HUB || {};
  // Drop hidden content entries so the Learn hub never lists a page that
  // redirects when clicked (matches the SSR filter in ssrLearnHub).
  const visible = (arr) => (arr || []).filter(x => x && !x.hidden);
  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1180 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "Learn" },
      ]}/>
      <ContentHero eyebrow={d.eyebrow} h1={d.h1} sub={d.sub} intro={d.intro}/>

      <LearnSection
        title="Pillar guide"
        blurb="Everything we know about being a HubSpot Solutions Partner — what the tiers mean, what to expect from delivery, what it costs."
        items={[window.PILLAR].filter(Boolean)}
        urlFn={() => "/hubspot-solutions-partner"}
        labelFn={(p) => p.h1}
        descFn={(p) => p.sub}
        onNav={() => goToPillar()}
      />

      <LearnSection
        title="HubSpot services"
        blurb="Every HubSpot implementation, RevOps, integration, development and AI service we deliver — discovery pages for teams searching for a partner who can do X."
        items={visible(window.SERVICES)}
        urlFn={(s) => `/hubspot/${s.id}`}
        labelFn={(s) => s.name}
        descFn={(s) => s.sub}
        onNav={(s) => goToService(s.id)}
      />

      <LearnSection
        title="By region"
        blurb="Region-specific landing pages with local pricing, contracting, and compliance notes."
        items={visible(window.REGIONS)}
        urlFn={(r) => `/${r.id}`}
        labelFn={(r) => `HubSpot Solutions Partner in the ${r.name}`}
        descFn={(r) => r.sub}
        onNav={(r) => goToRegion(r.id)}
      />

      <LearnSection
        title="By industry"
        blurb="The five problems HubSpot solves per vertical, what we ship most, and what to avoid."
        items={visible(window.INDUSTRIES)}
        urlFn={(r) => `/industries/${r.id}`}
        labelFn={(r) => `HubSpot for ${r.name}`}
        descFn={(r) => r.desc}
        onNav={(r) => goToIndustry(r.id)}
      />

      <LearnSection
        title="CRM migration playbooks"
        blurb="What it actually takes to migrate cleanly off Pipedrive, Marketo and friends — fixed scope, fixed timeline."
        items={visible(window.MIGRATIONS)}
        urlFn={(r) => `/migrate/${r.id}`}
        labelFn={(r) => `${r.from} → HubSpot migration`}
        descFn={(r) => r.sub}
        onNav={(r) => goToMigration(r.id)}
      />

      <LearnSection
        title="Integration guides"
        blurb="HubSpot + the tools your team already uses — Stripe, Slack, Segment, and AI agents via MCP."
        items={visible(window.INTEGRATIONS)}
        urlFn={(r) => `/integrations/${r.id}`}
        labelFn={(r) => r.name}
        descFn={(r) => r.sub}
        onNav={(r) => goToIntegration(r.id)}
      />

      <LearnSection
        title="Glossary"
        blurb="The HubSpot and RevOps terms you'll keep running into — defined plainly, with real-world tradeoffs."
        items={visible(window.GLOSSARY)}
        urlFn={(r) => `/glossary/${r.id}`}
        labelFn={(r) => r.term}
        descFn={(r) => r.sub}
        onNav={(r) => goToGlossary(r.id)}
      />

      <LearnSection
        title="Case studies"
        blurb="Real outcomes from named clients across SaaS, fintech, and demand-gen."
        items={visible(window.CASE_STUDIES).slice().sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))}
        urlFn={(r) => `/case-studies/${r.id}`}
        labelFn={(r) => `${r.company}: ${r.headline || r.industry}`}
        descFn={(r) => r.sub}
        onNav={(r) => goToCaseStudy(r.id)}
      />

      <LearnSection
        title="Free tools"
        blurb="Interactive calculators, pickers, and generators — answer a handful of questions, get an immediately useful output."
        items={visible(window.TOOLS)}
        urlFn={(r) => `/tools/${r.id}`}
        labelFn={(r) => r.name}
        descFn={(r) => r.sub}
        onNav={(r) => goToTool(r.id)}
      />

      <ContentCtaInline tone="primary"/>
    </section>
  );
};

/* ===================================================================
 * FREE TOOLS — /tools index + /tools/:slug per-tool pages.
 *
 * Each tool is an interactive React widget. The shared ToolPage wraps
 * the per-tool component with the standard content shell (breadcrumb,
 * hero, related links, CTAs, FAQ) so adding a new tool only requires
 * (1) data entry in src/data.jsx, (2) a new *Tool component below,
 * (3) a row in TOOL_COMPONENTS, and (4) a sitemap entry (auto via the
 * TOOLS export in api/_seo-content.js).
 * =================================================================== */

const toolPaneStyle = {
  background: "var(--bg-2)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-l)",
  padding: "28px 28px",
  margin: "0 0 24px",
};

const toolFieldRow = {
  display: "flex", flexDirection: "column", gap: 8, marginBottom: 18,
};

const toolLabelStyle = {
  fontSize: 13, fontWeight: 600, color: "var(--ink)",
  textTransform: "uppercase", letterSpacing: ".06em",
};

const toolSelectStyle = {
  padding: "10px 12px",
  fontSize: 15,
  fontFamily: "var(--f-body)",
  background: "var(--bg-1)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-m)",
  width: "100%",
  appearance: "auto",
};

const toolOutputCard = {
  background: "linear-gradient(135deg, rgba(59,105,255,0.06), rgba(59,105,255,0.02))",
  border: "1px solid rgba(59,105,255,0.25)",
  borderRadius: "var(--r-l)",
  padding: "28px 28px",
  margin: "0 0 24px",
};

const toolNoteStyle = {
  fontSize: 13, color: "var(--muted)", lineHeight: 1.6, margin: "10px 0 0",
};

/* --- Tool 1: HubSpot Tier Picker ------------------------------------ */
const TierPickerTool = () => {
  const [teamSize, setTeamSize] = useState("11-50");
  const [stage, setStage] = useState("seed");
  const [primaryHub, setPrimaryHub] = useState("sales");
  const [needsAutomation, setNeedsAutomation] = useState("yes");
  const [needsCustomObjects, setNeedsCustomObjects] = useState("no");

  const result = useMemo(() => {
    const sizeWeight = { "1-10": 1, "11-50": 2, "51-200": 3, "201-500": 4, "500+": 5 }[teamSize];
    const stageWeight = { bootstrapped: 1, seed: 2, "series-a": 3, "series-b": 4, public: 5 }[stage];
    const wantsAutomation = needsAutomation === "yes";
    const wantsCO = needsCustomObjects === "yes";

    // tier logic — opinionated, based on real-world fit
    let tier;
    if (wantsCO || sizeWeight >= 4 || stageWeight >= 4) tier = "Enterprise";
    else if (wantsAutomation || sizeWeight >= 2 || stageWeight >= 2) tier = "Pro";
    else tier = "Starter";

    // multi-hub recommendation
    let hubs = primaryHub === "all"
      ? ["Sales Hub", "Marketing Hub", "Service Hub"]
      : primaryHub === "sales" ? ["Sales Hub"]
      : primaryHub === "marketing" ? ["Marketing Hub"]
      : ["Service Hub"];

    // cost rough ballparks (USD/mo, before negotiation)
    const tierCost = { Starter: { Sales: 50, Marketing: 50, Service: 50 }, Pro: { Sales: 100, Marketing: 800, Service: 100 }, Enterprise: { Sales: 150, Marketing: 3600, Service: 150 } };
    const seats = { "1-10": 5, "11-50": 15, "51-200": 60, "201-500": 200, "500+": 500 }[teamSize];
    const flatHubs = hubs.map(h => h.replace(" Hub", ""));
    let monthlyLow = 0, monthlyHigh = 0;
    for (const h of flatHubs) {
      const base = tierCost[tier][h];
      // sales/service are per-seat; marketing is platform + contacts
      if (h === "Sales" || h === "Service") {
        monthlyLow += base * seats * 0.8;  // partner discount
        monthlyHigh += base * seats * 1.0;
      } else {
        monthlyLow += base * 0.8;
        monthlyHigh += base * 1.0;
      }
    }
    monthlyLow = Math.round(monthlyLow / 100) * 100;
    monthlyHigh = Math.round(monthlyHigh / 100) * 100;

    const giveUp = tier === "Starter" ? "Going up to Pro adds: workflows / automation, sequences, advanced reporting, lifecycle stages, custom properties at scale."
      : tier === "Pro" ? "Going down to Starter loses: workflows, sequences, advanced reporting, lifecycle automation, marketing automation. Going up to Enterprise adds: custom objects, advanced permissions, programmable automation, AI Copilot at higher seat caps."
      : "Going down to Pro loses: custom objects, advanced reporting tiers, role-based permissions, multi-team segregation, programmable automation.";

    const isHubSpotRightAtAll = stage === "public" && (sizeWeight >= 5) && wantsCO
      ? "At your scale, Salesforce + Marketo or a fully custom stack may genuinely beat HubSpot Enterprise. Worth a comparison engagement — see our HubSpot vs Salesforce guide."
      : "HubSpot Enterprise is genuinely competitive at your stage. Most enterprise HubSpot wins we see are TCO + speed-to-value over Salesforce.";

    let recommended = tier === "Starter" ? "Sales / Marketing Onboarding (Essential plan, $1.6k each)"
      : tier === "Pro" ? "Sales Onboarding Pro ($2.5k) + Marketing Onboarding Pro ($3.6k)"
      : "Enterprise onboarding bundle + Lifecycle Stages + Lead Scoring System ($12-15k total)";

    return { tier, hubs, monthlyLow, monthlyHigh, giveUp, isHubSpotRightAtAll: tier === "Enterprise" ? isHubSpotRightAtAll : null, recommended };
  }, [teamSize, stage, primaryHub, needsAutomation, needsCustomObjects]);

  return (
    <>
      <div style={toolPaneStyle}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 18px" }}>Tell us about your team</h2>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Team size</label>
          <select style={toolSelectStyle} value={teamSize} onChange={e => setTeamSize(e.target.value)}>
            <option value="1-10">1–10 people</option>
            <option value="11-50">11–50 people</option>
            <option value="51-200">51–200 people</option>
            <option value="201-500">201–500 people</option>
            <option value="500+">500+ people</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Funding stage</label>
          <select style={toolSelectStyle} value={stage} onChange={e => setStage(e.target.value)}>
            <option value="bootstrapped">Bootstrapped</option>
            <option value="seed">Pre-seed / Seed</option>
            <option value="series-a">Series A</option>
            <option value="series-b">Series B+</option>
            <option value="public">Public / Late stage</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Primary hub you need</label>
          <select style={toolSelectStyle} value={primaryHub} onChange={e => setPrimaryHub(e.target.value)}>
            <option value="sales">Sales Hub (CRM, pipeline)</option>
            <option value="marketing">Marketing Hub (email, automation)</option>
            <option value="service">Service Hub (support, tickets)</option>
            <option value="all">All three</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Need marketing automation (workflows, sequences)?</label>
          <select style={toolSelectStyle} value={needsAutomation} onChange={e => setNeedsAutomation(e.target.value)}>
            <option value="yes">Yes</option>
            <option value="no">No, just CRM basics</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Need custom objects or advanced reporting?</label>
          <select style={toolSelectStyle} value={needsCustomObjects} onChange={e => setNeedsCustomObjects(e.target.value)}>
            <option value="no">No, standard objects are fine</option>
            <option value="yes">Yes (subscriptions, properties, etc.)</option>
          </select>
        </div>
      </div>

      <div style={toolOutputCard}>
        <p style={{ fontSize: 13, color: "var(--brand-ink)", margin: "0 0 6px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Recommendation</p>
        <h2 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 12px" }}>
          {result.hubs.join(" + ")} — <span style={{ color: "var(--brand-ink)" }}>{result.tier}</span>
        </h2>
        <p style={{ fontSize: 18, margin: "0 0 14px" }}>
          Estimated cost: <b>${result.monthlyLow.toLocaleString()}–${result.monthlyHigh.toLocaleString()}/month</b> <span style={{ color: "var(--muted)", fontWeight: 400 }}>(after typical partner discount)</span>
        </p>
        <p style={toolNoteStyle}><b>What you'd give up by changing tiers:</b> {result.giveUp}</p>
        {result.isHubSpotRightAtAll && (
          <p style={toolNoteStyle}><b>Is HubSpot right for you?</b> {result.isHubSpotRightAtAll}</p>
        )}
        <p style={toolNoteStyle}><b>Recommended Vestal Hub service to install this:</b> {result.recommended}</p>
        <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact us</a>
          <a href="/book" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToBook(); }}>Book a 30-min consult</a>
        </div>
      </div>
    </>
  );
};

/* --- Tool 2: HubSpot Migration Cost Estimator ----------------------- */
const MigrationEstimatorTool = () => {
  const [source, setSource] = useState("salesforce");
  const [contacts, setContacts] = useState("1k-10k");
  const [customFields, setCustomFields] = useState("medium");
  const [integrations, setIntegrations] = useState("2");
  const [preserveHistory, setPreserveHistory] = useState("yes");
  const [parallelRun, setParallelRun] = useState("no");

  const result = useMemo(() => {
    const sourceBase = { salesforce: 6200, pipedrive: 5800, marketo: 6200, pardot: 6200, close: 5800, other: 6000 }[source];
    const sourceMult = { salesforce: 1.0, pipedrive: 0.9, marketo: 1.1, pardot: 1.1, close: 0.9, other: 1.0 }[source];
    const contactsMult = { "<1k": 0.9, "1k-10k": 1.0, "10k-100k": 1.2, "100k+": 1.5 }[contacts];
    const fieldsMult = { low: 0.9, medium: 1.0, high: 1.3 }[customFields];
    const intCount = Number(integrations);
    const intAdd = intCount * 1200; // each integration ~$1.2k

    let total = Math.round(sourceBase * sourceMult * contactsMult * fieldsMult + intAdd);
    if (parallelRun === "yes") total += 1500;
    if (preserveHistory === "no") total -= 800; // less work if no history needed

    const totalLow = Math.round(total * 0.85 / 100) * 100;
    const totalHigh = Math.round(total * 1.15 / 100) * 100;

    let weeks;
    const complexityScore = (sourceMult * fieldsMult * contactsMult) + intCount * 0.4 + (parallelRun === "yes" ? 0.5 : 0);
    if (complexityScore < 1.2) weeks = "2";
    else if (complexityScore < 2.0) weeks = "3";
    else if (complexityScore < 3.0) weeks = "4–5";
    else weeks = "6–8";

    let complexity;
    if (complexityScore < 1.2) complexity = "Simple";
    else if (complexityScore < 2.5) complexity = "Standard";
    else complexity = "Complex";

    const drivers = [];
    if (source === "marketo" || source === "pardot") drivers.push(`Source (${source === "marketo" ? "Marketo" : "Pardot"}) — multi-axis scoring + Engagement Studio rebuild`);
    if (source === "salesforce") drivers.push("Salesforce-specific quirks — field-level mapping, OpportunityHistory, RecordType handling");
    if (contacts === "10k-100k" || contacts === "100k+") drivers.push(`Data volume (${contacts}) — extended cutover + validation`);
    if (customFields === "high") drivers.push("Custom field volume — extensive mapping + de-duplication");
    if (intCount >= 3) drivers.push(`Integration count (${intCount}) — each integration is its own mini-project`);
    if (parallelRun === "yes") drivers.push("Parallel run — bi-directional sync for the cutover window (+$1,500)");
    while (drivers.length < 3) drivers.push("None unusual for this profile");

    const sourceMap = { salesforce: "Salesforce", pipedrive: "Pipedrive", marketo: "Marketo", pardot: "Pardot", close: "Close.com", other: "your current CRM" };
    const recommended = `CRM Migration ($6,200 base) + ${intCount >= 1 ? `${intCount} Third-Party Integrations ($${(intCount * 1200).toLocaleString()})` : "no integrations"}${parallelRun === "yes" ? " + parallel-run setup (+$1,500)" : ""}.`;

    return { totalLow, totalHigh, weeks, complexity, drivers: drivers.slice(0, 3), recommended, sourceName: sourceMap[source] };
  }, [source, contacts, customFields, integrations, preserveHistory, parallelRun]);

  return (
    <>
      <div style={toolPaneStyle}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 18px" }}>Tell us about your migration</h2>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Source CRM</label>
          <select style={toolSelectStyle} value={source} onChange={e => setSource(e.target.value)}>
            <option value="salesforce">Salesforce</option>
            <option value="pipedrive">Pipedrive</option>
            <option value="marketo">Marketo (Adobe)</option>
            <option value="pardot">Pardot (Account Engagement)</option>
            <option value="close">Close.com</option>
            <option value="other">Other (spreadsheets, in-house, etc.)</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Contact count</label>
          <select style={toolSelectStyle} value={contacts} onChange={e => setContacts(e.target.value)}>
            <option value="<1k">Less than 1,000</option>
            <option value="1k-10k">1,000 – 10,000</option>
            <option value="10k-100k">10,000 – 100,000</option>
            <option value="100k+">100,000+</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Custom field volume</label>
          <select style={toolSelectStyle} value={customFields} onChange={e => setCustomFields(e.target.value)}>
            <option value="low">Low (&lt; 20 custom fields)</option>
            <option value="medium">Medium (20–50 custom fields)</option>
            <option value="high">High (50+ custom fields)</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Active integrations that need to follow</label>
          <select style={toolSelectStyle} value={integrations} onChange={e => setIntegrations(e.target.value)}>
            <option value="0">None</option>
            <option value="1">1 integration</option>
            <option value="2">2 integrations</option>
            <option value="3">3 integrations</option>
            <option value="5">5+ integrations</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Preserve full historical data?</label>
          <select style={toolSelectStyle} value={preserveHistory} onChange={e => setPreserveHistory(e.target.value)}>
            <option value="yes">Yes — every record + history</option>
            <option value="no">No — current snapshot is fine</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Parallel running during cutover?</label>
          <select style={toolSelectStyle} value={parallelRun} onChange={e => setParallelRun(e.target.value)}>
            <option value="no">No — clean cutover</option>
            <option value="yes">Yes — both systems live for 1–3 weeks</option>
          </select>
        </div>
      </div>

      <div style={toolOutputCard}>
        <p style={{ fontSize: 13, color: "var(--brand-ink)", margin: "0 0 6px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Your estimate</p>
        <h2 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 12px" }}>
          ${result.totalLow.toLocaleString()}–${result.totalHigh.toLocaleString()}
          <span style={{ fontSize: 18, color: "var(--muted)", fontWeight: 400, marginLeft: 10 }}>· {result.weeks} weeks · {result.complexity}</span>
        </h2>
        <p style={{ fontSize: 15, lineHeight: 1.6, margin: "0 0 14px", color: "var(--muted)" }}>
          Estimated total cost to migrate from {result.sourceName} to HubSpot, including data migration, field mapping, integration setup, cutover testing, and validation.
        </p>
        <p style={{ ...toolNoteStyle, color: "var(--ink)", fontWeight: 600, marginTop: 16, marginBottom: 8 }}>Top cost drivers for your setup</p>
        <ul style={{ margin: "0 0 14px", paddingLeft: 20, fontSize: 14, lineHeight: 1.6, color: "var(--muted)" }}>
          {result.drivers.map((d, i) => <li key={i} style={{ margin: "4px 0" }}>{d}</li>)}
        </ul>
        <p style={toolNoteStyle}><b>Recommended Vestal Hub service:</b> {result.recommended}</p>
        <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <a href="/contact" className="btn btn-primary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact us</a>
          <a href="/book" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); goToBook(); }}>Book a 30-min consult</a>
        </div>
      </div>
    </>
  );
};

/* --- Tool 3: Lead Scoring Template Generator ----------------------- */
const ScoringTemplateTool = () => {
  const [model, setModel] = useState("hybrid");
  const [icpSize, setIcpSize] = useState("smb");
  const [icpRole, setIcpRole] = useState("director");
  const [cycle, setCycle] = useState("4-12");
  const [primaryAction, setPrimaryAction] = useState("demo");
  const [negativeFreeEmail, setNegativeFreeEmail] = useState("yes");

  const template = useMemo(() => {
    // Fit attributes
    const sizePoints = { smb: { name: "1–50 employees", pts: 10 }, mid: { name: "51–500 employees", pts: 20 }, ent: { name: "500+ employees", pts: 25 } }[icpSize];
    const rolePoints = { exec: 30, director: 20, manager: 10, ic: 5 }[icpRole];
    const roleLabel = { exec: "C-suite / VP", director: "Director", manager: "Manager", ic: "Individual contributor" }[icpRole];

    const fit = [
      { attribute: `Company size: ${sizePoints.name}`, pts: sizePoints.pts },
      { attribute: `Company size: outside target (-5)`, pts: -5 },
      { attribute: `Role: ${roleLabel}`, pts: rolePoints },
      { attribute: "Role: adjacent (e.g. Ops, Marketing for Sales tool)", pts: Math.round(rolePoints * 0.4) },
      { attribute: "Industry: ICP match", pts: 15 },
      { attribute: "Region: target geo", pts: 10 },
    ];

    // Engagement actions — vary by primary action
    const engagement = [];
    if (model === "plg" || model === "hybrid") {
      engagement.push({ action: "Signed up for product (free trial / freemium)", pts: 25 });
      engagement.push({ action: "Activated key feature within 24h of signup", pts: 30 });
      engagement.push({ action: "Hit usage threshold (defined per product)", pts: 35 });
      engagement.push({ action: "Invited 1+ teammate", pts: 20 });
    }
    if (model === "sales-led" || model === "hybrid") {
      const primaryPts = { demo: 30, trial: 25, contact: 20, pricing: 15 }[primaryAction];
      const primaryLabel = { demo: "Booked a demo", trial: "Started a trial", contact: "Submitted contact form", pricing: "Viewed pricing page" }[primaryAction];
      engagement.push({ action: primaryLabel, pts: primaryPts });
      engagement.push({ action: "Visited pricing page 2+ times in 14 days", pts: 15 });
      engagement.push({ action: "Downloaded gated asset (ebook / template / report)", pts: 10 });
      engagement.push({ action: "Attended live webinar or watched recording 50%+", pts: 12 });
      engagement.push({ action: "Opened 3+ marketing emails in 14 days", pts: 5 });
      engagement.push({ action: "Clicked 2+ marketing emails in 14 days", pts: 8 });
    }

    // Decay rules
    const decay = [
      { rule: "Engagement points decay -25% per 30 days of inactivity" },
      { rule: "Engagement points reset to 0 after 180 days of no activity" },
      { rule: "Fit points never decay (they're stable attributes)" },
    ];

    // Negative signals
    const negative = [];
    if (negativeFreeEmail === "yes") {
      negative.push({ signal: "Free email domain (gmail, yahoo, etc.)", pts: -10 });
    }
    negative.push({ signal: "Email unsubscribed from marketing", pts: -20 });
    negative.push({ signal: "Marked as competitor (manual flag)", pts: -50 });
    negative.push({ signal: "Region: not served", pts: -15 });
    negative.push({ signal: "Hard bounce on email", pts: -10 });

    // MQL threshold — based on sales cycle (shorter cycles = lower threshold)
    let mqlThreshold;
    if (cycle === "1-4") mqlThreshold = 50;
    else if (cycle === "4-12") mqlThreshold = 70;
    else if (cycle === "12-26") mqlThreshold = 90;
    else mqlThreshold = 110;

    const reasoning = cycle === "1-4"
      ? "Short cycles (1–4 weeks) — set a lower MQL threshold so sales gets leads while intent is hot. Re-tune in 30 days based on conversion."
      : cycle === "4-12"
      ? "Standard cycles (1–3 months) — moderate threshold. The 70 number lands roughly where most sales-led B2B SaaS sees MQL → SQL > 30%."
      : cycle === "12-26"
      ? "Longer cycles (3–6 months) — higher threshold so MQL signal is stronger before sales effort. Pair with nurture for sub-threshold leads."
      : "Enterprise cycles (6+ months) — high threshold. Sub-threshold leads stay in marketing nurture for quarters before sales engagement.";

    return { fit, engagement, decay, negative, mqlThreshold, reasoning };
  }, [model, icpSize, icpRole, cycle, primaryAction, negativeFreeEmail]);

  const copyToClipboard = () => {
    const text = [
      "# HubSpot Lead Scoring Template (generated by Vestal Hub)",
      "",
      "## Fit attributes (positive)",
      ...template.fit.map(f => `- ${f.attribute}: ${f.pts >= 0 ? "+" : ""}${f.pts}`),
      "",
      "## Engagement scoring",
      ...template.engagement.map(e => `- ${e.action}: +${e.pts}`),
      "",
      "## Decay rules",
      ...template.decay.map(d => `- ${d.rule}`),
      "",
      "## Negative signals",
      ...template.negative.map(n => `- ${n.signal}: ${n.pts}`),
      "",
      `## MQL threshold: ${template.mqlThreshold} points`,
      template.reasoning,
      "",
      "Generated at https://vestalhub.com/tools/lead-scoring-template",
    ].join("\n");
    try {
      navigator.clipboard.writeText(text);
      alert("Scoring template copied to clipboard. Paste into HubSpot, Notion, or your team doc.");
    } catch {
      alert("Copy failed. Select the text manually.");
    }
  };

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--line)", fontSize: 14 };

  return (
    <>
      <div style={toolPaneStyle}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 18px" }}>Tell us about your motion</h2>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Business model</label>
          <select style={toolSelectStyle} value={model} onChange={e => setModel(e.target.value)}>
            <option value="sales-led">Sales-led (forms → demos → sales cycle)</option>
            <option value="plg">PLG (signup → activation → expansion)</option>
            <option value="hybrid">Hybrid (PLG + sales-assist)</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Target company size</label>
          <select style={toolSelectStyle} value={icpSize} onChange={e => setIcpSize(e.target.value)}>
            <option value="smb">SMB (1–50 employees)</option>
            <option value="mid">Mid-market (51–500 employees)</option>
            <option value="ent">Enterprise (500+ employees)</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Target buyer role</label>
          <select style={toolSelectStyle} value={icpRole} onChange={e => setIcpRole(e.target.value)}>
            <option value="exec">C-suite / VP</option>
            <option value="director">Director</option>
            <option value="manager">Manager</option>
            <option value="ic">Individual contributor</option>
          </select>
        </div>
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Average sales cycle</label>
          <select style={toolSelectStyle} value={cycle} onChange={e => setCycle(e.target.value)}>
            <option value="1-4">1–4 weeks</option>
            <option value="4-12">1–3 months</option>
            <option value="12-26">3–6 months</option>
            <option value="26+">6+ months</option>
          </select>
        </div>
        {(model === "sales-led" || model === "hybrid") && (
          <div style={toolFieldRow}>
            <label style={toolLabelStyle}>Primary conversion action</label>
            <select style={toolSelectStyle} value={primaryAction} onChange={e => setPrimaryAction(e.target.value)}>
              <option value="demo">Book a demo</option>
              <option value="trial">Start a trial</option>
              <option value="contact">Contact us form</option>
              <option value="pricing">View pricing</option>
            </select>
          </div>
        )}
        <div style={toolFieldRow}>
          <label style={toolLabelStyle}>Treat free email domains (gmail, etc.) as negative signal?</label>
          <select style={toolSelectStyle} value={negativeFreeEmail} onChange={e => setNegativeFreeEmail(e.target.value)}>
            <option value="yes">Yes (B2B-only)</option>
            <option value="no">No (B2C / mixed)</option>
          </select>
        </div>
      </div>

      <div style={toolOutputCard}>
        <p style={{ fontSize: 13, color: "var(--brand-ink)", margin: "0 0 6px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Your starter scoring model</p>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 18px" }}>MQL threshold: <span style={{ color: "var(--brand-ink)" }}>{template.mqlThreshold} points</span></h2>
        <p style={{ ...toolNoteStyle, marginBottom: 22 }}>{template.reasoning}</p>

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "18px 0 8px" }}>Fit attributes (who they are)</h3>
        {template.fit.map((f, i) => (
          <div key={i} style={rowStyle}><span>{f.attribute}</span><b style={{ color: f.pts >= 0 ? "var(--brand-ink)" : "var(--accent)" }}>{f.pts >= 0 ? "+" : ""}{f.pts}</b></div>
        ))}

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "22px 0 8px" }}>Engagement actions (what they do)</h3>
        {template.engagement.map((e, i) => (
          <div key={i} style={rowStyle}><span>{e.action}</span><b style={{ color: "var(--brand-ink)" }}>+{e.pts}</b></div>
        ))}

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "22px 0 8px" }}>Negative signals</h3>
        {template.negative.map((n, i) => (
          <div key={i} style={rowStyle}><span>{n.signal}</span><b style={{ color: "var(--accent)" }}>{n.pts}</b></div>
        ))}

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "22px 0 8px" }}>Decay rules</h3>
        <ul style={{ margin: "0 0 18px", paddingLeft: 20, fontSize: 14, lineHeight: 1.6, color: "var(--muted)" }}>
          {template.decay.map((d, i) => <li key={i} style={{ margin: "4px 0" }}>{d.rule}</li>)}
        </ul>

        <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button onClick={copyToClipboard} className="btn btn-primary" type="button">Copy template to clipboard</button>
          <a href="/contact" className="btn btn-secondary" onClick={(e) => { e.preventDefault(); goToContact(); }}>Have us install + tune it ($3,200)</a>
        </div>
      </div>
    </>
  );
};

const TOOL_COMPONENTS = {
  TierPickerTool,
  MigrationEstimatorTool,
  ScoringTemplateTool,
};

const ToolPage = ({ id }) => {
  const t = (window.TOOLS || []).find(r => r.id === id);
  if (!t) {
    return (
      <section className="wrap" style={{ paddingTop: 60, paddingBottom: 80, textAlign: "center" }}>
        <h1>Tool not found</h1>
        <p style={{ marginTop: 12 }}><a href="/tools" onClick={(e) => { e.preventDefault(); goToToolsIndex(); }}>Browse all free tools →</a></p>
      </section>
    );
  }
  const Comp = TOOL_COMPONENTS[t.component];

  /* buildRelated handles glossary + migrations; add "Other tools" group inline
     so the cross-tool link graph stays strong from each tool page. */
  const groups = buildRelated(t);
  if (t.relatedTools && t.relatedTools.length) {
    const allTools = window.TOOLS || [];
    groups.push({
      label: "More free tools",
      links: t.relatedTools.map(id => {
        const other = allTools.find(x => x.id === id);
        return other ? { label: other.name, href: `/tools/${id}`, onClick: () => goToTool(id) } : null;
      }).filter(Boolean),
    });
  }

  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 920 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "Learn", href: "/learn", onClick: goToLearn },
        { label: "Free tools", href: "/tools", onClick: goToToolsIndex },
        { label: t.name },
      ]}/>
      <ContentHero eyebrow={t.eyebrow} h1={t.h1} sub={t.sub} intro={t.intro}/>
      {Comp ? <Comp /> : <p>Tool unavailable.</p>}
      <ContentRelatedProducts ids={t.relatedProducts}/>
      <ContentFaq items={t.faq}/>
      <ContentRelated groups={groups}/>
      <ContentCtaInline tone="primary"/>
    </section>
  );
};

const ToolsIndexPage = () => {
  const tools = window.TOOLS || [];
  return (
    <section className="wrap" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1080 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "Learn", href: "/learn", onClick: goToLearn },
        { label: "Free tools" },
      ]}/>
      <ContentHero
        eyebrow="Free tools"
        h1="Free interactive HubSpot tools"
        sub="Built by a HubSpot Gold Solutions Partner. Tier picker, migration cost estimator, lead scoring template generator — answer a handful of questions and get an immediately useful output."
        intro="No signup, no email gate. The output is yours to copy, paste, or use as a starting point. If you want us to install + tune the recommendation, every tool ends with a clear path to the relevant Vestal Hub service."
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 18, margin: "8px 0 32px" }}>
        {tools.map(t => (
          <a key={t.id}
             href={`/tools/${t.id}`}
             onClick={(e) => { e.preventDefault(); goToTool(t.id); }}
             style={{
               display: "flex", flexDirection: "column",
               padding: "24px 26px",
               background: "var(--bg-2)",
               border: "1px solid var(--line)",
               borderRadius: "var(--r-l)",
               transition: "border-color .2s var(--ease), transform .2s var(--ease)",
             }}
             onMouseOver={(e) => { e.currentTarget.style.borderColor = "var(--brand-ink)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
             onMouseOut={(e) =>  { e.currentTarget.style.borderColor = "var(--line)";        e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ fontSize: 12, color: "var(--brand-ink)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>{t.eyebrow}</div>
            <div style={{ fontFamily: "var(--f-display)", fontSize: "var(--fs-2xl)", fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>{t.name}</div>
            <div style={{ fontSize: "var(--fs-base)", color: "var(--muted)", lineHeight: 1.55 }}>{t.sub}</div>
          </a>
        ))}
      </div>
      <ContentCtaInline tone="primary"/>
    </section>
  );
};

const CaseStudiesIndexPage = () => {
  const studies = visibleCaseStudies();
  const featured = studies.filter((s) => s.featured);
  const rest = studies.filter((s) => !s.featured);
  return (
    <section className="wrap cs-index" style={{ paddingTop: 36, paddingBottom: 80, maxWidth: 1080 }}>
      <ContentBreadcrumb items={[
        { label: "Home", href: "/", onClick: goHome },
        { label: "Case studies" },
      ]}/>
      <ContentHero
        eyebrow="Case studies"
        h1="Results you can measure."
        sub="More meetings. More pipeline. More revenue. See the measurable business impact we’ve delivered for companies across SaaS, services, education, and more."
      />
      {featured.length > 0 && (
        <div className="cs-index-featured">
          {featured.map((s) => <CaseStudyCard key={s.id} s={s} featured />)}
        </div>
      )}
      <div className="cs-index-grid">
        {rest.map((s) => <CaseStudyCard key={s.id} s={s} />)}
      </div>
      <ContentCtaInline
        tone="primary"
        headline="Want this result for your company?"
      />
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="wrap">
        <div className="ft-grid">
          <div>
            <a href="#" onClick={(e) => { e.preventDefault(); goHome(); }} className="logo" aria-label="Vestal Hub"><img src="Vestal-hub.png" alt="Vestal Hub" className="logo-img logo-img-footer" width="154" height="32" loading="lazy" decoding="async" /></a>
            <p>HubSpot done right. Outbound that books meetings. Systems built to drive pipeline and revenue.<br/>Trusted by 100+ companies worldwide.</p>
            <div className="ft-social">
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Vestal Hub on LinkedIn">
                <Icon name="linkedin" size={16}/>
              </a>
              <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }} aria-label="Contact us">
                <Icon name="mail" size={16}/>
              </a>
            </div>
            <div className="ft-badge">
              <span className="ft-badge-dot" aria-hidden="true"/>
              <div className="ft-badge-text">
                <b>HubSpot Solutions Partner</b>
                <span>Gold tier · Trusted by 100+ companies</span>
              </div>
            </div>
          </div>
          <div>
            <h4>Company</h4>
            <a href="/about" onClick={(e) => { e.preventDefault(); goToAbout(); }}>About</a>
            <a href="/partners" onClick={(e) => { e.preventDefault(); goToPartners(); }}>Partner Program</a>
            <a href="/white-label" onClick={(e) => { e.preventDefault(); goToWhiteLabel(); }}>White-label Dev</a>
            <a href="/careers" onClick={(e) => { e.preventDefault(); goToCareers(); }}>Careers</a>
          </div>
          <div>
            <h4>Services</h4>
            <a href="/hubspot" onClick={(e) => { e.preventDefault(); goToServices(); }}>All Services</a>
            <a href="/hubspot-onboarding-packages" onClick={(e) => { e.preventDefault(); goToOnboardingPackages(); }}>HubSpot Onboarding</a>
            <a href="/hubspot-admin" onClick={(e) => { e.preventDefault(); goToAdmin(); }}>HubSpot Admin Support</a>
            <a href="/hubspot-technical-developers" onClick={(e) => { e.preventDefault(); goToTechSupport(); }}>Technical &amp; RevOps</a>
            <a href="/outbound-infrastructure" onClick={(e) => { e.preventDefault(); goToOutboundInfrastructure(); }}>Outbound Infrastructure</a>
            <a href="/outbound-optimization" onClick={(e) => { e.preventDefault(); goToOutboundOptimization(); }}>Managed Outbound</a>
          </div>
          <div>
            <h4>Resources</h4>
            <a href="/case-studies" onClick={(e) => { e.preventDefault(); goToCaseStudies(); }}>Case Studies</a>
            <a href="/learn" onClick={(e) => { e.preventDefault(); goToLearn(); }}>Resources Hub</a>
            <a href="/tools" onClick={(e) => { e.preventDefault(); goToToolsIndex(); }}>Free Tools</a>
            <a href="/hubspot" onClick={(e) => { e.preventDefault(); goToServices(); }}>FAQ</a>
            <a href="/hubspot-solutions-partner" onClick={(e) => { e.preventDefault(); goToPillar(); }}>HubSpot Solutions Partner</a>
            <a href="/hubspot-for-saas" onClick={(e) => { e.preventDefault(); goToSaas(); }}>HubSpot for SaaS</a>
          </div>
          <div>
            <h4>Contact</h4>
            <a href="/book" onClick={(e) => { e.preventDefault(); goToBook(); }}>Book a Call →</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact</a>
            <a href={`mailto:${SOCIAL.email}?subject=Help`}>Email Us</a>
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
        </div>
        <div className="ft-bot">
          <div>© 2026 Vestal Hub</div>
          <nav className="ft-legal" aria-label="Legal">
            <a href="/terms" onClick={(e) => { e.preventDefault(); goToTerms(); }}>Terms</a>
            <span aria-hidden="true">·</span>
            <a href="/privacy" onClick={(e) => { e.preventDefault(); goToPrivacy(); }}>Privacy</a>
            <span aria-hidden="true">·</span>
            <a href="/cookies" onClick={(e) => { e.preventDefault(); goToCookies(); }}>Cookies</a>
          </nav>
        </div>
      </div>
    </footer>
  );
};

/* ============ Checkout ============
 *
 * Two-column layout: form on the left, sticky order summary on the right.
 * Cart is read-only on this page — buyers can't edit line items here, only
 * fill in their info and submit. Successful submission POSTs to /api/checkout
 * which emails both sides + creates a HubSpot Contact + Deal, then we save a
 * cart snapshot to sessionStorage and route to the thank-you page.
 */
const COUNTRIES = [
  "United States","Canada","United Kingdom","Ireland","Germany","France","Spain",
  "Portugal","Netherlands","Sweden","Denmark","Norway","Finland","Switzerland",
  "Australia","New Zealand","Singapore","Japan","Brazil","Mexico","Argentina",
  "Chile","Colombia","Other"
];
const TEAM_SIZES = [
  "Just me","2–10","11–50","51–200","201–500","500+"
];
const KICKOFF_WINDOWS = [
  "This week (rush)","Next 2 weeks","Next month","Flexible / TBD"
];

const CheckoutPage = ({ items, addons, onCleared }) => {
  const totals = cartMath(items, addons);
  const addonList = Object.keys(addons || {})
    .filter(k => addons[k])
    .map(k => ADDONS.find(a => a.id === k))
    .filter(Boolean);

  const [buyer, setBuyer] = useState({ name: "", email: "", role: "" });
  const [company, setCompany] = useState({
    legalName: "", website: "", country: "United States", size: "11–50",
  });
  const [engagement, setEngagement] = useState({
    kickoff: "Next 2 weeks", channel: "Slack", brief: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Empty cart → bounce them back to the shop. (We don't want a "submit"
  // action with a $0 cart that creates phantom HubSpot deals.)
  if (!items || items.length === 0) {
    return (
      <>
        <PageHero
          eyebrow="CHECKOUT"
          title={<>Your cart is <em>empty.</em></>}
          lead="Add a fixed-scope service to your cart, then come back here to send a brief and receive your SOW within one business day."
          accent="#3B69FF"
        />
        <section className="page-body">
          <div className="wrap" style={{textAlign:"center", padding: "20px 0 40px"}}>
            <a className="btn btn-primary" href="/shop"
               onClick={(e) => { e.preventDefault(); goToShop(); }}>
               Browse services <Icon name="arrow" size={14}/>
            </a>
          </div>
        </section>
      </>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const payload = {
      buyer,
      company,
      engagement,
      cart: {
        items: items.map(it => ({
          id: it.id, name: it.name, category: it.category,
          duration: it.duration, price: it.price,
        })),
        addons: addonList.map(a => ({ id: a.id, name: a.name, price: a.price })),
      },
      totals,
    };

    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Read once as text so we can show real diagnostics on a non-JSON 5xx body.
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { /* non-JSON */ }
      if (!res.ok || !json.ok) {
        const detail = json.error || (raw && !json.error ? raw.slice(0, 140) : null);
        throw new Error(
          detail
            ? `${detail} (status ${res.status})`
            : `Submission failed (status ${res.status}). Please email hi@vestalhub.com directly.`
        );
      }

      // Snapshot for the thank-you page (the cart will be cleared right after).
      try {
        sessionStorage.setItem("vh:lastOrder", JSON.stringify({
          items: payload.cart.items,
          addons: payload.cart.addons,
          totals,
          buyer,
          calendarUrl: json.calendarUrl,
          at: Date.now(),
        }));
      } catch {}

      onCleared && onCleared();
      goToThankYou();
    } catch (err) {
      // Network failures (offline, DNS, CORS) reach this branch with the bare
      // "Failed to fetch" message — surface it instead of swallowing it.
      const msg = err?.message || "Network error. Please check your connection or email hi@vestalhub.com directly.";
      console.error("[checkout:submit] failed:", err);
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="CHECKOUT · SOW WITHIN 1 BUSINESS DAY"
        title={<>Send your brief. <em>SOW arrives tomorrow.</em></>}
        lead="No charge until you sign. No discovery call required. A senior operator drafts your Statement of Work directly from this brief."
        accent="#3B69FF"
      />
      <section className="page-body">
        <div className="wrap checkout-grid">
          <form className="checkout-form" onSubmit={submit}>
            <div className="checkout-block">
              <span className="page-section-eye">01 · YOU</span>
              <div className="checkout-row">
                <label>
                  <span>Full name *</span>
                  <input type="text" required value={buyer.name}
                         onChange={e => setBuyer({...buyer, name: e.target.value})}
                         placeholder="Jane Doe"/>
                </label>
                <label>
                  <span>Work email *</span>
                  <input type="email" required value={buyer.email}
                         onChange={e => setBuyer({...buyer, email: e.target.value})}
                         placeholder="jane@acme.com"/>
                </label>
              </div>
              <label>
                <span>Your role</span>
                <input type="text" value={buyer.role}
                       onChange={e => setBuyer({...buyer, role: e.target.value})}
                       placeholder="Head of RevOps"/>
              </label>
            </div>

            <div className="checkout-block">
              <span className="page-section-eye">02 · COMPANY</span>
              <div className="checkout-row">
                <label>
                  <span>Legal company name *</span>
                  <input type="text" required value={company.legalName}
                         onChange={e => setCompany({...company, legalName: e.target.value})}
                         placeholder="Acme Inc."/>
                </label>
                <label>
                  <span>Website</span>
                  <input type="url" value={company.website}
                         onChange={e => setCompany({...company, website: e.target.value})}
                         placeholder="https://acme.com"/>
                </label>
              </div>
              <div className="checkout-row">
                <label>
                  <span>Country</span>
                  <select value={company.country}
                          onChange={e => setCompany({...company, country: e.target.value})}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span>Team size</span>
                  <select value={company.size}
                          onChange={e => setCompany({...company, size: e.target.value})}>
                    {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            </div>

            <div className="checkout-block">
              <span className="page-section-eye">03 · ENGAGEMENT</span>
              <div className="checkout-row">
                <label>
                  <span>Preferred kickoff</span>
                  <select value={engagement.kickoff}
                          onChange={e => setEngagement({...engagement, kickoff: e.target.value})}>
                    {KICKOFF_WINDOWS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
                <label>
                  <span>Primary channel</span>
                  <select value={engagement.channel}
                          onChange={e => setEngagement({...engagement, channel: e.target.value})}>
                    <option value="Slack">Slack (we open a shared channel)</option>
                    <option value="Email">Email only</option>
                    <option value="Teams">Microsoft Teams</option>
                  </select>
                </label>
              </div>
              <label>
                <span>Anything we should know? <em className="opt">(optional, but useful)</em></span>
                <textarea rows={5} value={engagement.brief}
                          onChange={e => setEngagement({...engagement, brief: e.target.value})}
                          placeholder="Current HubSpot tier, integrated tools, hard deadlines, anything that could affect scope. The more specific, the tighter the SOW."/>
              </label>
            </div>

            {error && <div className="checkout-err">{error}</div>}

            <button type="submit" className="btn btn-primary checkout-submit" disabled={submitting}>
              {submitting ? "Sending brief…" : <>Send brief — SOW arrives within 1 business day <Icon name="arrow" size={14}/></>}
            </button>

            <div className="checkout-trust">
              <span><Icon name="shield" size={12}/> Fixed price</span>
              <span><Icon name="check"  size={12}/> No charge until SOW signed</span>
              <span><Icon name="zap"    size={12}/> Senior operator only</span>
            </div>
          </form>

          <aside className="checkout-summary">
            <span className="page-section-eye">YOUR ORDER</span>
            <div className="checkout-summary-card">
              {items.map(it => (
                <div className="cs-line" key={it._uid || it.id}>
                  <div className="cs-line-name">
                    <b>{it.name}</b>
                    <span>{it.category}{it.duration ? " · " + it.duration : ""}</span>
                  </div>
                  <div className="cs-line-p">
                    ${it.price.toLocaleString()}{it.duration === "monthly" ? <em>/mo</em> : null}
                  </div>
                </div>
              ))}
              {addonList.map(a => (
                <div className="cs-line cs-addon" key={a.id}>
                  <div className="cs-line-name">
                    <b>+ {a.name}</b>
                    <span>Add-on</span>
                  </div>
                  <div className="cs-line-p">+${a.price.toLocaleString()}</div>
                </div>
              ))}
              <div className="cs-totals">
                <div className="r"><span>Subtotal</span><span>${totals.subtotal.toLocaleString()}</span></div>
                {totals.addons > 0   && <div className="r"><span>Add-ons</span><span>+${totals.addons.toLocaleString()}</span></div>}
                {totals.discount > 0 && <div className="r save"><span>Multi-service discount</span><span>−${totals.discount.toLocaleString()}</span></div>}
                <div className="r t"><span>Total</span><span>${totals.total.toLocaleString()}</span></div>
              </div>
            </div>

            <div className="checkout-help">
              <b>Need to change something?</b>
              <p>Edit your cart from any page using the bag icon, or <a href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>email a custom brief</a> if your work doesn't fit a published SKU.</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

/* ============ Thank you ============ */
const ThankYouPage = () => {
  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("vh:lastOrder");
      if (raw) setSnapshot(JSON.parse(raw));
    } catch {}
  }, []);

  const calendarUrl =
    snapshot?.calendarUrl ||
    "https://grow.vestalhub.com/meetings/vestalhub/hubspot-call";

  return (
    <>
      <PageHero
        eyebrow="BRIEF RECEIVED"
        title={<>Your SOW arrives <em>within one business day.</em></>}
        lead={snapshot?.buyer?.email
          ? `We'll email it to ${snapshot.buyer.email}. Nothing has been charged. Signing the SOW kicks off the engagement.`
          : "We'll email it to you shortly. Nothing has been charged. Signing the SOW kicks off the engagement."}
        accent="#3B69FF"
      />
      <section className="page-body">
        <div className="wrap thankyou-grid">
          <div className="thankyou-main">
            <div className="thankyou-card">
              <span className="page-section-eye">WHAT HAPPENS NEXT</span>
              <ol className="thankyou-steps">
                <li>
                  <span className="ts-n">01</span>
                  <div>
                    <b>We draft your SOW</b>
                    <p>A senior operator pulls scope, deliverables, and timeline from the catalog and tailors them to your brief. No templates copy-pasted at you.</p>
                  </div>
                </li>
                <li>
                  <span className="ts-n">02</span>
                  <div>
                    <b>You receive it for signature</b>
                    <p>Usually by end of next business day. Reply with edits or sign as-is — there's no obligation either way.</p>
                  </div>
                </li>
                <li>
                  <span className="ts-n">03</span>
                  <div>
                    <b>Kickoff within 24h of signing</b>
                    <p>Slack channel opened, calendar invites sent, work begins. First milestone usually lands within a week.</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="thankyou-card thankyou-cal">
              <div>
                <span className="page-section-eye">OPTIONAL · 20 MIN</span>
                <h3>Want to talk before the SOW arrives?</h3>
                <p>Some teams want to compare options or sanity-check scope before signing. Grab time with a senior operator — no sales script.</p>
              </div>
              <a className="btn btn-primary" href={calendarUrl} target="_blank" rel="noopener noreferrer">
                Book 20 min <Icon name="arrow" size={14}/>
              </a>
            </div>
          </div>

          <aside className="thankyou-side">
            {snapshot ? (
              <div className="thankyou-card">
                <span className="page-section-eye">YOUR ORDER</span>
                <div className="thankyou-recap">
                  {snapshot.items.map(it => (
                    <div className="cs-line" key={it.id + (it.name || "")}>
                      <div className="cs-line-name">
                        <b>{it.name}</b>
                        <span>{it.category}{it.duration ? " · " + it.duration : ""}</span>
                      </div>
                      <div className="cs-line-p">${it.price.toLocaleString()}{it.duration === "monthly" ? <em>/mo</em> : null}</div>
                    </div>
                  ))}
                  {(snapshot.addons || []).map(a => (
                    <div className="cs-line cs-addon" key={a.id}>
                      <div className="cs-line-name"><b>+ {a.name}</b><span>Add-on</span></div>
                      <div className="cs-line-p">+${a.price.toLocaleString()}</div>
                    </div>
                  ))}
                  <div className="cs-totals">
                    {snapshot.totals.discount > 0 && (
                      <div className="r save"><span>Multi-service discount</span><span>−${snapshot.totals.discount.toLocaleString()}</span></div>
                    )}
                    <div className="r t"><span>Total</span><span>${snapshot.totals.total.toLocaleString()}</span></div>
                  </div>
                </div>
                <p className="thankyou-fwd">Forward this to a stakeholder if you need approval — the SOW will arrive at <b>{snapshot.buyer?.email}</b>.</p>
              </div>
            ) : (
              <div className="thankyou-card">
                <span className="page-section-eye">WE'RE ON IT</span>
                <p>If you don't see a confirmation email within a few minutes, check spam or email <a href="mailto:hi@vestalhub.com">hi@vestalhub.com</a> directly.</p>
              </div>
            )}

            <div className="thankyou-card thankyou-card-alt">
              <span className="page-section-eye">WHILE YOU WAIT</span>
              <a href="/about" onClick={(e) => { e.preventDefault(); goToAbout(); }} className="contact-quick">
                <span><b>Who you'll work with</b><em>Senior operators only — no juniors, no rotations</em></span>
                <Icon name="arrow" size={16}/>
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

/* ============ Not found (404) ============ */
const NotFoundPage = ({ path }) => (
  <>
    <PageHero
      eyebrow="404 · PAGE NOT FOUND"
      title={<>This page <em>doesn't exist.</em></>}
      lead={path
        ? `Nothing lives at ${path}. It may have moved, or the link may be wrong.`
        : "It may have moved, or the link may be wrong."}
      accent="#3B69FF"
    />
    <section className="page-body">
      <div className="wrap" style={{maxWidth: 720, textAlign: "center", padding: "8px 0 40px"}}>
        <p style={{color: "var(--muted)", fontSize: "var(--fs-xl)", lineHeight: 1.55, marginBottom: 28}}>
          Try one of these instead — or email <a href="mailto:hi@vestalhub.com" style={{color: "var(--brand)", textDecoration: "none"}}>hi@vestalhub.com</a> if you can't find what you're looking for.
        </p>
        <div style={{display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 32}}>
          <a className="btn btn-primary"  href="/shop"    onClick={(e) => { e.preventDefault(); goToShop();    }}>Browse all services <Icon name="arrow" size={14}/></a>
          <a className="btn btn-secondary" href="/contact" onClick={(e) => { e.preventDefault(); goToContact(); }}>Contact us</a>
          <a className="btn btn-secondary" href="/"        onClick={(e) => { e.preventDefault(); goHome();      }}>Back to home</a>
        </div>
      </div>
    </section>
  </>
);

/* ============ Cart math (shared between drawer + checkout) ============ */
const cartMath = (items, addons) => {
  const sub = items.reduce((s, i) => s + i.price, 0);
  const add = Object.keys(addons || {})
    .filter(k => addons[k])
    .reduce((s, k) => s + (ADDONS.find(x => x.id === k)?.price || 0), 0);
  const bundleSaving = items.length >= 3 ? Math.round(sub * 0.1) : 0;
  const total = sub + add - bundleSaving;
  return { subtotal: sub, addons: add, discount: bundleSaving, total };
};

/* ============ Cart drawer ============ */
const Cart = ({ open, items, addons, onClose, onRem, onTog, onCheckout }) => {
  const { subtotal: sub, addons: add, discount: bundleSaving, total } = cartMath(items, addons);
  return (
    <>
      <div className={`scrim ${open ? "on" : ""}`} onClick={onClose}/>
      <aside className={`drawer ${open ? "on" : ""}`}>
        <div className="dr-head">
          <h3>Your cart <span style={{color:"var(--muted)", fontWeight:400, fontSize:"var(--fs-base)"}}>({items.length})</span></h3>
          <button className="icon-btn" onClick={onClose}><Icon name="close"/></button>
        </div>
        <div className="dr-body">
          {items.length === 0 ? (
            <div className="empty">
              <div className="ic"><Icon name="bag" size={24}/></div>
              <div className="big">Your cart is empty</div>
              <p>Browse services to start building your order</p>
            </div>
          ) : (<>
            {items.map(it => (
              <div className="ci" key={it._uid}>
                <div className="ci-vis">{it.num}</div>
                <div>
                  <h4>{it.name}</h4>
                  <div className="s">{it.category} · {it.duration}</div>
                  <div className="r">
                    <div className="p">${it.price.toLocaleString()}{it.duration === "monthly" ? "/mo" : ""}</div>
                    <button className="rm" onClick={() => onRem(it._uid)}>Remove</button>
                  </div>
                </div>
              </div>
            ))}
            <div className="addons">
              <h5>Add to your order</h5>
              {ADDONS.map(a => (
                <div key={a.id} className={`addon ${addons[a.id] ? "on" : ""}`} onClick={() => onTog(a.id)}>
                  <div className="addon-chk"><Icon name="check" size={12}/></div>
                  <div className="addon-n">{a.name}<div className="d">{a.sub}</div></div>
                  <div className="addon-p">+${a.price}</div>
                </div>
              ))}
            </div>
          </>)}
        </div>
        {items.length > 0 && (
          <div className="dr-foot">
            <div className="tot">
              <div className="r"><span>Subtotal</span><span>${sub.toLocaleString()}</span></div>
              {add > 0 && <div className="r"><span>Add-ons</span><span>+${add.toLocaleString()}</span></div>}
              {bundleSaving > 0 && <div className="r save"><span>Multi-service discount</span><span>−${bundleSaving.toLocaleString()}</span></div>}
              <div className="r t"><span>Total</span><span>${total.toLocaleString()}</span></div>
            </div>
            <button
              className="btn btn-primary checkout"
              onClick={() => { onCheckout && onCheckout(); }}
            >Checkout → SOW in 24h <Icon name="arrow" size={14}/></button>
            <div className="trust-mini">
              <span className="i"><Icon name="shield" size={12}/> Fixed price</span>
              <span className="i"><Icon name="check" size={12}/> No charge until SOW signed</span>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

/* ============ Tweaks ============ */
const Tweaks = ({ visible, state, onChange }) => {
  if (!visible) return null;
  const accents = [
    { id: "orange",  c: "#FF3B00" },
    { id: "blue",    c: "#0042FF" },
    { id: "ink",     c: "#0A0A0A" },
    { id: "green",   c: "#0E8A4F" },
    { id: "purple",  c: "#7C3AED" },
  ];
  const heroes = [{id:"serif",n:"Italic serif"},{id:"solid",n:"Solid block"},{id:"plain",n:"Plain"}];
  return (
    <div className="tweaks on">
      <h4>Tweaks</h4>
      <div>
        <div className="tw-l">Accent color</div>
        <div className="tw-chips">
          {accents.map(b => (
            <button key={b.id} className={`tw-chip ${state.accent === b.id ? "on" : ""}`} onClick={() => onChange({accent:b.id})}>
              <span style={{display:"inline-block", width:9, height:9, background:b.c, borderRadius:2, marginRight:6, verticalAlign:"middle"}}/>
              {b.id}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="tw-l">Hero headline</div>
        <div className="tw-chips">
          {heroes.map(r => (
            <button key={r.id} className={`tw-chip ${state.hero === r.id ? "on" : ""}`} onClick={() => onChange({hero:r.id})}>{r.n}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============ App ============ */
const App = () => {
  const [cart, setCart]     = useState([]);
  const [open, setOpen]     = useState(false);
  const [addons, setAddons] = useState({});
  const [state, setState]   = useState(window.__TWEAKS_V4__ || {accent:"vestal", hero:"gradient"});
  const [editOn, setEditOn] = useState(false);

  useEffect(() => {
    // Remove SSR fallback blocks once React has mounted. Supports both the
    // legacy id (#ssr-product) and the generalized one (#vh-ssr) emitted by
    // api/page.js for all SSR'd routes.
    const ssrEl = document.getElementById("vh-ssr") || document.getElementById("ssr-product");
    if (ssrEl) ssrEl.remove();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const m = {
      vestal: ["#3B69FF", "#5C84FF", "rgba(59,105,255,0.14)"],
      blue:   ["#3B69FF", "#5C84FF", "rgba(59,105,255,0.14)"],
      cyan:   ["#22D3EE", "#67E8F9", "rgba(34,211,238,0.14)"],
      orange: ["#FF7A29", "#FF9A5C", "rgba(255,122,41,0.14)"],
      purple: ["#7C5CFC", "#9C82FF", "rgba(124,92,252,0.14)"],
      green:  ["#22C55E", "#4ADE80", "rgba(34,197,94,0.14)"],
      pink:   ["#F472B6", "#F9A8D4", "rgba(244,114,182,0.14)"],
      ink:    ["#F4F6FB", "#FFFFFF", "rgba(255,255,255,0.10)"],
    };
    const [b, bi, bs] = m[state.accent] || m.vestal;
    root.style.setProperty("--brand", b);
    root.style.setProperty("--brand-ink", bi);
    root.style.setProperty("--brand-soft", bs);
    document.querySelectorAll(".hero h1 .hl").forEach(el => {
      el.style.cssText = "";
    });
  }, [state]);

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === "__activate_edit_mode")   setEditOn(true);
      if (d.type === "__deactivate_edit_mode") setEditOn(false);
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ type: "__edit_mode_available" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const update = (patch) => {
    const next = {...state, ...patch};
    setState(next);
    window.parent.postMessage({ type: "__edit_mode_set_keys", edits: patch }, "*");
  };

  const add = (p) => { setCart(c => [...c, {...p, _uid: p._uid || `${p.id}-${Date.now()}`}]); setOpen(true); };
  const rem = (uid) => setCart(c => c.filter(i => i._uid !== uid));
  const tog = (id)  => setAddons(a => ({...a, [id]: !a[id]}));

  const route = useRoute();
  useDocumentMeta(route);

  // Stripped-chrome routes render their own minimal header + footer.
  // Keeping the cart drawer mounted is fine — it stays closed unless invoked,
  // and the LP intentionally has no surfaces that add to it.
  const isStrippedChrome = route.name === "consultation";

  return (
    <div data-screen-label="Vestal Hub">
      {!isStrippedChrome && <Header />}
      {route.name === "product" ? (
        <ProductPage id={route.id} onAdd={add} />
      ) : route.name === "category" ? (
        <CategoryPage id={route.id} onAdd={add} />
      ) : route.name === "shop" ? (
        <ShopPage key={route.q || ""} onAdd={add} initialCat={route.cat} initialSort={route.sort} initialQ={route.q} />
      ) : route.name === "custom" ? (
        <CustomPage onAdd={add} />
      ) : route.name === "wlabel" ? (
        <WhiteLabelPage />
      ) : route.name === "saas" ? (
        <HubSpotForSaasPage />
      ) : route.name === "academy-revops" ? (
        <AcademyRevenueOperationsPage />
      ) : route.name === "onboarding-packages" ? (
        <OnboardingPackagesPage />
      ) : route.name === "outbound-infrastructure" ? (
        <OutboundInfrastructurePage />
      ) : route.name === "outbound-optimization" ? (
        <OutboundOptimizationPage />
      ) : route.name === "admin" ? (
        <HubSpotAdminPage />
      ) : route.name === "plans" ? (
        <PlansPage />
      ) : route.name === "support" ? (
        <HubSpotSupportPage />
      ) : route.name === "home" ? (
        <HomeV2Page />
      ) : route.name === "techsupport" ? (
        <HubSpotTechnicalSupportPage />
      ) : route.name === "consultation" ? (
        <ConsultationPage />
      ) : route.name === "checkout" ? (
        <CheckoutPage
          items={cart}
          addons={addons}
          onCleared={() => { setCart([]); setAddons({}); setOpen(false); }}
        />
      ) : route.name === "thankyou" ? (
        <ThankYouPage />
      ) : route.name === "contact" ? (
        <ContactPage />
      ) : route.name === "book" ? (
        <BookingPage />
      ) : route.name === "about" ? (
        <AboutPage />
      ) : route.name === "process" ? (
        <ProcessPage />
      ) : route.name === "partners" ? (
        <PartnersPage />
      ) : route.name === "careers" ? (
        <CareersPage />
      ) : route.name === "terms" ? (
        <LegalPage kind="terms" />
      ) : route.name === "privacy" ? (
        <LegalPage kind="privacy" />
      ) : route.name === "cookies" ? (
        <LegalPage kind="cookies" />
      ) : route.name === "learn" ? (
        <LearnHub />
      ) : route.name === "pillar" ? (
        <PillarPage />
      ) : route.name === "region" ? (
        <RegionPage id={route.id} />
      ) : route.name === "services" ? (
        <ServicesIndexPage />
      ) : route.name === "service" ? (
        <ServicePage id={route.id} />
      ) : route.name === "industry" ? (
        <IndustryPage id={route.id} />
      ) : route.name === "migration" ? (
        <MigrationPage id={route.id} />
      ) : route.name === "integration" ? (
        <IntegrationPage id={route.id} />
      ) : route.name === "glossary" ? (
        <GlossaryPage id={route.id} />
      ) : route.name === "case-studies" ? (
        <CaseStudiesIndexPage />
      ) : route.name === "case-study" ? (
        <CaseStudyPage id={route.id} />
      ) : route.name === "tools" ? (
        <ToolsIndexPage />
      ) : route.name === "tool" ? (
        <ToolPage id={route.id} />
      ) : route.name === "notfound" ? (
        <NotFoundPage path={route.path} />
      ) : (
        <HomeV2Page />
      )}
      {!isStrippedChrome && <Footer />}
      {open && (
        <Cart
          open={open}
          items={cart}
          addons={addons}
          onClose={() => setOpen(false)}
          onRem={rem}
          onTog={tog}
          onCheckout={() => { setOpen(false); goToCheckout(); }}
        />
      )}
      <Tweaks visible={editOn} state={state} onChange={update}/>
    </div>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
